export type Locale = "en" | "es" | "pt";

export const messages = {
  en: {
    languageName: "English",
    nav: {
      features: "Features",
      workflow: "How it works",
      courtIntel: "Court Intelligence",
      foundingProgram: "Founding Venues",
      plans: "Plans",
      organizers: "Founding Organizers",
      login: "Log in",
      signup: "Sign up",
      authChooser: {
        loginTitle: "Log in to WhoPlays",
        loginDescription: "Choose the path that matches how you use WhoPlays.",
        signupTitle: "Create your WhoPlays account",
        signupDescription: "Choose the path that matches how you plan to use WhoPlays.",
        playerLabel: "Player",
        playerDescription: "Organize games, track matches, and check live venue visibility.",
        operatorLabel: "Venue owner or operator",
        operatorDescription: "Continue into Court Intelligence onboarding for your venue.",
        foundingLabel: "Founding Venue Program",
        foundingDescription: "Applying for one of the limited launch spots? Start here.",
        foundingCta: "See founding venues",
        closeLabel: "Close",
      },
    },
    auth: {
      otp: {
        loginTitle: "Log in to WhoPlays",
        signupTitle: "Sign up for WhoPlays",
        loginDescription: "Use your email or mobile number to receive a one-time passcode.",
        signupDescription: "Create your account with an email or mobile number. We will send you a one-time passcode to confirm.",
        displayNameLabel: "Display name",
        emailTab: "Email",
        phoneTab: "Phone",
        emailAddressLabel: "Email address",
        mobileNumberLabel: "Mobile number",
        countryCodeLabel: "Country code",
        smsHint: "Include your area code. We will send a 6 digit code via SMS.",
        captchaHint: "Protected by reCAPTCHA. Verification runs automatically.",
        errorCaptcha: "Unable to verify that you are human. Please try again.",
        verificationCodeLabel: "Verification code",
        resendIn: "Resend in {seconds}s",
        resendCode: "Resend code",
        errorPhoneRegionOnly: "Phone sign-in is US/Canada only. Please use email to continue.",
        errorSendCode: "Unable to send code.",
        errorStartVerification: "Unable to start verification.",
        infoCodeSent: "Verification code sent. Enter the 6 digit code to continue.",
        infoCodeResent: "Code resent. Check your email or phone.",
        errorResendCode: "Unable to resend code.",
        errorVerifyCode: "Unable to verify code.",
        submitWait: "Please wait...",
        submitSendCode: "Send code",
        submitVerifyContinue: "Verify and continue",
        socialDivider: "Or continue with",
        continueWithGoogle: "Continue with Google",
        continueWithApple: "Continue with Apple",
        loginNeedAccount: "Need an account?",
        loginNeedAccountCta: "Sign up",
        signupHasAccount: "Already have an account?",
        signupHasAccountCta: "Log in",
        userNotFound: "We couldn't find an account with that email or phone number.",
      },
    },
    adminVenues: {
      back: "Back to My Venues",
      title: "Pending venue verifications",
      subtitle: "Review newly submitted venues before publishing them.",
      loading: "Loading pending venues…",
      empty: "No venues are waiting for verification.",
      countLabel: "Pending venues: {count}",
      refresh: "Refresh list",
      error: "Unable to load pending venues.",
      reviewCta: "Open review",
      hoursHeading: "Hours of operation",
      courtsHeading: "Court breakdown",
      totalCourts: "{count} total courts",
      amenitiesHeading: "Amenities",
      openPlayHeading: "Open play sessions today",
      pendingBadge: "Pending verification",
    },
    adminAccess: {
      back: "Back to dashboard",
      title: "Access & Entitlements",
      subtitle: "Grant user roles, court roles, and subscription access.",
      loading: "Loading access data…",
      error: "Unable to load admin summary.",
      refresh: "Refresh",
      searchPlaceholder: "Search users or courts",
      usersEmpty: "No users match the current filters.",
      courtsEmpty: "No courts match the current filters.",
      adminOnly: "Admins only.",
      unknownCourt: "Unknown court",
      tabs: {
        users: "Users",
        courts: "Courts",
      },
      filters: {
        all: "All",
        roles: "Roles",
        courtRoles: "Court roles",
        subscriptions: "Subscriptions",
      },
      roles: {
        heading: "Roles",
        empty: "No roles yet.",
        add: "Add role",
        select: "Select a role",
      },
      courtRoles: {
        heading: "Court roles",
        empty: "No court roles yet.",
        add: "Add court role",
        update: "Update role",
        selectCourt: "Select a court",
        selectUser: "Select a user",
        roleLabel: "Role",
        searchPlaceholder: "Search courts",
        searchUsersPlaceholder: "Search users",
      },
      access: {
        heading: "Subscription access",
        empty: "No access grants.",
        add: "Add access",
        update: "Update access",
        selectPlan: "Select a plan",
        expiresAt: "Expires at",
      },
      actions: {
        remove: "Remove",
        edit: "Edit",
        close: "Close",
      },
      rules: {
        adminOnly: "Only super admins can add or remove the admin role.",
      },
    },
    adminStats: {
      back: "Back to dashboard",
      title: "App Stats",
      subtitle: "Monitor activity and growth.",
      loading: "Loading stats...",
      error: "Unable to load stats summary.",
      adminOnly: "Admins only.",
      periodLabel: "Period",
      controls: {
        period: "Period",
        timezone: "Timezone",
        topN: "Top N",
        pendingLimit: "Pending limit",
        apply: "Apply",
        periodLabel: "Selected period",
      },
      sections: {
        lifetime: "Lifetime",
        period: "Period",
        activation: "Activation funnel",
        events: "Events",
        engagement: "Engagement",
        geo: "Geo",
        pendingVenues: "Pending venues",
        subscriptions: "Subscriptions",
        subscriptionsPeriod: "Subscriptions (Period)",
        downloadsByOs: "Downloads by OS",
      },
      metrics: {
        uniqueUsers: "Users",
        uniqueDownloads: "Downloads",
        profilesCreated: "Profiles created",
        primaryCourtSelected: "Primary court selected",
        sessionsCount: "Sessions",
        eventsCount: "Events",
        sessionsDuration: "Sessions duration",
        sessionsDurationLifetime: "Lifetime duration",
      },
      empty: {
        downloads: "No downloads.",
      },
      funnel: {
        ofDownloads: "of downloads",
        ofPrevious: "of previous",
      },
    },
    toggles: {
      theme: "Theme",
      language: "Language",
      light: "Light",
      dark: "Dark",
      system: "System",
    },
    hero: {
      eyebrow: "Players, venue owners, and communities connected",
      title: "The pickleball platform that keeps your courts buzzing",
      description:
        "WhoPlays helps players organize games and helps venues operate smarter with live busyness, wait-time, and court-availability signals from Court Intelligence.",
      primaryCta: "Start on the web",
      primaryHref: "/auth/signup",
      secondaryCta: "Get the mobile app",
      secondaryHref: "/app-stores",
      badgesLabel: "Also available on iOS and Android",
      trust: "Trusted by players, clubs, and city programs.",
    },
    stats: [
      { value: "Pickleball court feed", label: "See when nearby pickleball courts heat up in real time" },
      { value: "SMS invites", label: "Fill pickleball ladders and drills with one-tap texting" },
      { value: "Pickleball stats", label: "Log DUPR-friendly pickleball matches and track progress" },
    ],
    appShell: {
      tabs: {
        realtime: "Real-Time",
        network: "My Network",
        competitions: "Competitions",
        matches: "Matches",
        rank: "Rank",
        messages: "MSG Center",
      },
      matches: {
        title: "Matches",
        pending: "Pending",
        addMatch: "Add Match",
        loadMore: "Load more",
        loading: "Loading matches...",
        empty: "No matches to show.",
        segments: {
          owned: "Owned",
          mygroup: "My Group",
          mycourts: "My Courts",
        },
        errors: {
          loadFailed: "Unable to load matches right now.",
          saveFailed: "Unable to save the match.",
        },
        editor: {
          title: "New Match",
          subtitle: "Add scores and players.",
          cancel: "Cancel",
          save: "Save",
          saving: "Saving...",
          format: "Format",
          singles: "Singles",
          doubles: "Doubles",
          winner: "Winner",
          selectWinner: "Select winner",
          team1: "Team 1",
          team2: "Team 2",
          player: "Player username",
          score: "Score",
        },
        validation: {
          missingPlayers: "Please add all required players.",
          missingWinner: "Please select a winner.",
        },
      },
      rank: {
        title: "Rank",
        loading: "Loading ranks...",
        empty: "No ranking data to show.",
        segments: {
          wprank: "WP Rank",
          overall: "Overall",
          h2h: "H2H",
        },
        filters: {
          skillAll: "All skills",
          singles: "Singles",
          doubles: "Doubles",
          week: "Week",
          month: "Month",
          all: "All time",
          allCourts: "All courts",
          groupNone: "No group filter",
          groupMyGroup: "My Group",
          groupFollowing: "Following",
          formatAll: "All formats",
          today: "Today",
          yesterday: "Yesterday",
          lastWeek: "Last week",
          lastMonth: "Last month",
          threeMonths: "3 months",
          sixMonths: "6 months",
          year: "1 year",
          fiveYears: "5 years",
          allTime: "All time",
        },
      labels: {
        wins: "Wins",
        losses: "Losses",
        matches: "Matches",
        winPct: "Win%",
        rankLabel: "Rank",
      },
    },
      messagesTab: {
        title: "Message Center",
        empty: "No messages yet.",
        clearAll: "Clear all",
        markAllRead: "Mark all as read",
        delete: "Delete",
        read: "Mark read",
        unread: "Mark unread",
        receivedAt: "Received",
      },
      socket: {
        schedule: "Schedule updated",
        match: "Match update",
        ping: "New ping received",
        dupr: "DUPR rating updated",
        generic: "New notification",
        subscriptionTitle: "Subscription updated",
        subscriptionBody: "Your subscription status has been refreshed.",
        matchUnanimousTitle: "Match unanimously accepted",
        matchUnanimousBody: "Fantastic news! A match you played has been officially accepted by all players. It's now official!",
        matchAcceptedTitle: "Player accepted match",
        matchAcceptedBody: "{player} has accepted the match! Waiting for other players to confirm.",
        matchPendingTitle: "Pending matches",
        matchPendingBody: "New pending match(es) result! Confirm and make it official. Check now to contribute to your ranking.",
        duprUpdatedTitle: "DUPR rating updated",
        duprUpdatedBody: "Singles: {singles}\nDoubles: {doubles}",
        questionTitle: "New schedule question",
      },
      subscriptions: {
        title: "Subscriptions",
        subtitle: "Choose a plan to unlock scheduling power with WhoPlays.",
        detailsLink: "View full plan details",
        currentPlan: "Current plan",
        statusLabel: "Status",
        free: "Free",
        active: "Active",
        select: "Select",
        checkout: "Checkout with Stripe",
        close: "Close",
        manage: "Manage / cancel in Stripe",
        manageHint: "We'll open Stripe's customer portal. If you don't see your subscription, use your receipt email.",
        providerManaged: "Managed by {provider}",
        providerManagedHint: "To manage or cancel this subscription, open {provider}.",
        error: "We couldn't start checkout. Please try again in a moment.",
        successTitle: "Checkout completed",
        successToast: "Subscription checkout finished. Refreshing your access…",
        cancelTitle: "Checkout canceled",
        cancelToast: "You canceled Stripe checkout.",
        plans: {
          "com.whoplays.scheduling.basic.monthly": {
            label: "Scheduler Basic (Monthly)",
            price: "Monthly billing",
          },
          "com.whoplays.scheduling.basic.yearly": {
            label: "Scheduler Basic (Yearly)",
            price: "Yearly billing",
          },
          "com.whoplays.scheduling.pro.monthly": {
            label: "Scheduler Pro (Monthly)",
            price: "Monthly billing",
          },
          "com.whoplays.scheduling.pro.yearly": {
            label: "Scheduler Pro (Yearly)",
            price: "Yearly billing",
          },
        },
      },
      courtIntelligenceResume: {
        title: "Finish Court Intelligence setup?",
        description:
          "We found saved progress in Court Intelligence onboarding. Choose how you want to continue.",
        savedStep: "Saved step: {step}",
        stepNames: {
          account: "Account",
          subscription: "Subscription",
          venues: "Venues",
        },
        continuePlayer: "Continue as player",
        continuePlayerHint: "Keep your current Court Intelligence progress and stay in player mode.",
        continuePlayerReset: "Continue as player and reset setup",
        continuePlayerResetHint:
          "Delete saved Court Intelligence progress. Next time, setup will start from step 1.",
        continueOnboarding: "Continue Court Intelligence onboarding",
        continueOnboardingHint: "Go to your saved step and finish setup.",
        resetError: "Unable to reset Court Intelligence setup right now. Please try again.",
      },
      courtIntelligenceUi: {
        sections: {
          profile: "Profile",
          community: "Community Ops",
          venues: "Venues",
          cameras: "Cameras",
          initialization: "Initialization",
          logs: "Logs",
          debug: "Diagnostics",
          stats: "Stats",
          security: "Security",
          subscription: "Subscription",
        },
        workspace: {
          loading: "Loading Court Intelligence…",
          tipsTitle: "Court Intelligence Tips",
          tipsProgress: "Tip {current} of {total}",
          tipsBack: "Back",
          tipsNext: "Next",
          tipsClose: "Close",
          openMenu: "Open Court Intelligence menu",
          closeMenu: "Close navigation",
          switchToPlayerMode: "Switch to player mode",
          brand: {
            alt: "Court Intelligence",
            eyebrow: "Court",
            title: "Intelligence",
          },
          accessRequired: {
            title: "Court Intelligence access required",
            description: "Your account does not have Court Intelligence subscription access or court-level permissions yet.",
            startSetup: "Start setup",
            backToPlayerMode: "Back to player mode",
          },
          readOnly: {
            venuesOwnerOnlyTitle: "Venue management is owner-only",
            venuesOwnerOnlyDescription: "Only subscription owners can claim or create venues in Court Intelligence mode.",
            camerasRestrictedTitle: "Cameras are restricted",
            camerasRestrictedDescription: "Only court owners and court admins can manage camera setup.",
            statsSubscriptionRequiredTitle: "Stats require an active Court Intelligence subscription",
            statsSubscriptionRequiredDescription:
              "Reactivate Court Intelligence subscription to view live stats and historical analytics.",
            securityOwnerOnlyTitle: "Security is owner-only",
            securityOwnerOnlyDescription: "Only court owners can grant or revoke court_admin/monitor permissions.",
            subscriptionOwnerOnlyTitle: "Subscription is owner-only",
            subscriptionOwnerOnlyDescription: "Only court owners can change or cancel Court Intelligence subscription.",
          },
          community: {
            title: "Community Ops",
            description:
              "Monitor-focused communication tools (alerts, board posts, and player-facing updates) will be available here.",
          },
          logs: {
            title: "Operational logs",
            description: "Review ML events, processing issues, and audit logs by venue. Log delete controls will also be available here.",
            labels: {
              court: "Court",
              notSelected: "No venue selected",
              notAvailable: "N/A",
              scope: "Scope",
              pageOffset: "Offset",
            },
            controls: {
              searchPlaceholder: "Search by venue name",
              cityPlaceholder: "City",
              statePlaceholder: "State",
              ownerPlaceholder: "Owner username",
              onlyWithCi: "Only venues with Court Intelligence",
              searchButton: "Search venues",
              venueLabel: "Venue",
              pageSizeLabel: "Page size",
              refreshButton: "Refresh logs",
              deleteSelectedButton: "Delete selected",
              deleteAllButton: "Delete all",
              prevPageButton: "Previous",
              nextPageButton: "Next",
            },
            loading: {
              courts: "Loading venues...",
              logs: "Loading logs...",
            },
            errors: {
              loadCourts: "Unable to load venue list.",
              loadLogs: "Unable to load logs.",
              deleteLogs: "Unable to delete logs.",
            },
            notices: {
              deletedCount: "{count} logs deleted.",
            },
            confirmDeleteAll: "Delete all logs for this venue?",
            table: {
              columns: {
                event: "Event",
                detail: "Detail",
                createdAt: "Created",
                deliveredAt: "Delivered",
              },
              empty: "No logs found for the selected venue.",
            },
          },
          debug: {
            title: "Diagnostics",
            description:
              "Admin-only tools for deep troubleshooting (ML payloads, debug artifacts, and processing traces) will be available here.",
          },
          stats: {
            title: "Court Intelligence analytics",
            description:
              "Use realtime portfolio status and historical readings to understand venue activity and mapped court occupancy.",
            labels: {
              court: "Court",
              notAvailable: "N/A",
            },
            controls: {
              searchPlaceholder: "Search by venue name",
              cityPlaceholder: "City",
              statePlaceholder: "State",
              ownerPlaceholder: "Owner username",
              onlyWithCi: "Only venues with Court Intelligence",
              searchButton: "Search venues",
              courtsLabel: "Venues in scope",
              fromLabel: "From",
              toLabel: "To",
              bucketLabel: "Bucket",
              bucketHour: "Hour",
              bucketDay: "Day",
              timezoneLabel: "Timezone",
              refreshRealtimeButton: "Refresh overview",
              refreshHistoryButton: "Apply history",
              downloadReadingsCsvButton: "Export readings CSV",
              downloadOccupancyCsvButton: "Export occupancy CSV",
            },
            loading: {
              courts: "Loading venues...",
              realtime: "Refreshing overview...",
              analytics: "Loading history...",
              occupancy: "Loading court occupancy...",
            },
            errors: {
              loadCourts: "Unable to load venue list.",
              loadRealtime: "Unable to load realtime portfolio.",
              loadAnalytics: "Unable to load readings history.",
              loadOccupancy: "Unable to load court occupancy history.",
            },
            cards: {
              peopleRightNow: "People right now",
              inUseOpen: "In use / playable",
              waitingPlayers: "Waiting players",
              avgQueueEta: "Avg wait ETA",
              avgBusyness: "Avg busyness",
              maxBusyness: "Peak busyness",
            },
            helpers: {
              courtAvailabilitySupport: "{supported} of {total} venues support court availability",
              waitMetricsSupport: "{supported} of {total} venues support wait metrics",
              scoredVenuesSupport: "{supported} of {total} venues are scored",
            },
            tables: {
              statusTitle: "Venue status",
              timelineTitle: "Readings trend",
              readingsSummary: "{count} readings in selected range",
              venueColumn: "Venue",
              busynessColumn: "Busyness",
              peopleColumn: "People",
              waitingColumn: "Waiting",
              queueColumn: "Wait ETA",
              timelineBucketColumn: "Bucket start",
              timelineReadingsColumn: "Readings",
              timelineAvgPeopleColumn: "Avg people / reading",
              timelineOccupancyColumn: "Activity occupancy",
              emptyRealtime: "No venues available for this scope.",
              emptyTimeline: "No readings history for the selected range.",
              occupancyTitle: "Court occupancy history",
              occupancySubtitle: "Per-court history is available only for mapped venues.",
              occupancyExplainer:
                "Each cell shows the percentage of readings in that time bucket where the court appeared occupied.",
              occupancyLegendOpen: "More open",
              occupancyLegendModerate: "Moderate",
              occupancyLegendBusy: "Busier",
              timelineAvailableSummary: "{available} of {total} venues have per-court history",
              emptyOccupancy: "No court occupancy history for the selected scope.",
              occupancyCourtColumn: "Court",
              occupancyRowSummary: "{percent} occupied",
              singlesDoublesSummary: "S: {singles} · D: {doubles}",
              avgPlayersWhenOccupiedShort: "Avg when occupied",
              maxPlayersWhenOccupiedShort: "Max when occupied",
              singlesShareShort: "Singles",
              doublesShareShort: "Doubles",
              singlesShareCompact: "S:",
              doublesShareCompact: "D:",
              readingsShort: "Readings",
              occupiedReadingsShort: "Occupied",
              occupancyMappingRequired: "Court mapping required to show per-court occupancy history.",
              occupancyUnavailable: "Per-court occupancy history is not available for this venue.",
            },
            badges: {
              courtAvailabilityEnabled: "Court availability enabled",
              waitMetricsEnabled: "Wait metrics enabled",
            },
          },
          liveBusyness: {
            title: "Live busyness",
            refresh: "Refresh",
            refreshing: "Refreshing…",
            loading: "Loading live busyness…",
            noData: "No live busyness data available right now.",
            loadError: "Unable to load live busyness.",
            courtLabel: "Court",
            previousVenue: "Previous venue",
            nextVenue: "Next venue",
            scoreLabel: "Busyness",
            scoreSecondary: "Score {score}",
            occupancyLabel: "In use / playable",
            occupancyLabelCompact: "In use",
            occupancyValueFormat: "{inUse} of {playable}",
            waitingLabel: "Waiting",
            etaLabel: "Queue ETA",
            peopleLabel: "People",
            confidenceLabel: "Confidence",
            confidenceInline: "Confidence {value}",
            modeInfoLabel: "Measurement mode details",
            estimateBadge: "Estimate",
            mappedCourtsLabel: "Mapped courts",
            queueAreasLabel: "Queue areas",
            detailsEyebrow: "Live measurement details",
            closeDetails: "Close",
            formulaTitle: "How this venue is measured",
            visibleKpisTitle: "Visible KPIs",
            hiddenKpisTitle: "Hidden KPIs",
            setupQualityTitle: "Setup quality",
            defaultsTitle: "Defaults",
            playersPerGameDefault: "Players per game: {value}",
            avgGameMinutesDefault: "Average game minutes: {value}",
            queueTargetMinutesDefault: "Queue target minutes: {value}",
            scoreMetric: "Busyness score",
            levelMetric: "Busyness band",
            weatherMetric: "Weather",
            updatedMetric: "Updated time",
            perCourtListMetric: "Per-court list",
            noneLabel: "None",
            modeCrowdOnly: "Crowd only",
            modeCapacityEstimated: "Capacity estimate",
            modeCourtMapped: "Court mapped",
            modeCourtAndQueue: "Court + queue",
            modeCrowdOnlyHelp: "Counts people in view for rough venue activity only.",
            modeCapacityEstimatedHelp: "Compares visible activity against configured court capacity for a quick estimate.",
            modeCourtMappedHelp: "Visible courts are mapped, so court availability is supported.",
            modeCourtAndQueueHelp: "Courts and queue areas are mapped, so wait metrics are supported.",
            formulaCrowdOnly: "No score. Uses observed people count from CI signal.",
            formulaCapacityEstimated: "score = clamp(totalPeople / (configuredCourts * playersPerGame)) * 100",
            formulaCourtMapped: "score = ((0.55 * courtSaturation) + (0.10 * backgroundPressure)) / 0.65 * 100",
            formulaCourtAndQueue: "score = ((0.55 * courtSaturation) + (0.35 * queuePressure) + (0.10 * backgroundPressure)) * 100",
            updatedAt: "Updated: {time}",
            notAvailable: "N/A",
          },
          security: {
            title: "Security and permissions",
            description: "Court owners can grant court-level access to other users as court_admin or monitor.",
            profileReadyInstruction:
              "Before granting access, ask the user to sign in to WhoPlays in Player mode and update their profile (name, nickname, and email or phone).",
            limitNotice: "Per venue limit: up to {adminLimit} court admins and {monitorLimit} monitors.",
            venueLabel: "Venue",
            roleLabel: "Role",
            addRoleButton: "Add role",
            addRoleModalTitle: "Add court role",
            addRoleModalSubtitle: "Select role and user for this venue.",
            searchLabel: "Search user",
            searchPlaceholder: "Name, nickname, or user ID",
            selectUserLabel: "User",
            selectUserPlaceholder: "Select a user",
            noUsersFound: "No users found for this search.",
            closeModal: "Close",
            cancelButton: "Cancel",
            noVenuesAvailable: "No venues available",
            adminsCountLabel: "Court admins",
            monitorsCountLabel: "Monitors",
            roleOwner: "Court owner",
            roleAdmin: "Court admin",
            roleMonitor: "Monitor",
            assignRole: "Assign role",
            updateRole: "Update role",
            removeRole: "Remove role",
            saving: "Saving…",
            loadingAssignments: "Loading assignments...",
            loadingUsers: "Searching users...",
            currentAssignmentsTitle: "Current assignments",
            noAssignments: "No role assignments yet.",
            assignmentInfoFallback: "Profile details pending.",
            ownerRoleReadOnly: "court_owner cannot be changed here.",
            userAlreadyHasRole: "{username} already has role {role}.",
            limitReachedAdmin: "Court admin limit reached (2). Remove one court_admin to continue.",
            limitReachedMonitor: "Monitor limit reached (3). Remove one monitor to continue.",
            errors: {
              loadRoles: "Unable to load assigned roles.",
              searchUsers: "Unable to search users.",
              assignRole: "Unable to assign role.",
              removeRole: "Unable to remove role.",
            },
            notices: {
              roleGranted: "Role {role} granted to {username}.",
              roleUpdated: "Role {role} updated for {username}.",
              roleRemoved: "Role removed for {username}.",
            },
          },
          subscription: {
            title: "Subscription",
            description: "Only court owners can modify or cancel Court Intelligence billing.",
            privacyTitle: "Privacy and player visibility",
            privacyItems: [
              "Cameras send snapshots outbound to the WhoPlays API. We do not connect to your local network.",
              "By default snapshots are processed and not stored, except optional debug retention controlled by your camera group settings.",
              "Players in WhoPlays US get free live busyness and wait indicators when your venue enables Court Intelligence.",
              "Camera hardware purchase and on-site installation are not included in Court Intelligence subscriptions.",
            ],
            privacyLinkLabel: "Read full Privacy Policy",
            currentPlanLabel: "Current plan",
            currentPlanBadge: "Current",
            currentSkuLabel: "Current subscription SKU",
            noneLabel: "None",
            statusLabel: "Status",
            notAvailableLabel: "N/A",
            expiresLabel: "Expires",
            usageTitle: "Capacity usage",
            usedVenuesLabel: "Used venues",
            maxVenuesLabel: "Venue limit",
            usedCamerasLabel: "Used cameras",
            maxCamerasLabel: "Camera limit",
            remainingVenuesLabel: "Remaining venues",
            remainingCamerasLabel: "Remaining cameras",
            unlimitedLabel: "Unlimited",
            addOnCountsTitle: "Add-on quantities",
            includedBaseLabel: "Included in base: {venues} venue and {cameras} cameras",
            activeVenueAddonsLabel: "Extra venue add-ons active",
            activeCameraAddonsLabel: "Extra camera add-ons active",
            safeRemoveVenueLabel: "Venue add-ons removable now",
            safeRemoveCameraLabel: "Camera add-ons removable now",
            refreshUsageButton: "Refresh usage",
            refreshingUsageLabel: "Refreshing…",
            plans: {
              baseMonthlyName: "Court Intelligence Base (Monthly)",
              baseMonthlyDetails: "Billed monthly. Cancel anytime.",
              baseYearlyName: "Court Intelligence Base (Yearly)",
              baseYearlyDetails: "Billed yearly. Best value for stable operations.",
            },
            baseFeatures: {
              venuesIncluded: "{count} venue included",
              camerasIncluded: "{count} cameras included",
              liveOps: "Live busyness, wait estimates, stats, logs, and role-based access",
            },
            planChangeTitle: "Change plan",
            planChangeDescription: "Switch billing cadence for your Court Intelligence base plan.",
            switchToPlanButton: "Switch to {plan}",
            alreadyCurrentPlan: "Current plan selected",
            addOnTitle: "Add-on catalog",
            addOnDescription: "Add-ons are available after base activation and managed from this subscription section.",
            addOnManageTitle: "Add venue and camera capacity",
            addOnManageDescription: "Use add-ons to increase limits without leaving the app.",
            billingMonthly: "Monthly",
            billingYearly: "Yearly",
            addOnLockedCycleNotice: "Add-ons billed {cycle} to match your base plan",
            addOnBaseRequired: "Activate a Court Intelligence base plan before managing add-ons.",
            cameraAddOnTitle: "Camera add-on",
            venueAddOnTitle: "Venue add-on",
            addCameraCapacityButton: "Add camera capacity",
            removeCameraCapacityButton: "Remove camera capacity",
            addVenueCapacityButton: "Add venue capacity",
            removeVenueCapacityButton: "Remove venue capacity",
            addOnModalTitle: "Confirm add-on change",
            addOnModalSubtitle: "Apply multiple seats in one transaction.",
            addOnTypeLabel: "Add-on type",
            addOnTypeCamera: "Camera capacity",
            addOnTypeVenue: "Venue capacity",
            addOnActionLabel: "Action",
            addOnActionAdd: "Add capacity",
            addOnActionRemove: "Remove capacity",
            addOnQuantityLabel: "Quantity",
            addOnUnitPriceLabel: "Unit price",
            addOnTotalLabel: "Estimated total",
            addOnImpactLabel: "Capacity impact:",
            addOnImpactVenues: "Venues {delta}",
            addOnImpactCameras: "Cameras {delta}",
            addOnCancelButton: "Cancel",
            addOnConfirmButton: "Confirm change",
            addOnConfirmingButton: "Applying…",
            billingTimingTitle: "Billing timing",
            billingTimingItems: [
              "Capacity updates immediately after confirmation.",
              "Charges and credits are reflected on the next invoice period.",
            ],
            startingCheckout: "Starting checkout…",
            checkoutButton: "Checkout with Stripe",
            openBillingPortalButton: "Open billing portal",
            errors: {
              noCheckoutUrl: "No checkout URL returned.",
              startCheckout: "Unable to start checkout.",
              noPortalUrl: "No billing portal URL returned.",
              openPortal: "Unable to open billing portal.",
              manageAddon: "Unable to update add-on capacity.",
              noCameraAddonsToRemove: "No removable camera add-ons. Remove cameras first if usage is at limit.",
              noVenueAddonsToRemove: "No removable venue add-ons. Remove venues or cameras first if usage is at limit.",
            },
            notices: {
              updatedSuccess: "Subscription updated successfully.",
              checkoutCanceled: "Checkout was canceled.",
              cameraAddonIncreased: "Added {count} camera add-ons. Camera limit: {before} -> {after}.",
              cameraAddonDecreased: "Removed {count} camera add-ons. Camera limit: {before} -> {after}.",
              venueAddonIncreased:
                "Added {count} venue add-ons. Venue limit: {before} -> {after}. Camera limit: {cameraBefore} -> {cameraAfter}.",
              venueAddonDecreased:
                "Removed {count} venue add-ons. Venue limit: {before} -> {after}. Camera limit: {cameraBefore} -> {cameraAfter}.",
            },
          },
          profile: {
            title: "Profile",
            description: "Update account details used for venue operations.",
            photo: {
              title: "Profile photo (optional)",
              alt: "Profile avatar",
              upload: "Upload photo",
              uploading: "Uploading…",
            },
            fields: {
              name: "Name",
              email: "Email",
              phoneNumber: "Phone number",
              countryCode: "Code",
              country: "Country",
              selectCountry: "Select country",
              city: "City",
              state: "State",
              zipcode: "Zip code",
            },
            helper: {
              emailLocked: "Locked because this email is your login identifier.",
              emailOptionalWhenPhoneLogin: "Optional when your account logs in with phone.",
              phoneLocked: "Locked because this phone is your login identifier.",
              phoneHint: "Type local number, or paste full +country-number.",
            },
            save: "Save profile",
            saving: "Saving…",
            notices: {
              profileSaved: "Profile saved.",
            },
            errors: {
              uploadAvatar: "Unable to upload avatar.",
              uploadAvatarNoFilename: "Avatar upload did not return a filename.",
              nameRequired: "Name is required.",
              saveProfile: "Unable to save profile.",
            },
          },
          tipsItems: [
            {
              title: "Camera coverage",
              body: "Use 1 camera for up to 2 courts for better occupancy accuracy.",
            },
            {
              title: "Camera placement",
              body: "Install cameras high and fixed to reduce occlusion and false reads.",
            },
            {
              title: "Zone setup order",
              body: "Start with full coverage, then add court, waiting, and bench polygons for cleaner signals.",
            },
            {
              title: "Queue signal",
              body: "Use queueSignal only on real waiting zones, not transit paths.",
            },
            {
              title: "People count precision",
              body: "People counts are operational estimates, not exact headcounts.",
            },
            {
              title: "Wait-time model",
              body: "Queue wait is estimated from waiting players, playable courts, game duration, and players per game.",
            },
            {
              title: "Lower-confidence conditions",
              body: "Wait estimates are less accurate with partial coverage, missing waiting zones, or delayed snapshots.",
            },
            {
              title: "Recommended cadence",
              body: "Recommended cadence is API every 30s and FTPS every 60s.",
            },
            {
              title: "Credential hygiene",
              body: "Rotate camera tokens or FTP passwords after installer handoff or staffing changes.",
            },
            {
              title: "Scope of service",
              body: "Hardware purchase and camera installation are not included in Court Intelligence plans.",
            },
          ],
          roleLabels: {
            owner: "Court Owner",
            admin: "Court Admin",
            monitor: "Monitor",
            systemAdmin: "Admin",
            operator: "Operator",
          },
        },
        venues: {
          capacityTitle: "Subscription capacity",
          capacityDescription:
            "Current limits for venues and cameras under your Court Intelligence subscription.",
          myOwnedVenuesMetric: "My owned venues",
          venueLimitMetric: "Venue limit",
          cameraLimitMetric: "Camera limit",
          refreshingOwnedVenues: "Refreshing owned venues…",
          findTitle: "Find a venue to claim",
          createNewVenue: "Create new venue",
          addVenueCapacity: "Add venue capacity",
          searchPlaceholder: "Search by venue name or address",
          search: "Search",
          useLocation: "Use location",
          locating: "Locating…",
          showingResults: "Showing results for: {query}",
          loadingVenues: "Loading venues…",
          noVenuesFound: "No venues found for this search.",
          loadMore: "Load more",
          claimOwnership: "Claim ownership",
          claiming: "Claiming…",
          owned: "Owned",
          viewDetails: "View details",
          hideDetails: "Hide details",
          myOwnedVenuesTitle: "My owned venues",
          noOwnedVenues: "No venues claimed yet.",
          updateDetails: "Update details",
          continue: "Continue",
          unlimited: "Unlimited",
          venueFallback: "Venue {id}",
          contactSupport: "Contact {email}",
          supportEmailSubject: "Court Intelligence ownership support",
          supportEmailBody:
            "Hi WhoPlays Support,\n\nI need help claiming ownership for a venue in Court Intelligence.\nUser: {username}\n",
          unknownUser: "unknown",
          loadingVenueDetails: "Loading venue details…",
          venueDetailsUnavailable: "Venue details are not available.",
          detailsOverview: "Overview",
          typeNotSet: "Type not set",
          courtsCount: "{count} courts",
          courtCountNotSet: "Court count not set",
          openPlaySessionsToday: "{count} open-play sessions today",
          detailsContactLinks: "Contact & links",
          website: "Website",
          map: "Map",
          amenities: "Amenities",
          moreAmenities: "+{count} more",
          hours: "Hours",
          closed: "Closed",
          previousPhoto: "Previous photo",
          nextPhoto: "Next photo",
          errors: {
            refreshOwnedVenues: "Unable to refresh owned venues.",
            ownerOnlyClaim: "Only the subscription owner can claim or create venues.",
            venueLimitReached: "Venue limit reached. Add an extra venue in Subscription to continue.",
            claimOwnership: "Unable to claim ownership for this venue.",
            searchInputRequired: "Type a venue name/address or use your location.",
            loadVenues: "Unable to load venues.",
            locationNotSupported: "Location is not supported in this browser.",
            locationPermission: "Unable to get location. Please enable location access.",
          },
          notices: {
            venueUpdated: "Venue profile updated for {name}.",
            ownershipClaimed: "Ownership claimed for {name}.",
          },
        },
        cameras: {
          loadingCameraVenues: "Loading camera venues…",
          noVenuesAvailable: "No venues available for camera setup yet. Claim venue ownership first.",
          venueLabel: "Venue",
          metricOwnedVenues: "Owned venues",
          metricVenueLimit: "Venue limit",
          metricCamerasUsedLimit: "Cameras used/limit",
          oneTimeApiCredentials: "One-time API credentials",
          oneTimeFtpCredentials: "One-time FTPS credentials",
          dismiss: "Dismiss",
          saveApiCredentials: "Save this token now. It is shown one time only.",
          saveFtpCredentials: "Save these credentials now. The FTPS password is shown one time only.",
          modePrefix: "Mode: {mode}",
          modeApi: "API push",
          modeFtps: "FTPS upload",
          modeApiShort: "API",
          modeFtpsShort: "FTPS",
          camerasTitle: "Cameras",
          camerasSubtitle: "API mode captures every 30s. FTPS mode recommends 60s.",
          setupGuidanceTitle: "Setup guidance",
          setupGuidanceSubtitle:
            "Choose the setup outcome you want, then use the guidance below to configure it.",
          expandQuickGuide: "Expand quick guide",
          collapseQuickGuide: "Collapse quick guide",
          setupGuidanceCollapsedSummary:
            "Use the quick guide for a compact setup summary, or open the full guide for workflow, outcomes, and calculations.",
          openSetupGuide: "Open full guide",
          setupGuideModalTitle: "Court Intelligence setup guide",
          setupGuideModalSubtitle:
            "Use these targets to decide how precise you want the venue signals to be.",
          setupGuideTabWorkflow: "Workflow",
          setupGuideTabOutcomes: "Outcomes",
          setupGuideTabCalculations: "Calculations",
          setupGuideWorkflowIntro:
            "Follow this order to get a camera online, capture the first snapshot, and unlock the venue KPIs you want.",
          workflowStepNumber: "Step {number}",
          workflowStepCameraTitle: "Create a camera",
          workflowStepCameraDescription:
            "Select the venue, create the camera, and give it a clear name and camera number so operators can identify it later.",
          workflowStepConnectionTitle: "Choose the connection type",
          workflowStepConnectionDescription:
            "Pick API push when the camera can send HTTP POST images. Pick FTPS upload when the camera supports outbound FTPS better than direct API delivery.",
          workflowStepGroupsTitle: "Optional: create camera groups",
          workflowStepGroupsDescription:
            "Use groups to organize related cameras and control debug retention for troubleshooting. Without debug, snapshots are processed normally and not retained, except initialization images.",
          workflowStepCredentialsTitle: "Configure the one-time credentials",
          workflowStepCredentialsDescription:
            "Copy the one-time token or FTPS password immediately, then configure the camera with the shown instructions and recommended capture interval.",
          workflowStepSnapshotTitle: "Wait for the first initialization snapshot",
          workflowStepSnapshotDescription:
            "After the camera sends its first image, open Initialization to confirm framing, visibility, and whether the useful venue footprint is clean.",
          workflowStepTargetTitle: "Pick the setup outcome",
          workflowStepTargetDescription:
            "Choose Quick estimate for rough activity, Court availability to map courts, or Court + queue to unlock waiting-player and ETA metrics.",
          workflowStepZonesTitle: "Create areas and attach coverages",
          workflowStepZonesDescription:
            "Build the area catalog first, then map the camera with manual polygons or AI drafts. Save each working zone only after the coverage matches the visible footprint.",
          workflowStepVerifyTitle: "Verify the unlocked KPIs",
          workflowStepVerifyDescription:
            "Use Live Busyness to confirm what is active. Add court areas to unlock occupied/playable and queue areas to unlock waiting players and wait ETA.",
          setupGuideCalculationsIntro:
            "These formulas explain what Court Intelligence can show at each venue setup level. Use them as guidance for operators, not as exact player-facing copy.",
          setupGuideDefaultPlayersPerGame: "Default players per game: 4",
          setupGuideDefaultAvgGameMinutes: "Default average game minutes: 15",
          setupGuideDefaultQueueTargetMinutes: "Default queue target minutes: 30",
          setupGuideFormulaLabel: "Formula",
          setupGuideVisibleKpisLabel: "Visible KPIs",
          setupGuideHiddenKpisLabel: "Hidden KPIs",
          setupGuideExplanationLabel: "Explanation",
          setupGuideNone: "None",
          currentModeLabel: "Current mode",
          currentModeUnavailable: "Not classified yet",
          setupStatusLoading: "Loading current setup status…",
          setupStatusUnavailable: "Current setup status is not available yet.",
          mappedCourtsLabel: "Mapped courts",
          queueAreasLabel: "Queue areas",
          targetQuickEstimate: "Quick estimate",
          targetCourtAvailability: "Court availability",
          targetCourtAndQueue: "Court + queue",
          capabilityBusyness: "Live activity",
          capabilityAvailability: "Court availability",
          capabilityWait: "Wait metrics",
          areaTypesLabel: "Area types",
          cameraTargetLabel: "Camera target",
          framingLabel: "Framing",
          nextStepLabel: "Next step",
          quickEstimateAreaTypes:
            "Use one general coverage for the useful venue footprint, or keep a single full coverage area when the frame is already clean.",
          quickEstimateCameraTarget: "One wide camera is fine for rough venue activity.",
          quickEstimateFraming:
            "Keep parking lots, roads, and unrelated areas out of the useful frame when possible.",
          quickEstimateNext:
            "Start with general venue coverage for quick activity estimates. Map courts later if you want availability.",
          quickEstimateNextReady:
            "Quick estimate is active. Use the targets above to add court availability or wait insights.",
          courtAvailabilityAreaTypes:
            "Create one court area for each visible court and attach each one to its own coverage polygon.",
          courtAvailabilityCameraTarget:
            "Aim for 1 to 2 courts per camera. Three can work if separation is visually clear.",
          courtAvailabilityFraming:
            "Use elevated, centered views with minimal fence, post, or player occlusion.",
          courtAvailabilityNextLocked:
            "Map visible court areas to unlock occupied / playable court metrics.",
          courtAvailabilityNextReady:
            "Court availability is active. Refine angles or split crowded views if confidence stays moderate.",
          courtQueueAreaTypes:
            "Use court areas plus waiting, bench, paddle rack, or queue-signal areas for the queue footprint you want to track.",
          courtQueueCameraTarget:
            "Use the same court guidance, then keep waiting, bench, or rack zones clearly visible.",
          courtQueueFraming:
            "Queue areas should be visually separate from active courts and not tiny in frame.",
          courtQueueNextLocked:
            "Add waiting, bench, or paddle rack zones to unlock wait estimates.",
          courtQueueNextReady:
            "Wait metrics are active. Keep queue areas clear and separated for better estimates.",
          areasTitle: "Area catalog",
          areasSubtitle: "Venue-level semantic areas shared across camera coverages.",
          createArea: "Create area",
          selectArea: "Select area",
          newArea: "New area",
          newAreaModalTitle: "Create new area",
          areaNamePlaceholder: "Area name",
          loadingAreas: "Loading area catalog…",
          noAreasYet: "No areas yet for this venue.",
          systemArea: "System area",
          edit: "Edit",
          pilotDebugBadge: "Pilot debug ON until {date}",
          manageGroups: "Manage groups",
          addCamera: "Add camera",
          closeAddCamera: "Close add camera",
          closeAddArea: "Close add area",
          addCameraTitle: "Add camera",
          cameraNamePlaceholder: "Camera name",
          cameraNumberPlaceholder: "Camera number",
          ingestModeLabel: "Ingest mode",
          ingestModeApi: "API push",
          ingestModeFtp: "FTPS upload",
          ingestHintFtp:
            "FTPS setup: Explicit TLS on port 21, passive mode, upload JPG/PNG files.",
          ingestHintApi: "API setup: HTTP POST multipart image with camera token auth.",
          noGroup: "No group",
          createCamera: "Create camera",
          creatingCamera: "Creating…",
          initCameraSelectorTitle: "Initialization camera",
          goToCameras: "Go to Cameras",
          noInitCamerasTitle: "No cameras available for initialization",
          noInitCamerasDescription:
            "Create at least one camera in Cameras before starting zone initialization.",
          showAllCameraStatuses: "Show all camera statuses",
          noInitializingCamerasDescription:
            "No cameras are currently in Initializing status. Enable \"Show all camera statuses\" to select another camera.",
          loadingCameras: "Loading cameras…",
          noCamerasVenue: "No cameras yet for this venue.",
          initZonesTitle: "Initialization and zones",
          initZonesSubtitle:
            "Use snapshots to map court areas manually or generate AI suggestions as drafts.",
          cameraLabel: "Camera",
          noneSelected: "None selected",
          selectCameraPrompt: "Select a camera to configure initialization and zones.",
          selectCameraPromptHelp: "Choose a camera above to open its snapshots and zone editor.",
          editingInitializingOnly:
            "Only cameras in Initializing status can be edited on this screen.",
          snapshotsTitle: "Initialization snapshots",
          loadingSnapshots: "Loading snapshots…",
          refresh: "Refresh",
          protocolFtps: "Protocol: FTPS (Explicit TLS) on port 21.",
          uploadFtps: "Upload images to the configured path prefix in passive mode.",
          intervalFtps: "Recommended capture interval: 60 seconds.",
          methodApi: "HTTP method: POST multipart with image file (jpg/png).",
          tokenApi: "Send camera token in header x-camera-token.",
          intervalApi: "Capture interval: 30 seconds.",
          unlimited: "Unlimited",
          notAvailable: "N/A",
          statusPrefix: "Status",
          statusInitializing: "Initializing",
          statusActive: "Active",
          statusInactive: "Inactive",
          lastSeenPrefix: "Last seen {time}",
          selected: "Selected",
          select: "Select",
          openInstructions: "Open setup instructions",
          instructionsTitle: "Setup instructions ({mode})",
          actionActivate: "Activate",
          actionDeactivate: "Deactivate",
          actionOpenSetup: "Open setup",
          discardDraft: "Discard draft",
          unsavedChangesBadge: "Unsaved zone changes",
          actionRotateFtpPassword: "Rotate password",
          actionRotateToken: "Rotate token",
          actionSwitchToApi: "Switch to API",
          actionSwitchToFtps: "Switch to FTPS",
          actionReinitialize: "Reinitialize",
          actionDelete: "Delete",
          ftpRotationDisabled: "Token rotation is disabled in FTPS mode. Use Rotate FTPS password when needed.",
          latestSnapshotPrefix: "Latest snapshot: {time}",
          snapshotAlt: "Initialization snapshot",
          drawingHint: "Click on the image to place polygon points ({count}).",
          noSnapshotYet: "No initialization snapshot available yet. Keep camera active until the first snapshot arrives.",
          noSnapshotTitle: "Waiting for first initialization snapshot",
          noSnapshotDescription:
            "Keep the camera active and verify camera-side upload settings. Once the first snapshot arrives, zone setup is enabled.",
          snapshotRequiredForActions:
            "Zone drawing and AI suggestion are enabled after the first initialization snapshot is received.",
          manualZoneDraftTitle: "Add zone",
          zoneNamePlaceholder: "Zone name",
          queueSignal: "Queue signal",
          exclusion: "Exclusion",
          startDrawing: "Start drawing",
          undoPoint: "Undo point",
          resetPolygon: "Reset polygon",
          addZoneDraft: "Add zone",
          aiZoneSuggestionTitle: "AI zone suggestion",
          aiPromptPlaceholder: "Optional instruction for AI",
          suggesting: "Suggesting…",
          suggestZones: "Suggest zones",
          applySuggestion: "Apply suggestion",
          revert: "Revert",
          aiPreparedHint: "AI prepared {count} zones. Apply when ready.",
          workingZonesTitle: "Working zones ({count})",
          saving: "Saving…",
          saveZones: "Save zones",
          loadingZones: "Loading zones…",
          remove: "Remove",
          pointsLabel: "Points",
          queueTag: "queue",
          exclusionTag: "exclusion",
          noZonesYet: "No zones yet. Draw manually or use AI suggestion.",
          savedZonesLive: "Saved zones currently live: {count}",
          groupsModalTitle: "Camera groups",
          hideForm: "Hide form",
          createGroup: "Create group",
          close: "Close",
          groupNamePlaceholder: "Group name",
          groupDescriptionPlaceholder: "Optional description",
          debugEnabled: "Debug enabled",
          creating: "Creating…",
          loadingGroups: "Loading groups…",
          save: "Save",
          noGroupsYet: "No groups yet. Cameras can still be created without a group.",
          deleteCameraTitle: "Delete camera",
          deleteCameraDescription: "This action is permanent and stops ingest for this camera.",
          deleteCameraLabel: "Camera",
          deleteModeLabel: "Mode",
          deleteIdLabel: "ID",
          deleteTypeToConfirm: "To confirm, type:",
          deleteDelayHint: "Please wait a moment before confirming.",
          cancel: "Cancel",
          deleting: "Deleting…",
          confirmDelete: "Confirm delete",
          tokenFieldCameraId: "Camera ID",
          tokenFieldFtpsHost: "FTPS host",
          tokenFieldFtpsUsername: "FTPS username",
          tokenFieldFtpsPassword: "FTPS password",
          tokenFieldFtpsPathPrefix: "FTPS path prefix",
          ftpsPathPrefixValue: "/ or Blank",
          ftpsSubfolderRuleOff: "If your camera has a subfolder generation rule, turn it off.",
          tokenFieldSnapshotEndpoint: "Snapshot endpoint",
          tokenFieldApiHeader: "API header",
          tokenFieldCameraToken: "Camera token",
          copy: "Copy",
          copyToken: "Copy token",
          copied: "Copied",
          copyFailed: "Copy failed. Please copy manually.",
          cameraNumberLabel: "Camera",
          groupLabel: "Group",
          suggestedZoneLabel: "Suggested zone",
          zoneSuffix: "Zone",
          confirmSwitchFtpToApi:
            "Switching this camera to API removes FTP credentials and stops current FTP delivery. Continue?",
          confirmDeleteGroup: "Delete this camera group? Cameras attached to it must be removed first.",
          confirmDeleteArea: "Delete area '{name}'? This fails if a camera coverage still references it.",
          confirmLeaveUnsaved:
            "You have unsaved zone changes. Leave this screen and discard them?",
          errors: {
            loadVenueAccess: "Failed to load venue access.",
            loadCameras: "Failed to load cameras.",
            loadCameraSetup: "Failed to load camera setup.",
            loadAreas: "Failed to load area catalog.",
            cameraActionFailed: "Camera action failed.",
            switchIngestMode: "Failed to switch camera ingest mode.",
            createGroup: "Failed to create camera group.",
            updateGroup: "Failed to update camera group.",
            deleteGroup: "Failed to delete camera group.",
            createArea: "Failed to create area.",
            updateArea: "Failed to update area.",
            deleteArea: "Failed to delete area.",
            createCamera: "Failed to create camera.",
            cameraLimitReached: "Camera limit reached. Add camera capacity in Subscription to continue.",
            deleteCamera: "Failed to delete camera.",
            suggestZones: "Failed to suggest zones.",
            removeFullCoverageFirst:
              "Remove the default full coverage before saving additional mapped coverages.",
            invalidCoveragePolygon:
              "Each non-full coverage needs at least 3 polygon points.",
            coverageOverlap:
              "Coverage overlaps another zone. Adjust polygon before saving.",
            missingAreaForCoverage:
              "Each coverage must be linked to an area before saving.",
            saveZones: "Failed to save zones.",
          },
          notices: {
            switchedToFtps: "Camera switched to FTPS mode.",
            switchedToApi: "Camera switched to API mode.",
            groupCreated: "Camera group created.",
            groupUpdated: "Camera group updated.",
            groupDeleted: "Camera group deleted.",
            areaCreated: "Area created.",
            areaUpdated: "Area updated.",
            areaDeleted: "Area deleted.",
            cameraCreated: "Camera created.",
            cameraDeleted: "Camera deleted.",
            cameraActivated: "Camera activated.",
            cameraDeactivated: "Camera deactivated.",
            ftpPasswordRotated: "FTPS password rotated.",
            cameraTokenRotated: "Camera token rotated.",
            cameraReinitialized: "Camera reinitialized.",
            aiNoZones: "AI suggestion did not return zones. Try a different prompt.",
            aiPrepared: "AI prepared {count} suggested zones. Review and apply when ready.",
            aiApplied: "AI suggestion added to working zones. Save each zone to persist.",
            aiReverted: "Reverted to previous working zones.",
            zonesSaved: "Zones saved.",
            draftZoneAdded: "Zone added to working zones. Save this zone to persist.",
            draftDiscarded: "Draft zone changes discarded.",
          },
        },
      },
      tips: {
        title: "Tips",
        subtitle: "Make the most of WhoPlays.",
        back: "Back",
        next: "Next",
        close: "Close",
        progress: "Tip {current} of {total}",
        items: [
          {
            title: "Check Real-Time first",
            body: "See who’s on court before you head out so you can join the right run.",
          },
          {
            title: "Invite by SMS",
            body: "Send quick invites from My Play Schedule to fill a game.",
          },
          {
            title: "Confirm early",
            body: "Lock in plans and help the host manage the lineup.",
          },
          {
            title: "Log scores right away",
            body: "Update match results to keep rankings accurate.",
          },
          {
            title: "Link your DUPR",
            body: "Sync your ratings so they show up everywhere.",
          },
          {
            title: "Tune notifications",
            body: "Pick the alerts that matter most to you.",
          },
          {
            title: "Complete your profile",
            body: "A strong profile helps others find and trust you.",
          },
          {
            title: "Rotate teams",
            body: "Use Rotate Teams to create more matches with the same four players.",
          },
        ],
      },
      subscriptionLimits: {
        title: "Limit reached",
        sms: "You've reached your monthly limit of {limit} SMS invites.",
        players: "You've reached the {limit}-player invite limit per schedule.",
        events: "You've hit your limit of {limit} active schedules with invites.",
        generic: "You've reached your current limit of {limit}.",
        upgrade: "Upgrade now",
        cancel: "Cancel",
      },
      sidebar: {
        welcome: "Welcome back",
        kpiWins: "Wins",
        kpiLosses: "Losses",
        kpiPending: "Pending",
        quickActions: {
          language: "Change language",
          languageNext: "Next: {language}",
          theme: "Toggle theme",
          tips: "Tips",
          tipsSoon: "Tips are coming soon.",
          courtIntel: "Switch to Court Intelligence mode",
          logout: "Log out",
        },
        toggles: {
          dnd: "Do Not Disturb",
          tips: "Tips",
          share: "Share",
          language: "Language",
        },
        actions: {
          profile: "Profile",
          preferredCourts: "My Venues",
          schedule: "My Play Schedule",
          linkDupr: "Link with DUPR®",
          subscriptions: "Subscriptions",
          invite: "Invite Players",
          privacy: "Privacy Policy",
          settings: "Settings & Preferences",
          appStats: "App Stats",
          logout: "Log out",
        },
        admin: {
          label: "Admin",
          access: "Access & Entitlements",
        },
        chartLabel: "Win / Loss ratio",
        chartWins: "Wins",
        chartLosses: "Losses",
        chartPending: "Pending",
        chartNoData: "No match history yet.",
        chartWinRate: "Win rate",
        chartTotal: "Total",
        skillLevels: {
          beginner: "Beginner",
          intermediate: "Intermediate",
          advanced: "Advanced",
        },
        rankings: {
          wpRankLabel: "WP Rank",
          duprLabel: "DUPR",
          singlesShort: "S",
          doublesShort: "D",
          notRated: "NR",
        },
      },
      header: {
        subtitleRank: "Rank · Global",
        moreLabel: "Menu",
        currentLabel: "Current",
      },
      analytics: {
        tabSwitch: "tab_switch",
        sidebarOpen: "sidebar_open",
        sidebarClose: "sidebar_close",
      },
      dupr: {
        title: "Link with DUPR®",
        subtitle: "Sign in to DUPR to sync your ratings.",
        instructions: "Complete the DUPR sign-in below. We never store your password.",
        waiting: "Waiting for DUPR sign-in...",
        processing: "Linking your DUPR account...",
        success: "DUPR account linked successfully.",
        error: "Could not link your DUPR account right now.",
        originError: "Ignored message from an untrusted source.",
        missingConfig: "DUPR integration is not configured.",
        close: "Close",
        unauthorized: "You need to be signed in to link DUPR. Please log in and try again.",
      },
      realTime: {
        ribbonLabel: "Your courts",
        refresh: "Refresh",
        noCourtsTitle: "Add a preferred court to see live activity.",
        courtSelectorLabel: "Select a court",
        courtIntelligence: {
          title: "Court Intelligence",
          refresh: "Refresh",
          refreshing: "Refreshing…",
          viewDetails: "View details",
          noDataBadge: "Player preview",
          noDataLogoAlt: "Court Intelligence by WhoPlays",
          noDataTitle: "This venue has not unlocked live court visibility yet.",
          noDataBody:
            "At enabled venues, players can check crowd levels and wait pressure before they head out. You can help bring it here.",
          noDataHint: "Show your club, HOA, or community manager what Court Intelligence can add.",
          noDataPrimaryCta: "See Court Intelligence",
          noDataSecondaryCta: "Share with your venue",
          requestCta: "Request Court Intelligence",
          requestHint: "Ask your community manager to enable it for this venue.",
          requestEmailSubject: "Court Intelligence request for {court}",
          requestEmailBody:
            "Hi WhoPlays team,\n\nI would like to request Court Intelligence for this venue:\n{court}\n\nUser: {username}\n\nThanks.",
          shareEmailSubject: "Could we bring Court Intelligence to {court}?",
          shareEmailBody:
            "Hi,\n\nI use WhoPlays at {court}, and I would love to see Court Intelligence here.\n\nIt gives players live crowd visibility before they head out.\n\nOverview: {link}\n\nSent by: {username}\n",
          scoreLabel: "Busyness score",
          occupancyLabel: "In use / playable",
          occupancyValueFormat: "{inUse} of {playable}",
          waitingLabel: "Queue",
          etaLabel: "Wait",
          peopleLabel: "People",
          scoreShort: "Score",
          confidenceLabel: "Confidence",
          estimateBadge: "Estimate",
          weatherLabel: "Weather",
          forecastLabel: "Next few hours",
          weatherUnavailable: "Weather unavailable",
          updatedAt: "Updated: {time}",
          permanentLink: "Want this at another venue?",
          infoTitle: "About Court Intelligence estimates",
          infoBody:
            "Busyness, wait time, and people counts are ML-based operational estimates from camera snapshots and may differ from exact counts.",
          detailTitle: "Court Intelligence details",
          detailsOverviewTab: "Overview",
          detailsTrafficTab: "Traffic by hour",
          currentSnapshotTitle: "Current snapshot",
          trendTitle: "Venue traffic by hour",
          trendSubtitle: "Hourly pattern from the last 7 days based on recent readings.",
          trendLoading: "Loading trend…",
          trendEmpty: "No trend data available for this venue yet.",
          trendError: "Unable to load trend right now.",
          trendInfoTitle: "How to read this chart",
          trendInfoBody:
            "Use lighter cells to spot quieter hours for an easier visit. Use darker cells to spot busier hours when more players are around. The gold dot means waiting was observed during that hour.",
          trendQuietLabel: "Quieter",
          trendBusyLabel: "Busier",
          trendWaitingObserved: "Waiting observed",
          trendNote: "Each cell shows one observed hour. Lighter means quieter; the gold dot means waiting was observed.",
          trendPeople: "People",
          trendWaiting: "Waiting",
          courtFallback: "Court",
          courtUnknown: "Unknown court",
          unknownUser: "unknown",
          notAvailable: "N/A",
          close: "Close",
          loadError: "Unable to load Court Intelligence data.",
        },
        summary: {
          title: "Players at {court}",
          allLevels: "All levels",
          advanced: "Advanced",
          intermediate: "Intermediate",
          beginner: "Beginner",
          metrics: {
            willing: "Willing to play",
            privateGroups: "Private groups",
            following: "Following up",
          },
          metricsShort: {
            willing: "Willing",
            privateGroups: "Private",
            following: "Following",
          },
          updated: "Updated {time}",
        },
        whosNextTitle: "Who's Next",
        myScheduleTitle: "My Schedule",
        myQuestionsTitle: "My Questions",
        day: {
          today: "Today",
          tomorrow: "Tomorrow",
        },
        metrics: {
          openPlay: "Open Play",
          event: "Event",
          privateGroup: "Private Group",
        },
        carousels: {
          noData: "No activity scheduled yet.",
          dotsLabel: "Jump to card {index}",
        },
        detail: {
          noActivity: "No upcoming activity for this play type.",
          close: "Close",
          profileTitle: "Player profile",
          openProfile: "Open full profile",
          unknownPlayer: "Unknown player",
          status: {
            host: "Host",
            accepted: "Accepted",
            pending: "Pending",
            declined: "Declined",
          },
        },
        schedule: {
          empty: "No upcoming matches yet.",
          badges: {
            invited: "Invited",
            hosted: "Hosted by {name} at {court}",
            hostedSelf: "Hosted by me at {court}",
          },
          cardTime: "My Sched: {time}",
          counts: {
            invited: "Invited",
            confirmed: "Confirmed",
          },
          confirmedLine: "Confirmed: {confirmed}",
          confirmedLineWithLimit: "Confirmed: {confirmed} of {limit}",
        },
        questions: {
          empty: "No questions for this court.",
          status: {
            pending: "Pending",
            answered: "Answered",
          },
        },
        emptyHero: {
          title: "Schedule a match and invite players!",
          bullets: [
            "See who's coming — live!",
            "Invite players via SMS & track responses",
            "Keep everyone updated via SMS or in-app",
          ],
          cta: "Schedule an Event",
        },
        fabLabel: "Schedule Event",
      },
      network: {
        title: "My Network",
        subtitle: "Find and manage players you follow.",
        searchPlaceholder: "Search players",
        search: "Search",
        segments: {
          suggestions: "Suggestions",
          following: "Following",
          followers: "Followers",
        },
        courtFilter: "Courts",
        allCourts: "All courts",
        loading: "Loading players…",
        error: "Unable to load players right now.",
        empty: "No players to show.",
        unknownSkill: "Skill unknown",
        follow: "Follow",
        following: "Following",
        refresh: "Refresh",
        loadMore: "Load more",
        actionError: "Could not update follow. Try again.",
        levelCompetition: {
          recreational: "Recreational",
          competitive: "Competitive",
        },
        followTitle: "Follow {name}",
        followingTitle: "Following {name}",
        close: "Close",
        toggleGroup: "Add to My Group stats",
        toggleNotify: "Notify me about this player",
        unfollow: "Unfollow",
        save: "Save",
      },
    },
    profileEdit: {
      loading: "Loading profile…",
      header: {
        title: "Edit Profile",
        back: "Back",
        cancel: "Cancel",
        save: "Save Preferences",
        saving: "Saving…",
      },
      sections: {
        avatar: {
          title: "Avatar",
          subtitle: "Upload a clear photo so players recognize you.",
          button: "Change photo",
          uploading: "Uploading…",
          helper: "JPG or PNG up to 5MB.",
        },
        personal: { title: "Personal info" },
        location: { title: "Location" },
        preferences: { title: "Preferences & skill" },
        bio: { title: "Bio", placeholder: "Share a short introduction" },
      },
      fields: {
        name: "Full name",
        nickname: "Nickname",
        email: "Email",
        phone: "Phone number",
        country: "Country",
        state: "State",
        city: "City",
        zipcode: "Zip code",
        status: "Status",
        preferredLevel: "Preferred level",
        matchType: "Match type",
        skill: "Skill level",
        selfRanking: "Self ranking",
        competition: "Competition level",
        defaultAudience: "Default audience",
        followerPrivacy: "Follower privacy",
      },
      helpers: {
        emailOptional: "Email is optional and can be used for notifications.",
        emailLogin: "Changing your email may require verification.",
        phoneLocked: "This phone number is your login and cannot be changed.",
        phoneFormat: "Use international format, e.g. +1…",
      },
      success: {
        saved: "Profile saved.",
        photo: "Profile photo updated.",
        email: "Email verified.",
        phone: "Phone verified.",
      },
      errors: {
        name: "Name is required.",
        email: "Enter a valid email address.",
        phone: "Enter a valid phone number in international format.",
        code: "Enter the 6-digit code.",
        save: "Unable to save profile.",
        upload: "Unable to upload avatar.",
        verificationStart: "Unable to start verification.",
        verificationConfirm: "Unable to verify code.",
      },
      verification: {
        verifyEmail: "Verify email",
        reverifyEmail: "Re-verify email",
        verifyPhone: "Verify phone",
        reverifyPhone: "Re-verify phone",
        badge: "Verified",
        modalTitle: "Enter verification code",
        modalDescriptionEmail: "We sent a 6-digit code to {target}. Enter it below to verify your email.",
        modalDescriptionPhone: "We sent a 6-digit code to {target}. Enter it below to verify your phone.",
        submit: "Verify",
        submitting: "Verifying…",
        cancel: "Cancel",
      },
      options: {
        competition: [
          { value: 1, label: "Recreational" },
          { value: 2, label: "Competitive" },
        ],
      },
      legal: "WhoPlays US · Profile settings",
    },
    myVenues: {
      back: "Back to app",
      title: "My Venues",
      subtitle: "Choose your home court and keep a shortlist of places you love to play.",
      loading: "Loading venues…",
      onboarding: {
        title: "Pick your first venue",
        subtitle: "Choose a primary pickleball venue so WhoPlays can personalize your local experience.",
        missingTitle: "Missing your venue?",
        missingBody: "Suggest it or share Court Intelligence to help unlock live court visibility for local pickleball players.",
        heroTitle: "Bring Court Intelligence to your venue",
        benefitOne: "See the players, venues, and updates that matter around your home court.",
        benefitTwo: "Get match reminders, invites, and activity tailored to where you actually play.",
        benefitThree: "Help your venue unlock live court visibility with Court Intelligence.",
        helper: "Share Court Intelligence with your club, HOA, or venue manager.",
        ctaPrimary: "Learn more",
        ctaExpand: "Show more",
        ctaCollapse: "Show less",
        ctaSecondary: "Share with manager",
        searchHeading: "Find your primary venue",
        required: "Choose a primary venue to continue into the app.",
        continue: "Continue",
        noResultsTitle: "Can’t find your venue?",
        noResultsBody: "Suggest it now, then share Court Intelligence so your venue can become more useful for local pickleball players.",
        shareSubject: "Could we bring Court Intelligence to our pickleball venue?",
        shareBody:
          "Hi,\n\nI’m using WhoPlays and I’d love to see our pickleball venue included and eventually enabled with Court Intelligence.\n\nIt helps players discover local activity and gives venues a path to live court visibility.",
      },
      primary: {
        heading: "Primary venue",
        empty: "You haven’t selected a primary venue yet.",
        required: "Pick a primary venue to personalize your experience.",
        clear: "Clear primary venue",
      },
      preferred: {
        heading: "Preferred venues",
        empty: "Add venues you play at regularly to keep them handy.",
        remove: "Remove",
        makePrimary: "Set as primary",
        add: "Add to preferred",
        countLabel: "selected",
      },
      badges: {
        primary: "Primary",
        preferred: "Preferred",
      },
      search: {
        heading: "Find venues",
        placeholder: "Search by venue name or address",
        action: "Search",
        clear: "Clear search",
        useLocation: "Use my location",
        locating: "Locating…",
        locationReady: "Location ready",
        locationError: "We couldn’t get your location.",
        prompt: "Search for a venue or share your location to see nearby options.",
        noResults: "No venues matched your search. Try different keywords.",
        loadMore: "Load more results",
        loading: "Loading venues…",
        setPrimary: "Set as primary",
        addPreferred: "Add to preferred",
        activeLabel: "Results for “{query}”",
        error: "We couldn’t search for venues right now.",
        viewDetails: "View details",
        hideDetails: "Hide details",
      },
      actions: {
        save: "Save venues",
        saving: "Saving venues…",
        saved: "Venues updated successfully.",
        error: "Unable to save venues.",
        suggest: "Suggest a venue",
        reviewPending: "Review pending venues",
        warning: "Venues saved, but we couldn’t refresh the latest details.",
      },
    },
    suggestVenue: {
      backToVenues: "Back to My Venues",
      title: "Suggest a Venue",
      subtitle: "Share details about a new location so we can review it for the community.",
      ownerFlow: {
        title: "Create a venue",
        subtitle: "Create or update your venue for Court Intelligence. Approved instantly for setup.",
        submitCreate: "Create venue",
        submitUpdate: "Save venue",
        submittingCreate: "Creating…",
        submittingUpdate: "Saving…",
        successCreated: "Venue created successfully.",
        successUpdated: "Venue updated successfully.",
      },
      steps: {
        basic: "Basics",
        details: "Details",
        openPlay: "Open Play",
        photos: "Photos",
        contact: "Contact",
        review: "Review",
      },
      actions: {
        previous: "Back",
        next: "Next",
        submit: "Submit suggestion",
        submitting: "Submitting…",
        approve: "Approve & publish",
        markPending: "Return for updates",
        delete: "Delete venue",
      },
      loadingExisting: "Loading existing venue details…",
      successMessage: "Thanks! We received your venue suggestion.",
      standaloneSuccess: {
        title: "Thanks for submitting a new venue!",
        body: "Our review team is validating the details. Venues typically go live within 24–72 hours once everything checks out.",
        helper: "We’ll reach out if we need more information.",
        ctaPrimary: "Suggest another venue",
        ctaSecondary: "Close",
      },
      successApproved: "Venue approved and published.",
      successPending: "Marked as pending updates.",
      successDeleted: "Venue deleted.",
      errors: {
        basicRequired: "Enter the name and full address before continuing.",
        detailsRequired: "Add at least one court configuration and pick a surface.",
        descriptionRequired: "Describe the venue so players know what to expect.",
        contactRequired: "Provide your contact details so we can follow up if needed.",
        confirmInformation: "Please confirm that the information you provided is accurate.",
        submitFailed: "We couldn’t save your suggestion right now. Try again shortly.",
        loadVenue: "We couldn’t load that venue. Try again shortly.",
        deleteFailed: "We couldn’t delete that venue. Try again shortly.",
      },
      confirmDelete: "Delete this venue? This action cannot be undone.",
      basic: {
        heading: "Basic information",
        subheading: "Start with name and address so we can locate the venue.",
        nameLabel: "Venue name",
        namePlaceholder: "Riverside Pickleball Complex",
        shortNameLabel: "Short name",
        shortNamePlaceholder: "Optional short label",
        addressLabel: "Street address",
        addressPlaceholder: "123 Main Street",
        cityLabel: "City",
        stateLabel: "State / Region",
        zipLabel: "Postal code",
        countryLabel: "Country",
        countryPlaceholder: "USA",
        latitudeLabel: "Latitude",
        longitudeLabel: "Longitude",
        mapHeading: "Map preview",
        mapHelper: "Set latitude and longitude to preview the location.",
        mapMissing: "Add coordinates to preview the map.",
        mapTitle: "Map preview for {name}",
        mapDefaultName: "this venue",
        mapLoadError: "We couldn’t load the map. Check your network and try again.",
        mapSearchingForward: "Looking up the location for this address…",
        mapSearchingReverse: "Looking up the address for these coordinates…",
        mapGeocodeError: "We couldn’t find coordinates for that address. Try refining it.",
        mapPendingHeading: "Update the address with this location?",
        mapPendingApply: "Use this address",
        mapPendingIgnore: "Keep current address",
      },
      details: {
        heading: "Venue details",
        subheading: "Tell us about the courts, surfaces, and amenities.",
        surfaceLabel: "Primary surface",
        surfacePlaceholder: "Select surface",
        typeLabel: "Venue type",
        typePublic: "Public (open to everyone)",
        typePrivate: "Private (membership required)",
        courtHeading: "Court breakdown",
        courtAdd: "Add court row",
        courtTypeLabel: "Court type",
        courtTypePlaceholder: "Choose type",
        courtTypeDedicated: "Dedicated",
        courtTypeShared: "Shared Net",
        courtTypeLined: "Lined Only",
        courtEnvironmentLabel: "Environment",
        courtEnvironmentPlaceholder: "Choose environment",
        environmentIndoor: "Indoor",
        environmentOutdoor: "Outdoor",
        environmentCovered: "Covered",
        courtQuantityLabel: "Quantity",
        courtRemove: "Remove row",
        amenitiesHeading: "Amenities",
        amenitiesOptions: [
          "Lighting",
          "Restrooms",
          "Water Stations",
          "Seating",
          "Pro Shop",
          "Lockers",
          "Showers",
          "Food/Drinks",
          "Parking",
          "Wheelchair Accessible",
          "Shade Structures",
          "Ball Machine",
          "Lessons Available",
        ],
        hoursHeading: "Hours of operation",
        timeSlots: [
          "5:00 AM",
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
          "11:00 PM",
          "12:00 AM",
        ],
        until: "to",
        closedLabel: "Closed",
        descriptionLabel: "Description",
        descriptionPlaceholder: "Highlight features, court quality, and anything players should know.",
        phoneLabel: "Phone",
        phonePlaceholder: "(Optional) Contact number",
        websiteLabel: "Website",
        websitePlaceholder: "https://…",
      },
      openPlay: {
        heading: "Open play schedule",
        subheading: "Add drop-in sessions so players know when to show up.",
        sessionsCount: "{count} sessions",
        copyWeekdays: "Copy to weekdays",
        copyWeekend: "Copy to weekend",
        addSession: "Add session",
        emptyLabel: "No open play added for this day yet.",
        startLabel: "Start",
        endLabel: "End",
        levelLabel: "Skill level",
        statusLabel: "Status",
        removeSession: "Remove session",
        timeSlots: [
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
        ],
        levelOptions: ["All Levels", "Beginner", "Beginner+", "Intermediate", "Intermediate+", "Advanced", "Tournament Players"],
        statusOptions: ["Available", "Few Spots", "Full", "Members Only", "Open to Public", "Registration Required"],
      },
      photos: {
        heading: "Venue photos",
        subheading: "Upload a few images so players can see the courts.",
        helper: "Drag and drop images or browse files from your device.",
        uploadCta: "Select photos",
        supported: "Use JPG or PNG files (max 5 MB each).",
        current: "Existing photos",
        pending: "Photos to upload",
        remove: "Remove",
      },
      contact: {
        heading: "Contact information",
        subheading: "Tell us who we can reach out to if we need more details.",
        nameLabel: "Your name",
        emailLabel: "Email",
        phoneLabel: "Phone number",
        roleLabel: "Your role",
        rolePlaceholder: "Select role",
        roleOwner: "Owner / Operator",
        roleManager: "Manager",
        roleEmployee: "Employee",
        rolePlayer: "Regular player",
        roleVisitor: "Visitor",
        roleOther: "Other",
        notesLabel: "Additional notes",
        notesPlaceholder: "Any helpful context for our review team.",
        confirmationLabel: "I confirm the information is accurate and follows the community guidelines.",
      },
      review: {
        heading: "Review & submit",
        subheading: "Look everything over before sending it to our team.",
        basic: "Basic information",
        name: "Venue name",
        address: "Address",
        type: "Venue type",
        surface: "Surface",
        courts: "Courts",
        amenities: "Amenities",
        description: "Description",
        photos: "Photos",
        contact: "Contact details",
        contactName: "Name",
        contactEmail: "Email",
        contactPhone: "Phone",
        contactRole: "Role",
        notes: "Notes",
        unknown: "Unknown",
      },
      duplicates: {
        title: "This venue may already exist",
        description: "We found similar venues. If one matches, open it instead of creating a duplicate.",
        view: "View venue",
        continue: "Continue anyway",
        cancel: "Go back",
      },
    },
    settings: {
      header: {
        title: "Settings & Preferences",
        description: "Control appearance, privacy, and notifications across WhoPlays.",
        back: "Back",
        cancel: "Cancel",
        save: "Save Preferences",
        saving: "Saving…",
      },
      guard: {
        title: "Settings unavailable",
        description: "Complete your profile to unlock settings and preferences.",
        cta: "Edit profile",
      },
      theme: {
        title: "Theme preference",
        description: "Choose how WhoPlays looks on this device.",
        options: {
          system: { label: "System", description: "Match your device setting" },
          light: { label: "Light", description: "Bright interface" },
          dark: { label: "Dark", description: "Low-light friendly" },
        },
      },
      language: {
        title: "Language",
        description: "Switch the interface language instantly.",
      },
      availability: {
        title: "Availability",
        options: {
          1: { title: "Available", description: "Players can invite you to matches." },
          2: { title: "Unavailable", description: "Appear offline to everyone." },
          6: { title: "Private group", description: "Visible only to members of private groups." },
        },
      },
      visibility: {
        title: "Profile visibility",
        options: {
          1: { title: "Public", description: "Anyone can view your profile." },
          3: { title: "Registered players", description: "Only WhoPlays members can view your profile." },
          4: { title: "Connections", description: "Only your connections can view full details." },
          5: { title: "Private", description: "Only you and admins can view your profile." },
        },
      },
      followers: {
        title: "Follower visibility",
        options: {
          1: { title: "Public", description: "Followers see your public activity." },
          2: { title: "Primary court", description: "Limit updates to your primary court community." },
          3: { title: "All courts", description: "Followers see updates from all courts you follow." },
          4: { title: "Friends", description: "Only approved friends see your updates." },
        },
      },
      notifications: {
        title: "Notifications",
        description: "Choose how you want to hear from WhoPlays.",
        items: {
          tournamentReminders: { title: "Tournament reminders", description: "Get notified before your registered tournaments start." },
          matchNotifications: { title: "Match updates", description: "Receive alerts when matches are scheduled or updated." },
          partnerRequests: { title: "Partner requests", description: "Know when players want to connect." },
          communityEvents: { title: "Community events", description: "Stay in the loop on events near you." },
          emailNotifications: { title: "Email notifications", description: "Receive updates via email." },
          eventInvitesSms: { title: "Event invites via SMS", description: "Get text invites you can reply to." },
          matchInvitesSms: { title: "Match alerts via SMS", description: "Receive SMS reminders for matches." },
          scoringUpdatesSms: { title: "Score by text", description: "Confirm results directly via SMS." },
        },
      },
      account: {
        title: "Account shortcuts",
        links: {
          connectedAccounts: { title: "Connected accounts", description: "Manage linked social accounts." },
          dataPrivacy: { title: "Data & privacy", description: "Review how We handle your data." },
          deleteAccount: { title: "Delete account", description: "Permanently remove your account." },
        },
      },
      messages: {
        saved: "Preferences saved successfully.",
        error: "We couldn't save your preferences. Please try again.",
      },
    },
    homeVenues: {
      title: "Courts near you",
      description:
        "Search WhoPlays venues by name or address and keep tabs on open play sessions happening today.",
      pageTitle: "Pickleball venues directory",
      pageDescription:
        "Search by name or address, filter by amenities, and explore open play schedules near you.",
      useMyLocation: "Use my location",
      locating: "Detecting location...",
      searchPlaceholder: "Search by venue name or address",
      searchAriaLabel: "Search venues",
      searchCta: "Search",
      promptLocation: "Share your location or search to discover nearby venues.",
      clearFilters: "Clear filters",
      errorLoading: "We couldn't load nearby venues. Please try again.",
      noResults: "No venues match your filters yet.",
      loading: "Loading venues...",
      loadMore: "Load more",
      loadingMore: "Loading more venues...",
      distanceAway: "{distance} away",
      verifiedBadge: "Verified",
      courtsHeading: "Court breakdown",
      courtsHeadingWithTotal: "Court breakdown · {count} courts",
      courtsFallback: "{count} total courts",
      surfaceLabel: "Primary surface: {surface}",
      totalCourts: "{count} total courts",
      amenitiesHeading: "Amenities",
      openPlayHeading: "Today's open play",
      hoursHeading: "Hours of operation",
      hoursClosed: "Closed",
      contactPhone: "Call {phone}",
      visitWebsite: "Visit website",
      viewOnMap: "View on map",
      viewAllVenues: "Explore all venues",
      viewDetails: "View details",
      backToHome: "Back to home",
      openStatusNow: "Open now",
      closedStatusNow: "Closed now",
      closedStatusToday: "Closed today",
      openUntil: "Open until {time}",
      opensAt: "Opens at {time}",
      closedForDay: "Closed for the day",
      statusUnknown: "Status unavailable",
      statusClosedTemporary: "Temporarily closed",
      statusClosedPermanent: "Permanently closed",
      statusClosedOutOfSeason: "Closed for the season",
      publicVenue: "Public",
      privateVenue: "Private",
      mapFallbackTitle: "Map for {name}",
      mapFallbackNoLocation: "Location details unavailable.",
    },
    features: {
      title: "Everything you need to keep games flowing",
      description:
        "Follow courts, send invites, and keep play history organized with tools made for public-court pickleball.",
      cards: [
        {
          title: "Live court awareness",
          description:
            "Check crowd levels before you leave home and share quick snapshots with everyone who follows the court.",
          points: [
            "Follow public courts and see who’s playing by skill",
            "Browse upcoming sessions and pickup games in one feed",
            "Post quick updates that notify followers instantly",
          ],
        },
        {
          title: "One-tap sessions & SMS invites",
          description:
            "Create a ladder, drill, or casual hit in under a minute and let players RSVP by text—no app required.",
          points: [
            "Flexible templates for open play, ladders, or private runs",
            "Invite anyone by SMS and track responses in real time",
            "Keep the whole crew in the loop with automatic reminders",
          ],
        },
        {
          title: "Player profiles & stats",
          description:
            "Track your history, sync DUPR results, and keep notes on partners and rivals so every match gets better.",
          points: [
            "Record scores, verified matches, and personal notes",
            "Self-rate or connect to DUPR to follow rating changes",
            "Follow players, start chats, and build your crew",
          ],
        },
      ],
    },
    social: {
      title: "Follow the WhoPlays pickleball community",
      description:
        "Tips, product drops, and behind-the-scenes footage from the players building the WhoPlays pickleball app.",
      links: [
        { name: "Instagram", handle: "@whoplaysus", href: "https://www.instagram.com/whoplaysus/" },
        { name: "Facebook", handle: "WhoPlays Pickleball", href: "https://www.facebook.com/profile.php?id=61550796384597" },
        { name: "YouTube", handle: "WhoPlays Pickleball", href: "https://www.youtube.com/channel/UC9JuYJ_QR3ykzjXgnKIRviA" },
        { name: "LinkedIn", handle: "WhoPlays US", href: "https://www.linkedin.com/company/whoplays-us-inc/?viewAsMember=true" },
        { name: "X", handle: "@WhoPlaysUS", href: "https://x.com/WhoPlaysUS" },
        { name: "TikTok", handle: "@whoplays05", href: "https://tiktok.com/@whoplays05" },
      ],
    },
    workflow: {
      title: "Meet up fast in four steps",
      description:
        "Find a crowd, schedule play, confirm by text, and keep your stats. WhoPlays handles the coordination so you can stay on court.",
      steps: [
        {
          title: "Discover",
          description:
            "Open the court feed to see who’s already out there and what skill mix is on the courts.",
        },
        {
          title: "Create",
          description:
            "Start a game, ladder, or drill session in seconds with friendly defaults made for public courts.",
        },
        {
          title: "Invite",
          description:
            "Send SMS invites or share the link—players reply with Yes/No and WhoPlays updates the roster instantly.",
        },
        {
          title: "Record",
          description:
            "Log scores, sync to DUPR if you want, and watch your personal history grow over time.",
        },
      ],
    },
    testimonials: {
      title: "Organizers who switched to WhoPlays",
      items: [
        {
          quote:
            "“The court feed alone changed how we plan. I can check who’s already confirmed and text the rest without leaving the app.”",
          name: "Lauren Mitchell",
          role: "Director, Rally Point Pickleball",
        },
        {
          quote:
            "“SMS RSVPs are a game changer. Even players who hate apps stay in the loop and ladders stay full.”",
          name: "Brian Kim",
          role: "Head Pro, Marina Courts",
        },
      ],
    },
    finalCta: {
      title: "Help shape the next release of WhoPlays",
      description:
        "Join the founding organizer group to test new pickleball scheduling features, request integrations, and get direct support from the team.",
      primaryCta: "Become a founding organizer",
      primaryHref: "/founding-organizers",
      secondaryCta: "Download the pickleball app",
      secondaryHref: "/app-stores",
    },
    footer: {
      tagline: "Built in the US for the global pickleball community.",
      rights: "All rights reserved.",
      links: {
        pickleballApp: "Pickleball App",
        privacy: "Privacy Policy",
        courtIntel: "Court Intelligence",
        foundingProgram: "Founding Venues",
        plans: "Plans",
        accountDeletion: "Account deletion",
      },
    },
    pickleballAppPage: {
      seo: {
        title: "Pickleball App for Scheduling Games and Finding Courts | WhoPlays",
        description:
          "WhoPlays is a pickleball app to find courts near you, schedule games, invite players by SMS, and check live venue pressure at supported locations.",
        openGraphTitle: "WhoPlays Pickleball App | Schedule Games and Find Courts",
        keywords: [
          "pickleball app",
          "best pickleball app",
          "pickleball scheduling app",
          "pickleball court finder app",
          "find pickleball courts near me",
          "pickleball open play app",
          "organize pickleball games",
          "pickleball venue app",
        ],
        breadcrumbName: "Pickleball App",
      },
      badge: "Pickleball app",
      title: "A pickleball app for scheduling games and finding courts near you",
      description:
        "WhoPlays helps players discover courts faster, schedule open play with less back-and-forth, and avoid arriving at overcrowded venues.",
      ctaBrowse: "Browse courts by location",
      ctaDownload: "Download the app",
      cards: [
        {
          title: "Find pickleball courts near you",
          description:
            "Search venues by state and city, then open detailed venue pages with maps, court counts, and contact links.",
        },
        {
          title: "Schedule games and open play",
          description:
            "Coordinate sessions quickly, invite players, and keep your local pickleball group active without long message chains.",
        },
        {
          title: "Check live venue pressure",
          description:
            "At supported venues, Court Intelligence helps you see busyness and wait pressure before you drive to the courts.",
        },
      ],
      players: {
        title: "For players",
        points: [
          "Find pickleball courts near you by state and city.",
          "Schedule games faster and invite players by SMS.",
          "Choose better play times with venue visibility.",
        ],
        cta: "Start on web",
      },
      operators: {
        title: "For venues and operators",
        points: [
          "Improve player experience with live visibility.",
          "Monitor patterns over time with historical context.",
          "Use Court Intelligence to support operations planning.",
        ],
        primaryCta: "Explore Court Intelligence",
        secondaryCta: "Get started",
      },
      faq: {
        title: "Pickleball app FAQ",
        items: [
          {
            question: "Is WhoPlays a pickleball scheduling app?",
            answer:
              "Yes. WhoPlays helps players schedule games and open play sessions while coordinating invites and venue details in one flow.",
          },
          {
            question: "Can I find pickleball courts near me with WhoPlays?",
            answer:
              "Yes. You can browse pickleball venues by location and open each venue page for maps, court details, and quick actions.",
          },
          {
            question: "Does WhoPlays show wait times or busyness?",
            answer:
              "At supported venues, Court Intelligence provides live busyness and wait-pressure signals so players can decide when to go.",
          },
          {
            question: "Does WhoPlays also help venue owners?",
            answer:
              "Yes. Court Intelligence supports owners and operators with live and historical court usage insights.",
          },
          {
            question: "Is WhoPlays free for players?",
            answer:
              "Core discovery and player workflows are available without a paid requirement. Venue analytics features are offered through Court Intelligence options.",
          },
        ],
      },
      jsonLd: {
        featureList: [
          "Find nearby pickleball venues and courts",
          "Organize matches and open play sessions",
          "Invite players with fast sharing tools",
          "See live busyness and wait pressure at supported venues",
          "View court-level activity with Court Intelligence",
        ],
      },
    },
    homeCourtIntelPromo: {
      badge: "For venue owners and operators",
      title: "Bring Court Intelligence to your venue",
      description:
        "Turn camera snapshots into live venue signals so you can act faster and run courts with confidence.",
      kpis: ["Live busyness", "Wait estimate", "Court availability"],
      foundingNote: "Founding Venue Program now open for 7 launch locations.",
      primaryCta: "Explore Court Intelligence",
      primaryHref: "/court-intelligence",
      foundingCta: "Founding venues",
      foundingHref: "/court-intelligence/founding-venues",
      secondaryCta: "See plans",
      secondaryHref: "/court-intelligence#pricing",
    },
    courtIntelligence: {
      hero: {
        eyebrow: "Live operations for pickleball venues",
        title: "See what is actually happening on your courts, even when no one is on site.",
        description:
          "Court Intelligence gives pickleball clubs, HOAs, and community operators real-time visibility into court traffic, queue pressure, and on-site activity while helping players know when to go through WhoPlays.",
        ownerCta: "Start Court Intelligence setup",
        ownerCtaNote: "Only for venue owners and managers.",
        ownerHref: "/court-intelligence/get-started",
        foundingCta: "See founding venue program",
        foundingHref: "/court-intelligence/founding-venues",
        playerCta: "I am a player",
        playerHref: "/auth/login",
        note: "Founding Venue Program now open for 7 launch locations.",
        visualNote: "Live venue activity, court availability, wait pressure, and historical trends in one operational dashboard.",
      },
      onboardingEntry: {
        eyebrow: "Owner onboarding",
        title: "Start your Court Intelligence setup",
        description:
          "This wizard entry is built for venue owners and managers. We will guide you through account access, subscription, venue setup, and camera initialization.",
        note: "You will use one WhoPlays account for both player features and venue operations access.",
        stepsTitle: "What happens next",
        steps: [
          { title: "Step 1: Account access", description: "Create your account or sign in with your existing WhoPlays account." },
          { title: "Step 2: Activate subscription", description: "Choose a Court Intelligence base plan and apply any beta coupon if available." },
          { title: "Step 3: Configure venue", description: "Create/select your venue, assign roles, and define operating details." },
          { title: "Step 4: Connect cameras", description: "Choose API or FTPS mode, copy one-time credentials, and initialize zones for live data." },
        ],
        checklistTitle: "Before you continue",
        checklist: [
          "You are a venue owner or authorized manager",
          "You can manage subscription and billing for this venue",
          "You can access cameras or coordinate with installer staff",
        ],
        primaryCta: "Continue to account setup",
        primaryHref: "/auth/signup?returnTo=/main-app/court-intelligence/onboarding/account",
        secondaryCta: "I already have an account",
        secondaryHref: "/auth/login?returnTo=/main-app/court-intelligence/onboarding/account",
        signedInPrimaryCta: "Continue to subscription",
        signedInSecondaryCta: "Review plans",
        backCta: "Back to Court Intelligence overview",
        backHref: "/court-intelligence",
        foundingCallout: {
          eyebrow: "Founding Venue Program",
          title: "Applying for one of 7 founding venue spots?",
          description:
            "Use the founding path if you want launch support, early access, and the founder application inside the main app.",
          cta: "Go to founding venues",
          href: "/court-intelligence/founding-venues",
        },
      },
      highlights: [
        {
          value: "Reduce peak-time complaints",
          label: "See live venue pressure before crowded sessions turn into player frustration.",
        },
        {
          value: "See court pressure in real time",
          label: "Understand how busy the venue feels now, whether courts are opening up, and when waits are building.",
        },
        {
          value: "Plan with real usage history",
          label: "Use historical trends to support staffing, court planning, and expansion decisions.",
        },
      ],
      whatYouGet: {
        title: "Why venue operators use it",
        description:
          "Court Intelligence helps operators make faster decisions during busy hours, improve player visibility, and build a real usage history for the facility.",
        items: [
          {
            title: "For clubs with reservations",
            points: [
              "Your reservation system shows bookings. Court Intelligence shows what is actually happening on court.",
              "See when booked courts are still occupied and when open play traffic is building into a wait.",
              "Support staff decisions and court policies with live on-site visibility.",
            ],
          },
          {
            title: "For HOAs and community courts",
            points: [
              "Help residents know when to go before they drive to the courts.",
              "Reduce open play crowding complaints without needing staff on site.",
              "Use real usage data to guide amenity decisions and future court expansion.",
            ],
          },
          {
            title: "For operators planning growth",
            points: [
              "Review historical traffic by time, venue, and court.",
              "Compare real activity against operating assumptions during busy periods.",
              "Build the case for staffing changes, policy updates, or more courts.",
            ],
          },
        ],
      },
      setupOutcomes: {
        title: "Choose your setup outcome",
        description:
          "Start with a quick estimate or map your venue in more detail to unlock court availability and wait insights.",
      },
      setupGuide: {
        title: "Choose your setup outcome",
        subtitle: "Start with a quick estimate or map courts and queue areas for deeper visibility.",
        openGuideLabel: "See setup options",
        eyebrowLabel: "Best fit",
        descriptionLabel: "What it means",
        outcomes: {
          quickEstimate: {
            label: "Quick estimate",
            eyebrow: "Best for first pilots",
            description: "Get rough live venue activity using a wide camera view.",
            unlocksTitle: "What you unlock",
            unlocks: [
              "Live crowd activity",
              "Busyness level",
              "Player-facing visibility in WhoPlays",
            ],
            setupTitle: "Recommended setup",
            setup: [
              "One wide camera can work",
              "General coverage or a clean full-coverage view",
              "Best when you want to launch quickly",
            ],
          },
          courtAvailability: {
            label: "Court availability",
            eyebrow: "Best for active facilities",
            description:
              "Map visible courts so the system can estimate which courts are in use and which may be opening up.",
            unlocksTitle: "What you unlock",
            unlocks: [
              "Occupied vs open court visibility",
              "Per-court activity context",
              "More useful player planning",
            ],
            setupTitle: "Recommended setup",
            setup: [
              "Aim for 1 to 2 courts per camera",
              "Create one court area per visible court",
              "Use a stable elevated angle with clear separation",
            ],
          },
          courtAndQueue: {
            label: "Court + queue",
            eyebrow: "Best for peak-time operations",
            description:
              "Add waiting, bench, or paddle-rack areas to estimate queue pressure and wait times.",
            unlocksTitle: "What you unlock",
            unlocks: [
              "Waiting player visibility",
              "Estimated wait pressure",
              "Stronger live operational signals",
            ],
            setupTitle: "Recommended setup",
            setup: [
              "Use the same court mapping setup",
              "Add clearly visible waiting or bench areas",
              "Keep queue areas visually separate from active courts",
            ],
          },
        },
        modal: {
          title: "Setup options",
          closeLabel: "Close",
        },
      },
      workflow: {
        title: "How it works",
        steps: [
          { title: "Subscribe", description: "Choose a Court Intelligence base plan and activate your venue access." },
          { title: "Connect cameras", description: "Create cameras, choose API or FTPS mode, copy one-time credentials, then initialize feeds." },
          { title: "Configure zones", description: "Draw polygons for courts, waiting areas, benches, racks, and full coverage." },
          { title: "Operate with data", description: "Use live KPIs and historical stats to optimize staffing and rotation policies." },
        ],
      },
      playerBenefit: {
        title: "Free player advantage in WhoPlays US",
        description: "When a venue enables Court Intelligence, players benefit automatically at no extra cost.",
        points: [
          "Check if a venue is currently busy before driving there",
          "See estimated wait pressure and better windows to play",
          "Reduce uncertainty and wasted trips during peak hours",
        ],
      },
      playerAdvocacy: {
        title: "Want this at your club, HOA, or community courts?",
        description:
          "Players do not buy the infrastructure, but they can help create demand. Share the founding venue page with the person who runs your courts.",
        points: [
          "Send one owner-facing page instead of explaining the product yourself.",
          "Give operators the standard setup path and the founding-program option in one place.",
          "Help your venue evaluate live busyness, wait pressure, and court visibility without guessing.",
        ],
        primaryCta: "Email it to your venue",
        secondaryCta: "Open the owner page",
        shareSubject: "Court Intelligence for our pickleball courts",
        shareBody:
          "Hi,\n\nI found this Court Intelligence page for pickleball venues and thought it could be useful for our courts. It explains the owner setup path and the limited founding venue program.\n\n{url}\n",
      },
      privacy: {
        title: "Camera data & privacy",
        description: "Court Intelligence is designed for operational visibility with venue-controlled camera behavior.",
        points: [
          "Cameras send snapshots outbound by API push or FTPS upload; we do not connect to your local network.",
          "By default, snapshots are processed for occupancy/wait signals and are not stored.",
          "If Camera Group debug mode is enabled, snapshots may be stored for ML improvement until the user-defined debug expiration.",
          "While a camera is in initializing status, one latest initialization snapshot is retained and replaced by newer ones.",
          "Operators can disable camera delivery at any time by removing camera configuration on their side.",
        ],
        playerNote:
          "Players in WhoPlays US receive live busyness and wait indicators for enabled venues, not camera setup controls.",
        policyCta: "Read full Privacy Policy",
        policyHref: "/privacy-policy",
      },
      pricing: {
        title: "Simple pricing for pickleball operators",
        description:
          "Start with one venue and four cameras, then add capacity as operations grow. Players receive live visibility through WhoPlays when your venue enables Court Intelligence.",
        cards: [
          {
            name: "Court Intelligence Base (Monthly)",
            price: "$99 / month",
            description: "Launch one venue with live operational visibility and flexible monthly billing.",
            highlighted: true,
            features: [
              "1 venue included",
              "Up to 4 cameras included",
              "Live venue traffic, availability, and wait insights based on setup level",
              "Historical analytics, logs, and role-based management",
              "Free player visibility through WhoPlays",
            ],
          },
          {
            name: "Court Intelligence Base (Yearly)",
            price: "$948 / year",
            description: "Launch one venue with the same base capacity and lower annual cost.",
            highlighted: false,
            features: [
              "1 venue included",
              "Up to 4 cameras included",
              "Same live and historical features as the monthly base plan",
              "Save $240/year compared to monthly billing",
              "Free player visibility through WhoPlays",
            ],
          },
          {
            name: "Camera Add-on",
            price: "$12 / month or $115 / year",
            description: "Add camera capacity when one venue needs broader coverage or more detailed visibility.",
            highlighted: false,
            features: ["Adds +1 camera slot", "Useful for larger sites or more precise mapping", "Managed directly from subscription settings"],
          },
          {
            name: "Venue Add-on",
            price: "$59 / month or $567 / year",
            description: "Add one more venue and include four camera slots for that added location.",
            highlighted: false,
            features: ["Adds +1 venue", "Includes +4 camera slots for that venue", "Ideal for HOAs, clubs with multiple sites, and growing operators"],
          },
        ],
        footnote:
          "Players always get live visibility for enabled venues through WhoPlays. Hardware, camera purchase, and on-site installation are not included. Founding Venue Program coupons may be available for selected locations during launch.",
      },
      faq: {
        title: "Common questions",
        items: [
          {
            question: "Do I need a separate account for Court Intelligence?",
            answer: "No. Use the same WhoPlays account and switch modes based on your access.",
          },
          {
            question: "Can players use Court Intelligence without paying?",
            answer: "Yes. Players receive free real-time visibility for venues that enable Court Intelligence.",
          },
          {
            question: "Can I start with one venue and scale later?",
            answer: "Yes. Start with the included venue and cameras, then add more via subscription add-ons.",
          },
          {
            question: "What camera connection modes are supported?",
            answer: "API push and FTPS (Explicit TLS on port 21). Recommended intervals: API every 30s, FTPS every 60s.",
          },
          {
            question: "Do managers and monitors need subscriptions?",
            answer: "Owners subscribe, then grant court-level roles so managers/monitors can collaborate.",
          },
          {
            question: "Do you provide camera hardware or installation?",
            answer: "No. Venues provide and configure their own cameras and network setup.",
          },
        ],
      },
      finalCta: {
        title: "Apply for a founding venue spot",
        description:
          "We are opening Court Intelligence to a limited number of clubs, HOAs, and community operators who want direct launch support, early access, and a chance to help shape the product.",
        primaryCta: "Apply now",
        primaryHref: "/court-intelligence/founding-venues",
        secondaryCta: "Talk with us",
        secondaryHref:
          "mailto:support@whoplays.us?subject=Court%20Intelligence%20Founding%20Venue",
      },
    },
    foundingVenues: {
      hero: {
        eyebrow: "Court Intelligence founding venue program",
        title: "Launch Court Intelligence with direct setup support.",
        highlight: "Limited to 7 launch locations for clubs, HOAs, and community operators.",
        description:
          "Founding venues get early access to Court Intelligence, launch guidance from the WhoPlays team, and the chance to influence the first operating playbooks and roadmap.",
        note: "Players always receive live venue visibility for enabled venues through WhoPlays at no extra cost.",
        primaryCta: "Apply now",
        primaryHref: "/court-intelligence/get-started",
        secondaryCta: "Talk with us",
        secondaryHref:
          "mailto:support@whoplays.us?subject=Court%20Intelligence%20Founding%20Venue",
        visualTitle: "What founding venues get",
        visualPoints: [
          "Direct launch support from onboarding to first live camera feeds",
          "Priority feedback loop on setup, operations, and reporting needs",
          "Potential launch coupons for selected locations when available",
        ],
        visualFootnote: "Hardware, camera purchase, and on-site installation are not included.",
      },
      highlights: [
        {
          value: "7 locations",
          label: "We are opening the founding program to a tightly managed first cohort.",
        },
        {
          value: "Early access",
          label: "Get new operator capabilities before broader rollout.",
        },
        {
          value: "Operator feedback",
          label: "Help shape queue, availability, and reporting workflows.",
        },
      ],
      benefits: {
        title: "What the program includes",
        description:
          "This program is built for venues that want a more hands-on launch while Court Intelligence expands to more operators.",
        items: [
          {
            title: "Launch guidance",
            points: [
              "Review your venue setup, camera plan, and rollout goals with the WhoPlays team.",
              "Move through account access, subscription, venue setup, and camera initialization with clearer support.",
              "Reduce setup mistakes before you go live with players.",
            ],
          },
          {
            title: "Early product access",
            points: [
              "Use Court Intelligence while launch workflows and operator reporting continue to evolve.",
              "Share practical feedback on busyness, court availability, and queue insights.",
              "Influence what gets prioritized for venue operations.",
            ],
          },
          {
            title: "Player visibility from day one",
            points: [
              "Players in WhoPlays can see live venue visibility when your location is enabled.",
              "Help players avoid wasted trips and better plan peak-time visits.",
              "Give your venue a more transparent live experience without adding a player paywall.",
            ],
          },
        ],
      },
      fit: {
        title: "Who should apply",
        description:
          "The best fit is a venue that can move through setup and wants to help shape launch operations with real-world feedback.",
        items: [
          "Pickleball clubs that want live visibility beyond reservations.",
          "HOAs and community courts that need crowd and wait awareness without staff on site.",
          "Operators managing multiple sites who want historical data for planning.",
          "Venues that can coordinate camera setup or work directly with their installer.",
        ],
      },
      launchSupport: {
        title: "What happens after you apply",
        steps: [
          {
            title: "Share venue details",
            description: "Tell us about your courts, operating model, and what outcome matters most at launch.",
          },
          {
            title: "Confirm fit",
            description: "We review whether your venue is a strong match for the current cohort and setup requirements.",
          },
          {
            title: "Set up access and cameras",
            description: "Move through owner onboarding, subscription, venue creation, and camera initialization.",
          },
          {
            title: "Go live and iterate",
            description: "Launch the venue, monitor live signals, and share feedback on what operators need next.",
          },
        ],
      },
      checklist: {
        title: "Before you apply",
        description:
          "The strongest founding venues can move quickly through setup and coordinate the people needed to get cameras live.",
        items: [
          "You are an owner, manager, or authorized operator for the venue.",
          "You can manage billing or coordinate with the person who can.",
          "You can access camera settings or work with your installer team.",
          "You want to launch one venue first and expand later if needed.",
        ],
        footnote:
          "Camera snapshots are processed for occupancy and wait signals. Optional debug retention is controlled by venue settings.",
      },
      finalCta: {
        title: "See if one of the 7 spots fits your venue",
        description:
          "Start the owner flow or contact us first if you want to confirm launch timing, camera setup, or multi-venue needs.",
        primaryCta: "Start owner setup",
        primaryHref: "/court-intelligence/get-started",
        secondaryCta: "Email the team",
        secondaryHref:
          "mailto:support@whoplays.us?subject=Court%20Intelligence%20Founding%20Venue",
      },
    },
    foundingCiApplication: {
      loading: "Loading your founding venue application...",
      backToProgram: "Back to founding venue program",
      signOutToProgram: "Sign out and return to program",
      supportCta: "Email support",
      signedInAs: "Signed in as {value}",
      retry: "Try again",
      loadErrorTitle: "We couldn't load your application status.",
      approvedRedirect: "Your application is approved. Redirecting you into Court Intelligence onboarding...",
      intro: {
        eyebrow: "Court Intelligence founding venue application",
        title: "Submit your founding venue application",
        description:
          "Use the same WhoPlays account you will use for onboarding. If your application is approved, you will continue through the standard Court Intelligence onboarding flow.",
      },
      form: {
        title: "Tell us about your venue",
        description:
          "We use this to confirm fit, launch timing, and support needs for the current founding cohort.",
        required: "Required fields are marked *.",
        sections: {
          contact: "Contact",
          venue: "Venue",
          operations: "Operations",
          notes: "Notes",
        },
        fields: {
          contactName: "Contact name",
          contactEmail: "Email",
          contactPhone: "Phone",
          organizationName: "Organization or operator name",
          venueName: "Venue name",
          venueType: "Venue type",
          city: "City",
          state: "State",
          country: "Country",
          courtsCount: "Estimated court count",
          hasExistingCameras: "Do you already have cameras installed?",
          operationsModel: "How does the venue operate today?",
          targetLaunchTiming: "Target launch timing",
          willingToGiveFeedback: "Are you willing to share operator feedback during launch?",
          notes: "Anything we should know before review?",
        },
        options: {
          empty: "Select one",
          boolean: {
            yes: "Yes",
            no: "No",
            notSure: "Not sure",
          },
          venueType: {
            hoa: "HOA",
            private_club: "Private club",
            city: "City or parks department",
            community: "Community venue",
            other: "Other",
          },
          operationsModel: {
            reservations: "Reservations",
            open_play: "Open play",
            both: "Both",
            unknown: "Unknown",
          },
          targetLaunchTiming: {
            asap: "ASAP",
            within_30_days: "Within 30 days",
            within_60_days: "Within 60 days",
            later: "Later",
            exploring: "Still exploring",
          },
        },
      },
      validation: {
        contactName: "Enter your name.",
        contactMethod: "Add an email or phone number.",
        organizationName: "Enter your organization or operator name.",
        venueType: "Select the venue type.",
        city: "Enter the city.",
        state: "Enter the state.",
        courtsCount: "Enter a valid court count.",
      },
      errors: {
        load: "Unable to load your founding application.",
        submit: "Unable to submit your application.",
      },
      actions: {
        submit: "Submit application",
        submitting: "Submitting...",
        reviewProgram: "Review program details",
      },
      summary: {
        title: "Application summary",
        organization: "Organization",
        venue: "Venue",
        location: "Location",
        courts: "Courts",
        submittedAt: "Submitted",
        status: "Status",
        statusReason: "Review note",
      },
      states: {
        submitted: {
          badge: "Pending review",
          title: "Application received",
          description:
            "We have your submission and will review fit, launch readiness, and current cohort capacity.",
          helper:
            "If approved, you will continue through the normal Court Intelligence onboarding and checkout flow.",
          summaryNote: "Your application is on file. No additional action is needed while we review it.",
        },
        waitlisted: {
          badge: "Waitlisted",
          title: "You are on the waitlist",
          description:
            "The current cohort is full or near capacity. We will keep your venue in line for the next opening.",
          helper: "You do not need to submit again. We will contact you if a founding slot opens.",
          summaryNote: "We are holding your submission for a future opening.",
        },
        rejected: {
          badge: "Not approved",
          title: "This application is not moving forward right now",
          description:
            "Your venue is not in the current founding cohort. You can still contact us if your setup or timing changes.",
          helper: "If there is a review note below, that is the most relevant guidance from our team.",
          summaryNote: "You can reference this submission if you contact support later.",
        },
      },
    },
    subscriptionPlans: {
      hero: {
        eyebrow: "Flexible pricing",
        title: "Plans built for every organizer",
        description:
          "Start free and scale into pro-grade automations the moment your programs grow. Switch tiers at any time.",
        note: "All plans include event reminders, schedule insights, and attendance tracking.",
      },
      courtIntelCallout: {
        title: "Running a venue or city program?",
        description:
          "Court Intelligence is built for operators and includes live busyness, wait insights, and historical analytics. Venues that enable it also unlock free live visibility for players in WhoPlays US.",
        note: "Hardware purchase and on-site installation are not included. Venues provide and configure their own cameras.",
        primaryLabel: "See Court Intelligence plans",
        primaryHref: "/court-intelligence",
      },
      cards: [
        {
          id: "free",
          name: "Free",
          badge: "Start here",
          priceMonthly: "Free / month",
          priceYearly: "Free / year",
          description: "Perfect for testing core scheduling flows with small rosters and manual confirmations.",
          features: [
            "Unlimited schedules without invitations",
            "Invite up to 5 players per event",
            "15 SMS invitations every month",
            "Schedule insights & notifications",
          ],
          ctaLabel: "Create free account",
        },
        {
          id: "basic",
          name: "Scheduling Basic",
          badge: "Popular",
          priceMonthly: "$2.99 / month",
          priceYearly: "$24.99 / year",
          description: "Handle weekly drop-ins with larger invitation lists and automated SMS follow-ups.",
          features: [
            "Up to 5 active invitation-enabled schedules",
            "Invite up to 20 players per event",
            "75 SMS invitations every month",
            "Ad-free, branded organizer experience",
          ],
          ctaLabel: "Choose Basic",
          highlighted: true,
        },
        {
          id: "pro",
          name: "Scheduling Pro",
          badge: "Best value",
          priceMonthly: "$4.99 / month",
          priceYearly: "$39.99 / year",
          description: "Unlock unlimited invitations, priority SMS delivery, and scale programs without limits.",
          features: [
            "Unlimited invitation-enabled schedules",
            "Unlimited players per event",
            "150 SMS invitations every month",
            "Priority SMS delivery for faster confirmations",
          ],
          ctaLabel: "Upgrade to Pro",
        },
      ],
      table: {
        title: "Compare what's included",
        description:
          "Every tier keeps core scheduling tools unlocked. Upgrade when you need more invitations or faster confirmations.",
        featureColumnLabel: "Feature",
        planLabels: {
          free: "Free",
          basic: "Scheduling Basic",
          pro: "Scheduling Pro",
        },
        rows: [
          {
            label: "Max active schedules without invitations",
            values: { free: "Unlimited", basic: "Unlimited", pro: "Unlimited" },
          },
          {
            label: "Max active schedules with invitations",
            values: { free: "1", basic: "5", pro: "Unlimited" },
          },
          {
            label: "Max players invited per schedule",
            values: { free: "5", basic: "20", pro: "Unlimited" },
          },
          {
            label: "Max courts per event",
            values: { free: "1", basic: "1", pro: "1" },
          },
          {
            label: "Max SMS invitations per month",
            values: { free: "15", basic: "75", pro: "150" },
          },
          {
            label: "Schedule insights (who’s joining?)",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Event reminders & notifications",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Ad-free experience",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Scheduling window (days in advance)",
            values: { free: "30", basic: "30", pro: "30" },
          },
          {
            label: "Organizer can be outside the roster",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Max round robin events per month",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Max players per round robin",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Max single & double elimination tournaments per month",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Max players per single & double elimination tournament",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Priority SMS delivery",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Monthly price",
            values: { free: "Free", basic: "$2.99", pro: "$4.99" },
          },
          {
            label: "Yearly price",
            values: { free: "Free", basic: "$24.99", pro: "$39.99" },
          },
        ],
        legend: {
          check: "Included",
          cross: "Not included",
        },
      },
      cta: {
        title: "Have questions about team access or tournaments?",
        description:
          "We’re adding competitive formats soon. Join the founding organizer program to shape the roadmap and secure early pricing.",
        primaryLabel: "Talk with us",
        primaryHref: "/founding-organizers",
        secondaryLabel: "Explore product updates",
        secondaryHref: "/bugtracker",
      },
      footnote: "Manage upgrades directly inside the WhoPlays mobile app. Pricing shown in USD.",
    },
    foundingOrganizers: {
      hero: {
        eyebrow: "Founding organizer program",
        title: "Run your pickleball league smarter — and free for life",
        highlight: "The first 100 organizers get lifetime access to WhoPlays US.",
        description:
          "Host events, automate confirmations, and shape upcoming features. Keep your groups active and we’ll lock in Scheduler Pro for life when subscriptions launch.",
        badges: [
          {
            id: "google-play",
            label: "Get it on Google Play",
            href: "https://play.google.com/store/apps/details?id=com.whosplays.app&pli=1",
            image: "/images/google-play-badge.png",
            width: 500,
            height: 120,
            className: "h-18 w-auto",
          },
          {
            id: "app-store",
            label: "Download on the App Store",
            href: "https://apps.apple.com/app/apple-store/id6451442623?pt=126911496&ct=Founding%20Organizers&mt=8",
            image: "/images/apple-store-badge.svg",
            width: 175,
            height: 60,
            className: "h-12 w-auto",
          },
        ],
        primaryCtaLabel: "Schedule your first event now",
          primaryCtaHref: "/app-stores",
      },
      lifetime: {
        title: "Lifetime Scheduler Pro for active organizers",
        description:
          "Once the subscription feature is live, we’ll automatically grant lifetime Pro access to organizers who stay engaged.",
        bullets: [
          "Use WhoPlays to schedule and host your sessions.",
          "Stay active with at least one event every 60 days.",
          "Keep your community growing and we’ll keep Pro unlocked.",
        ],
        footnote: "This reward is our thank-you to the leaders who help us build the future of pickleball scheduling.",
      },
      benefits: {
        title: "Key benefits",
        items: [
          "Schedule games in seconds",
          "Invite players by SMS — no app required",
          "Limit RSVPs and track confirmations",
          "See who’s playing by level and time",
          "Manage your events like a pro",
        ],
      },
      reminder: {
        headline: "Only 100 organizers get free lifetime access",
        subheadline: "Claim your spot before it’s gone.",
      },
      video: {
        title: "See WhoPlays scheduling in action",
        src: "/videos/WhoPlays-Scheduler-Web-Version.mp4",
        poster: "https://whoplays.us/wp-content/uploads/2025/03/Image.png",
      },
    },
    privacyPolicy: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: March 30, 2025",
      intro: [
        "This Privacy Policy describes our policies and procedures on the collection, use, and disclosure of your information when you use the Service and explains your privacy rights and how the law protects you.",
        "We use your personal data to provide and improve the Service. By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy.",
      ],
      sections: [
        {
          heading: "Interpretation and Definitions",
          subSections: [
            {
              subheading: "Interpretation",
              paragraphs: [
                "The words with capitalized initials have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.",
              ],
            },
            {
              subheading: "Definitions",
              paragraphs: ["For the purposes of this Privacy Policy:"],
              definitions: [
                {
                  term: "Account",
                  description: "Means a unique account created for you to access our Service or parts of our Service.",
                },
                {
                  term: "Affiliate",
                  description:
                    "Means an entity that controls, is controlled by, or is under common control with a party.",
                },
                {
                  term: "Application",
                  description:
                    "Refers to WhoPlays US – Pickleball Scheduler & Tracker, the software program provided by the Company.",
                },
                {
                  term: "Company",
                  description:
                    '("the Company", "We", "Us", or "Our") refers to WhoPlays US, Inc, 45 Whistler Trace, Ponte Vedra FL 32081.',
                },
                {
                  term: "Country",
                  description: "Refers to: Florida, United States.",
                },
                {
                  term: "Device",
                  description:
                    "Means any device that can access the Service such as a computer, a cellphone, or a digital tablet.",
                },
                {
                  term: "Personal Data",
                  description: "Is any information that relates to an identified or identifiable individual.",
                },
                { term: "Service", description: "Refers to the Application." },
                {
                  term: "Service Provider",
                  description:
                    "Means any natural or legal person who processes the data on behalf of the Company.",
                },
                {
                  term: "Usage Data",
                  description:
                    "Refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself.",
                },
                {
                  term: "You",
                  description: "Means the individual accessing or using the Service.",
                },
              ],
            },
          ],
        },
        {
          heading: "Collecting and Using Your Personal Data",
          subSections: [
            {
              subheading: "Personal Data",
              paragraphs: ["We may collect personal information such as:"],
              bullets: ["Email address", "First name and last name", "Phone number", "Address, State, Province, ZIP/Postal code, City"],
            },
            {
              subheading: "Usage Data",
              paragraphs: [
                "This includes your IP address, browser type and version, the pages you visit, the time and date of your visit, the time spent on those pages, and other diagnostic data.",
              ],
            },
            {
              subheading: "Data Collected via Application",
              paragraphs: [
                "With your permission, we may collect additional information:",
              ],
              bullets: [
                "Location data",
                "Contacts (for invite functionality)",
                "Access to camera and photo library (for profile pictures or event photos)",
              ],
              footnote:
                "You can enable or disable access to this data at any time through your device settings.",
            },
            {
              subheading: "Use of Your Personal Data",
              paragraphs: ["We use your data for the following purposes:"],
              bullets: [
                "To provide and maintain our Service",
                "To manage your account",
                "To facilitate SMS invitations and app notifications",
                "To manage subscriptions and validate user entitlements",
                "To provide support and respond to inquiries",
                "To inform you about updates, offers, and changes",
                "To analyze performance and usage for improvements",
              ],
            },
            {
              subheading: "Subscription Services",
              paragraphs: [
                "WhoPlays US offers free, basic, and pro-tier subscriptions alongside tournament-specific subscriptions. For promotional purposes, some users may receive lifetime access. We reserve the right to revoke such access if the user has not scheduled an event or used key features within a 60-day period.",
                "In-app subscriptions may be managed through the Apple App Store or Google Play. These subscriptions follow the billing, cancellation, and renewal policies outlined by those platforms.",
              ],
            },
          ],
        },
        {
          heading: "Court Intelligence Camera Data",
          paragraphs: [
            "Court Intelligence receives outbound camera snapshots from venue-configured devices via API push or FTPS upload. WhoPlays does not connect to venue local networks to pull images.",
            "By default, snapshots are processed to compute operational signals (such as busyness and wait estimates) and are not retained.",
          ],
          subSections: [
            {
              subheading: "Debug mode and initialization behavior",
              bullets: [
                "If Camera Group debug mode is enabled by the venue user, snapshots may be retained for ML improvement until the configured debug expiration time.",
                "While a camera remains in initializing status, one latest initialization snapshot is retained and replaced by newer snapshots.",
                "When the camera leaves initializing status (for example, active or inactive), the initialization snapshot behavior ends.",
              ],
            },
            {
              subheading: "Operator and player visibility",
              bullets: [
                "Venue operators can remove camera-side configuration at any time to stop snapshot delivery.",
                "Players in WhoPlays US see live busyness and wait indicators for enabled venues, not camera setup controls.",
              ],
            },
          ],
        },
        {
          heading: "Data Retention and Deletion",
          paragraphs: [
            "Your data is retained only as long as needed for legal, operational, and analytical purposes.",
            "You may delete your account or request deletion of your data via the app or by contacting us.",
          ],
        },
        {
          heading: "Legal Disclosure and Security",
          paragraphs: [
            "We may disclose your data when required by law, to protect our rights, or to ensure user safety.",
            "We use commercially acceptable means to protect your data but cannot guarantee absolute security.",
          ],
        },
        {
          heading: "Children's Privacy",
          paragraphs: [
            "We do not knowingly collect information from anyone under 13. If you believe we have collected data from a child, please contact us.",
          ],
        },
        {
          heading: "Changes to This Privacy Policy",
          paragraphs: [
            "We may update this Privacy Policy. If material changes are made, you will be notified via email or in-app notice.",
            "All changes are effective when posted.",
          ],
        },
      ],
      contact: {
        heading: "Contact Us",
        paragraphs: ["If you have any questions, please contact us at:"],
        entries: [{ label: "Email", value: "support@whoplays.us" }],
      },
    },
    mySchedule: {
      back: 'Back to app',           // es: 'Volver a la app', pt: 'Voltar ao app'
      title: 'My Play Schedule',     // es: 'Mi agenda', pt: 'Minha agenda'
      create: 'Create Event',        // es: 'Crear evento', pt: 'Criar evento'
      edit: 'Edit',                  // es: 'Editar', pt: 'Editar'
      delete: 'Delete',              // es: 'Eliminar', pt: 'Excluir'
      empty: 'No scheduled plays yet.', // es: 'Sin partidos programados.', pt: 'Sem partidas agendadas.'
      untitled: 'Scheduled Match',   // es: 'Partido programado', pt: 'Partida agendada'
      startLabel: 'Start',
      endLabel: 'End',
      typeLabel: 'Type',
      courtLabel: 'Court',
      loading: 'Loading schedule...',
      refreshError: 'Unable to refresh your schedule.',
      cancel: 'Cancel',
      confirmCancel: 'Cancel this schedule?',
    },
    myScheduleEditor: {
      titleCreate: 'Schedule Event',
      titleEdit: 'Edit Schedule',
      subtitle: 'Set your time, court, format, and invites.',
      encourage: 'Invite friends—even if they are not on the app—to make your match more fun.',
      dateLabel: 'Date',
      startLabel: 'Start time',
      endLabel: 'End time',
      courtLabel: 'Court',
      formatLabel: 'Format',
      invitesLabel: 'Invitations',
      invitesCount: 'Invited: {count}',
      inviteCta: 'Invite Players ({count})',
      limitToggle: 'Enable confirmation limit',
      limitLabel: 'Set Limit',
      limitHelper: 'Starts at 4 once a limit is enabled.',
      limitLocked: 'Set Limit unlocks after inviting 4 players.',
      saveCreate: 'Save Schedule',
      saveEdit: 'Save Changes',
      reset: 'Reset',
      validation: {
        dateRequired: 'Please select a date.',
        courtRequired: 'Please select a court.',
        endAfterStart: 'Please select an end time after the start time.',
        conflict: 'You already have a scheduled play at this time.',
        saveError: 'Failed to save. Please try again.',
        saveSuccess: 'Schedule saved successfully.',
      },
      noCourt: 'You must have a Primary court to schedule a play.',
    },
    myScheduleInvite: {
      title: 'Send Invites',
      selected: 'Selected {count} invitees',
      back: 'Back to schedule',
      addGuest: 'Add Guest',
      playerNamePlaceholder: 'Player name',
      phonePlaceholder: 'Phone number',
      add: 'Add',
      contactsUnavailable: 'Add from Contacts (unavailable)',
      contactsAvailable: 'Add from Contacts',
      myGroup: 'My Group',
      following: 'Following',
      searchPlaceholder: 'Search players',
      noPlayers: 'No players found.',
      validation: {
        nameEmpty: 'Name cannot be empty. Please enter the name of the guest.',
        phoneEmpty: 'Phone number cannot be empty. Please enter a valid phone number.',
        phoneNumeric: 'Phone number can only contain numeric characters.',
        phoneLength: 'Invalid USA phone number. It must contain exactly 10 digits.',
        phoneInvalid: 'Invalid phone number.',
        selfNumber: 'You have added your own number. You are already included in this schedule as the host.',
        duplicateGuest: 'This player has already been added as a guest to this schedule.',
        registered: 'This player is already registered in WhoPlays. We have added their existing account: {name}.',
      },
    },
  },
  es: {
    languageName: "Español",
    nav: {
      features: "Características",
      workflow: "Cómo funciona",
      courtIntel: "Court Intelligence",
      foundingProgram: "Sedes fundadoras",
      plans: "Planes",
      organizers: "Organizadores fundadores",
      login: "Iniciar sesión",
      signup: "Crear cuenta",
      authChooser: {
        loginTitle: "Inicia sesión en WhoPlays",
        loginDescription: "Elige la ruta que coincide con cómo usas WhoPlays.",
        signupTitle: "Crea tu cuenta de WhoPlays",
        signupDescription: "Elige la ruta que coincide con cómo planeas usar WhoPlays.",
        playerLabel: "Jugador",
        playerDescription: "Organiza juegos, registra partidos y consulta visibilidad en vivo de las sedes.",
        operatorLabel: "Dueño u operador de sede",
        operatorDescription: "Continúa al onboarding de Court Intelligence para tu sede.",
        foundingLabel: "Founding Venue Program",
        foundingDescription: "Si vas a postularte a una de las plazas limitadas de lanzamiento, empieza aquí.",
        foundingCta: "Ver sedes fundadoras",
        closeLabel: "Cerrar",
      },
    },
    auth: {
      otp: {
        loginTitle: "Inicia sesión en WhoPlays",
        signupTitle: "Crea tu cuenta en WhoPlays",
        loginDescription: "Usa tu correo electrónico o número móvil para recibir un código de acceso de un solo uso.",
        signupDescription: "Crea tu cuenta con correo electrónico o número móvil. Te enviaremos un código de acceso de un solo uso para confirmar.",
        displayNameLabel: "Nombre visible",
        emailTab: "Correo",
        phoneTab: "Teléfono",
        emailAddressLabel: "Correo electrónico",
        mobileNumberLabel: "Número móvil",
        countryCodeLabel: "Código de país",
        smsHint: "Incluye tu código de área. Enviaremos un código de 6 dígitos por SMS.",
        captchaHint: "Protegido por reCAPTCHA. La verificación se ejecuta automáticamente.",
        errorCaptcha: "No pudimos verificar que eres una persona. Inténtalo de nuevo.",
        verificationCodeLabel: "Código de verificación",
        resendIn: "Reenviar en {seconds}s",
        resendCode: "Reenviar código",
        errorPhoneRegionOnly: "El inicio de sesión por teléfono es solo para EE. UU./Canadá. Usa correo electrónico para continuar.",
        errorSendCode: "No se pudo enviar el código.",
        errorStartVerification: "No se pudo iniciar la verificación.",
        infoCodeSent: "Código de verificación enviado. Ingresa el código de 6 dígitos para continuar.",
        infoCodeResent: "Código reenviado. Revisa tu correo o teléfono.",
        errorResendCode: "No se pudo reenviar el código.",
        errorVerifyCode: "No se pudo verificar el código.",
        submitWait: "Espera...",
        submitSendCode: "Enviar código",
        submitVerifyContinue: "Verificar y continuar",
        socialDivider: "O continuar con",
        continueWithGoogle: "Continuar con Google",
        continueWithApple: "Continuar con Apple",
        loginNeedAccount: "¿Necesitas una cuenta?",
        loginNeedAccountCta: "Crear cuenta",
        signupHasAccount: "¿Ya tienes una cuenta?",
        signupHasAccountCta: "Iniciar sesión",
        userNotFound: "No encontramos una cuenta con ese correo electrónico o número de teléfono.",
      },
    },
    toggles: {
      theme: "Tema",
      language: "Idioma",
      light: "Claro",
      dark: "Oscuro",
      system: "Sistema",
    },
    hero: {
      eyebrow: "Jugadores, dueños de sedes y comunidades conectados",
      title: "La plataforma de pickleball que mantiene tus canchas activas",
      description:
        "WhoPlays ayuda a los jugadores a organizar partidos y a las sedes a operar mejor con señales en vivo de ocupación, tiempo de espera y canchas disponibles con Court Intelligence.",
      primaryCta: "Comenzar en la web",
      primaryHref: "/auth/signup",
      secondaryCta: "Obtener la app móvil",
      secondaryHref: "/app-stores",
      badgesLabel: "También disponible en iOS y Android",
      trust: "Confiado por jugadores, clubes y programas municipales.",
    },
    stats: [
      { value: "Feed de canchas", label: "Mira cuándo se activan las canchas de pickleball cerca de ti" },
      { value: "Invitaciones SMS", label: "Llena tus sesiones de pickleball con un toque y confirmaciones por texto" },
      { value: "Estadísticas", label: "Registra partidos de pickleball y sigue tu avance en DUPR" },
    ],
    appShell: {
      tabs: {
        realtime: "En vivo",
        network: "Mi red",
        competitions: "Competiciones",
        matches: "Partidos",
        rank: "Ranking",
        messages: "Centro MSG",
      },
      matches: {
        title: "Partidos",
        pending: "Pendientes",
        addMatch: "Agregar partido",
        loadMore: "Cargar más",
        loading: "Cargando partidos...",
        empty: "No hay partidos para mostrar.",
        segments: {
          owned: "Propios",
          mygroup: "Mi grupo",
          mycourts: "Mis sedes",
        },
        errors: {
          loadFailed: "No se pueden cargar los partidos ahora.",
          saveFailed: "No se pudo guardar el partido.",
        },
        editor: {
          title: "Nuevo partido",
          subtitle: "Agrega marcadores y jugadores.",
          cancel: "Cancelar",
          save: "Guardar",
          saving: "Guardando...",
          format: "Formato",
          singles: "Singles",
          doubles: "Dobles",
          winner: "Ganador",
          selectWinner: "Selecciona ganador",
          team1: "Equipo 1",
          team2: "Equipo 2",
          player: "Usuario",
          score: "Marcador",
        },
        validation: {
          missingPlayers: "Agrega todos los jugadores requeridos.",
          missingWinner: "Selecciona un ganador.",
        },
      },
      rank: {
        title: "Ranking",
        loading: "Cargando rankings...",
        empty: "No hay datos de ranking para mostrar.",
        segments: {
          wprank: "WP Rank",
          overall: "General",
          h2h: "H2H",
        },
        filters: {
          skillAll: "Todos los niveles",
          singles: "Singles",
          doubles: "Dobles",
          week: "Semana",
          month: "Mes",
          all: "Todo el tiempo",
          allCourts: "Todas las canchas",
          groupNone: "Sin filtro de grupo",
          groupMyGroup: "Mi grupo",
          groupFollowing: "Siguiendo",
          formatAll: "Todos los formatos",
          today: "Hoy",
          yesterday: "Ayer",
          lastWeek: "Semana pasada",
          lastMonth: "Mes pasado",
          threeMonths: "3 meses",
          sixMonths: "6 meses",
          year: "1 año",
          fiveYears: "5 años",
          allTime: "Todo el tiempo",
        },
        labels: {
          wins: "Ganados",
          losses: "Perdidos",
          matches: "Partidos",
          winPct: "Win%",
          rankLabel: "Ranking",
        },
      },
      messagesTab: {
        title: "Centro de mensajes",
        empty: "Aún no hay mensajes.",
        clearAll: "Borrar todo",
        markAllRead: "Marcar todo como leído",
        delete: "Eliminar",
        read: "Marcar leído",
        unread: "Marcar sin leer",
        receivedAt: "Recibido",
      },
      socket: {
        schedule: "Agenda actualizada",
        match: "Actualización de partido",
        ping: "Nuevo ping recibido",
        dupr: "DUPR actualizado",
        generic: "Nueva notificación",
        subscriptionTitle: "Suscripción actualizada",
        subscriptionBody: "Tu estado de suscripción se ha actualizado.",
        matchUnanimousTitle: "Partido aceptado",
        matchUnanimousBody: "¡Buenas noticias! Un partido que jugaste fue aceptado por todos. ¡Ya es oficial!",
        matchAcceptedTitle: "Jugador aceptó el partido",
        matchAcceptedBody: "{player} aceptó el partido. Esperando a los demás.",
        matchPendingTitle: "Partidos pendientes",
        matchPendingBody: "¡Nuevos resultados pendientes! Confirma para que cuente en tu ranking.",
        duprUpdatedTitle: "DUPR actualizado",
        duprUpdatedBody: "Singles: {singles}\nDobles: {doubles}",
        questionTitle: "Nueva pregunta de agenda",
      },
      subscriptions: {
        title: "Suscripciones",
        subtitle: "Elige un plan para potenciar tu programación en WhoPlays.",
        detailsLink: "Ver detalles completos del plan",
        currentPlan: "Plan actual",
        statusLabel: "Estado",
        free: "Gratis",
        active: "Activo",
        select: "Seleccionar",
        checkout: "Pagar con Stripe",
        close: "Cerrar",
        manage: "Gestionar / cancelar en Stripe",
        manageHint: "Abriremos el portal de Stripe. Si no ves tu suscripción, usa el correo del recibo.",
        providerManaged: "Gestionado por {provider}",
        providerManagedHint: "Para gestionar o cancelar esta suscripción, ve a {provider}.",
        error: "No pudimos iniciar el pago. Inténtalo nuevamente.",
        successTitle: "Pago completado",
        successToast: "Pago de suscripción completado. Actualizando tu acceso…",
        cancelTitle: "Pago cancelado",
        cancelToast: "Cancelaste el pago en Stripe.",
        plans: {
          "com.whoplays.scheduling.basic.monthly": {
            label: "Scheduler Básico (Mensual)",
            price: "Cobro mensual",
          },
          "com.whoplays.scheduling.basic.yearly": {
            label: "Scheduler Básico (Anual)",
            price: "Cobro anual",
          },
          "com.whoplays.scheduling.pro.monthly": {
            label: "Scheduler Pro (Mensual)",
            price: "Cobro mensual",
          },
          "com.whoplays.scheduling.pro.yearly": {
            label: "Scheduler Pro (Anual)",
            price: "Cobro anual",
          },
        },
      },
      courtIntelligenceResume: {
        title: "¿Continuar configuración de Court Intelligence?",
        description:
          "Encontramos progreso guardado en el onboarding de Court Intelligence. Elige cómo quieres continuar.",
        savedStep: "Paso guardado: {step}",
        stepNames: {
          account: "Cuenta",
          subscription: "Suscripción",
          venues: "Sedes",
        },
        continuePlayer: "Continuar como jugador",
        continuePlayerHint: "Mantén tu progreso actual de Court Intelligence y sigue en modo jugador.",
        continuePlayerReset: "Continuar como jugador y reiniciar configuración",
        continuePlayerResetHint:
          "Elimina el progreso guardado de Court Intelligence. La próxima vez iniciará desde el paso 1.",
        continueOnboarding: "Continuar onboarding de Court Intelligence",
        continueOnboardingHint: "Ve a tu paso guardado y termina la configuración.",
        resetError: "No se pudo reiniciar la configuración de Court Intelligence. Inténtalo de nuevo.",
      },
      courtIntelligenceUi: {
        sections: {
          profile: "Perfil",
          community: "Comunidad",
          venues: "Sedes",
          cameras: "Cámaras",
          initialization: "Inicialización",
          logs: "Registros",
          debug: "Diagnóstico",
          stats: "Estadísticas",
          security: "Seguridad",
          subscription: "Suscripción",
        },
        workspace: {
          loading: "Cargando Court Intelligence…",
          tipsTitle: "Consejos de Court Intelligence",
          tipsProgress: "Consejo {current} de {total}",
          tipsBack: "Atrás",
          tipsNext: "Siguiente",
          tipsClose: "Cerrar",
          openMenu: "Abrir menú de Court Intelligence",
          closeMenu: "Cerrar navegación",
          switchToPlayerMode: "Cambiar a modo jugador",
          brand: {
            alt: "Court Intelligence",
            eyebrow: "Court",
            title: "Intelligence",
          },
          accessRequired: {
            title: "Se requiere acceso a Court Intelligence",
            description: "Tu cuenta aún no tiene acceso por suscripción de Court Intelligence ni permisos por sede.",
            startSetup: "Iniciar configuración",
            backToPlayerMode: "Volver a modo jugador",
          },
          readOnly: {
            venuesOwnerOnlyTitle: "La gestión de sedes es solo para dueños",
            venuesOwnerOnlyDescription:
              "Solo los dueños de la suscripción pueden reclamar o crear sedes en modo Court Intelligence.",
            camerasRestrictedTitle: "Cámaras restringidas",
            camerasRestrictedDescription:
              "Solo los dueños de cancha y administradores de cancha pueden gestionar la configuración de cámaras.",
            statsSubscriptionRequiredTitle: "Las estadísticas requieren una suscripción activa de Court Intelligence",
            statsSubscriptionRequiredDescription:
              "Reactiva la suscripción de Court Intelligence para ver estadísticas en vivo y analítica histórica.",
            securityOwnerOnlyTitle: "Seguridad solo para dueños",
            securityOwnerOnlyDescription:
              "Solo los dueños de cancha pueden otorgar o revocar permisos de court_admin/monitor.",
            subscriptionOwnerOnlyTitle: "Suscripción solo para dueños",
            subscriptionOwnerOnlyDescription:
              "Solo los dueños de cancha pueden cambiar o cancelar la suscripción de Court Intelligence.",
          },
          community: {
            title: "Comunidad",
            description:
              "Aquí estarán las herramientas de comunicación para monitores (alertas, publicaciones y avisos visibles para jugadores).",
          },
          logs: {
            title: "Registros operativos",
            description:
              "Revisa eventos de ML, problemas de procesamiento y auditoría por sede. Aquí también estarán los controles para borrar logs.",
            labels: {
              court: "Cancha",
              notSelected: "Sin sede seleccionada",
              notAvailable: "N/D",
              scope: "Alcance",
              pageOffset: "Offset",
            },
            controls: {
              searchPlaceholder: "Buscar por nombre de sede",
              cityPlaceholder: "Ciudad",
              statePlaceholder: "Estado",
              ownerPlaceholder: "Username del owner",
              onlyWithCi: "Solo sedes con Court Intelligence",
              searchButton: "Buscar sedes",
              venueLabel: "Sede",
              pageSizeLabel: "Tamaño de página",
              refreshButton: "Refrescar logs",
              deleteSelectedButton: "Borrar seleccionados",
              deleteAllButton: "Borrar todos",
              prevPageButton: "Anterior",
              nextPageButton: "Siguiente",
            },
            loading: {
              courts: "Cargando sedes...",
              logs: "Cargando logs...",
            },
            errors: {
              loadCourts: "No se pudo cargar la lista de sedes.",
              loadLogs: "No se pudieron cargar los logs.",
              deleteLogs: "No se pudieron borrar los logs.",
            },
            notices: {
              deletedCount: "Se borraron {count} logs.",
            },
            confirmDeleteAll: "¿Borrar todos los logs de esta sede?",
            table: {
              columns: {
                event: "Evento",
                detail: "Detalle",
                createdAt: "Creado",
                deliveredAt: "Entregado",
              },
              empty: "No hay logs para la sede seleccionada.",
            },
          },
          debug: {
            title: "Diagnóstico",
            description:
              "Aquí estarán las herramientas solo para admin para troubleshooting profundo (payloads ML, artefactos debug y trazas de procesamiento).",
          },
          stats: {
            title: "Analítica de Court Intelligence",
            description:
              "Usa el estado realtime del portafolio y las lecturas históricas para entender la actividad de la sede y la ocupación de canchas mapeadas.",
            labels: {
              court: "Cancha",
              notAvailable: "N/D",
            },
            controls: {
              searchPlaceholder: "Buscar por nombre de sede",
              cityPlaceholder: "Ciudad",
              statePlaceholder: "Estado",
              ownerPlaceholder: "Username del owner",
              onlyWithCi: "Solo sedes con Court Intelligence",
              searchButton: "Buscar sedes",
              courtsLabel: "Sedes en alcance",
              fromLabel: "Desde",
              toLabel: "Hasta",
              bucketLabel: "Agrupación",
              bucketHour: "Hora",
              bucketDay: "Día",
              timezoneLabel: "Zona horaria",
              refreshRealtimeButton: "Refrescar overview",
              refreshHistoryButton: "Aplicar historial",
              downloadReadingsCsvButton: "Exportar CSV de lecturas",
              downloadOccupancyCsvButton: "Exportar CSV de ocupación",
            },
            loading: {
              courts: "Cargando sedes...",
              realtime: "Actualizando overview...",
              analytics: "Cargando historial...",
              occupancy: "Cargando ocupación por cancha...",
            },
            errors: {
              loadCourts: "No se pudo cargar la lista de sedes.",
              loadRealtime: "No se pudo cargar el portafolio realtime.",
              loadAnalytics: "No se pudo cargar el historial de lecturas.",
              loadOccupancy: "No se pudo cargar el historial de ocupación por cancha.",
            },
            cards: {
              peopleRightNow: "Personas ahora",
              inUseOpen: "En uso / jugables",
              waitingPlayers: "Jugadores esperando",
              avgQueueEta: "ETA promedio de espera",
              avgBusyness: "Ocupación promedio",
              maxBusyness: "Pico de ocupación",
            },
            helpers: {
              courtAvailabilitySupport: "{supported} de {total} sedes soportan disponibilidad de canchas",
              waitMetricsSupport: "{supported} de {total} sedes soportan métricas de espera",
              scoredVenuesSupport: "{supported} de {total} sedes tienen score",
            },
            tables: {
              statusTitle: "Estado por sede",
              timelineTitle: "Tendencia de lecturas",
              readingsSummary: "{count} lecturas en el rango seleccionado",
              venueColumn: "Sede",
              busynessColumn: "Ocupación",
              peopleColumn: "Personas",
              waitingColumn: "Espera",
              queueColumn: "ETA cola",
              timelineBucketColumn: "Inicio bucket",
              timelineReadingsColumn: "Lecturas",
              timelineAvgPeopleColumn: "Promedio por lectura",
              timelineOccupancyColumn: "Ocupación de actividad",
              emptyRealtime: "No hay sedes disponibles para este alcance.",
              emptyTimeline: "No hay historial de lecturas para el rango seleccionado.",
              occupancyTitle: "Historial de ocupación por cancha",
              occupancySubtitle: "El historial por cancha solo está disponible para sedes mapeadas.",
              occupancyExplainer:
                "Cada celda muestra el porcentaje de lecturas de ese bloque de tiempo en las que la cancha apareció ocupada.",
              occupancyLegendOpen: "Más libre",
              occupancyLegendModerate: "Moderado",
              occupancyLegendBusy: "Más ocupada",
              timelineAvailableSummary: "{available} de {total} sedes tienen historial por cancha",
              emptyOccupancy: "No hay historial de ocupación por cancha para este alcance.",
              occupancyCourtColumn: "Cancha",
              occupancyRowSummary: "{percent} ocupada",
              singlesDoublesSummary: "S: {singles} · D: {doubles}",
              avgPlayersWhenOccupiedShort: "Prom. ocupada",
              maxPlayersWhenOccupiedShort: "Máx. ocupada",
              singlesShareShort: "Singles",
              doublesShareShort: "Dobles",
              singlesShareCompact: "S:",
              doublesShareCompact: "D:",
              readingsShort: "Lecturas",
              occupiedReadingsShort: "Ocupadas",
              occupancyMappingRequired: "Se requiere mapear canchas para mostrar el historial de ocupación por cancha.",
              occupancyUnavailable: "El historial de ocupación por cancha no está disponible para esta sede.",
            },
            badges: {
              courtAvailabilityEnabled: "Disponibilidad de canchas habilitada",
              waitMetricsEnabled: "Métricas de espera habilitadas",
            },
          },
          liveBusyness: {
            title: "Ocupación en vivo",
            refresh: "Actualizar",
            refreshing: "Actualizando…",
            loading: "Cargando ocupación en vivo…",
            noData: "No hay datos de ocupación en vivo por ahora.",
            loadError: "No se pudo cargar la ocupación en vivo.",
            courtLabel: "Cancha",
            previousVenue: "Sede anterior",
            nextVenue: "Sede siguiente",
            scoreLabel: "Ocupación",
            scoreSecondary: "Puntaje {score}",
            occupancyLabel: "En uso / jugables",
            occupancyLabelCompact: "En uso",
            occupancyValueFormat: "{inUse} de {playable}",
            waitingLabel: "Esperando",
            etaLabel: "ETA fila",
            peopleLabel: "Personas",
            confidenceLabel: "Confianza",
            confidenceInline: "Confianza {value}",
            modeInfoLabel: "Detalles del modo de medición",
            estimateBadge: "Estimado",
            mappedCourtsLabel: "Canchas mapeadas",
            queueAreasLabel: "Zonas de fila",
            detailsEyebrow: "Detalles de medición en vivo",
            closeDetails: "Cerrar",
            formulaTitle: "Cómo se mide esta sede",
            visibleKpisTitle: "KPIs visibles",
            hiddenKpisTitle: "KPIs ocultos",
            setupQualityTitle: "Calidad de configuración",
            defaultsTitle: "Valores por defecto",
            playersPerGameDefault: "Jugadores por juego: {value}",
            avgGameMinutesDefault: "Minutos promedio por juego: {value}",
            queueTargetMinutesDefault: "Minutos objetivo de fila: {value}",
            scoreMetric: "Puntaje de ocupación",
            levelMetric: "Banda de ocupación",
            weatherMetric: "Clima",
            updatedMetric: "Hora de actualización",
            perCourtListMetric: "Lista por cancha",
            noneLabel: "Ninguno",
            modeCrowdOnly: "Solo afluencia",
            modeCapacityEstimated: "Capacidad estimada",
            modeCourtMapped: "Canchas mapeadas",
            modeCourtAndQueue: "Canchas + fila",
            modeCrowdOnlyHelp: "Cuenta personas en vista para una actividad aproximada de la sede.",
            modeCapacityEstimatedHelp: "Compara la actividad visible contra la capacidad configurada de canchas para una estimación rápida.",
            modeCourtMappedHelp: "Las canchas visibles están mapeadas, por eso se soporta disponibilidad de canchas.",
            modeCourtAndQueueHelp: "Canchas y zonas de fila están mapeadas, por eso se soportan métricas de espera.",
            formulaCrowdOnly: "Sin puntaje. Usa solo el conteo de personas observado por la señal CI.",
            formulaCapacityEstimated: "score = clamp(totalPeople / (configuredCourts * playersPerGame)) * 100",
            formulaCourtMapped: "score = ((0.55 * courtSaturation) + (0.10 * backgroundPressure)) / 0.65 * 100",
            formulaCourtAndQueue: "score = ((0.55 * courtSaturation) + (0.35 * queuePressure) + (0.10 * backgroundPressure)) * 100",
            updatedAt: "Actualizado: {time}",
            notAvailable: "N/D",
          },
          security: {
            title: "Seguridad y permisos",
            description: "Los dueños de cancha pueden otorgar acceso por sede a otros usuarios como court_admin o monitor.",
            profileReadyInstruction:
              "Antes de otorgar acceso, pide al usuario iniciar sesión en WhoPlays en modo Player y actualizar su perfil (nombre, nickname y email o teléfono).",
            limitNotice: "Límite por sede: hasta {adminLimit} court_admin y {monitorLimit} monitor.",
            venueLabel: "Sede",
            roleLabel: "Rol",
            addRoleButton: "Agregar rol",
            addRoleModalTitle: "Agregar rol por sede",
            addRoleModalSubtitle: "Selecciona rol y usuario para esta sede.",
            searchLabel: "Buscar usuario",
            searchPlaceholder: "Nombre, nickname o ID de usuario",
            selectUserLabel: "Usuario",
            selectUserPlaceholder: "Selecciona un usuario",
            noUsersFound: "No se encontraron usuarios para esta búsqueda.",
            closeModal: "Cerrar",
            cancelButton: "Cancelar",
            noVenuesAvailable: "No hay sedes disponibles",
            adminsCountLabel: "Court admin",
            monitorsCountLabel: "Monitor",
            roleOwner: "Court owner",
            roleAdmin: "Court admin",
            roleMonitor: "Monitor",
            assignRole: "Asignar rol",
            updateRole: "Actualizar rol",
            removeRole: "Quitar rol",
            saving: "Guardando…",
            loadingAssignments: "Cargando asignaciones...",
            loadingUsers: "Buscando usuarios...",
            currentAssignmentsTitle: "Asignaciones actuales",
            noAssignments: "Aún no hay asignaciones.",
            assignmentInfoFallback: "Detalles de perfil pendientes.",
            ownerRoleReadOnly: "No puedes cambiar court_owner aquí.",
            userAlreadyHasRole: "{username} ya tiene el rol {role}.",
            limitReachedAdmin: "Límite de court_admin alcanzado (2). Quita un court_admin para continuar.",
            limitReachedMonitor: "Límite de monitor alcanzado (3). Quita un monitor para continuar.",
            errors: {
              loadRoles: "No se pudieron cargar los roles asignados.",
              searchUsers: "No se pudo buscar usuarios.",
              assignRole: "No se pudo asignar el rol.",
              removeRole: "No se pudo quitar el rol.",
            },
            notices: {
              roleGranted: "Rol {role} otorgado a {username}.",
              roleUpdated: "Rol {role} actualizado para {username}.",
              roleRemoved: "Rol removido para {username}.",
            },
          },
          subscription: {
            title: "Suscripción",
            description: "Solo los dueños de cancha pueden modificar o cancelar la facturación de Court Intelligence.",
            privacyTitle: "Privacidad y visibilidad para jugadores",
            privacyItems: [
              "Las cámaras envían snapshots hacia la API de WhoPlays. No nos conectamos a tu red local.",
              "Por defecto los snapshots se procesan y no se almacenan, excepto retención opcional de debug controlada por grupos de cámara.",
              "Los jugadores en WhoPlays US reciben señales gratis de ocupación y espera cuando tu sede habilita Court Intelligence.",
              "La compra de hardware e instalación en sitio no están incluidas en las suscripciones de Court Intelligence.",
            ],
            privacyLinkLabel: "Leer Política de Privacidad completa",
            currentPlanLabel: "Plan actual",
            currentPlanBadge: "Actual",
            currentSkuLabel: "SKU de suscripción actual",
            noneLabel: "Ninguno",
            statusLabel: "Estado",
            notAvailableLabel: "N/D",
            expiresLabel: "Expira",
            usageTitle: "Uso de capacidad",
            usedVenuesLabel: "Sedes usadas",
            maxVenuesLabel: "Límite de sedes",
            usedCamerasLabel: "Cámaras usadas",
            maxCamerasLabel: "Límite de cámaras",
            remainingVenuesLabel: "Sedes restantes",
            remainingCamerasLabel: "Cámaras restantes",
            unlimitedLabel: "Ilimitado",
            addOnCountsTitle: "Cantidades de add-ons",
            includedBaseLabel: "Incluido en base: {venues} sede y {cameras} cámaras",
            activeVenueAddonsLabel: "Add-ons extra de sede activos",
            activeCameraAddonsLabel: "Add-ons extra de cámara activos",
            safeRemoveVenueLabel: "Add-ons de sede removibles ahora",
            safeRemoveCameraLabel: "Add-ons de cámara removibles ahora",
            refreshUsageButton: "Actualizar uso",
            refreshingUsageLabel: "Actualizando…",
            plans: {
              baseMonthlyName: "Court Intelligence Base (Mensual)",
              baseMonthlyDetails: "Cobro mensual. Cancela cuando quieras.",
              baseYearlyName: "Court Intelligence Base (Anual)",
              baseYearlyDetails: "Cobro anual. Mejor valor para operaciones estables.",
            },
            baseFeatures: {
              venuesIncluded: "{count} sede incluida",
              camerasIncluded: "{count} cámaras incluidas",
              liveOps: "Ocupación en vivo, estimaciones de espera, estadísticas, logs y acceso por roles",
            },
            planChangeTitle: "Cambiar plan",
            planChangeDescription: "Cambia la frecuencia de cobro de tu plan base de Court Intelligence.",
            switchToPlanButton: "Cambiar a {plan}",
            alreadyCurrentPlan: "Plan actual seleccionado",
            addOnTitle: "Catálogo de add-ons",
            addOnDescription: "Los add-ons se habilitan tras activar el plan base y se gestionan desde esta sección.",
            addOnManageTitle: "Agregar capacidad de sedes y cámaras",
            addOnManageDescription: "Usa add-ons para aumentar límites sin salir de la app.",
            billingMonthly: "Mensual",
            billingYearly: "Anual",
            addOnLockedCycleNotice: "Los add-ons se facturan {cycle} para coincidir con tu plan base",
            addOnBaseRequired: "Activa un plan base de Court Intelligence antes de gestionar add-ons.",
            cameraAddOnTitle: "Add-on de cámara",
            venueAddOnTitle: "Add-on de sede",
            addCameraCapacityButton: "Agregar capacidad de cámara",
            removeCameraCapacityButton: "Reducir capacidad de cámara",
            addVenueCapacityButton: "Agregar capacidad de sede",
            removeVenueCapacityButton: "Reducir capacidad de sede",
            addOnModalTitle: "Confirmar cambio de add-on",
            addOnModalSubtitle: "Aplica múltiples unidades en una sola transacción.",
            addOnTypeLabel: "Tipo de add-on",
            addOnTypeCamera: "Capacidad de cámara",
            addOnTypeVenue: "Capacidad de sede",
            addOnActionLabel: "Acción",
            addOnActionAdd: "Agregar capacidad",
            addOnActionRemove: "Reducir capacidad",
            addOnQuantityLabel: "Cantidad",
            addOnUnitPriceLabel: "Precio unitario",
            addOnTotalLabel: "Total estimado",
            addOnImpactLabel: "Impacto en capacidad:",
            addOnImpactVenues: "Sedes {delta}",
            addOnImpactCameras: "Cámaras {delta}",
            addOnCancelButton: "Cancelar",
            addOnConfirmButton: "Confirmar cambio",
            addOnConfirmingButton: "Aplicando…",
            billingTimingTitle: "Momento de facturación",
            billingTimingItems: [
              "La capacidad se actualiza inmediatamente después de confirmar.",
              "Los cargos y créditos se reflejan en el próximo período de factura.",
            ],
            startingCheckout: "Iniciando checkout…",
            checkoutButton: "Pagar con Stripe",
            openBillingPortalButton: "Abrir portal de facturación",
            errors: {
              noCheckoutUrl: "No se recibió URL de checkout.",
              startCheckout: "No se pudo iniciar el checkout.",
              noPortalUrl: "No se recibió URL del portal de facturación.",
              openPortal: "No se pudo abrir el portal de facturación.",
              manageAddon: "No se pudo actualizar la capacidad del add-on.",
              noCameraAddonsToRemove: "No hay add-ons de cámara removibles. Reduce cámaras primero si el uso está al límite.",
              noVenueAddonsToRemove: "No hay add-ons de sede removibles. Reduce sedes o cámaras primero si el uso está al límite.",
            },
            notices: {
              updatedSuccess: "Suscripción actualizada correctamente.",
              checkoutCanceled: "El checkout fue cancelado.",
              cameraAddonIncreased: "Se agregaron {count} add-ons de cámara. Límite de cámaras: {before} -> {after}.",
              cameraAddonDecreased: "Se removieron {count} add-ons de cámara. Límite de cámaras: {before} -> {after}.",
              venueAddonIncreased:
                "Se agregaron {count} add-ons de sede. Límite de sedes: {before} -> {after}. Límite de cámaras: {cameraBefore} -> {cameraAfter}.",
              venueAddonDecreased:
                "Se removieron {count} add-ons de sede. Límite de sedes: {before} -> {after}. Límite de cámaras: {cameraBefore} -> {cameraAfter}.",
            },
          },
          profile: {
            title: "Perfil",
            description: "Actualiza los datos de cuenta usados para operar sedes.",
            photo: {
              title: "Foto de perfil (opcional)",
              alt: "Avatar de perfil",
              upload: "Subir foto",
              uploading: "Subiendo…",
            },
            fields: {
              name: "Nombre",
              email: "Email",
              phoneNumber: "Teléfono",
              countryCode: "Código",
              country: "País",
              selectCountry: "Seleccionar país",
              city: "Ciudad",
              state: "Estado",
              zipcode: "Código postal",
            },
            helper: {
              emailLocked: "Bloqueado porque este email es tu identificador de acceso.",
              emailOptionalWhenPhoneLogin: "Opcional cuando tu cuenta inicia sesión con teléfono.",
              phoneLocked: "Bloqueado porque este teléfono es tu identificador de acceso.",
              phoneHint: "Escribe el número local, o pega el número completo con +código país.",
            },
            save: "Guardar perfil",
            saving: "Guardando…",
            notices: {
              profileSaved: "Perfil guardado.",
            },
            errors: {
              uploadAvatar: "No se pudo subir el avatar.",
              uploadAvatarNoFilename: "La carga del avatar no devolvió nombre de archivo.",
              nameRequired: "El nombre es obligatorio.",
              saveProfile: "No se pudo guardar el perfil.",
            },
          },
          tipsItems: [
            {
              title: "Cobertura de cámaras",
              body: "Usa 1 cámara para hasta 2 canchas para mejor precisión de ocupación.",
            },
            {
              title: "Ubicación de cámara",
              body: "Instala cámaras en alto y fijas para reducir oclusiones y lecturas falsas.",
            },
            {
              title: "Orden de zonas",
              body: "Empieza con cobertura total y luego agrega polígonos de cancha, espera y banca.",
            },
            {
              title: "Señal de fila",
              body: "Usa queueSignal solo en zonas reales de espera, no en rutas de tránsito.",
            },
            {
              title: "Precisión del conteo",
              body: "El conteo de personas es una estimación operativa, no un conteo exacto.",
            },
            {
              title: "Modelo de tiempo de espera",
              body: "La espera se estima por jugadores esperando, canchas jugables, duración de juego y jugadores por juego.",
            },
            {
              title: "Menor confianza",
              body: "La estimación es menos precisa con cobertura parcial, sin zonas de espera o snapshots demorados.",
            },
            {
              title: "Cadencia recomendada",
              body: "Cadencia recomendada: API cada 30s y FTPS cada 60s.",
            },
            {
              title: "Higiene de credenciales",
              body: "Rota tokens de cámara o contraseñas FTP después de instalación o cambios de personal.",
            },
            {
              title: "Alcance del servicio",
              body: "La compra de hardware e instalación de cámaras no están incluidas en los planes de Court Intelligence.",
            },
          ],
          roleLabels: {
            owner: "Dueño de cancha",
            admin: "Administrador de cancha",
            monitor: "Monitor",
            systemAdmin: "Admin",
            operator: "Operador",
          },
        },
        venues: {
          capacityTitle: "Capacidad de suscripción",
          capacityDescription:
            "Límites actuales de sedes y cámaras en tu suscripción de Court Intelligence.",
          myOwnedVenuesMetric: "Mis sedes propias",
          venueLimitMetric: "Límite de sedes",
          cameraLimitMetric: "Límite de cámaras",
          refreshingOwnedVenues: "Actualizando sedes propias…",
          findTitle: "Buscar una sede para reclamar",
          createNewVenue: "Crear nueva sede",
          addVenueCapacity: "Agregar capacidad de sedes",
          searchPlaceholder: "Busca por nombre o dirección de la sede",
          search: "Buscar",
          useLocation: "Usar ubicación",
          locating: "Ubicando…",
          showingResults: "Mostrando resultados para: {query}",
          loadingVenues: "Cargando sedes…",
          noVenuesFound: "No se encontraron sedes para esta búsqueda.",
          loadMore: "Cargar más",
          claimOwnership: "Reclamar propiedad",
          claiming: "Reclamando…",
          owned: "Propia",
          viewDetails: "Ver detalles",
          hideDetails: "Ocultar detalles",
          myOwnedVenuesTitle: "Mis sedes propias",
          noOwnedVenues: "Aún no hay sedes reclamadas.",
          updateDetails: "Actualizar detalles",
          continue: "Continuar",
          unlimited: "Ilimitado",
          venueFallback: "Sede {id}",
          contactSupport: "Contactar a {email}",
          supportEmailSubject: "Soporte de propiedad Court Intelligence",
          supportEmailBody:
            "Hola soporte de WhoPlays,\n\nNecesito ayuda para reclamar la propiedad de una sede en Court Intelligence.\nUsuario: {username}\n",
          unknownUser: "desconocido",
          loadingVenueDetails: "Cargando detalles de la sede…",
          venueDetailsUnavailable: "Los detalles de la sede no están disponibles.",
          detailsOverview: "Resumen",
          typeNotSet: "Tipo no definido",
          courtsCount: "{count} canchas",
          courtCountNotSet: "Cantidad de canchas no definida",
          openPlaySessionsToday: "{count} sesiones de open-play hoy",
          detailsContactLinks: "Contacto y enlaces",
          website: "Sitio web",
          map: "Mapa",
          amenities: "Amenidades",
          moreAmenities: "+{count} más",
          hours: "Horario",
          closed: "Cerrado",
          previousPhoto: "Foto anterior",
          nextPhoto: "Siguiente foto",
          errors: {
            refreshOwnedVenues: "No se pudieron actualizar tus sedes propias.",
            ownerOnlyClaim: "Solo el dueño de la suscripción puede reclamar o crear sedes.",
            venueLimitReached: "Se alcanzó el límite de sedes. Agrega una sede extra en Suscripción para continuar.",
            claimOwnership: "No se pudo reclamar la propiedad de esta sede.",
            searchInputRequired: "Escribe un nombre/dirección o usa tu ubicación.",
            loadVenues: "No se pudieron cargar las sedes.",
            locationNotSupported: "La ubicación no está disponible en este navegador.",
            locationPermission: "No se pudo obtener tu ubicación. Habilita el acceso a ubicación.",
          },
          notices: {
            venueUpdated: "Perfil de sede actualizado para {name}.",
            ownershipClaimed: "Propiedad reclamada para {name}.",
          },
        },
        cameras: {
          loadingCameraVenues: "Cargando sedes de cámaras…",
          noVenuesAvailable:
            "Aún no hay sedes disponibles para configurar cámaras. Primero reclama una sede.",
          venueLabel: "Sede",
          metricOwnedVenues: "Sedes propias",
          metricVenueLimit: "Límite de sedes",
          metricCamerasUsedLimit: "Cámaras usadas/límite",
          oneTimeApiCredentials: "Credenciales API de un solo uso",
          oneTimeFtpCredentials: "Credenciales FTPS de un solo uso",
          dismiss: "Cerrar",
          saveApiCredentials: "Guarda este token ahora. Se muestra solo una vez.",
          saveFtpCredentials:
            "Guarda estas credenciales ahora. La contraseña FTPS se muestra solo una vez.",
          modePrefix: "Modo: {mode}",
          modeApi: "Envío API",
          modeFtps: "Carga FTPS",
          modeApiShort: "API",
          modeFtpsShort: "FTPS",
          camerasTitle: "Cámaras",
          camerasSubtitle: "El modo API captura cada 30s. En FTPS se recomiendan 60s.",
          setupGuidanceTitle: "Guía de configuración",
          setupGuidanceSubtitle:
            "Elige el resultado de configuración que quieres y usa la guía de abajo para lograrlo.",
          expandQuickGuide: "Expandir guía rápida",
          collapseQuickGuide: "Contraer guía rápida",
          setupGuidanceCollapsedSummary:
            "Usa la guía rápida para un resumen compacto o abre la guía completa para ver flujo, resultados y cálculos.",
          openSetupGuide: "Abrir guía completa",
          setupGuideModalTitle: "Guía de configuración de Court Intelligence",
          setupGuideModalSubtitle:
            "Usa estos objetivos para decidir qué tan precisas quieres que sean las señales del recinto.",
          setupGuideTabWorkflow: "Flujo",
          setupGuideTabOutcomes: "Resultados",
          setupGuideTabCalculations: "Cálculos",
          setupGuideWorkflowIntro:
            "Sigue este orden para poner una cámara en línea, capturar el primer snapshot y desbloquear los KPIs del recinto que necesitas.",
          workflowStepNumber: "Paso {number}",
          workflowStepCameraTitle: "Crear una cámara",
          workflowStepCameraDescription:
            "Selecciona la sede, crea la cámara y asígnale un nombre y número claros para que los operadores la identifiquen después.",
          workflowStepConnectionTitle: "Elegir el tipo de conexión",
          workflowStepConnectionDescription:
            "Usa API push cuando la cámara pueda enviar imágenes por HTTP POST. Usa FTPS upload cuando la cámara soporte mejor salida FTPS que entrega directa por API.",
          workflowStepGroupsTitle: "Opcional: crear grupos de cámaras",
          workflowStepGroupsDescription:
            "Usa grupos para organizar cámaras relacionadas y controlar la retención de debug para troubleshooting. Sin debug, los snapshots se procesan normalmente y no se retienen, excepto las imágenes de inicialización.",
          workflowStepCredentialsTitle: "Configurar las credenciales de una sola vez",
          workflowStepCredentialsDescription:
            "Copia de inmediato el token o la contraseña FTPS, luego configura la cámara con las instrucciones mostradas y el intervalo de captura recomendado.",
          workflowStepSnapshotTitle: "Esperar el primer snapshot de inicialización",
          workflowStepSnapshotDescription:
            "Después de que la cámara envíe su primera imagen, abre Inicialización para confirmar encuadre, visibilidad y si la huella útil del recinto está limpia.",
          workflowStepTargetTitle: "Elegir el resultado de configuración",
          workflowStepTargetDescription:
            "Elige Estimación rápida para actividad general, Disponibilidad de canchas para mapear canchas, o Canchas + fila para desbloquear espera y ETA.",
          workflowStepZonesTitle: "Crear áreas y asociar coberturas",
          workflowStepZonesDescription:
            "Construye primero el catálogo de áreas y luego mapea la cámara con polígonos manuales o borradores de IA. Guarda cada zona solo cuando la cobertura coincida con la huella visible.",
          workflowStepVerifyTitle: "Verificar los KPIs desbloqueados",
          workflowStepVerifyDescription:
            "Usa Ocupación en vivo para confirmar lo que ya está activo. Agrega áreas court para ocupadas/jugables y áreas de fila para waiting players y wait ETA.",
          setupGuideCalculationsIntro:
            "Estas fórmulas explican qué puede mostrar Court Intelligence en cada nivel de configuración. Úsalas como guía para operadores, no como texto exacto para jugadores.",
          setupGuideDefaultPlayersPerGame: "Jugadores por juego por defecto: 4",
          setupGuideDefaultAvgGameMinutes: "Minutos promedio por juego por defecto: 15",
          setupGuideDefaultQueueTargetMinutes: "Minutos objetivo de fila por defecto: 30",
          setupGuideFormulaLabel: "Fórmula",
          setupGuideVisibleKpisLabel: "KPIs visibles",
          setupGuideHiddenKpisLabel: "KPIs ocultos",
          setupGuideExplanationLabel: "Explicación",
          setupGuideNone: "Ninguno",
          currentModeLabel: "Modo actual",
          currentModeUnavailable: "Aún sin clasificar",
          setupStatusLoading: "Cargando el estado actual de configuración…",
          setupStatusUnavailable: "El estado actual de configuración aún no está disponible.",
          mappedCourtsLabel: "Canchas mapeadas",
          queueAreasLabel: "Áreas de fila",
          targetQuickEstimate: "Estimación rápida",
          targetCourtAvailability: "Disponibilidad de canchas",
          targetCourtAndQueue: "Canchas + fila",
          capabilityBusyness: "Actividad en vivo",
          capabilityAvailability: "Disponibilidad de canchas",
          capabilityWait: "Métricas de fila",
          areaTypesLabel: "Tipos de área",
          cameraTargetLabel: "Objetivo de cámara",
          framingLabel: "Encuadre",
          nextStepLabel: "Siguiente paso",
          quickEstimateAreaTypes:
            "Usa una cobertura general para la huella útil del recinto, o deja una sola área full coverage si el encuadre ya está limpio.",
          quickEstimateCameraTarget:
            "Una sola cámara amplia sirve para actividad general aproximada.",
          quickEstimateFraming:
            "Intenta que estacionamientos, calles y áreas ajenas no dominen el encuadre útil.",
          quickEstimateNext:
            "Empieza con cobertura general del recinto para estimación rápida. Mapea canchas después si quieres disponibilidad.",
          quickEstimateNextReady:
            "La estimación rápida ya está activa. Usa los objetivos de arriba para añadir disponibilidad o espera.",
          courtAvailabilityAreaTypes:
            "Crea una área court por cada cancha visible y vincula cada una a su propio polígono de cobertura.",
          courtAvailabilityCameraTarget:
            "Apunta a 1 o 2 canchas por cámara. Tres pueden funcionar si la separación visual es clara.",
          courtAvailabilityFraming:
            "Usa vistas elevadas y centradas con mínima oclusión de mallas, postes o jugadores.",
          courtAvailabilityNextLocked:
            "Mapea las canchas visibles para desbloquear métricas de ocupadas / jugables.",
          courtAvailabilityNextReady:
            "La disponibilidad de canchas ya está activa. Ajusta ángulos o divide vistas cargadas si la confianza sigue moderada.",
          courtQueueAreaTypes:
            "Usa áreas court más áreas waiting, bench, paddle rack o con queue signal para la fila que quieres medir.",
          courtQueueCameraTarget:
            "Usa la misma guía de canchas y luego mantén visibles las zonas de espera, banca o rack.",
          courtQueueFraming:
            "Las zonas de fila deben verse separadas de las canchas activas y no ser demasiado pequeñas.",
          courtQueueNextLocked:
            "Agrega zonas de espera, banca o rack para desbloquear estimaciones de espera.",
          courtQueueNextReady:
            "Las métricas de fila ya están activas. Mantén esas zonas claras y separadas para mejores estimaciones.",
          areasTitle: "Catálogo de áreas",
          areasSubtitle: "Áreas semánticas a nivel sede, compartidas entre coberturas de cámaras.",
          createArea: "Crear área",
          selectArea: "Seleccionar área",
          newArea: "Nueva área",
          newAreaModalTitle: "Crear nueva área",
          areaNamePlaceholder: "Nombre del área",
          loadingAreas: "Cargando catálogo de áreas…",
          noAreasYet: "Aún no hay áreas para esta sede.",
          systemArea: "Área del sistema",
          edit: "Editar",
          pilotDebugBadge: "Debug piloto ACTIVADO hasta {date}",
          manageGroups: "Gestionar grupos",
          addCamera: "Agregar cámara",
          closeAddCamera: "Cerrar agregar cámara",
          closeAddArea: "Cerrar agregar área",
          addCameraTitle: "Agregar cámara",
          cameraNamePlaceholder: "Nombre de la cámara",
          cameraNumberPlaceholder: "Número de cámara",
          ingestModeLabel: "Modo de ingestión",
          ingestModeApi: "Envío API",
          ingestModeFtp: "Carga FTPS",
          ingestHintFtp:
            "Configuración FTPS: TLS explícito en puerto 21, modo pasivo, subir archivos JPG/PNG.",
          ingestHintApi: "Configuración API: imagen multipart por HTTP POST con token de cámara.",
          noGroup: "Sin grupo",
          createCamera: "Crear cámara",
          creatingCamera: "Creando…",
          initCameraSelectorTitle: "Cámara para inicialización",
          goToCameras: "Ir a Cámaras",
          noInitCamerasTitle: "No hay cámaras disponibles para inicialización",
          noInitCamerasDescription:
            "Crea al menos una cámara en Cámaras antes de empezar la inicialización de zonas.",
          showAllCameraStatuses: "Mostrar todos los estados de cámara",
          noInitializingCamerasDescription:
            "No hay cámaras en estado Inicializando. Activa \"Mostrar todos los estados de cámara\" para seleccionar otra cámara.",
          loadingCameras: "Cargando cámaras…",
          noCamerasVenue: "Aún no hay cámaras para esta sede.",
          initZonesTitle: "Inicialización y zonas",
          initZonesSubtitle:
            "Usa snapshots para mapear áreas manualmente o generar sugerencias de IA como borrador.",
          cameraLabel: "Cámara",
          noneSelected: "Sin selección",
          selectCameraPrompt: "Selecciona una cámara para configurar inicialización y zonas.",
          selectCameraPromptHelp:
            "Elige una cámara arriba para abrir sus snapshots y el editor de zonas.",
          editingInitializingOnly:
            "Solo se pueden editar aquí cámaras en estado Inicializando.",
          snapshotsTitle: "Snapshots de inicialización",
          loadingSnapshots: "Cargando snapshots…",
          refresh: "Actualizar",
          protocolFtps: "Protocolo: FTPS (TLS explícito) en puerto 21.",
          uploadFtps: "Sube imágenes al prefijo de ruta configurado en modo pasivo.",
          intervalFtps: "Intervalo recomendado de captura: 60 segundos.",
          methodApi: "Método HTTP: POST multipart con archivo de imagen (jpg/png).",
          tokenApi: "Envía el token de cámara en el header x-camera-token.",
          intervalApi: "Intervalo de captura: 30 segundos.",
          unlimited: "Ilimitado",
          notAvailable: "N/D",
          statusPrefix: "Estado",
          statusInitializing: "Inicializando",
          statusActive: "Activa",
          statusInactive: "Inactiva",
          lastSeenPrefix: "Última vez vista {time}",
          selected: "Seleccionada",
          select: "Seleccionar",
          openInstructions: "Abrir instrucciones de configuración",
          instructionsTitle: "Instrucciones de configuración ({mode})",
          actionActivate: "Activar",
          actionDeactivate: "Desactivar",
          actionOpenSetup: "Abrir configuración",
          discardDraft: "Descartar borrador",
          unsavedChangesBadge: "Cambios de zonas sin guardar",
          actionRotateFtpPassword: "Rotar contraseña",
          actionRotateToken: "Rotar token",
          actionSwitchToApi: "Cambiar a API",
          actionSwitchToFtps: "Cambiar a FTPS",
          actionReinitialize: "Reinicializar",
          actionDelete: "Eliminar",
          ftpRotationDisabled:
            "La rotación de token está deshabilitada en modo FTPS. Usa Rotar contraseña FTPS cuando sea necesario.",
          latestSnapshotPrefix: "Último snapshot: {time}",
          snapshotAlt: "Snapshot de inicialización",
          drawingHint: "Haz clic en la imagen para colocar puntos del polígono ({count}).",
          noSnapshotYet:
            "Aún no hay snapshot de inicialización. Mantén la cámara activa hasta que llegue el primero.",
          noSnapshotTitle: "Esperando el primer snapshot de inicialización",
          noSnapshotDescription:
            "Mantén la cámara activa y verifica la configuración de carga en la cámara. Cuando llegue el primer snapshot, se habilita la configuración de zonas.",
          snapshotRequiredForActions:
            "El dibujo de zonas y la sugerencia de IA se habilitan después de recibir el primer snapshot de inicialización.",
          manualZoneDraftTitle: "Agregar zona",
          zoneNamePlaceholder: "Nombre de la zona",
          queueSignal: "Señal de fila",
          exclusion: "Exclusión",
          startDrawing: "Empezar dibujo",
          undoPoint: "Deshacer punto",
          resetPolygon: "Reiniciar polígono",
          addZoneDraft: "Agregar zona",
          aiZoneSuggestionTitle: "Sugerencia de zonas con IA",
          aiPromptPlaceholder: "Instrucción opcional para IA",
          suggesting: "Sugiriendo…",
          suggestZones: "Sugerir zonas",
          applySuggestion: "Aplicar sugerencia",
          revert: "Revertir",
          aiPreparedHint: "La IA preparó {count} zonas. Aplícalas cuando estés listo.",
          workingZonesTitle: "Zonas de trabajo ({count})",
          saving: "Guardando…",
          saveZones: "Guardar zonas",
          loadingZones: "Cargando zonas…",
          remove: "Quitar",
          pointsLabel: "Puntos",
          queueTag: "fila",
          exclusionTag: "exclusión",
          noZonesYet: "Aún no hay zonas. Dibuja manualmente o usa sugerencia de IA.",
          savedZonesLive: "Zonas guardadas activas: {count}",
          groupsModalTitle: "Grupos de cámaras",
          hideForm: "Ocultar formulario",
          createGroup: "Crear grupo",
          close: "Cerrar",
          groupNamePlaceholder: "Nombre del grupo",
          groupDescriptionPlaceholder: "Descripción opcional",
          debugEnabled: "Debug habilitado",
          creating: "Creando…",
          loadingGroups: "Cargando grupos…",
          save: "Guardar",
          noGroupsYet: "Aún no hay grupos. Puedes crear cámaras sin grupo.",
          deleteCameraTitle: "Eliminar cámara",
          deleteCameraDescription: "Esta acción es permanente y detiene la ingestión de esta cámara.",
          deleteCameraLabel: "Cámara",
          deleteModeLabel: "Modo",
          deleteIdLabel: "ID",
          deleteTypeToConfirm: "Para confirmar, escribe:",
          deleteDelayHint: "Espera un momento antes de confirmar.",
          cancel: "Cancelar",
          deleting: "Eliminando…",
          confirmDelete: "Confirmar eliminación",
          tokenFieldCameraId: "ID de cámara",
          tokenFieldFtpsHost: "Host FTPS",
          tokenFieldFtpsUsername: "Usuario FTPS",
          tokenFieldFtpsPassword: "Contraseña FTPS",
          tokenFieldFtpsPathPrefix: "Prefijo de ruta FTPS",
          ftpsPathPrefixValue: "/ o vacío",
          ftpsSubfolderRuleOff: "Si tu cámara tiene regla de generación de subcarpetas, desactívala.",
          tokenFieldSnapshotEndpoint: "Endpoint de snapshot",
          tokenFieldApiHeader: "Header API",
          tokenFieldCameraToken: "Token de cámara",
          copy: "Copiar",
          copyToken: "Copiar token",
          copied: "Copiado",
          copyFailed: "No se pudo copiar. Copia manualmente.",
          cameraNumberLabel: "Cámara",
          groupLabel: "Grupo",
          suggestedZoneLabel: "Zona sugerida",
          zoneSuffix: "Zona",
          confirmSwitchFtpToApi:
            "Cambiar esta cámara a API elimina credenciales FTP y detiene la entrega FTP actual. ¿Continuar?",
          confirmDeleteGroup: "¿Eliminar este grupo de cámaras? Primero debes quitar las cámaras asociadas.",
          confirmDeleteArea:
            "¿Eliminar el área '{name}'? Fallará si una cobertura de cámara todavía la referencia.",
          confirmLeaveUnsaved:
            "Tienes cambios de zonas sin guardar. ¿Salir de esta pantalla y descartarlos?",
          errors: {
            loadVenueAccess: "No se pudo cargar el acceso a sedes.",
            loadCameras: "No se pudieron cargar las cámaras.",
            loadCameraSetup: "No se pudo cargar la configuración de cámara.",
            loadAreas: "No se pudo cargar el catálogo de áreas.",
            cameraActionFailed: "La acción de cámara falló.",
            switchIngestMode: "No se pudo cambiar el modo de ingestión.",
            createGroup: "No se pudo crear el grupo de cámaras.",
            updateGroup: "No se pudo actualizar el grupo de cámaras.",
            deleteGroup: "No se pudo eliminar el grupo de cámaras.",
            createArea: "No se pudo crear el área.",
            updateArea: "No se pudo actualizar el área.",
            deleteArea: "No se pudo eliminar el área.",
            createCamera: "No se pudo crear la cámara.",
            cameraLimitReached: "Se alcanzó el límite de cámaras. Agrega capacidad de cámaras en Suscripción para continuar.",
            deleteCamera: "No se pudo eliminar la cámara.",
            suggestZones: "No se pudieron sugerir zonas.",
            removeFullCoverageFirst:
              "Elimina primero la cobertura completa por defecto antes de guardar coberturas mapeadas adicionales.",
            invalidCoveragePolygon:
              "Cada cobertura que no sea completa necesita al menos 3 puntos en el polígono.",
            coverageOverlap:
              "La cobertura se superpone con otra zona. Ajusta el polígono antes de guardar.",
            missingAreaForCoverage:
              "Cada cobertura debe estar vinculada a un área antes de guardar.",
            saveZones: "No se pudieron guardar las zonas.",
          },
          notices: {
            switchedToFtps: "La cámara cambió a modo FTPS.",
            switchedToApi: "La cámara cambió a modo API.",
            groupCreated: "Grupo de cámaras creado.",
            groupUpdated: "Grupo de cámaras actualizado.",
            groupDeleted: "Grupo de cámaras eliminado.",
            areaCreated: "Área creada.",
            areaUpdated: "Área actualizada.",
            areaDeleted: "Área eliminada.",
            cameraCreated: "Cámara creada.",
            cameraDeleted: "Cámara eliminada.",
            cameraActivated: "Cámara activada.",
            cameraDeactivated: "Cámara desactivada.",
            ftpPasswordRotated: "Contraseña FTPS rotada.",
            cameraTokenRotated: "Token de cámara rotado.",
            cameraReinitialized: "Cámara reinicializada.",
            aiNoZones: "La sugerencia de IA no devolvió zonas. Prueba con otro prompt.",
            aiPrepared: "La IA preparó {count} zonas sugeridas. Revísalas y aplícalas cuando estés listo.",
            aiApplied: "La sugerencia de IA se agregó a las zonas de trabajo. Guarda cada zona para persistir.",
            aiReverted: "Se revirtió al estado anterior de zonas de trabajo.",
            zonesSaved: "Zonas guardadas.",
            draftZoneAdded: "Zona agregada a zonas de trabajo. Guarda esta zona para persistir.",
            draftDiscarded: "Se descartaron los cambios de zonas en borrador.",
          },
        },
      },
      tips: {
        title: "Consejos",
        subtitle: "Aprovecha al máximo WhoPlays.",
        back: "Atrás",
        next: "Siguiente",
        close: "Cerrar",
        progress: "Consejo {current} de {total}",
        items: [
          {
            title: "Revisa Real-Time primero",
            body: "Mira quién está en la cancha antes de llegar para unirte al juego correcto.",
          },
          {
            title: "Invita por SMS",
            body: "Envía invitaciones rápidas desde Mi Play Schedule para completar el juego.",
          },
          {
            title: "Confirma temprano",
            body: "Ayuda al anfitrión confirmando cuanto antes.",
          },
          {
            title: "Registra el marcador al terminar",
            body: "Actualiza los resultados para mantener los rankings al día.",
          },
          {
            title: "Vincula tu DUPR",
            body: "Sincroniza tus calificaciones para verlas en la app.",
          },
          {
            title: "Ajusta las notificaciones",
            body: "Elige las alertas que más te importan.",
          },
          {
            title: "Completa tu perfil",
            body: "Un buen perfil ayuda a que otros te encuentren y confíen en ti.",
          },
          {
            title: "Rota equipos",
            body: "Usa Rotar equipos para crear más partidos con los mismos cuatro jugadores.",
          },
        ],
      },
      subscriptionLimits: {
        title: "Límite alcanzado",
        sms: "Has alcanzado tu límite mensual de {limit} invitaciones por SMS.",
        players: "Has alcanzado el límite de {limit} jugadores por programación.",
        events: "Has alcanzado tu límite de {limit} programaciones activas con invitaciones.",
        generic: "Has alcanzado tu límite actual de {limit}.",
        upgrade: "Actualizar ahora",
        cancel: "Cancelar",
      },
      sidebar: {
        welcome: "Bienvenido de nuevo",
        kpiWins: "Victorias",
        kpiLosses: "Derrotas",
        kpiPending: "Pendientes",
        quickActions: {
          language: "Cambiar idioma",
          languageNext: "Siguiente: {language}",
          theme: "Cambiar tema",
          tips: "Consejos",
          tipsSoon: "Los consejos estarán disponibles muy pronto.",
          courtIntel: "Cambiar a modo Court Intelligence",
          logout: "Cerrar sesión",
        },
        toggles: {
          dnd: "No molestar",
          tips: "Consejos",
          share: "Compartir",
          language: "Idioma",
        },
        actions: {
          profile: "Perfil",
          preferredCourts: "Mis sedes",
          schedule: "Mi agenda",
          linkDupr: "Vincular DUPR®",
          subscriptions: "Suscripciones",
          invite: "Invitar jugadores",
          privacy: "Política de privacidad",
          settings: "Configuración y preferencias",
          appStats: "Estadísticas",
          logout: "Cerrar sesión",
        },
        admin: {
          label: "Admin",
          access: "Acceso y permisos",
        },
        chartLabel: "Relación victorias/derrotas",
        chartWins: "Victorias",
        chartLosses: "Derrotas",
        chartPending: "Pendientes",
        chartNoData: "Aún no hay historial de partidos.",
        chartWinRate: "Porcentaje de victorias",
        chartTotal: "Total",
        skillLevels: {
          beginner: "Principiante",
          intermediate: "Intermedio",
          advanced: "Avanzado",
        },
        rankings: {
          wpRankLabel: "Rango WP",
          duprLabel: "DUPR",
          singlesShort: "S",
          doublesShort: "D",
          notRated: "NR",
        },
      },
      header: {
        subtitleRank: "Ranking · Global",
        moreLabel: "Menú",
        currentLabel: "Actual",
      },
      analytics: {
        tabSwitch: "cambio_de_tab",
        sidebarOpen: "sidebar_abierta",
        sidebarClose: "sidebar_cerrada",
      },
      dupr: {
        title: "Vincular con DUPR®",
        subtitle: "Inicia sesión en DUPR para sincronizar tus clasificaciones.",
        instructions: "Completa el inicio de sesión de DUPR abajo. No guardamos tu contraseña.",
        waiting: "Esperando inicio de sesión de DUPR...",
        processing: "Vinculando tu cuenta DUPR...",
        success: "Cuenta DUPR vinculada correctamente.",
        error: "No se pudo vincular tu cuenta DUPR en este momento.",
        originError: "Mensaje ignorado de un origen no confiable.",
        missingConfig: "La integración con DUPR no está configurada.",
        close: "Cerrar",
        unauthorized: "Debes iniciar sesión para vincular DUPR. Inicia sesión y vuelve a intentarlo.",
      },
      realTime: {
        ribbonLabel: "Tus sedes",
        refresh: "Actualizar",
        noCourtsTitle: "Agrega una sede preferida para ver la actividad en vivo.",
        courtSelectorLabel: "Seleccionar una sede",
        courtIntelligence: {
          title: "Court Intelligence",
          refresh: "Actualizar",
          refreshing: "Actualizando…",
          viewDetails: "Ver detalles",
          noDataBadge: "Vista para jugadores",
          noDataLogoAlt: "Court Intelligence de WhoPlays",
          noDataTitle: "Esta sede aún no ha desbloqueado visibilidad en vivo de las canchas.",
          noDataBody:
            "En las sedes habilitadas, los jugadores pueden revisar el nivel de ocupación y la presión de espera antes de salir. Tú puedes ayudar a traerlo aquí.",
          noDataHint: "Muéstrale a tu club, HOA o community manager lo que Court Intelligence puede aportar.",
          noDataPrimaryCta: "Ver Court Intelligence",
          noDataSecondaryCta: "Compartir con tu sede",
          requestCta: "Solicitar Court Intelligence",
          requestHint: "Pide a tu community manager que lo habilite para esta sede.",
          requestEmailSubject: "Solicitud de Court Intelligence para {court}",
          requestEmailBody:
            "Hola equipo de WhoPlays,\n\nQuiero solicitar Court Intelligence para esta sede:\n{court}\n\nUsuario: {username}\n\nGracias.",
          shareEmailSubject: "¿Podemos llevar Court Intelligence a {court}?",
          shareEmailBody:
            "Hola,\n\nUso WhoPlays en {court} y me encantaría ver Court Intelligence aquí.\n\nLes da a los jugadores visibilidad en vivo antes de salir.\n\nResumen: {link}\n\nEnviado por: {username}\n",
          scoreLabel: "Índice de ocupación",
          occupancyLabel: "En uso / jugables",
          occupancyValueFormat: "{inUse} de {playable}",
          waitingLabel: "Fila",
          etaLabel: "Espera",
          peopleLabel: "Personas",
          scoreShort: "Índice",
          confidenceLabel: "Confianza",
          estimateBadge: "Estimado",
          weatherLabel: "Clima",
          forecastLabel: "Próximas horas",
          weatherUnavailable: "Clima no disponible",
          updatedAt: "Actualizado: {time}",
          permanentLink: "¿Lo quieres en otra sede?",
          infoTitle: "Sobre los estimados de Court Intelligence",
          infoBody:
            "La ocupación, la espera y el conteo de personas son estimaciones operativas basadas en ML sobre snapshots de cámara y pueden diferir del conteo exacto.",
          detailTitle: "Detalles de Court Intelligence",
          detailsOverviewTab: "Resumen",
          detailsTrafficTab: "Tráfico por hora",
          currentSnapshotTitle: "Estado actual",
          trendTitle: "Tráfico de la sede por hora",
          trendSubtitle: "Patrón por hora de los últimos 7 días según las lecturas recientes.",
          trendLoading: "Cargando tendencia…",
          trendEmpty: "Aún no hay datos de tendencia para esta sede.",
          trendError: "No se pudo cargar la tendencia ahora.",
          trendInfoTitle: "Cómo leer este gráfico",
          trendInfoBody:
            "Usa las celdas más claras para encontrar horas más tranquilas y tener una visita más cómoda. Usa las celdas más oscuras para ubicar horas con más movimiento y más jugadores. El punto dorado indica que se observó espera en esa hora.",
          trendQuietLabel: "Más tranquilo",
          trendBusyLabel: "Más ocupado",
          trendWaitingObserved: "Se observó espera",
          trendNote: "Cada celda muestra una hora observada. Más claro significa más tranquilo; el punto dorado indica que se observó espera.",
          trendPeople: "Personas",
          trendWaiting: "En espera",
          courtFallback: "Sede",
          courtUnknown: "Sede desconocida",
          unknownUser: "desconocido",
          notAvailable: "N/D",
          close: "Cerrar",
          loadError: "No se pudieron cargar los datos de Court Intelligence.",
        },
        summary: {
          title: "Jugadores en {court}",
          allLevels: "Todos los niveles",
          advanced: "Avanzado",
          intermediate: "Intermedio",
          beginner: "Principiante",
          metrics: {
            willing: "Listos para jugar",
            privateGroups: "Grupos privados",
            following: "Siguiendo",
          },
          metricsShort: {
            willing: "Listos",
            privateGroups: "Privados",
            following: "Siguiendo",
          },
          updated: "Actualizado {time}",
        },
        whosNextTitle: "Quién sigue",
        myScheduleTitle: "Mi agenda",
        myQuestionsTitle: "Mis preguntas",
        day: {
          today: "Hoy",
          tomorrow: "Mañana",
        },
        metrics: {
          openPlay: "Open Play",
          event: "Evento",
          privateGroup: "Grupo privado",
        },
        carousels: {
          noData: "Sin actividad programada.",
          dotsLabel: "Ir a la tarjeta {index}",
        },
        detail: {
          noActivity: "No hay actividad programada para este tipo.",
          close: "Cerrar",
          profileTitle: "Perfil del jugador",
          openProfile: "Abrir perfil completo",
          unknownPlayer: "Jugador desconocido",
          status: {
            host: "Anfitrión",
            accepted: "Aceptado",
            pending: "Pendiente",
            declined: "Rechazado",
          },
        },
        schedule: {
          empty: "Sin partidos próximos.",
          badges: {
            invited: "Invitado",
            hosted: "Organizado por {name} en {court}",
            hostedSelf: "Organizado por mí en {court}",
          },
          cardTime: "Mi agenda: {time}",
          counts: {
            invited: "Invitados",
            confirmed: "Confirmados",
          },
          confirmedLine: "Confirmados: {confirmed}",
          confirmedLineWithLimit: "Confirmados: {confirmed} de {limit}",
        },
        questions: {
          empty: "Sin preguntas para esta sede.",
          status: {
            pending: "Pendiente",
            answered: "Respondido",
          },
        },
        emptyHero: {
          title: "¡Programa un partido e invita jugadores!",
          bullets: [
            "¡Mira quién viene — en vivo!",
            "Invita por SMS y sigue las respuestas",
            "Mantén a todos al día por SMS o en la app",
          ],
          cta: "Programar evento",
        },
        fabLabel: "Programar evento",
      },
      network: {
        title: "Mi Red",
        subtitle: "Encuentra y gestiona a tus jugadores.",
        searchPlaceholder: "Buscar jugadores",
        search: "Buscar",
        segments: {
          suggestions: "Sugerencias",
          following: "Siguiendo",
          followers: "Seguidores",
        },
        courtFilter: "Canchas",
        allCourts: "Todas las canchas",
        loading: "Cargando jugadores…",
        error: "No se pueden cargar los jugadores ahora.",
        empty: "No hay jugadores para mostrar.",
        unknownSkill: "Nivel desconocido",
        follow: "Seguir",
        following: "Siguiendo",
        refresh: "Actualizar",
        loadMore: "Cargar más",
        actionError: "No se pudo actualizar el seguimiento.",
        levelCompetition: {
          recreational: "Recreativo",
          competitive: "Competitivo",
        },
        followTitle: "Seguir a {name}",
        followingTitle: "Siguiendo a {name}",
        close: "Cerrar",
        toggleGroup: "Agregar a estadísticas de Mi Grupo",
        toggleNotify: "Avisarme sobre este jugador",
        unfollow: "Dejar de seguir",
        save: "Guardar",
      },
    },
    profileEdit: {
      loading: "Cargando perfil…",
      header: {
        title: "Editar perfil",
        back: "Volver",
        cancel: "Cancelar",
        save: "Guardar preferencias",
        saving: "Guardando…",
      },
      sections: {
        avatar: {
          title: "Avatar",
          subtitle: "Sube una foto clara para que los jugadores te reconozcan.",
          button: "Cambiar foto",
          uploading: "Subiendo…",
          helper: "JPG o PNG hasta 5 MB.",
        },
        personal: { title: "Información personal" },
        location: { title: "Ubicación" },
        preferences: { title: "Preferencias y nivel" },
        bio: { title: "Biografía", placeholder: "Comparte una breve introducción" },
      },
      fields: {
        name: "Nombre completo",
        nickname: "Apodo",
        email: "Correo electrónico",
        phone: "Número de teléfono",
        country: "País",
        state: "Estado",
        city: "Ciudad",
        zipcode: "Código postal",
        status: "Estado",
        preferredLevel: "Nivel preferido",
        matchType: "Tipo de partido",
        skill: "Nivel de habilidad",
        selfRanking: "Auto clasificación",
        competition: "Nivel de competencia",
        defaultAudience: "Audiencia predeterminada",
        followerPrivacy: "Privacidad de seguidores",
      },
      helpers: {
        emailOptional: "El correo es opcional y puede usarse para notificaciones.",
        emailLogin: "Cambiar tu correo puede requerir verificación.",
        phoneLocked: "Este número de teléfono es tu inicio de sesión y no puede cambiarse.",
        phoneFormat: "Usa formato internacional, por ejemplo +1…",
      },
      success: {
        saved: "Perfil guardado.",
        photo: "Foto de perfil actualizada.",
        email: "Correo verificado.",
        phone: "Teléfono verificado.",
      },
      errors: {
        name: "El nombre es obligatorio.",
        email: "Introduce un correo electrónico válido.",
        phone: "Introduce un número de teléfono válido en formato internacional.",
        code: "Introduce el código de 6 dígitos.",
        save: "No se pudo guardar el perfil.",
        upload: "No se pudo subir el avatar.",
        verificationStart: "No se pudo iniciar la verificación.",
        verificationConfirm: "No se pudo verificar el código.",
      },
      verification: {
        verifyEmail: "Verificar correo",
        reverifyEmail: "Reverificar correo",
        verifyPhone: "Verificar teléfono",
        reverifyPhone: "Reverificar teléfono",
        badge: "Verificado",
        modalTitle: "Ingresa el código de verificación",
        modalDescriptionEmail: "Enviamos un código de 6 dígitos a {target}. Escríbelo para verificar tu correo.",
        modalDescriptionPhone: "Enviamos un código de 6 dígitos a {target}. Escríbelo para verificar tu teléfono.",
        submit: "Verificar",
        submitting: "Verificando…",
        cancel: "Cancelar",
      },
      options: {
        competition: [
          { value: 1, label: "Recreativo" },
          { value: 2, label: "Competitivo" },
        ],
      },
      legal: "WhoPlays US · Configuración de perfil",
    },
    myVenues: {
      back: "Volver a la app",
      title: "Mis sedes",
      subtitle: "Elige tu sede principal y guarda una lista de los lugares donde más juegas.",
      loading: "Cargando sedes…",
      onboarding: {
        title: "Elige tu primera sede",
        subtitle: "Selecciona una sede principal de pickleball para que WhoPlays personalice tu experiencia local.",
        missingTitle: "¿No encuentras tu sede?",
        missingBody: "Sugierela o comparte Court Intelligence para ayudar a habilitar visibilidad en vivo de las canchas para jugadores locales.",
        heroTitle: "Lleva Court Intelligence a tu sede",
        benefitOne: "Ve los jugadores, sedes y actualizaciones que importan alrededor de tu cancha principal.",
        benefitTwo: "Recibe recordatorios, invitaciones y actividad adaptada al lugar donde realmente juegas.",
        benefitThree: "Ayuda a tu sede a habilitar visibilidad en vivo con Court Intelligence.",
        helper: "Comparte Court Intelligence con tu club, HOA o administrador de sede.",
        ctaPrimary: "Conocer más",
        ctaExpand: "Ver más",
        ctaCollapse: "Ver menos",
        ctaSecondary: "Compartir con manager",
        searchHeading: "Busca tu sede principal",
        required: "Selecciona una sede principal para continuar en la app.",
        continue: "Continuar",
        noResultsTitle: "¿No encuentras tu sede?",
        noResultsBody: "Sugiérela ahora y luego comparte Court Intelligence para que tu sede sea más útil para la comunidad local de pickleball.",
        shareSubject: "¿Podemos llevar Court Intelligence a nuestra sede de pickleball?",
        shareBody:
          "Hola,\n\nEstoy usando WhoPlays y me gustaría ver nuestra sede de pickleball incluida y eventualmente habilitada con Court Intelligence.\n\nEsto ayuda a los jugadores a descubrir actividad local y le da a la sede un camino para ofrecer visibilidad en vivo de las canchas.",
      },
      primary: {
        heading: "Sede principal",
        empty: "Todavía no seleccionaste una sede principal.",
        required: "Selecciona una sede principal para personalizar tu experiencia.",
        clear: "Quitar sede principal",
      },
      preferred: {
        heading: "Sedes preferidas",
        empty: "Agrega las sedes donde juegas con frecuencia para tenerlas a mano.",
        remove: "Eliminar",
        makePrimary: "Convertir en principal",
        add: "Agregar a preferidas",
        countLabel: "seleccionadas",
      },
      badges: {
        primary: "Principal",
        preferred: "Preferida",
      },
      search: {
        heading: "Buscar sedes",
        placeholder: "Busca por nombre de sede o dirección",
        action: "Buscar",
        clear: "Limpiar búsqueda",
        useLocation: "Usar mi ubicación",
        locating: "Buscando…",
        locationReady: "Ubicación lista",
        locationError: "No pudimos obtener tu ubicación.",
        prompt: "Busca una sede o comparte tu ubicación para ver opciones cercanas.",
        noResults: "No encontramos sedes con esa búsqueda. Prueba con otros términos.",
        loadMore: "Cargar más resultados",
        loading: "Cargando sedes…",
        setPrimary: "Convertir en principal",
        addPreferred: "Agregar a preferidas",
        activeLabel: "Resultados para “{query}”",
        error: "No pudimos buscar sedes en este momento.",
        viewDetails: "Ver detalles",
        hideDetails: "Ocultar detalles",
      },
      actions: {
        save: "Guardar sedes",
        saving: "Guardando sedes…",
        saved: "Sedes actualizadas correctamente.",
        error: "No se pudieron guardar las sedes.",
        suggest: "Sugerir una sede",
        reviewPending: "Revisar sedes pendientes",
        warning: "Las sedes se guardaron, pero no pudimos actualizar los datos.",
      },
    },
    adminVenues: {
      back: "Volver a Mis sedes",
      title: "Sedes pendientes de verificación",
      subtitle: "Revisa las sedes recién enviadas antes de publicarlas.",
      loading: "Cargando sedes pendientes…",
      empty: "No hay sedes en espera de verificación.",
      countLabel: "Sedes pendientes: {count}",
      refresh: "Actualizar lista",
      error: "No pudimos cargar las sedes pendientes.",
      reviewCta: "Abrir revisión",
      hoursHeading: "Horario de atención",
      courtsHeading: "Configuración de canchas",
      totalCourts: "{count} canchas en total",
      amenitiesHeading: "Servicios",
      openPlayHeading: "Sesiones de open play hoy",
      pendingBadge: "Pendiente de verificación",
    },
    adminAccess: {
      back: "Volver al panel",
      title: "Acceso y permisos",
      subtitle: "Concede roles, roles de sede y acceso a suscripciones.",
      loading: "Cargando datos de acceso…",
      error: "No pudimos cargar el resumen de administración.",
      refresh: "Actualizar",
      searchPlaceholder: "Buscar usuarios o sedes",
      usersEmpty: "No hay usuarios que coincidan con los filtros.",
      courtsEmpty: "No hay sedes que coincidan con los filtros.",
      adminOnly: "Solo administradores.",
      unknownCourt: "Sede desconocida",
      tabs: {
        users: "Usuarios",
        courts: "Sedes",
      },
      filters: {
        all: "Todas",
        roles: "Roles",
        courtRoles: "Roles de sede",
        subscriptions: "Suscripciones",
      },
      roles: {
        heading: "Roles",
        empty: "Sin roles todavía.",
        add: "Agregar rol",
        select: "Selecciona un rol",
      },
      courtRoles: {
        heading: "Roles de sede",
        empty: "Sin roles de sede.",
        add: "Agregar rol de sede",
        update: "Actualizar rol",
        selectCourt: "Selecciona una sede",
        selectUser: "Selecciona un usuario",
        roleLabel: "Rol",
        searchPlaceholder: "Buscar sedes",
        searchUsersPlaceholder: "Buscar usuarios",
      },
      access: {
        heading: "Acceso a suscripciones",
        empty: "Sin accesos concedidos.",
        add: "Agregar acceso",
        update: "Actualizar acceso",
        selectPlan: "Selecciona un plan",
        expiresAt: "Vence el",
      },
      actions: {
        remove: "Eliminar",
        edit: "Editar",
        close: "Cerrar",
      },
      rules: {
        adminOnly: "Solo super admins pueden agregar o quitar el rol admin.",
      },
    },
    adminStats: {
      back: "Volver al panel",
      title: "Estadisticas de la app",
      subtitle: "Monitorea actividad y crecimiento.",
      loading: "Cargando estadisticas...",
      error: "No pudimos cargar el resumen de estadisticas.",
      adminOnly: "Solo administradores.",
      periodLabel: "Periodo",
      controls: {
        period: "Periodo",
        timezone: "Zona horaria",
        topN: "Top N",
        pendingLimit: "Limite de pendientes",
        apply: "Aplicar",
        periodLabel: "Periodo seleccionado",
      },
      sections: {
        lifetime: "Acumulado",
        period: "Periodo",
        activation: "Embudo de activacion",
        events: "Eventos",
        engagement: "Actividad",
        geo: "Geografia",
        pendingVenues: "Sedes pendientes",
        subscriptions: "Suscripciones",
        subscriptionsPeriod: "Suscripciones (Periodo)",
        downloadsByOs: "Descargas por sistema",
      },
      metrics: {
        uniqueUsers: "Usuarios",
        uniqueDownloads: "Descargas",
        profilesCreated: "Perfiles creados",
        primaryCourtSelected: "Sede principal seleccionada",
        sessionsCount: "Sesiones",
        eventsCount: "Eventos",
        sessionsDuration: "Duracion de sesiones",
        sessionsDurationLifetime: "Duracion acumulada",
      },
      empty: {
        downloads: "Sin descargas.",
      },
      funnel: {
        ofDownloads: "de descargas",
        ofPrevious: "del paso anterior",
      },
    },
    suggestVenue: {
      backToVenues: "Volver a Mis sedes",
      title: "Sugerir una sede",
      subtitle: "Comparte los datos de un nuevo lugar para que nuestro equipo lo revise.",
      ownerFlow: {
        title: "Crear una sede",
        subtitle: "Crea o actualiza tu sede para Court Intelligence. Se aprueba al instante para la configuración.",
        submitCreate: "Crear sede",
        submitUpdate: "Guardar sede",
        submittingCreate: "Creando…",
        submittingUpdate: "Guardando…",
        successCreated: "Sede creada correctamente.",
        successUpdated: "Sede actualizada correctamente.",
      },
      steps: {
        basic: "Básicos",
        details: "Detalles",
        openPlay: "Open play",
        photos: "Fotos",
        contact: "Contacto",
        review: "Revisión",
      },
      actions: {
        previous: "Atrás",
        next: "Siguiente",
        submit: "Enviar sugerencia",
        submitting: "Enviando…",
        approve: "Aprobar y publicar",
        markPending: "Marcar como pendiente",
        delete: "Eliminar sede",
      },
      loadingExisting: "Cargando datos de la sede…",
      successMessage: "¡Gracias! Recibimos tu sugerencia de sede.",
      standaloneSuccess: {
        title: "¡Gracias por enviarnos una nueva sede!",
        body: "Nuestro equipo revisará los datos y normalmente publicará la sede dentro de 24 a 72 horas una vez que todo esté validado.",
        helper: "Te contactaremos si necesitamos más información.",
        ctaPrimary: "Sugerir otra sede",
        ctaSecondary: "Cerrar",
      },
      successApproved: "Sede aprobada y publicada.",
      successPending: "Marcada como pendiente de actualizaciones.",
      successDeleted: "Sede eliminada.",
      errors: {
        basicRequired: "Ingresa el nombre y la dirección completa antes de continuar.",
        detailsRequired: "Agrega al menos una configuración de cancha y el tipo de superficie.",
        descriptionRequired: "Describe la sede para que otros jugadores sepan qué esperar.",
        contactRequired: "Necesitamos tus datos de contacto por si debemos escribirte.",
        confirmInformation: "Confirma que la información proporcionada es correcta.",
        submitFailed: "No pudimos guardar la sugerencia. Inténtalo más tarde.",
        loadVenue: "No pudimos cargar los datos de la sede. Inténtalo nuevamente.",
        deleteFailed: "No pudimos eliminar la sede. Inténtalo nuevamente.",
      },
      confirmDelete: "¿Eliminar esta sede? Esta acción no se puede deshacer.",
      basic: {
        heading: "Información básica",
        subheading: "Comienza con el nombre y la dirección para ubicar la sede.",
        nameLabel: "Nombre de la sede",
        namePlaceholder: "Complejo Riverside Pickleball",
        shortNameLabel: "Nombre corto",
        shortNamePlaceholder: "Etiqueta opcional",
        addressLabel: "Dirección",
        addressPlaceholder: "Calle Principal 123",
        cityLabel: "Ciudad",
        stateLabel: "Estado / región",
        zipLabel: "Código postal",
        countryLabel: "País",
        countryPlaceholder: "USA",
        latitudeLabel: "Latitud",
        longitudeLabel: "Longitud",
        mapHeading: "Vista previa del mapa",
        mapHelper: "Ingresa latitud y longitud para ver la ubicación.",
        mapMissing: "Agrega coordenadas para mostrar el mapa.",
        mapTitle: "Vista previa del mapa para {name}",
        mapDefaultName: "esta sede",
        mapLoadError: "No pudimos cargar el mapa. Verifica tu conexión e intenta nuevamente.",
        mapSearchingForward: "Buscando la ubicación para esta dirección…",
        mapSearchingReverse: "Buscando la dirección para estas coordenadas…",
        mapGeocodeError: "No encontramos coordenadas para esa dirección. Intenta ser más específico.",
        mapPendingHeading: "¿Actualizar la dirección con esta ubicación?",
        mapPendingApply: "Usar esta dirección",
        mapPendingIgnore: "Conservar dirección actual",
      },
      details: {
        heading: "Detalles de la sede",
        subheading: "Indica el tipo de cancha, superficie y servicios disponibles.",
        surfaceLabel: "Superficie principal",
        surfacePlaceholder: "Elige superficie",
        typeLabel: "Tipo de sede",
        typePublic: "Pública (abierta a todos)",
        typePrivate: "Privada (requiere membresía)",
        courtHeading: "Configuración de canchas",
        courtAdd: "Agregar fila",
        courtTypeLabel: "Tipo de cancha",
        courtTypePlaceholder: "Selecciona tipo",
        courtTypeDedicated: "Dedicada",
        courtTypeShared: "Red compartida",
        courtTypeLined: "Solo líneas",
        courtEnvironmentLabel: "Ambiente",
        courtEnvironmentPlaceholder: "Selecciona ambiente",
        environmentIndoor: "Interior",
        environmentOutdoor: "Exterior",
        environmentCovered: "Cubierta",
        courtQuantityLabel: "Cantidad",
        courtRemove: "Eliminar fila",
        amenitiesHeading: "Servicios",
        amenitiesOptions: [
          "Alumbrado",
          "Baños",
          "Puntos de agua",
          "Asientos",
          "Pro Shop",
          "Casilleros",
          "Duchas",
          "Comida/Bebida",
          "Estacionamiento",
          "Acceso para sillas de ruedas",
          "Áreas con sombra",
          "Lanzapelotas",
          "Clases disponibles",
        ],
        hoursHeading: "Horario de atención",
        timeSlots: [
          "5:00 AM",
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
          "11:00 PM",
          "12:00 AM",
        ],
        until: "a",
        closedLabel: "Cerrado",
        descriptionLabel: "Descripción",
        descriptionPlaceholder: "Cuenta características, estado de las canchas y recomendaciones para los jugadores.",
        phoneLabel: "Teléfono",
        phonePlaceholder: "(Opcional) Número de contacto",
        websiteLabel: "Sitio web",
        websitePlaceholder: "https://…",
      },
      openPlay: {
        heading: "Horarios de open play",
        subheading: "Agrega horarios de juego libre para que otros sepan cuándo ir.",
        sessionsCount: "{count} sesiones",
        copyWeekdays: "Copiar a lunes-viernes",
        copyWeekend: "Copiar a fin de semana",
        addSession: "Agregar sesión",
        emptyLabel: "Aún no hay open play cargado para este día.",
        startLabel: "Inicio",
        endLabel: "Fin",
        levelLabel: "Nivel",
        statusLabel: "Estado",
        removeSession: "Eliminar sesión",
        timeSlots: [
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
        ],
        levelOptions: ["Todos los niveles", "Principiante", "Principiante+", "Intermedio", "Intermedio+", "Avanzado", "Jugadores de torneo"],
        statusOptions: ["Disponible", "Pocos cupos", "Completo", "Solo socios", "Abierto al público", "Requiere registro"],
      },
      photos: {
        heading: "Fotos de la sede",
        subheading: "Sube imágenes para que los jugadores reconozcan el lugar.",
        helper: "Arrastra y suelta imágenes o selecciona archivos de tu dispositivo.",
        uploadCta: "Elegir fotos",
        supported: "Formatos JPG o PNG (máx 5 MB por archivo).",
        current: "Fotos existentes",
        pending: "Fotos para subir",
        remove: "Quitar",
      },
      contact: {
        heading: "Información de contacto",
        subheading: "Cuéntanos a quién podemos contactar si necesitamos más detalles.",
        nameLabel: "Tu nombre",
        emailLabel: "Correo electrónico",
        phoneLabel: "Teléfono",
        roleLabel: "Tu relación con la sede",
        rolePlaceholder: "Selecciona tu rol",
        roleOwner: "Propietario / operador",
        roleManager: "Administrador",
        roleEmployee: "Empleado",
        rolePlayer: "Jugador habitual",
        roleVisitor: "Visitante",
        roleOther: "Otro",
        notesLabel: "Notas adicionales",
        notesPlaceholder: "Información extra que ayude al equipo de revisión.",
        confirmationLabel: "Confirmo que la información es correcta y respeta las normas de la comunidad.",
      },
      review: {
        heading: "Revisar y enviar",
        subheading: "Verifica todo antes de enviar la sugerencia.",
        basic: "Información básica",
        name: "Nombre de la sede",
        address: "Dirección",
        type: "Tipo de sede",
        surface: "Superficie",
        courts: "Canchas",
        amenities: "Servicios",
        description: "Descripción",
        photos: "Fotos",
        contact: "Contacto",
        contactName: "Nombre",
        contactEmail: "Correo",
        contactPhone: "Teléfono",
        contactRole: "Rol",
        notes: "Notas",
        unknown: "Desconocido",
      },
      duplicates: {
        title: "Esta sede podría existir",
        description: "Encontramos sedes similares. Si alguna coincide, ábrela en lugar de crear un duplicado.",
        view: "Ver sede",
        continue: "Continuar de todas formas",
        cancel: "Volver",
      },
    },
    settings: {
      header: {
        title: "Configuración y preferencias",
        description: "Controla apariencia, privacidad y notificaciones en WhoPlays.",
        back: "Volver",
        cancel: "Cancelar",
        save: "Guardar preferencias",
        saving: "Guardando…",
      },
      guard: {
        title: "Configuración no disponible",
        description: "Completa tu perfil para desbloquear la configuración.",
        cta: "Editar perfil",
      },
      theme: {
        title: "Preferencia de tema",
        description: "Elige cómo se ve WhoPlays en este dispositivo.",
        options: {
          system: { label: "Sistema", description: "Usar configuración del dispositivo" },
          light: { label: "Claro", description: "Interfaz luminosa" },
          dark: { label: "Oscuro", description: "Ideal para poca luz" },
        },
      },
      language: {
        title: "Idioma",
        description: "Cambia el idioma de la interfaz al instante.",
      },
      availability: {
        title: "Disponibilidad",
        options: {
          1: { title: "Disponible", description: "Los jugadores pueden invitarte a partidos." },
          2: { title: "No disponible", description: "Aparece desconectado para todos." },
          6: { title: "Grupo privado", description: "Visible solo para miembros de grupos privados." },
        },
      },
      visibility: {
        title: "Visibilidad del perfil",
        options: {
          1: { title: "Público", description: "Cualquiera puede ver tu perfil." },
          3: { title: "Usuarios registrados", description: "Solo miembros de WhoPlays pueden ver tu perfil." },
          4: { title: "Conexiones", description: "Solo tus conexiones ven todos los detalles." },
          5: { title: "Privado", description: "Solo tú y administradores ven tu perfil." },
        },
      },
      followers: {
        title: "Visibilidad para seguidores",
        options: {
          1: { title: "Público", description: "Los seguidores ven tu actividad pública." },
          2: { title: "Cancha principal", description: "Limita las actualizaciones a tu comunidad principal." },
          3: { title: "Todas las canchas", description: "Los seguidores ven todas tus actualizaciones." },
          4: { title: "Amigos", description: "Solo amigos aprobados ven tus novedades." },
        },
      },
      notifications: {
        title: "Notificaciones",
        description: "Elige cómo deseas recibir información de WhoPlays.",
        items: {
          tournamentReminders: { title: "Recordatorios de torneos", description: "Recibe avisos antes de tus torneos registrados." },
          matchNotifications: { title: "Actualizaciones de partidos", description: "Avisos cuando se programan o cambian partidos." },
          partnerRequests: { title: "Solicitudes de compañero", description: "Entérate cuando quieran conectarse contigo." },
          communityEvents: { title: "Eventos comunitarios", description: "Mantente al tanto de eventos cercanos." },
          emailNotifications: { title: "Notificaciones por correo", description: "Recibe actualizaciones también por email." },
          eventInvitesSms: { title: "Invitaciones por SMS", description: "Recibe invitaciones por texto para responder rápido." },
          matchInvitesSms: { title: "Alertas de partidos por SMS", description: "Recibe recordatorios y detalles por SMS." },
          scoringUpdatesSms: { title: "Marcador por SMS", description: "Confirma resultados desde mensajes de texto." },
        },
      },
      account: {
        title: "Accesos directos de cuenta",
        links: {
          connectedAccounts: { title: "Cuentas conectadas", description: "Gestiona tus redes vinculadas." },
          dataPrivacy: { title: "Datos y privacidad", description: "Revisa cómo gestionamos tu información." },
          deleteAccount: { title: "Eliminar cuenta", description: "Elimina tu cuenta y datos de forma permanente." },
        },
      },
      messages: {
        saved: "Preferencias guardadas correctamente.",
        error: "No pudimos guardar tus preferencias. Inténtalo de nuevo.",
      },
    },
    homeVenues: {
      title: "Sedes cerca de ti",
      description:
        "Busca sedes WhoPlays por nombre o dirección y revisa las sesiones de juego abierto de hoy.",
      pageTitle: "Directorio de sedes de pickleball",
      pageDescription:
        "Busca por nombre o dirección, revisa servicios y descubre sesiones de juego abierto en tu zona.",
      useMyLocation: "Usar mi ubicación",
      locating: "Detectando ubicación...",
      searchPlaceholder: "Buscar por nombre o dirección",
      searchAriaLabel: "Buscar sedes",
      searchCta: "Buscar",
      promptLocation: "Comparte tu ubicación o busca por nombre para encontrar sedes cercanas.",
      clearFilters: "Limpiar filtros",
      errorLoading: "No pudimos cargar las sedes cercanas. Inténtalo de nuevo.",
      noResults: "No hay sedes que coincidan con tu búsqueda.",
      loading: "Cargando sedes...",
      loadMore: "Cargar más",
      loadingMore: "Cargando más sedes...",
      distanceAway: "A {distance}",
      verifiedBadge: "Verificada",
      courtsHeading: "Detalle de canchas",
      courtsHeadingWithTotal: "Detalle de canchas · {count} canchas",
      courtsFallback: "{count} canchas en total",
      surfaceLabel: "Superficie principal: {surface}",
      totalCourts: "{count} canchas en total",
      amenitiesHeading: "Servicios",
      openPlayHeading: "Juego abierto de hoy",
      hoursHeading: "Horario de operación",
      hoursClosed: "Cerrado",
      contactPhone: "Llamar al {phone}",
      visitWebsite: "Visitar sitio web",
      viewOnMap: "Ver en el mapa",
      viewAllVenues: "Ver todas las sedes",
      viewDetails: "Ver detalle",
      backToHome: "Volver al inicio",
      openStatusNow: "Abierta ahora",
      closedStatusNow: "Cerrada ahora",
      closedStatusToday: "Cerrada hoy",
      openUntil: "Abierta hasta las {time}",
      opensAt: "Abre a las {time}",
      closedForDay: "Cerrada por hoy",
      statusUnknown: "Estado no disponible",
      statusClosedTemporary: "Cerrada temporalmente",
      statusClosedPermanent: "Cerrada permanentemente",
      statusClosedOutOfSeason: "Cerrada fuera de temporada",
      publicVenue: "Pública",
      privateVenue: "Privada",
      mapFallbackTitle: "Mapa de {name}",
      mapFallbackNoLocation: "Sin datos de ubicación.",
    },
    features: {
      title: "Todo lo que necesita tu comunidad pickleball",
      description:
        "Sigue canchas, envía invitaciones y registra partidos con herramientas creadas para el juego en canchas públicas.",
      cards: [
        {
          title: "Visión en vivo de canchas",
          description:
            "Conoce el movimiento antes de salir de casa y comparte un resumen para que todos sepan qué está pasando.",
          points: [
            "Sigue canchas públicas y mira quién juega según el nivel",
            "Revisa sesiones programadas y juegos abiertos en un solo lugar",
            "Publica avisos rápidos para notificar a quienes siguen la cancha",
          ],
        },
        {
          title: "Sesiones rápidas e invitaciones SMS",
          description:
            "Crea un juego, ladder o entrenamiento en segundos e invita por texto. Los jugadores responden Sí/No y la lista se actualiza sola.",
          points: [
            "Plantillas flexibles para juegos abiertos, ladders o clínicas",
            "Invita por SMS sin obligar a instalar la app",
            "Recibe respuestas en tiempo real y envía recordatorios automáticos",
          ],
        },
        {
          title: "Perfiles y estadísticas",
          description:
            "Registra tus partidos, vincula tu DUPR y arma tu red de compañeros, rivales y nuevos contactos.",
          points: [
            "Guarda marcadores, partidos verificados y notas personales",
            "Autoevalúa tu nivel o sincroniza con DUPR para ver tu rating",
            "Sigue jugadores, crea chats y arma tu cuadrilla habitual",
          ],
        },
      ],
    },
    social: {
      title: "Sigue a la comunidad WhoPlays de pickleball",
      description:
        "Consejos, novedades del producto y momentos detrás de cámaras de los jugadores que construyen la app WhoPlays.",
      links: [
        { name: "Instagram", handle: "@whoplaysus", href: "https://www.instagram.com/whoplaysus/" },
        { name: "Facebook", handle: "WhoPlays Pickleball", href: "https://www.facebook.com/profile.php?id=61550796384597" },
        { name: "YouTube", handle: "WhoPlays Pickleball", href: "https://www.youtube.com/channel/UC9JuYJ_QR3ykzjXgnKIRviA" },
        { name: "LinkedIn", handle: "WhoPlays US", href: "https://www.linkedin.com/company/whoplays-us-inc/?viewAsMember=true" },
        { name: "X", handle: "@WhoPlaysUS", href: "https://x.com/WhoPlaysUS" },
        { name: "TikTok", handle: "@whoplays05", href: "https://tiktok.com/@whoplays05" },
      ],
    },
    workflow: {
      title: "Conéctate en cuatro pasos",
      description:
        "Descubre, organiza, invita y registra. WhoPlays se encarga de la coordinación para que pases más tiempo en la cancha.",
      steps: [
        {
          title: "Descubre",
          description:
            "Abre el feed de canchas para ver quién ya está jugando y qué niveles hay en pista.",
        },
        {
          title: "Crea",
          description:
            "Inicia un juego, ladder o entrenamiento en segundos con plantillas pensadas para canchas públicas.",
        },
        {
          title: "Invita",
          description:
            "Envía SMS o comparte el enlace; los jugadores responden Sí/No y WhoPlays actualiza la lista al instante.",
        },
        {
          title: "Registra",
          description:
            "Guarda resultados, sincroniza con DUPR si quieres y sigue el historial de tu comunidad.",
        },
      ],
    },
    testimonials: {
      title: "Organizadores que eligieron WhoPlays",
      items: [
        {
          quote:
            "“El feed de canchas cambió nuestra planificación. Veo quién ya confirmó y texto al resto sin salir de la app.”",
          name: "Lauren Mitchell",
          role: "Directora, Rally Point Pickleball",
        },
        {
          quote:
            "“Los RSVPs por SMS son un antes y un después. Incluso quienes odian las apps se mantienen informados y los cupos se llenan.”",
          name: "Brian Kim",
          role: "Head Pro, Marina Courts",
        },
      ],
    },
    finalCta: {
      title: "Ayúdanos a construir la próxima versión de WhoPlays",
      description:
        "Únete al grupo de organizadores fundadores para probar nuevas funciones de pickleball, pedir integraciones y recibir soporte directo mientras crecemos.",
      primaryCta: "Ser organizador fundador",
      primaryHref: "/founding-organizers",
      secondaryCta: "Descargar la app de pickleball",
      secondaryHref: "/app-stores",
    },
    footer: {
      tagline: "Hecho en EE. UU. para la comunidad global de pickleball.",
      rights: "Todos los derechos reservados.",
      links: {
        pickleballApp: "App de Pickleball",
        privacy: "Política de privacidad",
        courtIntel: "Court Intelligence",
        foundingProgram: "Sedes fundadoras",
        plans: "Planes",
        accountDeletion: "Eliminar cuenta",
      },
    },
    pickleballAppPage: {
      seo: {
        title: "App de Pickleball para Organizar Juegos y Encontrar Canchas | WhoPlays",
        description:
          "WhoPlays es una app de pickleball para encontrar canchas cercanas, organizar juegos, invitar jugadores por SMS y consultar ocupación en vivo en sedes compatibles.",
        openGraphTitle: "App de Pickleball WhoPlays | Organiza Juegos y Encuentra Canchas",
        keywords: [
          "app de pickleball",
          "mejor app de pickleball",
          "app para organizar juegos de pickleball",
          "app para buscar canchas de pickleball",
          "encontrar canchas de pickleball cerca de mi",
          "organizar juegos de pickleball",
          "app para sedes de pickleball",
        ],
        breadcrumbName: "App de Pickleball",
      },
      badge: "App de pickleball",
      title: "Una app de pickleball para organizar juegos y encontrar canchas cercanas",
      description:
        "WhoPlays ayuda a encontrar canchas más rápido, coordinar open play con menos mensajes y evitar llegar a sedes saturadas.",
      ctaBrowse: "Explorar canchas por ubicación",
      ctaDownload: "Descargar la app",
      cards: [
        {
          title: "Encuentra canchas de pickleball cerca",
          description:
            "Busca y explora sedes por ubicación para descubrir dónde jugar, con enlaces directos a detalles y mapas de cada sede.",
        },
        {
          title: "Organiza juegos y open play",
          description:
            "Coordina partidos y open play con menos ida y vuelta para llenar canchas y mantener sesiones activas.",
        },
        {
          title: "Consulta la presión en vivo",
          description:
            "En sedes compatibles, Court Intelligence ayuda a entender ocupación y presión de espera antes de salir.",
        },
      ],
      players: {
        title: "Para jugadores",
        points: [
          "Encuentra canchas por estado y ciudad.",
          "Organiza juegos más rápido e invita por SMS.",
          "Decide cuándo jugar con mejor visibilidad.",
        ],
        cta: "Comenzar en web",
      },
      operators: {
        title: "Para dueños y operadores",
        points: [
          "Mejora la experiencia de jugadores con visibilidad en vivo.",
          "Monitorea patrones históricos para decidir mejor.",
          "Usa Court Intelligence para apoyar la operación.",
        ],
        primaryCta: "Explorar Court Intelligence",
        secondaryCta: "Comenzar",
      },
      faq: {
        title: "Preguntas frecuentes de la app de pickleball",
        items: [
          {
            question: "¿WhoPlays sirve para organizar juegos de pickleball?",
            answer:
              "Sí. WhoPlays ayuda a organizar partidos y open play mientras centraliza invitaciones y datos de la sede en un solo flujo.",
          },
          {
            question: "¿Puedo encontrar canchas de pickleball cerca de mí?",
            answer:
              "Sí. Puedes explorar sedes por ubicación y abrir cada página para ver mapas, detalles de canchas y accesos rápidos.",
          },
          {
            question: "¿WhoPlays también ayuda a dueños de sedes?",
            answer:
              "Sí. Court Intelligence ayuda a dueños y operadores con información en vivo e histórica sobre uso de canchas.",
          },
          {
            question: "¿WhoPlays es gratis para jugadores?",
            answer:
              "Los flujos principales para descubrir y coordinar juego están disponibles sin requerir plan pago. Las capacidades para sedes se ofrecen con Court Intelligence.",
          },
        ],
      },
      jsonLd: {
        featureList: [
          "Encontrar sedes y canchas de pickleball cercanas",
          "Organizar partidos y sesiones de open play",
          "Invitar jugadores con herramientas rápidas para compartir",
          "Consultar ocupación y presión de espera en sedes compatibles",
          "Ver actividad por cancha con Court Intelligence",
        ],
      },
    },
    homeCourtIntelPromo: {
      badge: "Para dueños y operadores",
      title: "Lleva Court Intelligence a tu sede",
      description:
        "Convierte snapshots de cámaras en señales operativas en vivo para actuar más rápido y gestionar canchas con confianza.",
      kpis: ["Ocupación en vivo", "Espera estimada", "Disponibilidad de canchas"],
      foundingNote: "Founding Venue Program abierto ahora para 7 sedes de lanzamiento.",
      primaryCta: "Explorar Court Intelligence",
      primaryHref: "/court-intelligence",
      foundingCta: "Sedes fundadoras",
      foundingHref: "/court-intelligence/founding-venues",
      secondaryCta: "Ver planes",
      secondaryHref: "/court-intelligence#pricing",
    },
    courtIntelligence: {
      hero: {
        eyebrow: "Operación en vivo para sedes de pickleball",
        title: "Ve lo que realmente está pasando en tus canchas, incluso cuando no hay nadie en el lugar.",
        description:
          "Court Intelligence ofrece a clubes de pickleball, HOA y operadores comunitarios visibilidad en tiempo real sobre tráfico en canchas, presión de fila y actividad en sitio, mientras ayuda a los jugadores a decidir cuándo ir a través de WhoPlays.",
        ownerCta: "Iniciar configuración de Court Intelligence",
        ownerCtaNote: "Solo para dueños y managers de sedes.",
        ownerHref: "/court-intelligence/get-started",
        foundingCta: "Ver Founding Venue Program",
        foundingHref: "/court-intelligence/founding-venues",
        playerCta: "Soy jugador",
        playerHref: "/auth/login",
        note: "Founding Venue Program abierto ahora para 7 sedes de lanzamiento.",
        visualNote: "Actividad en vivo, disponibilidad de canchas, presión de espera y tendencias históricas en un solo panel operativo.",
      },
      onboardingEntry: {
        eyebrow: "Onboarding para owners",
        title: "Inicia la configuración de Court Intelligence",
        description:
          "Esta entrada de asistente está pensada para dueños y managers de sedes. Te guiaremos por acceso de cuenta, suscripción, configuración de sede e inicialización de cámaras.",
        note: "Usarás una sola cuenta de WhoPlays para funciones de jugador y operaciones de sede.",
        stepsTitle: "Qué sigue",
        steps: [
          { title: "Paso 1: Acceso de cuenta", description: "Crea tu cuenta o inicia sesión con tu cuenta actual de WhoPlays." },
          { title: "Paso 2: Activar suscripción", description: "Elige el plan base de Court Intelligence y aplica cupón beta si está disponible." },
          { title: "Paso 3: Configurar sede", description: "Crea/selecciona tu sede, asigna roles y define detalles operativos." },
          { title: "Paso 4: Conectar cámaras", description: "Elige modo API o FTPS, copia credenciales de una sola vez e inicializa zonas para datos en vivo." },
        ],
        checklistTitle: "Antes de continuar",
        checklist: [
          "Eres dueño de sede o manager autorizado",
          "Puedes gestionar suscripción y pagos de esta sede",
          "Tienes acceso a cámaras o coordinación con el instalador",
        ],
        primaryCta: "Continuar con configuración de cuenta",
        primaryHref: "/auth/signup?returnTo=/main-app/court-intelligence/onboarding/account",
        secondaryCta: "Ya tengo una cuenta",
        secondaryHref: "/auth/login?returnTo=/main-app/court-intelligence/onboarding/account",
        signedInPrimaryCta: "Continuar a suscripción",
        signedInSecondaryCta: "Ver planes",
        backCta: "Volver a Court Intelligence",
        backHref: "/court-intelligence",
        foundingCallout: {
          eyebrow: "Founding Venue Program",
          title: "¿Te postulas para una de las 7 plazas fundadoras?",
          description:
            "Usa la ruta fundadora si quieres acompañamiento de lanzamiento, acceso temprano y la solicitud fundadora dentro de la app principal.",
          cta: "Ir a sedes fundadoras",
          href: "/court-intelligence/founding-venues",
        },
      },
      highlights: [
        {
          value: "Reduce reclamos en horas pico",
          label: "Detecta la presión en la sede antes de que las sesiones saturadas se conviertan en frustración para los jugadores.",
        },
        {
          value: "Ve la presión de las canchas en tiempo real",
          label: "Entiende qué tan cargada se siente la sede, si las canchas se están liberando y cuándo empieza a formarse la espera.",
        },
        {
          value: "Planifica con historial real de uso",
          label: "Usa tendencias históricas para respaldar decisiones de personal, planeación de canchas y expansión.",
        },
      ],
      whatYouGet: {
        title: "Por qué los operadores lo usan",
        description:
          "Court Intelligence ayuda a los operadores a tomar decisiones más rápidas en horas ocupadas, mejorar la visibilidad para jugadores y construir un historial real de uso de la instalación.",
        items: [
          {
            title: "Para clubes con reservas",
            points: [
              "Tu sistema de reservas muestra reservas. Court Intelligence muestra lo que realmente está pasando en cancha.",
              "Ve cuándo las canchas reservadas siguen ocupadas y cuándo el flujo de open play se convierte en espera.",
              "Respalda decisiones del staff y políticas de rotación con visibilidad real en sitio.",
            ],
          },
          {
            title: "Para HOA y canchas comunitarias",
            points: [
              "Ayuda a los residentes a saber cuándo ir antes de manejar hasta las canchas.",
              "Reduce reclamos por saturación en open play sin necesitar personal fijo en el lugar.",
              "Usa datos reales de uso para guiar decisiones sobre amenidades y futuras canchas.",
            ],
          },
          {
            title: "Para operadores que planean crecer",
            points: [
              "Revisa el tráfico histórico por horario, sede y cancha.",
              "Compara la actividad real contra tus supuestos operativos en horas de mayor demanda.",
              "Construye el caso para más personal, ajustes de política o más canchas.",
            ],
          },
        ],
      },
      setupOutcomes: {
        title: "Elige el resultado de tu configuración",
        description:
          "Comienza con una estimación rápida o mapea la sede con más detalle para desbloquear disponibilidad de canchas e insights de espera.",
      },
      setupGuide: {
        title: "Elige el resultado de tu configuración",
        subtitle: "Empieza con una estimación rápida o mapea canchas y zonas de fila para obtener más visibilidad.",
        openGuideLabel: "Ver opciones de configuración",
        eyebrowLabel: "Mejor para",
        descriptionLabel: "Qué significa",
        outcomes: {
          quickEstimate: {
            label: "Estimación rápida",
            eyebrow: "Ideal para pilotos iniciales",
            description: "Obtén una señal general de actividad en vivo con una vista amplia de la sede.",
            unlocksTitle: "Lo que desbloquea",
            unlocks: [
              "Actividad general en vivo",
              "Nivel de ocupación",
              "Visibilidad para jugadores en WhoPlays",
            ],
            setupTitle: "Configuración recomendada",
            setup: [
              "Una cámara amplia puede ser suficiente",
              "Cobertura general o una vista limpia de cobertura completa",
              "Ideal cuando quieres lanzar rápido",
            ],
          },
          courtAvailability: {
            label: "Disponibilidad de canchas",
            eyebrow: "Ideal para sedes activas",
            description:
              "Mapea las canchas visibles para estimar cuáles están en uso y cuáles podrían liberarse pronto.",
            unlocksTitle: "Lo que desbloquea",
            unlocks: [
              "Visibilidad de canchas ocupadas vs libres",
              "Contexto por cancha",
              "Mejor planificación para jugadores",
            ],
            setupTitle: "Configuración recomendada",
            setup: [
              "Apunta a 1 o 2 canchas por cámara",
              "Crea un área de tipo cancha por cada cancha visible",
              "Usa un ángulo estable y elevado con separación clara",
            ],
          },
          courtAndQueue: {
            label: "Canchas + fila",
            eyebrow: "Ideal para horas pico",
            description:
              "Agrega áreas de espera, bancas o racks para estimar presión de fila y tiempo de espera.",
            unlocksTitle: "Lo que desbloquea",
            unlocks: [
              "Visibilidad de jugadores esperando",
              "Presión de espera estimada",
              "Señales operativas más fuertes en vivo",
            ],
            setupTitle: "Configuración recomendada",
            setup: [
              "Usa la misma base de mapeo de canchas",
              "Agrega áreas de espera o bancas claramente visibles",
              "Mantén las zonas de fila separadas visualmente de las canchas activas",
            ],
          },
        },
        modal: {
          title: "Opciones de configuración",
          closeLabel: "Cerrar",
        },
      },
      workflow: {
        title: "Cómo funciona",
        steps: [
          { title: "Suscripción", description: "Activa un plan base de Court Intelligence para habilitar acceso operativo." },
          { title: "Conectar cámaras", description: "Crea cámaras, elige modo API o FTPS, copia credenciales de una sola vez y realiza la inicialización del feed." },
          { title: "Configurar zonas", description: "Dibuja polígonos para canchas, espera, bancas, racks y cobertura completa." },
          { title: "Operar con datos", description: "Usa KPIs en vivo e histórico para optimizar staff y políticas de rotación." },
        ],
      },
      playerBenefit: {
        title: "Ventaja gratis para jugadores en WhoPlays US",
        description: "Cuando una sede activa Court Intelligence, los jugadores se benefician sin costo extra.",
        points: [
          "Consultar si una sede está muy ocupada antes de salir",
          "Ver presión de espera y mejores ventanas para jugar",
          "Reducir incertidumbre y viajes perdidos en horas pico",
        ],
      },
      playerAdvocacy: {
        title: "¿Quieres esto en tu club, HOA o canchas comunitarias?",
        description:
          "Los jugadores no compran la infraestructura, pero sí pueden ayudar a generar demanda. Comparte la página de sedes fundadoras con la persona que gestiona tus canchas.",
        points: [
          "Envía una sola página pensada para owners en vez de explicar el producto tú mismo.",
          "Dales la ruta estándar de configuración y la opción del programa fundador en un solo lugar.",
          "Ayuda a tu sede a evaluar ocupación en vivo, presión de espera y visibilidad de canchas sin adivinar.",
        ],
        primaryCta: "Enviar por correo a la sede",
        secondaryCta: "Abrir la página para owners",
        shareSubject: "Court Intelligence para nuestras canchas de pickleball",
        shareBody:
          "Hola,\n\nEncontré esta página de Court Intelligence para sedes de pickleball y pensé que podría ser útil para nuestras canchas. Explica la ruta para owners y el programa limitado de sedes fundadoras.\n\n{url}\n",
      },
      privacy: {
        title: "Privacidad y datos de cámaras",
        description: "Court Intelligence está diseñado para visibilidad operativa con control de cámaras por parte de la sede.",
        points: [
          "Las cámaras envían snapshots hacia WhoPlays por API push o carga FTPS; no nos conectamos a tu red local.",
          "Por defecto, los snapshots se procesan para señales de ocupación/espera y no se guardan.",
          "Si el modo debug del grupo de cámaras está activo, los snapshots pueden guardarse para mejora de ML hasta la fecha de expiración definida por el usuario.",
          "Mientras una cámara está en estado de inicialización, se conserva solo el snapshot más reciente y se reemplaza con uno nuevo.",
          "Los operadores pueden detener el envío en cualquier momento quitando la configuración en su cámara.",
        ],
        playerNote:
          "Los jugadores en WhoPlays US reciben indicadores en vivo de ocupación y espera en sedes habilitadas, no controles de configuración de cámaras.",
        policyCta: "Leer Política de Privacidad completa",
        policyHref: "/privacy-policy",
      },
      pricing: {
        title: "Precios simples para operadores de pickleball",
        description:
          "Comienza con una sede y cuatro cámaras, y amplía capacidad a medida que crecen las operaciones. Los jugadores reciben visibilidad en vivo a través de WhoPlays cuando tu sede habilita Court Intelligence.",
        cards: [
          {
            name: "Court Intelligence Base (Mensual)",
            price: "$99 / mes",
            description: "Lanza una sede con visibilidad operativa en vivo y facturación mensual flexible.",
            highlighted: true,
            features: [
              "Incluye 1 sede",
              "Incluye hasta 4 cámaras",
              "Tráfico del venue, disponibilidad y señales de espera en vivo según el nivel de setup",
              "Analítica histórica, logs y gestión por roles",
              "Visibilidad gratis para jugadores a través de WhoPlays",
            ],
          },
          {
            name: "Court Intelligence Base (Anual)",
            price: "$948 / año",
            description: "Lanza una sede con la misma capacidad base y menor costo anual.",
            highlighted: false,
            features: [
              "Incluye 1 sede",
              "Incluye hasta 4 cámaras",
              "Las mismas funciones en vivo e históricas del plan mensual",
              "Ahorra $240/año frente al plan mensual",
              "Visibilidad gratis para jugadores a través de WhoPlays",
            ],
          },
          {
            name: "Add-on de cámaras",
            price: "$12 / mes o $115 / año",
            description: "Agrega capacidad cuando una sede necesita más cobertura o visibilidad más detallada.",
            highlighted: false,
            features: ["Agrega +1 cupo de cámara", "Útil para sedes más grandes o mapeo más preciso", "Gestión directa desde la suscripción"],
          },
          {
            name: "Add-on de sedes",
            price: "$59 / mes o $567 / año",
            description: "Agrega una sede adicional e incluye cuatro cupos de cámara para esa ubicación.",
            highlighted: false,
            features: ["Agrega +1 sede", "Incluye +4 cupos de cámara para esa sede", "Ideal para HOA, clubes con varias sedes y operadores en crecimiento"],
          },
        ],
        footnote:
          "Los jugadores siempre reciben visibilidad en vivo para sedes habilitadas a través de WhoPlays. El hardware, la compra de cámaras y la instalación en sitio no están incluidos. Los cupones del Founding Venue Program pueden estar disponibles para ubicaciones seleccionadas durante el lanzamiento.",
      },
      faq: {
        title: "Preguntas frecuentes",
        items: [
          {
            question: "¿Necesito otra cuenta para Court Intelligence?",
            answer: "No. Usas la misma cuenta de WhoPlays y cambias de modo según tus permisos.",
          },
          {
            question: "¿Los jugadores deben pagar para ver los datos?",
            answer: "No. La visibilidad en tiempo real para jugadores es gratis en sedes habilitadas.",
          },
          {
            question: "¿Puedo iniciar con una sede y luego escalar?",
            answer: "Sí. Comienzas con sede/cámaras incluidas y amplías con add-ons.",
          },
          {
            question: "¿Qué modos de conexión de cámara son compatibles?",
            answer: "API push y FTPS (TLS explícito en puerto 21). Intervalos recomendados: API cada 30s y FTPS cada 60s.",
          },
          {
            question: "¿Managers y monitors necesitan suscripción propia?",
            answer: "El owner se suscribe y después otorga permisos por sede al equipo.",
          },
          {
            question: "¿Ustedes proveen hardware o instalación de cámaras?",
            answer: "No. Cada sede provee y configura sus cámaras y red local.",
          },
        ],
      },
      finalCta: {
        title: "Postúlate para una plaza del Founding Venue Program",
        description:
          "Estamos abriendo Court Intelligence para un número limitado de clubes, HOA y operadores comunitarios que quieren acompañamiento directo de lanzamiento, acceso temprano y la oportunidad de ayudar a dar forma al producto.",
        primaryCta: "Postularme",
        primaryHref: "/court-intelligence/founding-venues",
        secondaryCta: "Hablar con el equipo",
        secondaryHref:
          "mailto:support@whoplays.us?subject=Court%20Intelligence%20Founding%20Venue",
      },
    },
    foundingVenues: {
      hero: {
        eyebrow: "Founding Venue Program de Court Intelligence",
        title: "Lanza Court Intelligence con apoyo directo de configuración.",
        highlight: "Limitado a 7 sedes de lanzamiento para clubes, HOA y operadores comunitarios.",
        description:
          "Las sedes fundadoras reciben acceso temprano a Court Intelligence, acompañamiento del equipo de WhoPlays y la oportunidad de influir en los primeros playbooks operativos y en la hoja de ruta.",
        note: "Los jugadores siempre reciben visibilidad en vivo de las sedes habilitadas a través de WhoPlays sin costo adicional.",
        primaryCta: "Postularme",
        primaryHref: "/court-intelligence/get-started",
        secondaryCta: "Hablar con el equipo",
        secondaryHref:
          "mailto:support@whoplays.us?subject=Court%20Intelligence%20Founding%20Venue",
        visualTitle: "Qué obtienen las sedes fundadoras",
        visualPoints: [
          "Acompañamiento directo desde el onboarding hasta las primeras cámaras en vivo",
          "Canal prioritario de feedback sobre setup, operación y reporting",
          "Posibles cupones de lanzamiento para ubicaciones seleccionadas cuando estén disponibles",
        ],
        visualFootnote: "El hardware, la compra de cámaras y la instalación en sitio no están incluidos.",
      },
      highlights: [
        {
          value: "7 sedes",
          label: "Estamos abriendo el programa fundador a una primera cohorte muy controlada.",
        },
        {
          value: "Acceso temprano",
          label: "Obtén nuevas capacidades para operadores antes del despliegue amplio.",
        },
        {
          value: "Feedback operativo",
          label: "Ayuda a dar forma a los flujos de fila, disponibilidad y reporting.",
        },
      ],
      benefits: {
        title: "Qué incluye el programa",
        description:
          "Este programa está diseñado para sedes que quieren un lanzamiento más acompañado mientras Court Intelligence se abre a más operadores.",
        items: [
          {
            title: "Guía de lanzamiento",
            points: [
              "Revisamos contigo la configuración de la sede, el plan de cámaras y tus metas de rollout.",
              "Avanza por acceso de cuenta, suscripción, configuración de sede e inicialización de cámaras con apoyo más claro.",
              "Reduce errores de configuración antes de salir en vivo con jugadores.",
            ],
          },
          {
            title: "Acceso temprano al producto",
            points: [
              "Usa Court Intelligence mientras siguen evolucionando los workflows de lanzamiento y los reportes para operadores.",
              "Comparte feedback práctico sobre busyness, disponibilidad de canchas e insights de espera.",
              "Influye en lo que se prioriza para operaciones de sedes.",
            ],
          },
          {
            title: "Visibilidad para jugadores desde el día uno",
            points: [
              "Los jugadores en WhoPlays pueden ver visibilidad en vivo cuando tu sede está habilitada.",
              "Ayuda a los jugadores a evitar viajes perdidos y a planificar mejor las horas pico.",
              "Da a tu sede una experiencia en vivo más transparente sin agregar un paywall para jugadores.",
            ],
          },
        ],
      },
      fit: {
        title: "Quién debería postularse",
        description:
          "La mejor opción es una sede que pueda avanzar con la configuración y quiera ayudar a moldear la operación de lanzamiento con feedback real.",
        items: [
          "Clubes de pickleball que quieren visibilidad en vivo más allá de las reservas.",
          "HOA y canchas comunitarias que necesitan visibilidad de afluencia y espera sin staff fijo en el lugar.",
          "Operadores con varias sedes que quieren datos históricos para planificar.",
          "Sedes que pueden coordinar la configuración de cámaras o trabajar directamente con su instalador.",
        ],
      },
      launchSupport: {
        title: "Qué pasa después de postularte",
        steps: [
          {
            title: "Comparte detalles de la sede",
            description: "Cuéntanos sobre tus canchas, tu modelo operativo y qué resultado importa más en el lanzamiento.",
          },
          {
            title: "Confirmamos encaje",
            description: "Revisamos si tu sede es una buena opción para la cohorte actual y para los requisitos de setup.",
          },
          {
            title: "Configura acceso y cámaras",
            description: "Avanza por el onboarding del owner, la suscripción, la creación de la sede y la inicialización de cámaras.",
          },
          {
            title: "Sal en vivo e itera",
            description: "Lanza la sede, monitorea señales en vivo y comparte feedback sobre lo que los operadores necesitan después.",
          },
        ],
      },
      checklist: {
        title: "Antes de postularte",
        description:
          "Las mejores sedes fundadoras pueden avanzar rápido con la configuración y coordinar a las personas necesarias para activar las cámaras.",
        items: [
          "Eres owner, manager u operador autorizado de la sede.",
          "Puedes gestionar la facturación o coordinar con quien lo haga.",
          "Puedes acceder a la configuración de cámaras o trabajar con tu equipo instalador.",
          "Quieres lanzar una sede primero y expandir después si hace falta.",
        ],
        footnote:
          "Los snapshots de cámaras se procesan para señales de ocupación y espera. La retención opcional de debug se controla desde la configuración de la sede.",
      },
      finalCta: {
        title: "Comprueba si una de las 7 plazas encaja con tu sede",
        description:
          "Inicia el flujo para owners o contáctanos antes si quieres confirmar tiempos de lanzamiento, setup de cámaras o necesidades multi-sede.",
        primaryCta: "Iniciar setup para owners",
        primaryHref: "/court-intelligence/get-started",
        secondaryCta: "Escribir al equipo",
        secondaryHref:
          "mailto:support@whoplays.us?subject=Court%20Intelligence%20Founding%20Venue",
      },
    },
    foundingCiApplication: {
      loading: "Cargando tu solicitud de sede fundadora...",
      backToProgram: "Volver al programa de sedes fundadoras",
      signOutToProgram: "Cerrar sesion y volver al programa",
      supportCta: "Enviar correo a soporte",
      signedInAs: "Sesion iniciada como {value}",
      retry: "Intentar de nuevo",
      loadErrorTitle: "No pudimos cargar el estado de tu solicitud.",
      approvedRedirect: "Tu solicitud fue aprobada. Redirigiendo al onboarding de Court Intelligence...",
      intro: {
        eyebrow: "Solicitud de sede fundadora de Court Intelligence",
        title: "Envía tu solicitud de sede fundadora",
        description:
          "Usa la misma cuenta de WhoPlays que usarás durante el onboarding. Si tu solicitud es aprobada, continuarás con el flujo estándar de onboarding de Court Intelligence.",
      },
      form: {
        title: "Cuéntanos sobre tu sede",
        description:
          "Usamos esta información para confirmar encaje, tiempos de lanzamiento y necesidades de soporte para la cohorte actual.",
        required: "Los campos obligatorios están marcados con *.",
        sections: {
          contact: "Contacto",
          venue: "Sede",
          operations: "Operación",
          notes: "Notas",
        },
        fields: {
          contactName: "Nombre de contacto",
          contactEmail: "Correo electrónico",
          contactPhone: "Teléfono",
          organizationName: "Nombre de la organización u operador",
          venueName: "Nombre de la sede",
          venueType: "Tipo de sede",
          city: "Ciudad",
          state: "Estado",
          country: "País",
          courtsCount: "Cantidad estimada de canchas",
          hasExistingCameras: "Ya tienen cámaras instaladas?",
          operationsModel: "Cómo opera hoy la sede?",
          targetLaunchTiming: "Fecha objetivo de lanzamiento",
          willingToGiveFeedback: "Están dispuestos a compartir feedback operativo durante el lanzamiento?",
          notes: "Hay algo que debamos saber antes de revisar?",
        },
        options: {
          empty: "Selecciona una opción",
          boolean: {
            yes: "Sí",
            no: "No",
            notSure: "No estoy seguro",
          },
          venueType: {
            hoa: "HOA",
            private_club: "Club privado",
            city: "Ciudad o departamento de parques",
            community: "Sede comunitaria",
            other: "Otro",
          },
          operationsModel: {
            reservations: "Reservas",
            open_play: "Juego abierto",
            both: "Ambos",
            unknown: "No definido",
          },
          targetLaunchTiming: {
            asap: "Lo antes posible",
            within_30_days: "Dentro de 30 días",
            within_60_days: "Dentro de 60 días",
            later: "Más adelante",
            exploring: "Solo explorando",
          },
        },
      },
      validation: {
        contactName: "Ingresa tu nombre.",
        contactMethod: "Agrega un correo o un teléfono.",
        organizationName: "Ingresa el nombre de la organización u operador.",
        venueType: "Selecciona el tipo de sede.",
        city: "Ingresa la ciudad.",
        state: "Ingresa el estado.",
        courtsCount: "Ingresa una cantidad válida de canchas.",
      },
      errors: {
        load: "No se pudo cargar tu solicitud.",
        submit: "No se pudo enviar tu solicitud.",
      },
      actions: {
        submit: "Enviar solicitud",
        submitting: "Enviando...",
        reviewProgram: "Revisar detalles del programa",
      },
      summary: {
        title: "Resumen de la solicitud",
        organization: "Organización",
        venue: "Sede",
        location: "Ubicación",
        courts: "Canchas",
        submittedAt: "Enviada",
        status: "Estado",
        statusReason: "Nota de revisión",
      },
      states: {
        submitted: {
          badge: "En revisión",
          title: "Solicitud recibida",
          description:
            "Ya recibimos tu solicitud y revisaremos encaje, preparación de lanzamiento y capacidad actual de la cohorte.",
          helper:
            "Si es aprobada, continuarás con el flujo normal de onboarding y checkout de Court Intelligence.",
          summaryNote: "Tu solicitud ya está registrada. No necesitas hacer nada más mientras la revisamos.",
        },
        waitlisted: {
          badge: "En lista de espera",
          title: "Tu sede quedó en lista de espera",
          description:
            "La cohorte actual está llena o cerca del límite. Mantendremos tu sede en fila para la próxima apertura.",
          helper: "No necesitas enviarla de nuevo. Te contactaremos si se libera un cupo fundador.",
          summaryNote: "Estamos conservando tu solicitud para una futura apertura.",
        },
        rejected: {
          badge: "No aprobada",
          title: "Esta solicitud no avanzará por ahora",
          description:
            "Tu sede no entra en la cohorte fundadora actual. Aun así puedes contactarnos si cambia tu momento o tu configuración.",
          helper: "Si ves una nota de revisión abajo, esa es la guía más relevante de nuestro equipo.",
          summaryNote: "Puedes referenciar esta solicitud si luego contactas a soporte.",
        },
      },
    },
    subscriptionPlans: {
      hero: {
        eyebrow: "Precios flexibles",
        title: "Planes pensados para cada organizador",
        description:
          "Comienza gratis y escala a automatizaciones profesionales cuando tus programas crezcan. Cambia de nivel en cualquier momento.",
        note: "Todos los planes incluyen recordatorios de eventos, insights de asistencia y seguimiento de participación.",
      },
      courtIntelCallout: {
        title: "¿Gestionas una sede o programa municipal?",
        description:
          "Court Intelligence está diseñado para operadores e incluye busyness en vivo, insights de espera y analítica histórica. Cuando una sede lo activa, también habilita visibilidad en vivo gratis para jugadores en WhoPlays US.",
        note: "La compra de hardware y la instalación en sitio no están incluidas. Cada sede provee y configura sus propias cámaras.",
        primaryLabel: "Ver planes de Court Intelligence",
        primaryHref: "/court-intelligence",
      },
      cards: [
        {
          id: "free",
          name: "Free",
          badge: "Empieza aquí",
          priceMonthly: "Gratis / mes",
          priceYearly: "Gratis / año",
          description: "Ideal para probar los flujos de programación básicos con grupos pequeños y confirmaciones manuales.",
          features: [
            "Programaciones ilimitadas sin invitaciones",
            "Invita hasta 5 jugadores por evento",
            "15 invitaciones SMS al mes",
            "Insights del calendario y notificaciones",
          ],
          ctaLabel: "Crear cuenta gratis",
        },
        {
          id: "basic",
          name: "Scheduling Basic",
          badge: "Más popular",
          priceMonthly: "$2.99 / mes",
          priceYearly: "$24.99 / año",
          description: "Gestiona drop-ins semanales con listas de invitación más grandes y recordatorios SMS automáticos.",
          features: [
            "Hasta 5 calendarios activos con invitaciones",
            "Invita hasta 20 jugadores por evento",
            "75 invitaciones SMS al mes",
            "Experiencia sin anuncios y con tu marca",
          ],
          ctaLabel: "Elegir Basic",
          highlighted: true,
        },
        {
          id: "pro",
          name: "Scheduling Pro",
          badge: "Mejor valor",
          priceMonthly: "$4.99 / mes",
          priceYearly: "$39.99 / año",
          description: "Desbloquea invitaciones ilimitadas, prioridad en SMS y escala tus programas sin límites.",
          features: [
            "Calendarios ilimitados con invitaciones",
            "Jugadores ilimitados por evento",
            "150 invitaciones SMS al mes",
            "Entrega prioritaria de SMS para confirmar más rápido",
          ],
          ctaLabel: "Mejorar a Pro",
        },
      ],
      table: {
        title: "Compara lo que incluye cada plan",
        description:
          "Los planes mantienen desbloqueadas las herramientas principales. Actualiza cuando necesites más invitaciones o confirmaciones más rápidas.",
        featureColumnLabel: "Función",
        planLabels: {
          free: "Free",
          basic: "Scheduling Basic",
          pro: "Scheduling Pro",
        },
        rows: [
          {
            label: "Máx. calendarios activos sin invitaciones",
            values: { free: "Ilimitados", basic: "Ilimitados", pro: "Ilimitados" },
          },
          {
            label: "Máx. calendarios activos con invitaciones",
            values: { free: "1", basic: "5", pro: "Ilimitados" },
          },
          {
            label: "Máx. jugadores invitados por calendario",
            values: { free: "5", basic: "20", pro: "Ilimitados" },
          },
          {
            label: "Máx. canchas por evento",
            values: { free: "1", basic: "1", pro: "1" },
          },
          {
            label: "Máx. invitaciones SMS por mes",
            values: { free: "15", basic: "75", pro: "150" },
          },
          {
            label: "Insights de asistencia (¿quién se suma?)",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Recordatorios y notificaciones",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Experiencia sin anuncios",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Ventana de programación (días a futuro)",
            values: { free: "30", basic: "30", pro: "30" },
          },
          {
            label: "El organizador puede quedar fuera del roster",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. eventos de round robin al mes",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. jugadores por round robin",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. torneos de eliminación simple/doble al mes",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. jugadores por torneo de eliminación",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Entrega prioritaria de SMS",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Precio mensual",
            values: { free: "Gratis", basic: "$2.99", pro: "$4.99" },
          },
          {
            label: "Precio anual",
            values: { free: "Gratis", basic: "$24.99", pro: "$39.99" },
          },
        ],
        legend: {
          check: "Incluido",
          cross: "No incluido",
        },
      },
      cta: {
        title: "¿Necesitas acceso para equipo o torneos?",
        description:
          "Los formatos competitivos llegarán pronto. Súmate como organizador fundador para influir en la hoja de ruta y asegurar precios tempranos.",
        primaryLabel: "Habla con nosotros",
        primaryHref: "/founding-organizers",
        secondaryLabel: "Ver novedades del producto",
        secondaryHref: "/bugtracker",
      },
      footnote: "Gestiona upgrades directamente en la app móvil de WhoPlays. Precios expresados en USD.",
    },
    foundingOrganizers: {
      hero: {
        eyebrow: "Programa de organizadores fundadores",
        title: "Gestiona tu liga de pickleball mejor y gratis de por vida",
        highlight: "Los primeros 100 organizadores obtienen acceso de por vida a WhoPlays US.",
        description:
          "Organiza eventos, automatiza confirmaciones y ayuda a definir las próximas funciones. Mantén tus grupos activos y te daremos Scheduler Pro de por vida cuando lancemos las suscripciones.",
        badges: [
          {
            id: "google-play",
            label: "Disponible en Google Play",
            href: "https://play.google.com/store/apps/details?id=com.whosplays.app&pli=1",
            image: "/images/google-play-badge.png",
            width: 209,
            height: 63,
            className: "h-18 w-auto",
          },
          {
            id: "app-store",
            label: "Descargar en App Store",
            href: "https://apps.apple.com/app/apple-store/id6451442623?pt=126911496&ct=Founding%20Organizers&mt=8",
            image: "/images/apple-store-badge.svg",
            width: 175,
            height: 60,
            className: "h-12 w-auto",
          },
        ],
        primaryCtaLabel: "Programa tu primer evento",
          primaryCtaHref: "/app-stores",
      },
      lifetime: {
        title: "Scheduler Pro de por vida para organizadores activos",
        description:
          "Cuando lancemos las suscripciones, otorgaremos acceso Pro vitalicio a los organizadores que se mantengan activos.",
        bullets: [
          "Usa WhoPlays para programar y gestionar tus sesiones.",
          "Mantén al menos un evento cada 60 días.",
          "Haz crecer a tu comunidad y mantendremos Pro desbloqueado.",
        ],
        footnote: "Es nuestra forma de agradecer a quienes impulsan el crecimiento del pickleball desde el primer día.",
      },
      benefits: {
        title: "Beneficios clave",
        items: [
          "Programa partidos en segundos",
          "Invita a jugadores por SMS — sin app",
          "Controla cupos y confirma asistencias",
          "Visualiza quién juega por nivel y horario",
          "Gestiona tus eventos como un profesional",
        ],
      },
      reminder: {
        headline: "Solo 100 organizadores obtienen acceso vitalicio gratuito",
        subheadline: "Asegura tu lugar antes de que se agoten.",
      },
      video: {
        title: "Mira la programación de WhoPlays en acción",
        src: "/videos/WhoPlays-Scheduler-Web-Version.mp4",
        poster: "https://whoplays.us/wp-content/uploads/2025/03/Image.png",
      },
    },
    privacyPolicy: {
      title: "Política de privacidad",
      lastUpdated: "Última actualización: 30 de marzo de 2025",
      intro: [
        "Esta Política de Privacidad describe nuestras políticas y procedimientos sobre la recopilación, el uso y la divulgación de su información cuando utiliza el Servicio, y explica sus derechos de privacidad y cómo la ley lo protege.",
        "Utilizamos sus datos personales para prestar y mejorar el Servicio. Al utilizar el Servicio, usted acepta la recopilación y el uso de la información de acuerdo con esta Política de Privacidad.",
      ],
      sections: [
        {
          heading: "Interpretación y definiciones",
          subSections: [
            {
              subheading: "Interpretación",
              paragraphs: [
                "Las palabras con iniciales en mayúscula tienen los significados definidos en las siguientes condiciones. Las definiciones siguientes tendrán el mismo significado, independientemente de que aparezcan en singular o en plural.",
              ],
            },
            {
              subheading: "Definiciones",
              paragraphs: ["Para los fines de esta Política de Privacidad:"],
              definitions: [
                {
                  term: "Cuenta",
                  description: "Significa una cuenta única creada para que usted acceda a nuestro Servicio o a partes de nuestro Servicio.",
                },
                {
                  term: "Afiliada",
                  description:
                    "Significa una entidad que controla, es controlada por o está bajo control común con una parte.",
                },
                {
                  term: "Aplicación",
                  description:
                    "Se refiere a WhoPlays US – Pickleball Scheduler & Tracker, el software proporcionado por la Compañía.",
                },
                {
                  term: "Compañía",
                  description:
                    '("la Compañía", "Nosotros" o "Nuestro") se refiere a WhoPlays US, Inc, 45 Whistler Trace, Ponte Vedra FL 32081.',
                },
                {
                  term: "País",
                  description: "Se refiere a: Florida, Estados Unidos.",
                },
                {
                  term: "Dispositivo",
                  description:
                    "Significa cualquier dispositivo que pueda acceder al Servicio, como una computadora, un teléfono móvil o una tableta digital.",
                },
                {
                  term: "Datos Personales",
                  description: "Es cualquier información que se relaciona con una persona identificada o identificable.",
                },
                { term: "Servicio", description: "Se refiere a la Aplicación." },
                {
                  term: "Proveedor de servicios",
                  description:
                    "Significa cualquier persona física o jurídica que procese los datos en nombre de la Compañía.",
                },
                {
                  term: "Datos de uso",
                  description:
                    "Se refiere a los datos recopilados automáticamente, ya sea generados por el uso del Servicio o por la propia infraestructura del Servicio.",
                },
                {
                  term: "Usted",
                  description: "Significa el individuo que accede o utiliza el Servicio.",
                },
              ],
            },
          ],
        },
        {
          heading: "Recopilación y uso de sus datos personales",
          subSections: [
            {
              subheading: "Datos personales",
              paragraphs: ["Podemos recopilar información personal como:"],
              bullets: [
                "Dirección de correo electrónico",
                "Nombre y apellido",
                "Número de teléfono",
                "Dirección, estado, provincia, código postal y ciudad",
              ],
            },
            {
              subheading: "Datos de uso",
              paragraphs: [
                "Esto incluye su dirección IP, el tipo y la versión del navegador, las páginas visitadas, la fecha y hora de la visita, el tiempo dedicado y otros datos de diagnóstico.",
              ],
            },
            {
              subheading: "Datos recopilados en la aplicación",
              paragraphs: ["Con su permiso, podemos recopilar información adicional:"],
              bullets: [
                "Datos de ubicación",
                "Contactos (para la función de invitaciones)",
                "Acceso a la cámara y biblioteca de fotos (para fotos de perfil o de eventos)",
              ],
              footnote: "Puede habilitar o deshabilitar el acceso a estos datos en cualquier momento desde la configuración de su dispositivo.",
            },
            {
              subheading: "Uso de sus datos personales",
              paragraphs: ["Utilizamos sus datos para los siguientes fines:"],
              bullets: [
                "Proporcionar y mantener nuestro Servicio",
                "Gestionar su cuenta",
                "Facilitar invitaciones por SMS y notificaciones de la aplicación",
                "Gestionar suscripciones y validar derechos de usuario",
                "Brindar soporte y responder a consultas",
                "Informarle sobre actualizaciones, ofertas y cambios",
                "Analizar el rendimiento y el uso para realizar mejoras",
              ],
            },
            {
              subheading: "Servicios de suscripción",
              paragraphs: [
                "WhoPlays US ofrece suscripciones gratuitas, básicas y profesionales, además de suscripciones específicas para torneos. Con fines promocionales, algunos usuarios pueden recibir acceso de por vida. Nos reservamos el derecho de revocar dicho acceso si el usuario no ha programado un evento ni utilizado funciones clave en un período de 60 días.",
                "Las suscripciones dentro de la aplicación pueden administrarse a través de Apple App Store o Google Play. Estas suscripciones siguen las políticas de facturación, cancelación y renovación definidas por esas plataformas.",
              ],
            },
          ],
        },
        {
          heading: "Datos de cámaras en Court Intelligence",
          paragraphs: [
            "Court Intelligence recibe snapshots enviados por cámaras configuradas por la sede mediante API push o carga FTPS. WhoPlays no se conecta a la red local de la sede para extraer imágenes.",
            "Por defecto, los snapshots se procesan para calcular señales operativas (como ocupación y espera) y no se almacenan.",
          ],
          subSections: [
            {
              subheading: "Modo debug y comportamiento de inicialización",
              bullets: [
                "Si el usuario de la sede activa el modo debug del grupo de cámaras, los snapshots pueden conservarse para mejorar ML hasta la fecha/hora de expiración configurada.",
                "Mientras la cámara permanece en estado de inicialización, se conserva solo el snapshot más reciente y se reemplaza por snapshots nuevos.",
                "Cuando la cámara sale de inicialización (por ejemplo, activa o inactiva), finaliza ese comportamiento de snapshot de inicialización.",
              ],
            },
            {
              subheading: "Visibilidad para operadores y jugadores",
              bullets: [
                "Los operadores de sede pueden quitar la configuración en la cámara en cualquier momento para detener el envío de snapshots.",
                "Los jugadores en WhoPlays US ven indicadores en vivo de ocupación y espera en sedes habilitadas, no controles de configuración de cámaras.",
              ],
            },
          ],
        },
        {
          heading: "Conservación y eliminación de datos",
          paragraphs: [
            "Sus datos se conservan solo durante el tiempo necesario para fines legales, operativos y analíticos.",
            "Puede eliminar su cuenta o solicitar la eliminación de sus datos desde la aplicación o contactándonos.",
          ],
        },
        {
          heading: "Divulgación legal y seguridad",
          paragraphs: [
            "Podemos divulgar sus datos cuando la ley lo requiera, para proteger nuestros derechos o para garantizar la seguridad de los usuarios.",
            "Utilizamos medios comercialmente aceptables para proteger sus datos, pero no podemos garantizar una seguridad absoluta.",
          ],
        },
        {
          heading: "Privacidad de los menores",
          paragraphs: [
            "No recopilamos conscientemente información de personas menores de 13 años. Si cree que hemos recopilado datos de un menor, contáctenos.",
          ],
        },
        {
          heading: "Cambios en esta Política de Privacidad",
          paragraphs: [
            "Podemos actualizar esta Política de Privacidad. Si se realizan cambios materiales, se le notificará por correo electrónico o mediante un aviso en la aplicación.",
            "Todos los cambios entran en vigor cuando se publican.",
          ],
        },
      ],
      contact: {
        heading: "Contacto",
        paragraphs: ["Si tiene preguntas, comuníquese con nosotros a:"],
        entries: [{ label: "Correo electrónico", value: "support@whoplays.us" }],
      },
    },
    mySchedule: {
      back: "Volver a la app",
      title: "Mi agenda",
      create: "Crear evento",
      edit: "Editar",
      delete: "Eliminar",
      empty: "Sin partidos programados.",
      untitled: "Partido programado",
      startLabel: "Inicio",
      endLabel: "Fin",
      typeLabel: "Tipo",
      courtLabel: "Cancha",
      loading: "Cargando agenda...",
      refreshError: "No se pudo actualizar tu agenda.",
      cancel: "Cancelar",
      confirmCancel: "¿Cancelar este horario?",
    },
    myScheduleEditor: {
      titleCreate: "Programar evento",
      titleEdit: "Editar evento",
      subtitle: "Configura horario, cancha, formato e invitaciones.",
      encourage: "Invita amigos, incluso si no usan la app, para que tu partido sea más divertido.",
      dateLabel: "Fecha",
      startLabel: "Hora de inicio",
      endLabel: "Hora de fin",
      courtLabel: "Cancha",
      formatLabel: "Formato",
      invitesLabel: "Invitaciones",
      invitesCount: "Invitados: {count}",
      inviteCta: "Invitar jugadores ({count})",
      limitToggle: "Habilitar límite de confirmación",
      limitLabel: "Definir límite",
      limitHelper: "Comienza en 4 cuando habilitas el límite.",
      limitLocked: "El límite se habilita después de invitar a 4 jugadores.",
      saveCreate: "Guardar evento",
      saveEdit: "Guardar cambios",
      reset: "Reiniciar",
      validation: {
        dateRequired: "Selecciona una fecha.",
        courtRequired: "Selecciona una cancha.",
        endAfterStart: "La hora de fin debe ser después de la de inicio.",
        conflict: "Ya tienes un partido programado en este horario.",
        saveError: "No se pudo guardar. Intenta de nuevo.",
        saveSuccess: "Evento guardado correctamente.",
      },
      noCourt: "Necesitas una cancha primaria para programar un juego.",
    },
    myScheduleInvite: {
      title: "Enviar invitaciones",
      selected: "Seleccionados {count} invitados",
      back: "Volver a la agenda",
      addGuest: "Agregar invitado",
      playerNamePlaceholder: "Nombre del jugador",
      phonePlaceholder: "Número de teléfono",
      add: "Agregar",
      contactsUnavailable: "Agregar desde contactos (no disponible)",
      contactsAvailable: "Agregar desde contactos",
      myGroup: "Mi grupo",
      following: "Siguiendo",
      searchPlaceholder: "Buscar jugadores",
      noPlayers: "No se encontraron jugadores.",
      validation: {
        nameEmpty: "El nombre no puede estar vacío. Ingresa el nombre del invitado.",
        phoneEmpty: "El número de teléfono no puede estar vacío. Ingresa un teléfono válido.",
        phoneNumeric: "El número de teléfono solo puede contener dígitos.",
        phoneLength: "Número de EE. UU. inválido. Debe tener exactamente 10 dígitos.",
        phoneInvalid: "Número de teléfono inválido.",
        selfNumber: "Has agregado tu propio número. Ya estás incluido como anfitrión.",
        duplicateGuest: "Este jugador ya fue agregado como invitado en este horario.",
        registered: "Este jugador ya está registrado en WhoPlays. Agregamos su cuenta existente: {name}.",
      },
    },
  },
  pt: {
    languageName: "Português",
    nav: {
      features: "Recursos",
      workflow: "Como funciona",
      courtIntel: "Court Intelligence",
      foundingProgram: "Sedes fundadoras",
      plans: "Planos",
      organizers: "Organizadores fundadores",
      login: "Entrar",
      signup: "Criar conta",
      authChooser: {
        loginTitle: "Entre no WhoPlays",
        loginDescription: "Escolha o caminho que combina com a forma como voce usa o WhoPlays.",
        signupTitle: "Crie sua conta WhoPlays",
        signupDescription: "Escolha o caminho que combina com a forma como voce pretende usar o WhoPlays.",
        playerLabel: "Jogador",
        playerDescription: "Organize jogos, registre partidas e veja a visibilidade ao vivo das quadras.",
        operatorLabel: "Dono ou operador de sede",
        operatorDescription: "Continue para o onboarding do Court Intelligence da sua sede.",
        foundingLabel: "Founding Venue Program",
        foundingDescription: "Vai se candidatar a uma das vagas limitadas de lancamento? Comece aqui.",
        foundingCta: "Ver sedes fundadoras",
        closeLabel: "Fechar",
      },
    },
    auth: {
      otp: {
        loginTitle: "Entre no WhoPlays",
        signupTitle: "Crie sua conta no WhoPlays",
        loginDescription: "Use seu e-mail ou número de celular para receber um código de acesso de uso único.",
        signupDescription: "Crie sua conta com e-mail ou número de celular. Enviaremos um código de acesso de uso único para confirmar.",
        displayNameLabel: "Nome de exibição",
        emailTab: "E-mail",
        phoneTab: "Telefone",
        emailAddressLabel: "Endereço de e-mail",
        mobileNumberLabel: "Número de celular",
        countryCodeLabel: "Código do país",
        smsHint: "Inclua seu DDD. Enviaremos um código de 6 dígitos por SMS.",
        captchaHint: "Protegido pelo reCAPTCHA. A verificação é executada automaticamente.",
        errorCaptcha: "Não foi possível verificar que você é uma pessoa. Tente novamente.",
        verificationCodeLabel: "Código de verificação",
        resendIn: "Reenviar em {seconds}s",
        resendCode: "Reenviar código",
        errorPhoneRegionOnly: "O login por telefone é apenas para EUA/Canadá. Use e-mail para continuar.",
        errorSendCode: "Não foi possível enviar o código.",
        errorStartVerification: "Não foi possível iniciar a verificação.",
        infoCodeSent: "Código de verificação enviado. Digite o código de 6 dígitos para continuar.",
        infoCodeResent: "Código reenviado. Verifique seu e-mail ou telefone.",
        errorResendCode: "Não foi possível reenviar o código.",
        errorVerifyCode: "Não foi possível verificar o código.",
        submitWait: "Aguarde...",
        submitSendCode: "Enviar código",
        submitVerifyContinue: "Verificar e continuar",
        socialDivider: "Ou continuar com",
        continueWithGoogle: "Continuar com Google",
        continueWithApple: "Continuar com Apple",
        loginNeedAccount: "Precisa de uma conta?",
        loginNeedAccountCta: "Criar conta",
        signupHasAccount: "Já tem uma conta?",
        signupHasAccountCta: "Entrar",
        userNotFound: "Não encontramos uma conta com esse e-mail ou número de telefone.",
      },
    },
    toggles: {
      theme: "Tema",
      language: "Idioma",
      light: "Claro",
      dark: "Escuro",
      system: "Sistema",
    },
    hero: {
      eyebrow: "Jogadores, donos de sedes e comunidades conectados",
      title: "A plataforma de pickleball que mantém suas quadras ativas",
      description:
        "O WhoPlays ajuda jogadores a organizar jogos e ajuda sedes a operar melhor com sinais ao vivo de lotação, tempo de espera e quadras disponíveis com o Court Intelligence.",
      primaryCta: "Começar na web",
      primaryHref: "/auth/signup",
      secondaryCta: "Obter o app móvel",
      secondaryHref: "/app-stores",
      badgesLabel: "Também disponível no iOS e Android",
      trust: "Confiado por jogadores, clubes e programas municipais.",
    },
    stats: [
      { value: "Feed de quadras", label: "Veja quando as quadras de pickleball estão lotadas em tempo real" },
      { value: "Convites SMS", label: "Preencha sessões de pickleball com convites por texto" },
      { value: "Estatísticas", label: "Registre partidas de pickleball e acompanhe seu rating DUPR" },
    ],
    appShell: {
      tabs: {
        realtime: "Ao vivo",
        network: "Minha rede",
        competitions: "Competições",
        matches: "Partidas",
        rank: "Ranking",
        messages: "MSG Center",
      },
      matches: {
        title: "Partidas",
        pending: "Pendentes",
        addMatch: "Adicionar partida",
        loadMore: "Carregar mais",
        loading: "Carregando partidas...",
        empty: "Nenhuma partida para mostrar.",
        segments: {
          owned: "Minhas",
          mygroup: "Meu grupo",
          mycourts: "Minhas sedes",
        },
        errors: {
          loadFailed: "Não foi possível carregar as partidas agora.",
          saveFailed: "Não foi possível salvar a partida.",
        },
        editor: {
          title: "Nova partida",
          subtitle: "Adicione placares e jogadores.",
          cancel: "Cancelar",
          save: "Salvar",
          saving: "Salvando...",
          format: "Formato",
          singles: "Simples",
          doubles: "Duplas",
          winner: "Vencedor",
          selectWinner: "Selecione o vencedor",
          team1: "Equipe 1",
          team2: "Equipe 2",
          player: "Usuário",
          score: "Placar",
        },
        validation: {
          missingPlayers: "Adicione todos os jogadores necessários.",
          missingWinner: "Selecione um vencedor.",
        },
      },
      rank: {
        title: "Ranking",
        loading: "Carregando rankings...",
        empty: "Sem dados de ranking para mostrar.",
        segments: {
          wprank: "WP Rank",
          overall: "Geral",
          h2h: "H2H",
        },
        filters: {
          skillAll: "Todos os níveis",
          singles: "Simples",
          doubles: "Duplas",
          week: "Semana",
          month: "Mês",
          all: "Todo período",
          allCourts: "Todas as quadras",
          groupNone: "Sem filtro de grupo",
          groupMyGroup: "Meu grupo",
          groupFollowing: "Seguindo",
          formatAll: "Todos os formatos",
          today: "Hoje",
          yesterday: "Ontem",
          lastWeek: "Semana passada",
          lastMonth: "Mês passado",
          threeMonths: "3 meses",
          sixMonths: "6 meses",
          year: "1 ano",
          fiveYears: "5 anos",
          allTime: "Todo período",
        },
        labels: {
          wins: "Vitórias",
          losses: "Derrotas",
          matches: "Partidas",
          winPct: "Win%",
          rankLabel: "Ranking",
        },
      },
      messagesTab: {
        title: "Central de mensagens",
        empty: "Nenhuma mensagem ainda.",
        clearAll: "Limpar tudo",
        markAllRead: "Marcar tudo como lido",
        delete: "Excluir",
        read: "Marcar lido",
        unread: "Marcar não lido",
        receivedAt: "Recebido",
      },
      socket: {
        schedule: "Agenda atualizada",
        match: "Atualização de partida",
        ping: "Novo ping recebido",
        dupr: "DUPR atualizado",
        generic: "Nova notificação",
        subscriptionTitle: "Assinatura atualizada",
        subscriptionBody: "Seu status de assinatura foi atualizado.",
        matchUnanimousTitle: "Partida aceita",
        matchUnanimousBody: "Boa notícia! Uma partida que você jogou foi aceita por todos. Agora é oficial!",
        matchAcceptedTitle: "Jogador aceitou a partida",
        matchAcceptedBody: "{player} aceitou a partida. Aguardando os demais.",
        matchPendingTitle: "Partidas pendentes",
        matchPendingBody: "Novos resultados pendentes! Confirme para contar no seu ranking.",
        duprUpdatedTitle: "DUPR atualizado",
        duprUpdatedBody: "Simples: {singles}\nDuplas: {doubles}",
        questionTitle: "Nova pergunta de agenda",
      },
      subscriptions: {
        title: "Assinaturas",
        subtitle: "Escolha um plano para destravar o agendamento no WhoPlays.",
        detailsLink: "Ver detalhes completos do plano",
        currentPlan: "Plano atual",
        statusLabel: "Status",
        free: "Grátis",
        active: "Ativo",
        select: "Selecionar",
        checkout: "Pagar com Stripe",
        close: "Fechar",
        manage: "Gerenciar / cancelar no Stripe",
        manageHint: "Abriremos o portal do Stripe. Se não vir sua assinatura, use o e-mail do recibo.",
        providerManaged: "Gerenciado por {provider}",
        providerManagedHint: "Para gerenciar ou cancelar esta assinatura, acesse {provider}.",
        error: "Não foi possível iniciar o checkout. Tente novamente.",
        successTitle: "Checkout concluído",
        successToast: "Checkout de assinatura concluído. Atualizando seu acesso…",
        cancelTitle: "Checkout cancelado",
        cancelToast: "Você cancelou o checkout no Stripe.",
        plans: {
          "com.whoplays.scheduling.basic.monthly": {
            label: "Scheduler Básico (Mensal)",
            price: "Cobrança mensal",
          },
          "com.whoplays.scheduling.basic.yearly": {
            label: "Scheduler Básico (Anual)",
            price: "Cobrança anual",
          },
          "com.whoplays.scheduling.pro.monthly": {
            label: "Scheduler Pro (Mensal)",
            price: "Cobrança mensal",
          },
          "com.whoplays.scheduling.pro.yearly": {
            label: "Scheduler Pro (Anual)",
            price: "Cobrança anual",
          },
        },
      },
      courtIntelligenceResume: {
        title: "Concluir configuração do Court Intelligence?",
        description:
          "Encontramos progresso salvo no onboarding do Court Intelligence. Escolha como deseja continuar.",
        savedStep: "Etapa salva: {step}",
        stepNames: {
          account: "Conta",
          subscription: "Assinatura",
          venues: "Locais",
        },
        continuePlayer: "Continuar como jogador",
        continuePlayerHint: "Mantenha seu progresso atual do Court Intelligence e fique no modo jogador.",
        continuePlayerReset: "Continuar como jogador e reiniciar configuração",
        continuePlayerResetHint:
          "Excluir o progresso salvo do Court Intelligence. Na próxima vez, começará no passo 1.",
        continueOnboarding: "Continuar onboarding do Court Intelligence",
        continueOnboardingHint: "Vá para a etapa salva e conclua a configuração.",
        resetError: "Não foi possível reiniciar a configuração do Court Intelligence agora. Tente novamente.",
      },
      courtIntelligenceUi: {
        sections: {
          profile: "Perfil",
          community: "Comunidade",
          venues: "Locais",
          cameras: "Câmeras",
          initialization: "Inicialização",
          logs: "Logs",
          debug: "Diagnóstico",
          stats: "Estatísticas",
          security: "Segurança",
          subscription: "Assinatura",
        },
        workspace: {
          loading: "Carregando Court Intelligence…",
          tipsTitle: "Dicas do Court Intelligence",
          tipsProgress: "Dica {current} de {total}",
          tipsBack: "Voltar",
          tipsNext: "Próxima",
          tipsClose: "Fechar",
          openMenu: "Abrir menu do Court Intelligence",
          closeMenu: "Fechar navegação",
          switchToPlayerMode: "Trocar para modo jogador",
          brand: {
            alt: "Court Intelligence",
            eyebrow: "Court",
            title: "Intelligence",
          },
          accessRequired: {
            title: "Acesso ao Court Intelligence necessário",
            description: "Sua conta ainda não possui acesso por assinatura Court Intelligence nem permissões por local.",
            startSetup: "Iniciar configuração",
            backToPlayerMode: "Voltar para modo jogador",
          },
          readOnly: {
            venuesOwnerOnlyTitle: "Gestão de locais é somente para donos",
            venuesOwnerOnlyDescription:
              "Somente donos da assinatura podem reivindicar ou criar locais no modo Court Intelligence.",
            camerasRestrictedTitle: "Câmeras restritas",
            camerasRestrictedDescription:
              "Somente donos de quadra e administradores de quadra podem gerenciar a configuração de câmeras.",
            statsSubscriptionRequiredTitle: "Estatísticas exigem assinatura ativa de Court Intelligence",
            statsSubscriptionRequiredDescription:
              "Reative a assinatura Court Intelligence para ver estatísticas ao vivo e análises históricas.",
            securityOwnerOnlyTitle: "Segurança é somente para donos",
            securityOwnerOnlyDescription:
              "Somente donos de quadra podem conceder ou revogar permissões de court_admin/monitor.",
            subscriptionOwnerOnlyTitle: "Assinatura é somente para donos",
            subscriptionOwnerOnlyDescription:
              "Somente donos de quadra podem alterar ou cancelar a assinatura do Court Intelligence.",
          },
          community: {
            title: "Comunidade",
            description:
              "Ferramentas de comunicação focadas em monitor (alertas, postagens e avisos para jogadores) ficarão disponíveis aqui.",
          },
          logs: {
            title: "Logs operacionais",
            description:
              "Revise eventos de ML, falhas de processamento e auditoria por local. Os controles de exclusão de logs também ficarão aqui.",
            labels: {
              court: "Quadra",
              notSelected: "Nenhum local selecionado",
              notAvailable: "N/D",
              scope: "Escopo",
              pageOffset: "Offset",
            },
            controls: {
              searchPlaceholder: "Buscar por nome do local",
              cityPlaceholder: "Cidade",
              statePlaceholder: "Estado",
              ownerPlaceholder: "Username do owner",
              onlyWithCi: "Somente locais com Court Intelligence",
              searchButton: "Buscar locais",
              venueLabel: "Local",
              pageSizeLabel: "Tamanho da página",
              refreshButton: "Atualizar logs",
              deleteSelectedButton: "Excluir selecionados",
              deleteAllButton: "Excluir todos",
              prevPageButton: "Anterior",
              nextPageButton: "Próxima",
            },
            loading: {
              courts: "Carregando locais...",
              logs: "Carregando logs...",
            },
            errors: {
              loadCourts: "Não foi possível carregar a lista de locais.",
              loadLogs: "Não foi possível carregar os logs.",
              deleteLogs: "Não foi possível excluir os logs.",
            },
            notices: {
              deletedCount: "{count} logs excluídos.",
            },
            confirmDeleteAll: "Excluir todos os logs deste local?",
            table: {
              columns: {
                event: "Evento",
                detail: "Detalhe",
                createdAt: "Criado",
                deliveredAt: "Entregue",
              },
              empty: "Nenhum log encontrado para o local selecionado.",
            },
          },
          debug: {
            title: "Diagnóstico",
            description:
              "Ferramentas somente para admin para troubleshooting profundo (payloads de ML, artefatos de debug e rastros de processamento).",
          },
          stats: {
            title: "Análises do Court Intelligence",
            description:
              "Use o status realtime do portfólio e as leituras históricas para entender a atividade do local e a ocupação das quadras mapeadas.",
            labels: {
              court: "Quadra",
              notAvailable: "N/D",
            },
            controls: {
              searchPlaceholder: "Buscar por nome do local",
              cityPlaceholder: "Cidade",
              statePlaceholder: "Estado",
              ownerPlaceholder: "Username do owner",
              onlyWithCi: "Somente locais com Court Intelligence",
              searchButton: "Buscar locais",
              courtsLabel: "Locais no escopo",
              fromLabel: "De",
              toLabel: "Até",
              bucketLabel: "Agrupamento",
              bucketHour: "Hora",
              bucketDay: "Dia",
              timezoneLabel: "Fuso horário",
              refreshRealtimeButton: "Atualizar overview",
              refreshHistoryButton: "Aplicar histórico",
              downloadReadingsCsvButton: "Exportar CSV de leituras",
              downloadOccupancyCsvButton: "Exportar CSV de ocupação",
            },
            loading: {
              courts: "Carregando locais...",
              realtime: "Atualizando overview...",
              analytics: "Carregando histórico...",
              occupancy: "Carregando ocupação por quadra...",
            },
            errors: {
              loadCourts: "Não foi possível carregar a lista de locais.",
              loadRealtime: "Não foi possível carregar o portfólio realtime.",
              loadAnalytics: "Não foi possível carregar o histórico de leituras.",
              loadOccupancy: "Não foi possível carregar o histórico de ocupação por quadra.",
            },
            cards: {
              peopleRightNow: "Pessoas agora",
              inUseOpen: "Em uso / jogáveis",
              waitingPlayers: "Jogadores aguardando",
              avgQueueEta: "ETA médio de espera",
              avgBusyness: "Ocupação média",
              maxBusyness: "Pico de ocupação",
            },
            helpers: {
              courtAvailabilitySupport: "{supported} de {total} locais suportam disponibilidade de quadras",
              waitMetricsSupport: "{supported} de {total} locais suportam métricas de espera",
              scoredVenuesSupport: "{supported} de {total} locais possuem pontuação",
            },
            tables: {
              statusTitle: "Status por local",
              timelineTitle: "Tendência de leituras",
              readingsSummary: "{count} leituras no intervalo selecionado",
              venueColumn: "Local",
              busynessColumn: "Ocupação",
              peopleColumn: "Pessoas",
              waitingColumn: "Espera",
              queueColumn: "ETA da fila",
              timelineBucketColumn: "Início do bucket",
              timelineReadingsColumn: "Leituras",
              timelineAvgPeopleColumn: "Média por leitura",
              timelineOccupancyColumn: "Ocupação de atividade",
              emptyRealtime: "Nenhum local disponível para este escopo.",
              emptyTimeline: "Sem histórico de leituras para o intervalo selecionado.",
              occupancyTitle: "Histórico de ocupação por quadra",
              occupancySubtitle: "O histórico por quadra só está disponível para locais mapeados.",
              occupancyExplainer:
                "Cada célula mostra a porcentagem de leituras naquele bloco de tempo em que a quadra apareceu ocupada.",
              occupancyLegendOpen: "Mais livre",
              occupancyLegendModerate: "Moderado",
              occupancyLegendBusy: "Mais ocupada",
              timelineAvailableSummary: "{available} de {total} locais têm histórico por quadra",
              emptyOccupancy: "Sem histórico de ocupação por quadra para este escopo.",
              occupancyCourtColumn: "Quadra",
              occupancyRowSummary: "{percent} ocupada",
              singlesDoublesSummary: "S: {singles} · D: {doubles}",
              avgPlayersWhenOccupiedShort: "Méd. ocupada",
              maxPlayersWhenOccupiedShort: "Máx. ocupada",
              singlesShareShort: "Singles",
              doublesShareShort: "Duplas",
              singlesShareCompact: "S:",
              doublesShareCompact: "D:",
              readingsShort: "Leituras",
              occupiedReadingsShort: "Ocupadas",
              occupancyMappingRequired: "O mapeamento de quadras é necessário para mostrar o histórico de ocupação por quadra.",
              occupancyUnavailable: "O histórico de ocupação por quadra não está disponível para este local.",
            },
            badges: {
              courtAvailabilityEnabled: "Disponibilidade de quadras habilitada",
              waitMetricsEnabled: "Métricas de espera habilitadas",
            },
          },
          liveBusyness: {
            title: "Ocupação ao vivo",
            refresh: "Atualizar",
            refreshing: "Atualizando…",
            loading: "Carregando ocupação ao vivo…",
            noData: "Sem dados de ocupação ao vivo no momento.",
            loadError: "Não foi possível carregar a ocupação ao vivo.",
            courtLabel: "Quadra",
            previousVenue: "Local anterior",
            nextVenue: "Próximo local",
            scoreLabel: "Ocupação",
            scoreSecondary: "Pontuação {score}",
            occupancyLabel: "Em uso / jogáveis",
            occupancyLabelCompact: "Em uso",
            occupancyValueFormat: "{inUse} de {playable}",
            waitingLabel: "Aguardando",
            etaLabel: "ETA fila",
            peopleLabel: "Pessoas",
            confidenceLabel: "Confiança",
            confidenceInline: "Confiança {value}",
            modeInfoLabel: "Detalhes do modo de medição",
            estimateBadge: "Estimativa",
            mappedCourtsLabel: "Quadras mapeadas",
            queueAreasLabel: "Zonas de fila",
            detailsEyebrow: "Detalhes da medição ao vivo",
            closeDetails: "Fechar",
            formulaTitle: "Como este local é medido",
            visibleKpisTitle: "KPIs visíveis",
            hiddenKpisTitle: "KPIs ocultos",
            setupQualityTitle: "Qualidade da configuração",
            defaultsTitle: "Padrões",
            playersPerGameDefault: "Jogadores por partida: {value}",
            avgGameMinutesDefault: "Minutos médios por partida: {value}",
            queueTargetMinutesDefault: "Minutos alvo de fila: {value}",
            scoreMetric: "Pontuação de ocupação",
            levelMetric: "Faixa de ocupação",
            weatherMetric: "Clima",
            updatedMetric: "Hora da atualização",
            perCourtListMetric: "Lista por quadra",
            noneLabel: "Nenhum",
            modeCrowdOnly: "Somente movimento",
            modeCapacityEstimated: "Capacidade estimada",
            modeCourtMapped: "Quadras mapeadas",
            modeCourtAndQueue: "Quadras + fila",
            modeCrowdOnlyHelp: "Conta pessoas na imagem para atividade aproximada da sede.",
            modeCapacityEstimatedHelp: "Compara a atividade visível com a capacidade configurada de quadras para uma estimativa rápida.",
            modeCourtMappedHelp: "As quadras visíveis estão mapeadas, então a disponibilidade de quadras é suportada.",
            modeCourtAndQueueHelp: "Quadras e zonas de fila estão mapeadas, então métricas de espera são suportadas.",
            formulaCrowdOnly: "Sem pontuação. Usa apenas a contagem de pessoas observada pelo sinal CI.",
            formulaCapacityEstimated: "score = clamp(totalPeople / (configuredCourts * playersPerGame)) * 100",
            formulaCourtMapped: "score = ((0.55 * courtSaturation) + (0.10 * backgroundPressure)) / 0.65 * 100",
            formulaCourtAndQueue: "score = ((0.55 * courtSaturation) + (0.35 * queuePressure) + (0.10 * backgroundPressure)) * 100",
            updatedAt: "Atualizado: {time}",
            notAvailable: "N/D",
          },
          security: {
            title: "Segurança e permissões",
            description: "Donos de quadra podem conceder acesso por local para outros usuários como court_admin ou monitor.",
            profileReadyInstruction:
              "Antes de conceder acesso, peça ao usuário para entrar no WhoPlays em modo Player e atualizar o perfil (nome, nickname e email ou telefone).",
            limitNotice: "Limite por local: até {adminLimit} court_admin e {monitorLimit} monitor.",
            venueLabel: "Local",
            roleLabel: "Função",
            addRoleButton: "Adicionar função",
            addRoleModalTitle: "Adicionar função no local",
            addRoleModalSubtitle: "Selecione função e usuário para este local.",
            searchLabel: "Buscar usuário",
            searchPlaceholder: "Nome, nickname ou ID do usuário",
            selectUserLabel: "Usuário",
            selectUserPlaceholder: "Selecione um usuário",
            noUsersFound: "Nenhum usuário encontrado para esta busca.",
            closeModal: "Fechar",
            cancelButton: "Cancelar",
            noVenuesAvailable: "Nenhum local disponível",
            adminsCountLabel: "Court admin",
            monitorsCountLabel: "Monitor",
            roleOwner: "Court owner",
            roleAdmin: "Court admin",
            roleMonitor: "Monitor",
            assignRole: "Atribuir função",
            updateRole: "Atualizar função",
            removeRole: "Remover função",
            saving: "Salvando…",
            loadingAssignments: "Carregando atribuições...",
            loadingUsers: "Buscando usuários...",
            currentAssignmentsTitle: "Atribuições atuais",
            noAssignments: "Nenhuma atribuição ainda.",
            assignmentInfoFallback: "Detalhes do perfil pendentes.",
            ownerRoleReadOnly: "court_owner não pode ser alterado aqui.",
            userAlreadyHasRole: "{username} já possui a função {role}.",
            limitReachedAdmin: "Limite de court_admin atingido (2). Remova um court_admin para continuar.",
            limitReachedMonitor: "Limite de monitor atingido (3). Remova um monitor para continuar.",
            errors: {
              loadRoles: "Não foi possível carregar funções atribuídas.",
              searchUsers: "Não foi possível buscar usuários.",
              assignRole: "Não foi possível atribuir a função.",
              removeRole: "Não foi possível remover a função.",
            },
            notices: {
              roleGranted: "Função {role} concedida para {username}.",
              roleUpdated: "Função {role} atualizada para {username}.",
              roleRemoved: "Função removida para {username}.",
            },
          },
          subscription: {
            title: "Assinatura",
            description: "Somente donos de quadra podem modificar ou cancelar a cobrança do Court Intelligence.",
            privacyTitle: "Privacidade e visibilidade para jogadores",
            privacyItems: [
              "As câmeras enviam snapshots para a API WhoPlays. Não nos conectamos à sua rede local.",
              "Por padrão os snapshots são processados e não armazenados, exceto retenção opcional de debug controlada pelos grupos de câmera.",
              "Jogadores no WhoPlays US recebem sinais gratuitos de ocupação e espera quando seu local habilita Court Intelligence.",
              "Compra de hardware e instalação em campo não estão incluídas nas assinaturas Court Intelligence.",
            ],
            privacyLinkLabel: "Ler Política de Privacidade completa",
            currentPlanLabel: "Plano atual",
            currentPlanBadge: "Atual",
            currentSkuLabel: "SKU da assinatura atual",
            noneLabel: "Nenhum",
            statusLabel: "Status",
            notAvailableLabel: "N/D",
            expiresLabel: "Expira",
            usageTitle: "Uso de capacidade",
            usedVenuesLabel: "Locais usados",
            maxVenuesLabel: "Limite de locais",
            usedCamerasLabel: "Câmeras usadas",
            maxCamerasLabel: "Limite de câmeras",
            remainingVenuesLabel: "Locais restantes",
            remainingCamerasLabel: "Câmeras restantes",
            unlimitedLabel: "Ilimitado",
            addOnCountsTitle: "Quantidades de add-ons",
            includedBaseLabel: "Incluído no base: {venues} local e {cameras} câmeras",
            activeVenueAddonsLabel: "Add-ons extras de local ativos",
            activeCameraAddonsLabel: "Add-ons extras de câmera ativos",
            safeRemoveVenueLabel: "Add-ons de local removíveis agora",
            safeRemoveCameraLabel: "Add-ons de câmera removíveis agora",
            refreshUsageButton: "Atualizar uso",
            refreshingUsageLabel: "Atualizando…",
            plans: {
              baseMonthlyName: "Court Intelligence Base (Mensal)",
              baseMonthlyDetails: "Cobrança mensal. Cancele quando quiser.",
              baseYearlyName: "Court Intelligence Base (Anual)",
              baseYearlyDetails: "Cobrança anual. Melhor valor para operações estáveis.",
            },
            baseFeatures: {
              venuesIncluded: "{count} local incluído",
              camerasIncluded: "{count} câmeras incluídas",
              liveOps: "Ocupação ao vivo, estimativas de espera, estatísticas, logs e acesso por papéis",
            },
            planChangeTitle: "Trocar plano",
            planChangeDescription: "Troque a periodicidade de cobrança do seu plano base Court Intelligence.",
            switchToPlanButton: "Trocar para {plan}",
            alreadyCurrentPlan: "Plano atual selecionado",
            addOnTitle: "Catálogo de add-ons",
            addOnDescription: "Add-ons ficam disponíveis após ativar o plano base e são gerenciados nesta seção.",
            addOnManageTitle: "Adicionar capacidade de locais e câmeras",
            addOnManageDescription: "Use add-ons para ampliar limites sem sair do app.",
            billingMonthly: "Mensal",
            billingYearly: "Anual",
            addOnLockedCycleNotice: "Add-ons cobrados no ciclo {cycle} para combinar com o plano base",
            addOnBaseRequired: "Ative um plano base Court Intelligence antes de gerenciar add-ons.",
            cameraAddOnTitle: "Add-on de câmera",
            venueAddOnTitle: "Add-on de local",
            addCameraCapacityButton: "Adicionar capacidade de câmera",
            removeCameraCapacityButton: "Reduzir capacidade de câmera",
            addVenueCapacityButton: "Adicionar capacidade de local",
            removeVenueCapacityButton: "Reduzir capacidade de local",
            addOnModalTitle: "Confirmar alteração de add-on",
            addOnModalSubtitle: "Aplique várias unidades em uma única transação.",
            addOnTypeLabel: "Tipo de add-on",
            addOnTypeCamera: "Capacidade de câmera",
            addOnTypeVenue: "Capacidade de local",
            addOnActionLabel: "Ação",
            addOnActionAdd: "Adicionar capacidade",
            addOnActionRemove: "Remover capacidade",
            addOnQuantityLabel: "Quantidade",
            addOnUnitPriceLabel: "Preço unitário",
            addOnTotalLabel: "Total estimado",
            addOnImpactLabel: "Impacto na capacidade:",
            addOnImpactVenues: "Locais {delta}",
            addOnImpactCameras: "Câmeras {delta}",
            addOnCancelButton: "Cancelar",
            addOnConfirmButton: "Confirmar alteração",
            addOnConfirmingButton: "Aplicando…",
            billingTimingTitle: "Momento de cobrança",
            billingTimingItems: [
              "A capacidade é atualizada imediatamente após a confirmação.",
              "Cobranças e créditos são refletidos no próximo período de fatura.",
            ],
            startingCheckout: "Iniciando checkout…",
            checkoutButton: "Checkout com Stripe",
            openBillingPortalButton: "Abrir portal de cobrança",
            errors: {
              noCheckoutUrl: "Nenhuma URL de checkout retornada.",
              startCheckout: "Não foi possível iniciar o checkout.",
              noPortalUrl: "Nenhuma URL do portal de cobrança retornada.",
              openPortal: "Não foi possível abrir o portal de cobrança.",
              manageAddon: "Não foi possível atualizar a capacidade do add-on.",
              noCameraAddonsToRemove: "Não há add-ons de câmera removíveis. Remova câmeras primeiro se o uso estiver no limite.",
              noVenueAddonsToRemove: "Não há add-ons de local removíveis. Remova locais ou câmeras primeiro se o uso estiver no limite.",
            },
            notices: {
              updatedSuccess: "Assinatura atualizada com sucesso.",
              checkoutCanceled: "Checkout cancelado.",
              cameraAddonIncreased: "Adicionados {count} add-ons de câmera. Limite de câmeras: {before} -> {after}.",
              cameraAddonDecreased: "Removidos {count} add-ons de câmera. Limite de câmeras: {before} -> {after}.",
              venueAddonIncreased:
                "Adicionados {count} add-ons de local. Limite de locais: {before} -> {after}. Limite de câmeras: {cameraBefore} -> {cameraAfter}.",
              venueAddonDecreased:
                "Removidos {count} add-ons de local. Limite de locais: {before} -> {after}. Limite de câmeras: {cameraBefore} -> {cameraAfter}.",
            },
          },
          profile: {
            title: "Perfil",
            description: "Atualize os dados da conta usados para operações do local.",
            photo: {
              title: "Foto de perfil (opcional)",
              alt: "Avatar de perfil",
              upload: "Enviar foto",
              uploading: "Enviando…",
            },
            fields: {
              name: "Nome",
              email: "Email",
              phoneNumber: "Telefone",
              countryCode: "Código",
              country: "País",
              selectCountry: "Selecionar país",
              city: "Cidade",
              state: "Estado",
              zipcode: "CEP",
            },
            helper: {
              emailLocked: "Bloqueado porque este email é seu identificador de login.",
              emailOptionalWhenPhoneLogin: "Opcional quando sua conta faz login com telefone.",
              phoneLocked: "Bloqueado porque este telefone é seu identificador de login.",
              phoneHint: "Digite o número local, ou cole o número completo com +código do país.",
            },
            save: "Salvar perfil",
            saving: "Salvando…",
            notices: {
              profileSaved: "Perfil salvo.",
            },
            errors: {
              uploadAvatar: "Não foi possível enviar o avatar.",
              uploadAvatarNoFilename: "O upload do avatar não retornou nome do arquivo.",
              nameRequired: "Nome é obrigatório.",
              saveProfile: "Não foi possível salvar o perfil.",
            },
          },
          tipsItems: [
            {
              title: "Cobertura de câmera",
              body: "Use 1 câmera para até 2 quadras para melhor precisão de ocupação.",
            },
            {
              title: "Posicionamento da câmera",
              body: "Instale as câmeras em posição alta e fixa para reduzir oclusões e leituras falsas.",
            },
            {
              title: "Ordem de configuração das zonas",
              body: "Comece com cobertura total e depois adicione polígonos de quadra, espera e banco.",
            },
            {
              title: "Sinal de fila",
              body: "Use queueSignal apenas em zonas reais de espera, não em áreas de trânsito.",
            },
            {
              title: "Precisão da contagem",
              body: "A contagem de pessoas é uma estimativa operacional, não uma contagem exata.",
            },
            {
              title: "Modelo de tempo de espera",
              body: "A espera é estimada por jogadores aguardando, quadras jogáveis, duração do jogo e jogadores por jogo.",
            },
            {
              title: "Condições de menor confiança",
              body: "As estimativas ficam menos precisas com cobertura parcial, ausência de zonas de espera ou snapshots atrasados.",
            },
            {
              title: "Cadência recomendada",
              body: "Cadência recomendada: API a cada 30s e FTPS a cada 60s.",
            },
            {
              title: "Higiene de credenciais",
              body: "Rotacione tokens de câmera ou senhas FTP após instalação ou mudanças de equipe.",
            },
            {
              title: "Escopo do serviço",
              body: "Compra de hardware e instalação de câmeras não estão incluídas nos planos Court Intelligence.",
            },
          ],
          roleLabels: {
            owner: "Dono da quadra",
            admin: "Admin da quadra",
            monitor: "Monitor",
            systemAdmin: "Admin",
            operator: "Operador",
          },
        },
        venues: {
          capacityTitle: "Capacidade da assinatura",
          capacityDescription:
            "Limites atuais de locais e câmeras na sua assinatura do Court Intelligence.",
          myOwnedVenuesMetric: "Meus locais próprios",
          venueLimitMetric: "Limite de locais",
          cameraLimitMetric: "Limite de câmeras",
          refreshingOwnedVenues: "Atualizando locais próprios…",
          findTitle: "Encontrar um local para reivindicar",
          createNewVenue: "Criar novo local",
          addVenueCapacity: "Adicionar capacidade de locais",
          searchPlaceholder: "Buscar por nome ou endereço do local",
          search: "Buscar",
          useLocation: "Usar localização",
          locating: "Localizando…",
          showingResults: "Mostrando resultados para: {query}",
          loadingVenues: "Carregando locais…",
          noVenuesFound: "Nenhum local encontrado para esta busca.",
          loadMore: "Carregar mais",
          claimOwnership: "Reivindicar propriedade",
          claiming: "Reivindicando…",
          owned: "Próprio",
          viewDetails: "Ver detalhes",
          hideDetails: "Ocultar detalhes",
          myOwnedVenuesTitle: "Meus locais próprios",
          noOwnedVenues: "Ainda não há locais reivindicados.",
          updateDetails: "Atualizar detalhes",
          continue: "Continuar",
          unlimited: "Ilimitado",
          venueFallback: "Local {id}",
          contactSupport: "Contato {email}",
          supportEmailSubject: "Suporte de propriedade Court Intelligence",
          supportEmailBody:
            "Olá suporte WhoPlays,\n\nPreciso de ajuda para reivindicar a propriedade de um local no Court Intelligence.\nUsuário: {username}\n",
          unknownUser: "desconhecido",
          loadingVenueDetails: "Carregando detalhes do local…",
          venueDetailsUnavailable: "Os detalhes do local não estão disponíveis.",
          detailsOverview: "Visão geral",
          typeNotSet: "Tipo não definido",
          courtsCount: "{count} quadras",
          courtCountNotSet: "Quantidade de quadras não definida",
          openPlaySessionsToday: "{count} sessões de open-play hoje",
          detailsContactLinks: "Contato e links",
          website: "Website",
          map: "Mapa",
          amenities: "Comodidades",
          moreAmenities: "+{count} mais",
          hours: "Horários",
          closed: "Fechado",
          previousPhoto: "Foto anterior",
          nextPhoto: "Próxima foto",
          errors: {
            refreshOwnedVenues: "Não foi possível atualizar seus locais próprios.",
            ownerOnlyClaim: "Somente o dono da assinatura pode reivindicar ou criar locais.",
            venueLimitReached: "Limite de locais atingido. Adicione capacidade extra em Assinatura para continuar.",
            claimOwnership: "Não foi possível reivindicar a propriedade deste local.",
            searchInputRequired: "Digite nome/endereço do local ou use sua localização.",
            loadVenues: "Não foi possível carregar os locais.",
            locationNotSupported: "Localização não é suportada neste navegador.",
            locationPermission: "Não foi possível obter localização. Habilite o acesso à localização.",
          },
          notices: {
            venueUpdated: "Perfil do local atualizado para {name}.",
            ownershipClaimed: "Propriedade reivindicada para {name}.",
          },
        },
        cameras: {
          loadingCameraVenues: "Carregando locais de câmeras…",
          noVenuesAvailable:
            "Ainda não há locais disponíveis para configuração de câmeras. Reivindique um local primeiro.",
          venueLabel: "Local",
          metricOwnedVenues: "Locais próprios",
          metricVenueLimit: "Limite de locais",
          metricCamerasUsedLimit: "Câmeras usadas/limite",
          oneTimeApiCredentials: "Credenciais de API de uso único",
          oneTimeFtpCredentials: "Credenciais FTPS de uso único",
          dismiss: "Dispensar",
          saveApiCredentials: "Salve este token agora. Ele é exibido apenas uma vez.",
          saveFtpCredentials:
            "Salve estas credenciais agora. A senha FTPS é exibida apenas uma vez.",
          modePrefix: "Modo: {mode}",
          modeApi: "Envio por API",
          modeFtps: "Upload FTPS",
          modeApiShort: "API",
          modeFtpsShort: "FTPS",
          camerasTitle: "Câmeras",
          camerasSubtitle: "O modo API captura a cada 30s. O modo FTPS recomenda 60s.",
          setupGuidanceTitle: "Guia de configuração",
          setupGuidanceSubtitle:
            "Escolha o resultado de configuração desejado e use a orientação abaixo para chegar lá.",
          expandQuickGuide: "Expandir guia rápido",
          collapseQuickGuide: "Recolher guia rápido",
          setupGuidanceCollapsedSummary:
            "Use o guia rápido para um resumo compacto ou abra o guia completo para fluxo, resultados e cálculos.",
          openSetupGuide: "Abrir guia completo",
          setupGuideModalTitle: "Guia de configuração do Court Intelligence",
          setupGuideModalSubtitle:
            "Use estes objetivos para decidir quão precisos você quer que sejam os sinais do local.",
          setupGuideTabWorkflow: "Fluxo",
          setupGuideTabOutcomes: "Resultados",
          setupGuideTabCalculations: "Cálculos",
          setupGuideWorkflowIntro:
            "Siga esta ordem para colocar uma câmera online, capturar o primeiro snapshot e liberar os KPIs do local que você deseja.",
          workflowStepNumber: "Etapa {number}",
          workflowStepCameraTitle: "Criar uma câmera",
          workflowStepCameraDescription:
            "Selecione o local, crie a câmera e dê um nome e número claros para que os operadores a reconheçam depois.",
          workflowStepConnectionTitle: "Escolher o tipo de conexão",
          workflowStepConnectionDescription:
            "Use API push quando a câmera puder enviar imagens por HTTP POST. Use FTPS upload quando a câmera suportar melhor saída FTPS do que entrega direta por API.",
          workflowStepGroupsTitle: "Opcional: criar grupos de câmeras",
          workflowStepGroupsDescription:
            "Use grupos para organizar câmeras relacionadas e controlar a retenção de debug para troubleshooting. Sem debug, os snapshots são processados normalmente e não ficam retidos, exceto as imagens de inicialização.",
          workflowStepCredentialsTitle: "Configurar as credenciais de uso único",
          workflowStepCredentialsDescription:
            "Copie imediatamente o token ou a senha FTPS e depois configure a câmera com as instruções exibidas e o intervalo de captura recomendado.",
          workflowStepSnapshotTitle: "Aguardar o primeiro snapshot de inicialização",
          workflowStepSnapshotDescription:
            "Depois que a câmera enviar a primeira imagem, abra Inicialização para confirmar enquadramento, visibilidade e se a área útil do local está limpa.",
          workflowStepTargetTitle: "Escolher o resultado da configuração",
          workflowStepTargetDescription:
            "Escolha Estimativa rápida para atividade geral, Disponibilidade de quadras para mapear quadras, ou Quadras + fila para liberar espera e ETA.",
          workflowStepZonesTitle: "Criar áreas e anexar coberturas",
          workflowStepZonesDescription:
            "Monte primeiro o catálogo de áreas e depois mapeie a câmera com polígonos manuais ou rascunhos de IA. Salve cada zona apenas quando a cobertura corresponder à área visível.",
          workflowStepVerifyTitle: "Verificar os KPIs liberados",
          workflowStepVerifyDescription:
            "Use Ocupação ao vivo para confirmar o que já está ativo. Adicione áreas court para ocupadas/jogáveis e áreas de fila para waiting players e wait ETA.",
          setupGuideCalculationsIntro:
            "Estas fórmulas explicam o que o Court Intelligence pode mostrar em cada nível de configuração do local. Use-as como referência para operadores, não como texto exato para jogadores.",
          setupGuideDefaultPlayersPerGame: "Jogadores por partida padrão: 4",
          setupGuideDefaultAvgGameMinutes: "Minutos médios por partida padrão: 15",
          setupGuideDefaultQueueTargetMinutes: "Minutos alvo de fila padrão: 30",
          setupGuideFormulaLabel: "Fórmula",
          setupGuideVisibleKpisLabel: "KPIs visíveis",
          setupGuideHiddenKpisLabel: "KPIs ocultos",
          setupGuideExplanationLabel: "Explicação",
          setupGuideNone: "Nenhum",
          currentModeLabel: "Modo atual",
          currentModeUnavailable: "Ainda não classificado",
          setupStatusLoading: "Carregando o status atual da configuração…",
          setupStatusUnavailable: "O status atual da configuração ainda não está disponível.",
          mappedCourtsLabel: "Quadras mapeadas",
          queueAreasLabel: "Áreas de fila",
          targetQuickEstimate: "Estimativa rápida",
          targetCourtAvailability: "Disponibilidade de quadras",
          targetCourtAndQueue: "Quadras + fila",
          capabilityBusyness: "Atividade ao vivo",
          capabilityAvailability: "Disponibilidade de quadras",
          capabilityWait: "Métricas de fila",
          areaTypesLabel: "Tipos de área",
          cameraTargetLabel: "Objetivo da câmera",
          framingLabel: "Enquadramento",
          nextStepLabel: "Próximo passo",
          quickEstimateAreaTypes:
            "Use uma cobertura geral para a área útil do local, ou mantenha uma única área full coverage quando o enquadramento já estiver limpo.",
          quickEstimateCameraTarget:
            "Uma câmera ampla já funciona para atividade geral aproximada do local.",
          quickEstimateFraming:
            "Evite que estacionamentos, ruas e áreas sem relação dominem o quadro útil.",
          quickEstimateNext:
            "Comece com cobertura geral do local para estimativa rápida. Mapeie quadras depois se quiser disponibilidade.",
          quickEstimateNextReady:
            "A estimativa rápida já está ativa. Use os objetivos acima para adicionar disponibilidade ou espera.",
          courtAvailabilityAreaTypes:
            "Crie uma área court para cada quadra visível e conecte cada uma ao seu próprio polígono de cobertura.",
          courtAvailabilityCameraTarget:
            "Busque 1 a 2 quadras por câmera. Três podem funcionar se a separação visual for clara.",
          courtAvailabilityFraming:
            "Use vistas elevadas e centralizadas com o mínimo de oclusão por grades, postes ou jogadores.",
          courtAvailabilityNextLocked:
            "Mapeie as quadras visíveis para liberar métricas de ocupadas / jogáveis.",
          courtAvailabilityNextReady:
            "A disponibilidade de quadras já está ativa. Ajuste ângulos ou divida vistas carregadas se a confiança continuar moderada.",
          courtQueueAreaTypes:
            "Use áreas court mais áreas waiting, bench, paddle rack ou com queue signal para a fila que deseja medir.",
          courtQueueCameraTarget:
            "Use a mesma orientação de quadras e depois mantenha áreas de espera, banco ou rack claramente visíveis.",
          courtQueueFraming:
            "As áreas de fila devem aparecer separadas das quadras ativas e não podem ficar pequenas demais no quadro.",
          courtQueueNextLocked:
            "Adicione áreas de espera, banco ou rack para liberar estimativas de espera.",
          courtQueueNextReady:
            "As métricas de fila já estão ativas. Mantenha essas áreas claras e separadas para melhores estimativas.",
          areasTitle: "Catálogo de áreas",
          areasSubtitle: "Áreas semânticas no nível do local, compartilhadas entre coberturas de câmera.",
          createArea: "Criar área",
          selectArea: "Selecionar área",
          newArea: "Nova área",
          newAreaModalTitle: "Criar nova área",
          areaNamePlaceholder: "Nome da área",
          loadingAreas: "Carregando catálogo de áreas…",
          noAreasYet: "Ainda não há áreas para este local.",
          systemArea: "Área do sistema",
          edit: "Editar",
          pilotDebugBadge: "Debug piloto LIGADO até {date}",
          manageGroups: "Gerenciar grupos",
          addCamera: "Adicionar câmera",
          closeAddCamera: "Fechar adicionar câmera",
          closeAddArea: "Fechar adicionar área",
          addCameraTitle: "Adicionar câmera",
          cameraNamePlaceholder: "Nome da câmera",
          cameraNumberPlaceholder: "Número da câmera",
          ingestModeLabel: "Modo de ingestão",
          ingestModeApi: "Envio por API",
          ingestModeFtp: "Upload FTPS",
          ingestHintFtp:
            "Configuração FTPS: TLS explícito na porta 21, modo passivo, upload de arquivos JPG/PNG.",
          ingestHintApi: "Configuração API: imagem multipart via HTTP POST com token da câmera.",
          noGroup: "Sem grupo",
          createCamera: "Criar câmera",
          creatingCamera: "Criando…",
          initCameraSelectorTitle: "Câmera para inicialização",
          goToCameras: "Ir para Câmeras",
          noInitCamerasTitle: "Não há câmeras disponíveis para inicialização",
          noInitCamerasDescription:
            "Crie pelo menos uma câmera em Câmeras antes de iniciar a configuração de zonas.",
          showAllCameraStatuses: "Mostrar todos os status de câmera",
          noInitializingCamerasDescription:
            "Não há câmeras no status Inicializando. Ative \"Mostrar todos os status de câmera\" para selecionar outra câmera.",
          loadingCameras: "Carregando câmeras…",
          noCamerasVenue: "Ainda não há câmeras para este local.",
          initZonesTitle: "Inicialização e zonas",
          initZonesSubtitle:
            "Use snapshots para mapear áreas manualmente ou gerar sugestões de IA como rascunho.",
          cameraLabel: "Câmera",
          noneSelected: "Nenhuma selecionada",
          selectCameraPrompt: "Selecione uma câmera para configurar inicialização e zonas.",
          selectCameraPromptHelp:
            "Escolha uma câmera acima para abrir seus snapshots e o editor de zonas.",
          editingInitializingOnly:
            "Somente câmeras no status Inicializando podem ser editadas nesta tela.",
          snapshotsTitle: "Snapshots de inicialização",
          loadingSnapshots: "Carregando snapshots…",
          refresh: "Atualizar",
          protocolFtps: "Protocolo: FTPS (TLS explícito) na porta 21.",
          uploadFtps: "Envie imagens para o prefixo de caminho configurado no modo passivo.",
          intervalFtps: "Intervalo recomendado de captura: 60 segundos.",
          methodApi: "Método HTTP: POST multipart com arquivo de imagem (jpg/png).",
          tokenApi: "Envie o token da câmera no header x-camera-token.",
          intervalApi: "Intervalo de captura: 30 segundos.",
          unlimited: "Ilimitado",
          notAvailable: "N/D",
          statusPrefix: "Status",
          statusInitializing: "Inicializando",
          statusActive: "Ativa",
          statusInactive: "Inativa",
          lastSeenPrefix: "Última visualização {time}",
          selected: "Selecionada",
          select: "Selecionar",
          openInstructions: "Abrir instruções de configuração",
          instructionsTitle: "Instruções de configuração ({mode})",
          actionActivate: "Ativar",
          actionDeactivate: "Desativar",
          actionOpenSetup: "Abrir configuração",
          discardDraft: "Descartar rascunho",
          unsavedChangesBadge: "Alterações de zonas não salvas",
          actionRotateFtpPassword: "Rotacionar senha",
          actionRotateToken: "Rotacionar token",
          actionSwitchToApi: "Mudar para API",
          actionSwitchToFtps: "Mudar para FTPS",
          actionReinitialize: "Reinicializar",
          actionDelete: "Excluir",
          ftpRotationDisabled:
            "A rotação de token está desabilitada no modo FTPS. Use Rotacionar senha FTPS quando necessário.",
          latestSnapshotPrefix: "Último snapshot: {time}",
          snapshotAlt: "Snapshot de inicialização",
          drawingHint: "Clique na imagem para colocar pontos do polígono ({count}).",
          noSnapshotYet:
            "Ainda não há snapshot de inicialização. Mantenha a câmera ativa até chegar o primeiro.",
          noSnapshotTitle: "Aguardando o primeiro snapshot de inicialização",
          noSnapshotDescription:
            "Mantenha a câmera ativa e verifique as configurações de upload na câmera. Quando o primeiro snapshot chegar, a configuração de zonas será habilitada.",
          snapshotRequiredForActions:
            "O desenho de zonas e a sugestão por IA são habilitados após o recebimento do primeiro snapshot de inicialização.",
          manualZoneDraftTitle: "Adicionar zona",
          zoneNamePlaceholder: "Nome da zona",
          queueSignal: "Sinal de fila",
          exclusion: "Exclusão",
          startDrawing: "Iniciar desenho",
          undoPoint: "Desfazer ponto",
          resetPolygon: "Resetar polígono",
          addZoneDraft: "Adicionar zona",
          aiZoneSuggestionTitle: "Sugestão de zonas por IA",
          aiPromptPlaceholder: "Instrução opcional para IA",
          suggesting: "Sugerindo…",
          suggestZones: "Sugerir zonas",
          applySuggestion: "Aplicar sugestão",
          revert: "Reverter",
          aiPreparedHint: "A IA preparou {count} zonas. Aplique quando estiver pronto.",
          workingZonesTitle: "Zonas de trabalho ({count})",
          saving: "Salvando…",
          saveZones: "Salvar zonas",
          loadingZones: "Carregando zonas…",
          remove: "Remover",
          pointsLabel: "Pontos",
          queueTag: "fila",
          exclusionTag: "exclusão",
          noZonesYet: "Ainda não há zonas. Desenhe manualmente ou use sugestão de IA.",
          savedZonesLive: "Zonas salvas ativas: {count}",
          groupsModalTitle: "Grupos de câmeras",
          hideForm: "Ocultar formulário",
          createGroup: "Criar grupo",
          close: "Fechar",
          groupNamePlaceholder: "Nome do grupo",
          groupDescriptionPlaceholder: "Descrição opcional",
          debugEnabled: "Debug habilitado",
          creating: "Criando…",
          loadingGroups: "Carregando grupos…",
          save: "Salvar",
          noGroupsYet: "Ainda não há grupos. Câmeras ainda podem ser criadas sem grupo.",
          deleteCameraTitle: "Excluir câmera",
          deleteCameraDescription: "Esta ação é permanente e interrompe a ingestão desta câmera.",
          deleteCameraLabel: "Câmera",
          deleteModeLabel: "Modo",
          deleteIdLabel: "ID",
          deleteTypeToConfirm: "Para confirmar, digite:",
          deleteDelayHint: "Aguarde um momento antes de confirmar.",
          cancel: "Cancelar",
          deleting: "Excluindo…",
          confirmDelete: "Confirmar exclusão",
          tokenFieldCameraId: "ID da câmera",
          tokenFieldFtpsHost: "Host FTPS",
          tokenFieldFtpsUsername: "Usuário FTPS",
          tokenFieldFtpsPassword: "Senha FTPS",
          tokenFieldFtpsPathPrefix: "Prefixo de caminho FTPS",
          ftpsPathPrefixValue: "/ ou em branco",
          ftpsSubfolderRuleOff: "Se sua câmera tiver regra de geração de subpasta, desative-a.",
          tokenFieldSnapshotEndpoint: "Endpoint de snapshot",
          tokenFieldApiHeader: "Header API",
          tokenFieldCameraToken: "Token da câmera",
          copy: "Copiar",
          copyToken: "Copiar token",
          copied: "Copiado",
          copyFailed: "Falha ao copiar. Copie manualmente.",
          cameraNumberLabel: "Câmera",
          groupLabel: "Grupo",
          suggestedZoneLabel: "Zona sugerida",
          zoneSuffix: "Zona",
          confirmSwitchFtpToApi:
            "Mudar esta câmera para API remove credenciais FTP e interrompe o envio FTP atual. Continuar?",
          confirmDeleteGroup: "Excluir este grupo de câmeras? Primeiro remova as câmeras vinculadas.",
          confirmDeleteArea:
            "Excluir a área '{name}'? Isso falha se uma cobertura de câmera ainda estiver vinculada a ela.",
          confirmLeaveUnsaved:
            "Você tem alterações de zonas não salvas. Sair desta tela e descartá-las?",
          errors: {
            loadVenueAccess: "Falha ao carregar acesso aos locais.",
            loadCameras: "Falha ao carregar câmeras.",
            loadCameraSetup: "Falha ao carregar configuração da câmera.",
            loadAreas: "Falha ao carregar catálogo de áreas.",
            cameraActionFailed: "Ação da câmera falhou.",
            switchIngestMode: "Falha ao mudar o modo de ingestão da câmera.",
            createGroup: "Falha ao criar grupo de câmeras.",
            updateGroup: "Falha ao atualizar grupo de câmeras.",
            deleteGroup: "Falha ao excluir grupo de câmeras.",
            createArea: "Falha ao criar área.",
            updateArea: "Falha ao atualizar área.",
            deleteArea: "Falha ao excluir área.",
            createCamera: "Falha ao criar câmera.",
            cameraLimitReached: "Limite de câmeras atingido. Adicione capacidade de câmeras em Assinatura para continuar.",
            deleteCamera: "Falha ao excluir câmera.",
            suggestZones: "Falha ao sugerir zonas.",
            removeFullCoverageFirst:
              "Remova a cobertura completa padrão antes de salvar coberturas mapeadas adicionais.",
            invalidCoveragePolygon:
              "Cada cobertura não completa precisa de pelo menos 3 pontos de polígono.",
            coverageOverlap:
              "A cobertura sobrepõe outra zona. Ajuste o polígono antes de salvar.",
            missingAreaForCoverage:
              "Cada cobertura deve estar vinculada a uma área antes de salvar.",
            saveZones: "Falha ao salvar zonas.",
          },
          notices: {
            switchedToFtps: "Câmera alterada para modo FTPS.",
            switchedToApi: "Câmera alterada para modo API.",
            groupCreated: "Grupo de câmeras criado.",
            groupUpdated: "Grupo de câmeras atualizado.",
            groupDeleted: "Grupo de câmeras excluído.",
            areaCreated: "Área criada.",
            areaUpdated: "Área atualizada.",
            areaDeleted: "Área excluída.",
            cameraCreated: "Câmera criada.",
            cameraDeleted: "Câmera excluída.",
            cameraActivated: "Câmera ativada.",
            cameraDeactivated: "Câmera desativada.",
            ftpPasswordRotated: "Senha FTPS rotacionada.",
            cameraTokenRotated: "Token da câmera rotacionado.",
            cameraReinitialized: "Câmera reinicializada.",
            aiNoZones: "A sugestão de IA não retornou zonas. Tente outro prompt.",
            aiPrepared: "A IA preparou {count} zonas sugeridas. Revise e aplique quando estiver pronto.",
            aiApplied: "Sugestão de IA adicionada às zonas de trabalho. Salve cada zona para persistir.",
            aiReverted: "Revertido para as zonas de trabalho anteriores.",
            zonesSaved: "Zonas salvas.",
            draftZoneAdded: "Zona adicionada às zonas de trabalho. Salve esta zona para persistir.",
            draftDiscarded: "Alterações de zonas em rascunho descartadas.",
          },
        },
      },
      tips: {
        title: "Dicas",
        subtitle: "Aproveite ao máximo o WhoPlays.",
        back: "Voltar",
        next: "Próxima",
        close: "Fechar",
        progress: "Dica {current} de {total}",
        items: [
          {
            title: "Confira o Real-Time primeiro",
            body: "Veja quem está em quadra antes de chegar para entrar no jogo certo.",
          },
          {
            title: "Convide por SMS",
            body: "Envie convites rápidos do Meu Play Schedule para completar a partida.",
          },
          {
            title: "Confirme cedo",
            body: "Ajude o anfitrião confirmando o quanto antes.",
          },
          {
            title: "Registre o placar na hora",
            body: "Atualize os resultados para manter o ranking em dia.",
          },
          {
            title: "Vincule seu DUPR",
            body: "Sincronize suas avaliações para vê-las no app.",
          },
          {
            title: "Ajuste as notificações",
            body: "Escolha quais alertas são mais importantes para você.",
          },
          {
            title: "Complete seu perfil",
            body: "Um bom perfil ajuda outras pessoas a te encontrar e confiar em você.",
          },
          {
            title: "Rotacione times",
            body: "Use Rotacionar times para criar mais partidas com os mesmos quatro jogadores.",
          },
        ],
      },
      subscriptionLimits: {
        title: "Limite atingido",
        sms: "Você atingiu o limite mensal de {limit} convites por SMS.",
        players: "Você atingiu o limite de {limit} jogadores por agendamento.",
        events: "Você atingiu o limite de {limit} agendamentos ativos com convites.",
        generic: "Você atingiu o limite atual de {limit}.",
        upgrade: "Fazer upgrade",
        cancel: "Cancelar",
      },
      sidebar: {
        welcome: "Bem-vindo de volta",
        kpiWins: "Vitórias",
        kpiLosses: "Derrotas",
        kpiPending: "Pendentes",
        quickActions: {
          language: "Alterar idioma",
          languageNext: "Próximo: {language}",
          theme: "Alternar tema",
          tips: "Dicas",
          tipsSoon: "As dicas estarão disponíveis em breve.",
          courtIntel: "Alternar para modo Court Intelligence",
          logout: "Sair",
        },
        toggles: {
          dnd: "Não perturbe",
          tips: "Dicas",
          share: "Compartilhar",
          language: "Idioma",
        },
        actions: {
          profile: "Perfil",
          preferredCourts: "Minhas sedes",
          schedule: "Minha agenda",
          linkDupr: "Conectar DUPR®",
          subscriptions: "Assinaturas",
          invite: "Convidar jogadores",
          privacy: "Política de privacidade",
          settings: "Configurações e preferências",
          appStats: "App Stats",
          logout: "Sair",
        },
        admin: {
          label: "Admin",
          access: "Acesso e permissões",
        },
        chartLabel: "Vitórias x derrotas",
        chartWins: "Vitórias",
        chartLosses: "Derrotas",
        chartPending: "Pendentes",
        chartNoData: "Ainda não há histórico de partidas.",
        chartWinRate: "Taxa de vitória",
        chartTotal: "Total",
        skillLevels: {
          beginner: "Iniciante",
          intermediate: "Intermediário",
          advanced: "Avançado",
        },
        rankings: {
          wpRankLabel: "Rank WP",
          duprLabel: "DUPR",
          singlesShort: "S",
          doublesShort: "D",
          notRated: "NR",
        },
      },
      header: {
        subtitleRank: "Ranking · Global",
        moreLabel: "Menu",
        currentLabel: "Atual",
      },
      analytics: {
        tabSwitch: "troca_de_aba",
        sidebarOpen: "sidebar_aberta",
        sidebarClose: "sidebar_fechada",
      },
      dupr: {
        title: "Conectar ao DUPR®",
        subtitle: "Entre no DUPR para sincronizar suas classificações.",
        instructions: "Conclua o login do DUPR abaixo. Não armazenamos sua senha.",
        waiting: "Aguardando login do DUPR...",
        processing: "Conectando sua conta DUPR...",
        success: "Conta DUPR conectada com sucesso.",
        error: "Não foi possível conectar sua conta DUPR agora.",
        originError: "Mensagem ignorada de uma origem não confiável.",
        missingConfig: "A integração DUPR não está configurada.",
        close: "Fechar",
        unauthorized: "Você precisa estar logado para conectar o DUPR. Faça login e tente novamente.",
      },
      realTime: {
        ribbonLabel: "Suas sedes",
        refresh: "Atualizar",
        noCourtsTitle: "Adicione uma sede preferida para ver a atividade ao vivo.",
        courtSelectorLabel: "Selecionar uma quadra",
        courtIntelligence: {
          title: "Court Intelligence",
          refresh: "Atualizar",
          refreshing: "Atualizando…",
          viewDetails: "Ver detalhes",
          noDataBadge: "Prévia para jogadores",
          noDataLogoAlt: "Court Intelligence da WhoPlays",
          noDataTitle: "Esta sede ainda não liberou visibilidade ao vivo das quadras.",
          noDataBody:
            "Nas sedes habilitadas, os jogadores podem ver o movimento e a pressão de fila antes de sair. Você pode ajudar a trazer isso para cá.",
          noDataHint: "Mostre para seu clube, HOA ou community manager o que o Court Intelligence pode agregar.",
          noDataPrimaryCta: "Ver Court Intelligence",
          noDataSecondaryCta: "Compartilhar com a sede",
          requestCta: "Solicitar Court Intelligence",
          requestHint: "Peça ao community manager para habilitar nesta sede.",
          requestEmailSubject: "Solicitação de Court Intelligence para {court}",
          requestEmailBody:
            "Olá time da WhoPlays,\n\nQuero solicitar Court Intelligence para esta sede:\n{court}\n\nUsuário: {username}\n\nObrigado.",
          shareEmailSubject: "Podemos levar o Court Intelligence para {court}?",
          shareEmailBody:
            "Olá,\n\nEu uso o WhoPlays em {court} e gostaria muito de ver o Court Intelligence aqui.\n\nEle dá aos jogadores visibilidade ao vivo antes de saírem de casa.\n\nVisão geral: {link}\n\nEnviado por: {username}\n",
          scoreLabel: "Índice de ocupação",
          occupancyLabel: "Em uso / jogáveis",
          occupancyValueFormat: "{inUse} de {playable}",
          waitingLabel: "Fila",
          etaLabel: "Espera",
          peopleLabel: "Pessoas",
          scoreShort: "Índice",
          confidenceLabel: "Confiança",
          estimateBadge: "Estimativa",
          weatherLabel: "Clima",
          forecastLabel: "Próximas horas",
          weatherUnavailable: "Clima indisponível",
          updatedAt: "Atualizado: {time}",
          permanentLink: "Quer isso em outra sede?",
          infoTitle: "Sobre as estimativas do Court Intelligence",
          infoBody:
            "Ocupação, espera e contagem de pessoas são estimativas operacionais baseadas em ML a partir de snapshots de câmera e podem diferir da contagem exata.",
          detailTitle: "Detalhes do Court Intelligence",
          detailsOverviewTab: "Resumo",
          detailsTrafficTab: "Movimento por hora",
          currentSnapshotTitle: "Estado atual",
          trendTitle: "Movimento da sede por hora",
          trendSubtitle: "Padrão por hora dos últimos 7 dias com base nas leituras recentes.",
          trendLoading: "Carregando tendência…",
          trendEmpty: "Ainda não há dados de tendência para esta sede.",
          trendError: "Não foi possível carregar a tendência agora.",
          trendInfoTitle: "Como ler este gráfico",
          trendInfoBody:
            "Use as células mais claras para identificar horários mais tranquilos e ter uma visita mais confortável. Use as células mais escuras para identificar horários mais movimentados, quando há mais jogadores. O ponto dourado indica que houve fila naquela hora.",
          trendQuietLabel: "Mais tranquilo",
          trendBusyLabel: "Mais movimentado",
          trendWaitingObserved: "Fila observada",
          trendNote: "Cada célula mostra uma hora observada. Mais claro significa mais tranquilo; o ponto dourado indica que houve fila.",
          trendPeople: "Pessoas",
          trendWaiting: "Fila",
          courtFallback: "Sede",
          courtUnknown: "Sede desconhecida",
          unknownUser: "desconhecido",
          notAvailable: "N/D",
          close: "Fechar",
          loadError: "Não foi possível carregar os dados do Court Intelligence.",
        },
        summary: {
          title: "Jogadores em {court}",
          allLevels: "Todos os níveis",
          advanced: "Avançado",
          intermediate: "Intermediário",
          beginner: "Iniciante",
          metrics: {
            willing: "Prontos para jogar",
            privateGroups: "Grupos privados",
            following: "Acompanhando",
          },
          metricsShort: {
            willing: "Prontos",
            privateGroups: "Privados",
            following: "Acomp.",
          },
          updated: "Atualizado {time}",
        },
        whosNextTitle: "Quem vem a seguir",
        myScheduleTitle: "Minha agenda",
        myQuestionsTitle: "Minhas perguntas",
        day: {
          today: "Hoje",
          tomorrow: "Amanhã",
        },
        metrics: {
          openPlay: "Open Play",
          event: "Evento",
          privateGroup: "Grupo privado",
        },
        carousels: {
          noData: "Sem atividade programada.",
          dotsLabel: "Ir para o cartão {index}",
        },
        detail: {
          noActivity: "Sem atividade agendada para este tipo.",
          close: "Fechar",
          profileTitle: "Perfil do jogador",
          openProfile: "Abrir perfil completo",
          unknownPlayer: "Jogador desconhecido",
          status: {
            host: "Anfitrião",
            accepted: "Aceito",
            pending: "Pendente",
            declined: "Recusado",
          },
        },
        schedule: {
          empty: "Sem partidas futuras.",
          badges: {
            invited: "Convidado",
            hosted: "Organizado por {name} em {court}",
            hostedSelf: "Organizado por mim em {court}",
          },
          cardTime: "Minha agenda: {time}",
          counts: {
            invited: "Convidados",
            confirmed: "Confirmados",
          },
          confirmedLine: "Confirmados: {confirmed}",
          confirmedLineWithLimit: "Confirmados: {confirmed} de {limit}",
        },
        questions: {
          empty: "Sem perguntas para esta sede.",
          status: {
            pending: "Pendente",
            answered: "Respondida",
          },
        },
        emptyHero: {
          title: "Marque uma partida e convide jogadores!",
          bullets: [
            "Veja quem está chegando — ao vivo!",
            "Convide por SMS e acompanhe as respostas",
            "Mantenha todos informados por SMS ou no app",
          ],
          cta: "Agendar evento",
        },
        fabLabel: "Agendar evento",
      },
      network: {
        title: "Minha Rede",
        subtitle: "Encontre e gerencie jogadores que você segue.",
        searchPlaceholder: "Buscar jogadores",
        search: "Buscar",
        segments: {
          suggestions: "Sugestões",
          following: "Seguindo",
          followers: "Seguidores",
        },
        courtFilter: "Quadras",
        allCourts: "Todas as quadras",
        loading: "Carregando jogadores…",
        error: "Não foi possível carregar os jogadores agora.",
        empty: "Nenhum jogador para mostrar.",
        unknownSkill: "Nível desconhecido",
        follow: "Seguir",
        following: "Seguindo",
        refresh: "Atualizar",
        loadMore: "Carregar mais",
        actionError: "Não foi possível atualizar o seguimento.",
        levelCompetition: {
          recreational: "Recreativo",
          competitive: "Competitivo",
        },
        followTitle: "Seguir {name}",
        followingTitle: "Seguindo {name}",
        close: "Fechar",
        toggleGroup: "Adicionar às estatísticas do Meu Grupo",
        toggleNotify: "Notificar-me sobre este jogador",
        unfollow: "Deixar de seguir",
        save: "Salvar",
      },
    },
    profileEdit: {
      loading: "Carregando perfil…",
      header: {
        title: "Editar perfil",
        back: "Voltar",
        cancel: "Cancelar",
        save: "Salvar preferências",
        saving: "Salvando…",
      },
      sections: {
        avatar: {
          title: "Avatar",
          subtitle: "Envie uma foto nítida para que os jogadores reconheçam você.",
          button: "Alterar foto",
          uploading: "Enviando…",
          helper: "JPG ou PNG até 5 MB.",
        },
        personal: { title: "Informações pessoais" },
        location: { title: "Localização" },
        preferences: { title: "Preferências e nível" },
        bio: { title: "Biografia", placeholder: "Compartilhe uma breve introdução" },
      },
      fields: {
        name: "Nome completo",
        nickname: "Apelido",
        email: "E-mail",
        phone: "Número de telefone",
        country: "País",
        state: "Estado",
        city: "Cidade",
        zipcode: "CEP",
        status: "Status",
        preferredLevel: "Nível preferido",
        matchType: "Tipo de partida",
        skill: "Nível de habilidade",
        selfRanking: "Auto classificação",
        competition: "Nível de competição",
        defaultAudience: "Audiência padrão",
        followerPrivacy: "Privacidade de seguidores",
      },
      helpers: {
        emailOptional: "E-mail é opcional e pode ser usado para notificações.",
        emailLogin: "Alterar seu e-mail pode exigir verificação.",
        phoneLocked: "Este número é seu login e não pode ser alterado.",
        phoneFormat: "Use o formato internacional, ex.: +55…",
      },
      success: {
        saved: "Perfil salvo.",
        photo: "Foto de perfil atualizada.",
        email: "E-mail verificado.",
        phone: "Telefone verificado.",
      },
      errors: {
        name: "O nome é obrigatório.",
        email: "Informe um e-mail válido.",
        phone: "Informe um telefone válido no formato internacional.",
        code: "Informe o código de 6 dígitos.",
        save: "Não foi possível salvar o perfil.",
        upload: "Não foi possível enviar o avatar.",
        verificationStart: "Não foi possível iniciar a verificação.",
        verificationConfirm: "Não foi possível validar o código.",
      },
      verification: {
        verifyEmail: "Verificar e-mail",
        reverifyEmail: "Reverificar e-mail",
        verifyPhone: "Verificar telefone",
        reverifyPhone: "Reverificar telefone",
        badge: "Verificado",
        modalTitle: "Informe o código de verificação",
        modalDescriptionEmail: "Enviamos um código de 6 dígitos para {target}. Digite-o para verificar seu e-mail.",
        modalDescriptionPhone: "Enviamos um código de 6 dígitos para {target}. Digite-o para verificar seu telefone.",
        submit: "Verificar",
        submitting: "Verificando…",
        cancel: "Cancelar",
      },
      options: {
        competition: [
          { value: 1, label: "Recreativo" },
          { value: 2, label: "Competitivo" },
        ],
      },
      legal: "WhoPlays US · Configurações de perfil",
    },
    myVenues: {
      back: "Voltar ao app",
      title: "Minhas sedes",
      subtitle: "Escolha sua sede principal e mantenha uma lista dos lugares onde gosta de jogar.",
      loading: "Carregando sedes…",
      onboarding: {
        title: "Escolha sua primeira sede",
        subtitle: "Selecione uma sede principal de pickleball para o WhoPlays personalizar sua experiência local.",
        missingTitle: "Não encontrou sua sede?",
        missingBody: "Sugira a sede ou compartilhe o Court Intelligence para ajudar a liberar visibilidade ao vivo das quadras para jogadores locais.",
        heroTitle: "Leve Court Intelligence para sua sede",
        benefitOne: "Veja os jogadores, sedes e atualizações que importam ao redor da sua quadra principal.",
        benefitTwo: "Receba lembretes, convites e atividade adaptados ao local onde você realmente joga.",
        benefitThree: "Ajude sua sede a liberar visibilidade ao vivo com Court Intelligence.",
        helper: "Compartilhe Court Intelligence com seu clube, HOA ou gestor da sede.",
        ctaPrimary: "Saiba mais",
        ctaExpand: "Mostrar mais",
        ctaCollapse: "Mostrar menos",
        ctaSecondary: "Compartilhar com gestor",
        searchHeading: "Encontre sua sede principal",
        required: "Escolha uma sede principal para continuar no app.",
        continue: "Continuar",
        noResultsTitle: "Não encontrou sua sede?",
        noResultsBody: "Sugira agora e depois compartilhe o Court Intelligence para tornar sua sede mais útil para jogadores locais de pickleball.",
        shareSubject: "Podemos levar Court Intelligence para nossa sede de pickleball?",
        shareBody:
          "Olá,\n\nEstou usando WhoPlays e gostaria muito de ver nossa sede de pickleball incluída e futuramente habilitada com Court Intelligence.\n\nIsso ajuda jogadores a descobrir atividade local e dá à sede um caminho para oferecer visibilidade ao vivo das quadras.",
      },
      primary: {
        heading: "Sede principal",
        empty: "Você ainda não selecionou uma sede principal.",
        required: "Selecione uma sede principal para personalizar sua experiência.",
        clear: "Remover sede principal",
      },
      preferred: {
        heading: "Sedes preferidas",
        empty: "Adicione as sedes onde joga com frequência para acessá-las rapidamente.",
        remove: "Remover",
        makePrimary: "Definir como principal",
        add: "Adicionar às preferidas",
        countLabel: "selecionadas",
      },
      badges: {
        primary: "Principal",
        preferred: "Preferida",
      },
      search: {
        heading: "Buscar sedes",
        placeholder: "Busque pelo nome da sede ou endereço",
        action: "Buscar",
        clear: "Limpar busca",
        useLocation: "Usar minha localização",
        locating: "Localizando…",
        locationReady: "Localização pronta",
        locationError: "Não foi possível obter sua localização.",
        prompt: "Busque uma sede ou compartilhe sua localização para ver opções próximas.",
        noResults: "Não encontramos sedes para essa busca. Experimente outros termos.",
        loadMore: "Carregar mais resultados",
        loading: "Carregando sedes…",
        setPrimary: "Definir como principal",
        addPreferred: "Adicionar às preferidas",
        activeLabel: "Resultados para “{query}”",
        error: "Não foi possível buscar sedes no momento.",
        viewDetails: "Ver detalhes",
        hideDetails: "Ocultar detalhes",
      },
      actions: {
        save: "Salvar sedes",
        saving: "Salvando sedes…",
        saved: "Sedes atualizadas com sucesso.",
        error: "Não foi possível salvar as sedes.",
        suggest: "Sugerir uma sede",
        reviewPending: "Rever sedes pendentes",
        warning: "As sedes foram salvas, mas não conseguimos atualizar os dados agora.",
      },
    },
    adminVenues: {
      back: "Voltar para Minhas sedes",
      title: "Sedes pendentes de verificação",
      subtitle: "Revise as sedes recém-enviadas antes de publicá-las.",
      loading: "Carregando sedes pendentes…",
      empty: "Não há sedes aguardando verificação.",
      countLabel: "Sedes pendentes: {count}",
      refresh: "Atualizar lista",
      error: "Não foi possível carregar as sedes pendentes.",
      reviewCta: "Abrir revisão",
      hoursHeading: "Horário de funcionamento",
      courtsHeading: "Configuração das quadras",
      totalCourts: "{count} quadras no total",
      amenitiesHeading: "Comodidades",
      openPlayHeading: "Sessões de open play hoje",
      pendingBadge: "Pendente de verificação",
    },
    adminAccess: {
      back: "Voltar ao painel",
      title: "Acesso e permissões",
      subtitle: "Conceda funções, funções por quadra e acesso a assinaturas.",
      loading: "Carregando dados de acesso…",
      error: "Não foi possível carregar o resumo administrativo.",
      refresh: "Atualizar",
      searchPlaceholder: "Buscar usuários ou sedes",
      usersEmpty: "Nenhum usuário corresponde aos filtros.",
      courtsEmpty: "Nenhuma sede corresponde aos filtros.",
      adminOnly: "Somente administradores.",
      unknownCourt: "Sede desconhecida",
      tabs: {
        users: "Usuários",
        courts: "Sedes",
      },
      filters: {
        all: "Todas",
        roles: "Funções",
        courtRoles: "Funções de sede",
        subscriptions: "Assinaturas",
      },
      roles: {
        heading: "Funções",
        empty: "Sem funções ainda.",
        add: "Adicionar função",
        select: "Selecione uma função",
      },
      courtRoles: {
        heading: "Funções de sede",
        empty: "Sem funções de sede.",
        add: "Adicionar função de sede",
        update: "Atualizar função",
        selectCourt: "Selecione uma sede",
        selectUser: "Selecione um usuário",
        roleLabel: "Função",
        searchPlaceholder: "Buscar sedes",
        searchUsersPlaceholder: "Buscar usuários",
      },
      access: {
        heading: "Acesso a assinaturas",
        empty: "Sem acessos concedidos.",
        add: "Adicionar acesso",
        update: "Atualizar acesso",
        selectPlan: "Selecione um plano",
        expiresAt: "Expira em",
      },
      actions: {
        remove: "Remover",
        edit: "Editar",
        close: "Fechar",
      },
      rules: {
        adminOnly: "Somente super admins podem adicionar ou remover a função admin.",
      },
    },
    adminStats: {
      back: "Voltar ao painel",
      title: "Estatisticas do app",
      subtitle: "Monitore atividade e crescimento.",
      loading: "Carregando estatisticas...",
      error: "Nao foi possivel carregar o resumo de estatisticas.",
      adminOnly: "Somente administradores.",
      periodLabel: "Periodo",
      controls: {
        period: "Periodo",
        timezone: "Fuso horario",
        topN: "Top N",
        pendingLimit: "Limite de pendentes",
        apply: "Aplicar",
        periodLabel: "Periodo selecionado",
      },
      sections: {
        lifetime: "Acumulado",
        period: "Periodo",
        activation: "Funil de ativacao",
        events: "Eventos",
        engagement: "Atividade",
        geo: "Geografia",
        pendingVenues: "Sedes pendentes",
        subscriptions: "Assinaturas",
        subscriptionsPeriod: "Assinaturas (Periodo)",
        downloadsByOs: "Downloads por sistema",
      },
      metrics: {
        uniqueUsers: "Usuarios",
        uniqueDownloads: "Downloads",
        profilesCreated: "Perfis criados",
        primaryCourtSelected: "Sede principal selecionada",
        sessionsCount: "Sessoes",
        eventsCount: "Eventos",
        sessionsDuration: "Duracao das sessoes",
        sessionsDurationLifetime: "Duracao acumulada",
      },
      empty: {
        downloads: "Sem downloads.",
      },
      funnel: {
        ofDownloads: "de downloads",
        ofPrevious: "do anterior",
      },
    },
    suggestVenue: {
      backToVenues: "Voltar para Minhas sedes",
      title: "Sugerir uma sede",
      subtitle: "Envie os dados de um novo local para análise da nossa equipe.",
      ownerFlow: {
        title: "Criar uma sede",
        subtitle: "Crie ou atualize sua sede para Court Intelligence. Aprovação imediata para configuração.",
        submitCreate: "Criar sede",
        submitUpdate: "Salvar sede",
        submittingCreate: "Criando…",
        submittingUpdate: "Salvando…",
        successCreated: "Sede criada com sucesso.",
        successUpdated: "Sede atualizada com sucesso.",
      },
      steps: {
        basic: "Básico",
        details: "Detalhes",
        openPlay: "Open play",
        photos: "Fotos",
        contact: "Contato",
        review: "Revisão",
      },
      actions: {
        previous: "Voltar",
        next: "Avançar",
        submit: "Enviar sugestão",
        submitting: "Enviando…",
        approve: "Aprovar e publicar",
        markPending: "Marcar como pendente",
        delete: "Excluir sede",
      },
      loadingExisting: "Carregando dados da sede…",
      successMessage: "Obrigado! Recebemos sua sugestão de sede.",
      standaloneSuccess: {
        title: "Obrigado por enviar uma nova sede!",
        body: "Nossa equipe revisará as informações e normalmente publica a sede em 24 a 72 horas após a validação.",
        helper: "Entraremos em contato se precisarmos de mais detalhes.",
        ctaPrimary: "Sugerir outra sede",
        ctaSecondary: "Fechar",
      },
      successApproved: "Sede aprovada e publicada.",
      successPending: "Marcada como pendente de ajustes.",
      successDeleted: "Sede excluída.",
      errors: {
        basicRequired: "Informe o nome e o endereço completo antes de continuar.",
        detailsRequired: "Adicione pelo menos uma configuração de quadra e escolha a superfície.",
        descriptionRequired: "Descreva a sede para que os jogadores saibam o que esperar.",
        contactRequired: "Precisamos dos seus dados de contato para um eventual retorno.",
        confirmInformation: "Confirme que as informações fornecidas estão corretas.",
        submitFailed: "Não foi possível salvar a sugestão. Tente novamente em instantes.",
        loadVenue: "Não foi possível carregar os dados da sede. Tente novamente.",
        deleteFailed: "Não foi possível excluir a sede. Tente novamente.",
      },
      confirmDelete: "Excluir esta sede? Essa ação não pode ser desfeita.",
      basic: {
        heading: "Informações básicas",
        subheading: "Nome e endereço nos ajudam a localizar a sede.",
        nameLabel: "Nome da sede",
        namePlaceholder: "Complexo Riverside Pickleball",
        shortNameLabel: "Nome curto",
        shortNamePlaceholder: "Etiqueta opcional",
        addressLabel: "Endereço",
        addressPlaceholder: "Rua Principal 123",
        cityLabel: "Cidade",
        stateLabel: "Estado / região",
        zipLabel: "CEP",
        countryLabel: "País",
        countryPlaceholder: "Brasil",
        latitudeLabel: "Latitude",
        longitudeLabel: "Longitude",
        mapHeading: "Prévia do mapa",
        mapHelper: "Informe latitude e longitude para visualizar o local.",
        mapMissing: "Adicione coordenadas para exibir o mapa.",
        mapTitle: "Prévia do mapa para {name}",
        mapDefaultName: "esta sede",
        mapLoadError: "Não foi possível carregar o mapa. Verifique sua conexão e tente novamente.",
        mapSearchingForward: "Buscando a localização para este endereço…",
        mapSearchingReverse: "Buscando o endereço para estas coordenadas…",
        mapGeocodeError: "Não encontramos coordenadas para esse endereço. Tente ajustá-lo.",
        mapPendingHeading: "Atualizar o endereço com esta localização?",
        mapPendingApply: "Usar este endereço",
        mapPendingIgnore: "Manter endereço atual",
      },
      details: {
        heading: "Detalhes da sede",
        subheading: "Informe estrutura das quadras, superfície e comodidades.",
        surfaceLabel: "Superfície principal",
        surfacePlaceholder: "Escolha a superfície",
        typeLabel: "Tipo de sede",
        typePublic: "Pública (aberta a todos)",
        typePrivate: "Privada (exige associação)",
        courtHeading: "Configuração das quadras",
        courtAdd: "Adicionar linha",
        courtTypeLabel: "Tipo de quadra",
        courtTypePlaceholder: "Selecione",
        courtTypeDedicated: "Dedicada",
        courtTypeShared: "Rede compartilhada",
        courtTypeLined: "Somente linhas",
        courtEnvironmentLabel: "Ambiente",
        courtEnvironmentPlaceholder: "Selecione o ambiente",
        environmentIndoor: "Coberta",
        environmentOutdoor: "Aberta",
        environmentCovered: "Coberta parcial",
        courtQuantityLabel: "Quantidade",
        courtRemove: "Remover linha",
        amenitiesHeading: "Comodidades",
        amenitiesOptions: [
          "Iluminação",
          "Banheiros",
          "Pontos de água",
          "Assentos",
          "Pro Shop",
          "Armários",
          "Chuveiros",
          "Alimentos/Bebidas",
          "Estacionamento",
          "Acessível",
          "Áreas com sombra",
          "Lançapetas",
          "Aulas disponíveis",
        ],
        hoursHeading: "Horário de funcionamento",
        timeSlots: [
          "5:00 AM",
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
          "11:00 PM",
          "12:00 AM",
        ],
        until: "até",
        closedLabel: "Fechado",
        descriptionLabel: "Descrição",
        descriptionPlaceholder: "Conte sobre a qualidade das quadras e avisos importantes.",
        phoneLabel: "Telefone",
        phonePlaceholder: "(Opcional) Número de contato",
        websiteLabel: "Site",
        websitePlaceholder: "https://…",
      },
      openPlay: {
        heading: "Agenda de open play",
        subheading: "Informe horários de jogo livre para outros jogadores encontrarem partidas.",
        sessionsCount: "{count} sessões",
        copyWeekdays: "Copiar para dias úteis",
        copyWeekend: "Copiar para fim de semana",
        addSession: "Adicionar sessão",
        emptyLabel: "Nenhuma sessão adicionada para este dia.",
        startLabel: "Início",
        endLabel: "Fim",
        levelLabel: "Nível",
        statusLabel: "Status",
        removeSession: "Remover sessão",
        timeSlots: [
          "6:00 AM",
          "7:00 AM",
          "8:00 AM",
          "9:00 AM",
          "10:00 AM",
          "11:00 AM",
          "12:00 PM",
          "1:00 PM",
          "2:00 PM",
          "3:00 PM",
          "4:00 PM",
          "5:00 PM",
          "6:00 PM",
          "7:00 PM",
          "8:00 PM",
          "9:00 PM",
          "10:00 PM",
        ],
        levelOptions: ["Todos os níveis", "Iniciante", "Iniciante+", "Intermediário", "Intermediário+", "Avançado", "Jogadores de torneio"],
        statusOptions: ["Disponível", "Poucas vagas", "Lotado", "Somente membros", "Aberto ao público", "Requer inscrição"],
      },
      photos: {
        heading: "Fotos da sede",
        subheading: "Adicione imagens para que os jogadores reconheçam o local.",
        helper: "Arraste e solte ou escolha arquivos do seu dispositivo.",
        uploadCta: "Selecionar fotos",
        supported: "Formatos JPG ou PNG (máx. 5 MB cada).",
        current: "Fotos existentes",
        pending: "Fotos para envio",
        remove: "Remover",
      },
      contact: {
        heading: "Informações de contato",
        subheading: "Informe quem podemos procurar caso seja necessário.",
        nameLabel: "Seu nome",
        emailLabel: "E-mail",
        phoneLabel: "Telefone",
        roleLabel: "Sua relação com a sede",
        rolePlaceholder: "Selecione o papel",
        roleOwner: "Proprietário / operador",
        roleManager: "Gerente",
        roleEmployee: "Funcionário",
        rolePlayer: "Jogador frequente",
        roleVisitor: "Visitante",
        roleOther: "Outro",
        notesLabel: "Observações",
        notesPlaceholder: "Compartilhe informações adicionais úteis para a análise.",
        confirmationLabel: "Confirmo que as informações estão corretas e seguem as diretrizes da comunidade.",
      },
      review: {
        heading: "Revisar e enviar",
        subheading: "Confira tudo antes de enviar para revisão.",
        basic: "Informações básicas",
        name: "Nome da sede",
        address: "Endereço",
        type: "Tipo de sede",
        surface: "Superfície",
        courts: "Quadras",
        amenities: "Comodidades",
        description: "Descrição",
        photos: "Fotos",
        contact: "Contato",
        contactName: "Nome",
        contactEmail: "E-mail",
        contactPhone: "Telefone",
        contactRole: "Papel",
        notes: "Notas",
        unknown: "Desconhecido",
      },
      duplicates: {
        title: "Esta sede talvez já exista",
        description: "Localizamos sedes parecidas. Se alguma coincidir, abra-a em vez de criar um duplicado.",
        view: "Ver sede",
        continue: "Continuar mesmo assim",
        cancel: "Voltar",
      },
    },
    settings: {
      header: {
        title: "Configurações e preferências",
        description: "Controle aparência, privacidade e notificações no WhoPlays.",
        back: "Voltar",
        cancel: "Cancelar",
        save: "Salvar preferências",
        saving: "Salvando…",
      },
      guard: {
        title: "Configurações indisponíveis",
        description: "Complete seu perfil para desbloquear as configurações.",
        cta: "Editar perfil",
      },
      theme: {
        title: "Preferência de tema",
        description: "Escolha como o WhoPlays aparece neste dispositivo.",
        options: {
          system: { label: "Sistema", description: "Usar configuração do dispositivo" },
          light: { label: "Claro", description: "Interface iluminada" },
          dark: { label: "Escuro", description: "Ideal para ambientes com pouca luz" },
        },
      },
      language: {
        title: "Idioma",
        description: "Altere o idioma da interface instantaneamente.",
      },
      availability: {
        title: "Disponibilidade",
        options: {
          1: { title: "Disponível", description: "Jogadores podem convidar você para partidas." },
          2: { title: "Indisponível", description: "Apareça offline para todos." },
          6: { title: "Grupo privado", description: "Visível apenas para membros de grupos privados." },
        },
      },
      visibility: {
        title: "Visibilidade do perfil",
        options: {
          1: { title: "Público", description: "Qualquer pessoa pode ver seu perfil." },
          3: { title: "Jogadores registrados", description: "Somente membros do WhoPlays podem ver seu perfil." },
          4: { title: "Conexões", description: "Apenas suas conexões veem todos os detalhes." },
          5: { title: "Privado", description: "Apenas você e administradores veem seu perfil." },
        },
      },
      followers: {
        title: "Visibilidade para seguidores",
        options: {
          1: { title: "Público", description: "Seguidores veem sua atividade pública." },
          2: { title: "Quadra principal", description: "Limite atualizações à comunidade da sua quadra." },
          3: { title: "Todas as quadras", description: "Seguidores veem atualizações de todas as quadras." },
          4: { title: "Amigos", description: "Somente amigos aprovados veem suas atualizações." },
        },
      },
      notifications: {
        title: "Notificações",
        description: "Escolha como deseja receber informações do WhoPlays.",
        items: {
          tournamentReminders: { title: "Lembretes de torneios", description: "Receba avisos antes dos torneios registrados." },
          matchNotifications: { title: "Atualizações de partidas", description: "Alertas quando partidas são agendadas ou alteradas." },
          partnerRequests: { title: "Pedidos de parceria", description: "Saiba quando quiserem se conectar com você." },
          communityEvents: { title: "Eventos da comunidade", description: "Fique por dentro de eventos na sua região." },
          emailNotifications: { title: "Notificações por e-mail", description: "Receba atualizações também por e-mail." },
          eventInvitesSms: { title: "Convites por SMS", description: "Receba convites por texto para responder rapidamente." },
          matchInvitesSms: { title: "Alertas de partidas por SMS", description: "Receba lembretes e detalhes via SMS." },
          scoringUpdatesSms: { title: "Pontuação por SMS", description: "Confirme resultados diretamente por texto." },
        },
      },
      account: {
        title: "Atalhos da conta",
        links: {
          connectedAccounts: { title: "Contas conectadas", description: "Gerencie redes sociais vinculadas." },
          dataPrivacy: { title: "Dados e privacidade", description: "Revise como tratamos seus dados." },
          deleteAccount: { title: "Excluir conta", description: "Remova sua conta permanentemente." },
        },
      },
      messages: {
        saved: "Preferências salvas com sucesso.",
        error: "Não foi possível salvar suas preferências. Tente novamente.",
      },
    },
    homeVenues: {
      title: "Locais perto de você",
      description:
        "Busque locais WhoPlays por nome ou endereço e acompanhe as sessões de open play de hoje.",
      pageTitle: "Diretório de locais de pickleball",
      pageDescription:
        "Pesquise por nome ou endereço, confira comodidades e veja sessões open play na sua região.",
      useMyLocation: "Usar minha localização",
      locating: "Detectando localização...",
      searchPlaceholder: "Pesquisar por nome ou endereço",
      searchAriaLabel: "Pesquisar locais",
      searchCta: "Buscar",
      promptLocation: "Compartilhe sua localização ou pesquise para encontrar locais próximos.",
      clearFilters: "Limpar filtros",
      errorLoading: "Não foi possível carregar os locais próximos. Tente novamente.",
      noResults: "Nenhum local corresponde à sua busca.",
      loading: "Carregando locais...",
      loadMore: "Carregar mais",
      loadingMore: "Carregando mais locais...",
      distanceAway: "A {distance}",
      verifiedBadge: "Verificado",
      courtsHeading: "Detalhe das quadras",
      courtsHeadingWithTotal: "Detalhe das quadras · {count} quadras",
      courtsFallback: "{count} quadras no total",
      surfaceLabel: "Superfície principal: {surface}",
      totalCourts: "{count} quadras no total",
      amenitiesHeading: "Comodidades",
      openPlayHeading: "Open play de hoje",
      hoursHeading: "Horário de funcionamento",
      hoursClosed: "Fechado",
      contactPhone: "Ligar para {phone}",
      visitWebsite: "Visitar site",
      viewOnMap: "Ver no mapa",
      viewAllVenues: "Ver todos os locais",
      viewDetails: "Ver detalhes",
      backToHome: "Voltar para a página inicial",
      openStatusNow: "Aberto agora",
      closedStatusNow: "Fechado agora",
      closedStatusToday: "Fechado hoje",
      openUntil: "Aberto até {time}",
      opensAt: "Abre às {time}",
      closedForDay: "Fechado pelo resto do dia",
      statusUnknown: "Status indisponível",
      statusClosedTemporary: "Fechado temporariamente",
      statusClosedPermanent: "Fechado permanentemente",
      statusClosedOutOfSeason: "Fechado fora da temporada",
      publicVenue: "Público",
      privateVenue: "Privado",
      mapFallbackTitle: "Mapa de {name}",
      mapFallbackNoLocation: "Sem informações de localização.",
    },
    features: {
      title: "Tudo o que sua comunidade de pickleball precisa",
      description:
        "Siga quadras, envie convites e registre partidas de pickleball com ferramentas criadas para quadras públicas.",
      cards: [
        {
          title: "Visão de quadras em tempo real",
          description:
            "Descubra o movimento antes de sair de casa e compartilhe o clima com quem segue a quadra.",
          points: [
            "Siga quadras públicas e veja quem joga por nível",
            "Confira sessões programadas e jogos abertos em um feed",
            "Envie atualizações rápidas para alertar quem acompanha a quadra",
          ],
        },
        {
          title: "Agendamento instantâneo e SMS",
          description:
            "Monte um jogo, ladder ou treino em segundos e convide por texto. Os jogadores respondem Sim/Não e o elenco se atualiza sozinho.",
          points: [
            "Modelos flexíveis para jogos abertos, ladders ou clínicas",
            "Convide por SMS sem obrigar ninguém a instalar o app",
            "Veja respostas na hora e envie lembretes automáticos",
          ],
        },
        {
          title: "Rede de jogadores conectada",
          description:
            "Registre confrontos, acompanhe o DUPR e crie relacionamentos dentro da comunidade.",
          points: [
            "Salve resultados, partidas verificadas e anotações pessoais",
            "Autoavalie seu nível ou sincronize com o DUPR",
            "Siga jogadores, abra chats e monte sua turma fixa",
          ],
        },
      ],
    },
    social: {
      title: "Siga a comunidade de pickleball da WhoPlays",
      description:
        "Dicas, novidades do produto e bastidores com os jogadores que estão construindo o app WhoPlays.",
      links: [
        { name: "Instagram", handle: "@whoplaysus", href: "https://www.instagram.com/whoplaysus/" },
        { name: "Facebook", handle: "WhoPlays Pickleball", href: "https://www.facebook.com/profile.php?id=61550796384597" },
        { name: "YouTube", handle: "WhoPlays Pickleball", href: "https://www.youtube.com/channel/UC9JuYJ_QR3ykzjXgnKIRviA" },
        { name: "LinkedIn", handle: "WhoPlays US", href: "https://www.linkedin.com/company/whoplays-us-inc/?viewAsMember=true" },
        { name: "X", handle: "@WhoPlaysUS", href: "https://x.com/WhoPlaysUS" },
        { name: "TikTok", handle: "@whoplays05", href: "https://tiktok.com/@whoplays05" },
      ],
    },
    workflow: {
      title: "Encontre jogos em quatro passos",
      description:
        "Descubra, crie, convide e registre. O WhoPlays cuida da coordenação para você aproveitar a quadra.",
      steps: [
        {
          title: "Descubra",
          description:
            "Abra o feed de quadras para ver quem já está jogando e qual é a mistura de níveis.",
        },
        {
          title: "Crie",
          description:
            "Monte uma sessão com modelos rápidos e convide por SMS; as respostas Sim/Não atualizam o elenco automaticamente.",
        },
        {
          title: "Confirme",
          description:
            "Acompanhe respostas em tempo real e envie lembretes para quem ainda não respondeu.",
        },
        {
          title: "Registre",
          description:
            "Anote resultados, sincronize com o DUPR se quiser e mantenha o histórico da comunidade.",
        },
      ],
    },
    testimonials: {
      title: "Organizadores que migraram para o WhoPlays",
      items: [
        {
          quote:
            "“O feed de quadras mudou nossa rotina. Eu vejo quem já confirmou e mando mensagem pro resto sem sair do app.”",
          name: "Lauren Mitchell",
          role: "Diretora, Rally Point Pickleball",
        },
        {
          quote:
            "“Os RSVPs por SMS são sensacionais. Mesmo quem não curte apps fica informado e as vagas não sobram.”",
          name: "Brian Kim",
          role: "Head Pro, Marina Courts",
        },
      ],
    },
    finalCta: {
      title: "Ajude a construir a próxima versão do WhoPlays",
      description:
        "Entre para o grupo de organizadores fundadores para testar novos recursos de pickleball, pedir integrações e receber suporte direto enquanto evoluímos.",
      primaryCta: "Ser organizador fundador",
      primaryHref: "/founding-organizers",
      secondaryCta: "Baixar o app de pickleball",
      secondaryHref: "/app-stores",
    },
    footer: {
      tagline: "Feito nos EUA para a comunidade global de pickleball.",
      rights: "Todos os direitos reservados.",
      links: {
        pickleballApp: "App de Pickleball",
        privacy: "Política de privacidade",
        courtIntel: "Court Intelligence",
        foundingProgram: "Sedes fundadoras",
        plans: "Planos",
        accountDeletion: "Excluir conta",
      },
    },
    pickleballAppPage: {
      seo: {
        title: "App de Pickleball para Organizar Jogos e Encontrar Quadras | WhoPlays",
        description:
          "WhoPlays e um app de pickleball para encontrar quadras proximas, organizar jogos, convidar jogadores por SMS e checar lotacao ao vivo em locais suportados.",
        openGraphTitle: "App de Pickleball WhoPlays | Organize Jogos e Encontre Quadras",
        keywords: [
          "app de pickleball",
          "melhor app de pickleball",
          "app para organizar jogos de pickleball",
          "app para encontrar quadras de pickleball",
          "encontrar quadras de pickleball perto de mim",
          "organizar jogos de pickleball",
          "app para locais de pickleball",
        ],
        breadcrumbName: "App de Pickleball",
      },
      badge: "App de pickleball",
      title: "Um app de pickleball para organizar jogos e encontrar quadras perto de voce",
      description:
        "WhoPlays ajuda jogadores a encontrar quadras mais rapido, organizar open play com menos mensagens e evitar chegar em locais lotados.",
      ctaBrowse: "Ver quadras por localizacao",
      ctaDownload: "Baixar o app",
      cards: [
        {
          title: "Encontre quadras de pickleball perto",
          description:
            "Busque e navegue por locais para descobrir onde jogar, com links diretos para detalhes e mapas do local.",
        },
        {
          title: "Organize jogos e open play",
          description:
            "Coordene partidas e open play com menos troca de mensagens para manter as quadras ativas.",
        },
        {
          title: "Confira a pressao ao vivo",
          description:
            "Em locais suportados, o Court Intelligence ajuda a entender lotacao e pressao de espera antes de sair.",
        },
      ],
      players: {
        title: "Para jogadores",
        points: [
          "Encontre quadras por estado e cidade.",
          "Organize jogos mais rapido e convide por SMS.",
          "Decida quando jogar com melhor visibilidade.",
        ],
        cta: "Comecar na web",
      },
      operators: {
        title: "Para donos e operadores",
        points: [
          "Melhore a experiencia dos jogadores com visibilidade ao vivo.",
          "Monitore padroes ao longo do tempo com contexto historico.",
          "Use Court Intelligence para apoiar decisoes operacionais.",
        ],
        primaryCta: "Explorar Court Intelligence",
        secondaryCta: "Comecar",
      },
      faq: {
        title: "Perguntas frequentes do app de pickleball",
        items: [
          {
            question: "WhoPlays e um app para organizar jogos de pickleball?",
            answer:
              "Sim. WhoPlays ajuda a organizar partidas e open play enquanto centraliza convites e detalhes do local em um unico fluxo.",
          },
          {
            question: "Posso encontrar quadras de pickleball perto de mim?",
            answer:
              "Sim. Voce pode navegar por locais por estado e cidade e abrir cada pagina para ver mapas, detalhes das quadras e acoes rapidas.",
          },
          {
            question: "WhoPlays tambem ajuda donos de locais?",
            answer:
              "Sim. O Court Intelligence apoia donos e operadores com sinais ao vivo e historicos sobre uso das quadras.",
          },
          {
            question: "WhoPlays e gratis para jogadores?",
            answer:
              "Os fluxos principais para descoberta e organizacao de jogos estao disponiveis sem exigir plano pago. Recursos para locais sao oferecidos via Court Intelligence.",
          },
        ],
      },
      jsonLd: {
        featureList: [
          "Encontrar locais e quadras de pickleball proximos",
          "Organizar partidas e sessoes de open play",
          "Convidar jogadores com ferramentas rapidas de compartilhamento",
          "Ver lotacao e pressao de espera em locais suportados",
          "Acompanhar atividade por quadra com Court Intelligence",
        ],
      },
    },
    homeCourtIntelPromo: {
      badge: "Para donos e operadores",
      title: "Leve o Court Intelligence para sua quadra",
      description:
        "Transforme snapshots de câmeras em sinais operacionais ao vivo para agir mais rápido e gerir quadras com confiança.",
      kpis: ["Lotação ao vivo", "Espera estimada", "Disponibilidade de quadras"],
      foundingNote: "Founding Venue Program aberto agora para 7 locais de lancamento.",
      primaryCta: "Explorar Court Intelligence",
      primaryHref: "/court-intelligence",
      foundingCta: "Sedes fundadoras",
      foundingHref: "/court-intelligence/founding-venues",
      secondaryCta: "Ver planos",
      secondaryHref: "/court-intelligence#pricing",
    },
    courtIntelligence: {
      hero: {
        eyebrow: "Operação ao vivo para sedes de pickleball",
        title: "Veja o que realmente está acontecendo nas suas quadras, mesmo quando não há ninguém no local.",
        description:
          "Court Intelligence dá a clubes de pickleball, HOAs e operadores comunitários visibilidade em tempo real sobre tráfego nas quadras, pressão de fila e atividade no local, enquanto ajuda os jogadores a decidir quando ir pelo WhoPlays.",
        ownerCta: "Iniciar configuração do Court Intelligence",
        ownerCtaNote: "Somente para donos e gestores de sedes.",
        ownerHref: "/court-intelligence/get-started",
        foundingCta: "Ver Founding Venue Program",
        foundingHref: "/court-intelligence/founding-venues",
        playerCta: "Sou jogador",
        playerHref: "/auth/login",
        note: "Founding Venue Program aberto agora para 7 locais de lancamento.",
        visualNote: "Atividade ao vivo, disponibilidade de quadras, pressão de espera e tendências históricas em um único painel operacional.",
      },
      onboardingEntry: {
        eyebrow: "Onboarding de owners",
        title: "Inicie a configuração do Court Intelligence",
        description:
          "Esta entrada de assistente foi feita para donos e gestores de sedes. Vamos guiar você por acesso da conta, assinatura, configuração da sede e inicialização das câmeras.",
        note: "Você usará uma única conta WhoPlays para recursos de jogador e operações da sede.",
        stepsTitle: "Próximos passos",
        steps: [
          { title: "Passo 1: Acesso à conta", description: "Crie sua conta ou entre com sua conta atual do WhoPlays." },
          { title: "Passo 2: Ativar assinatura", description: "Escolha o plano base do Court Intelligence e aplique cupom beta quando disponível." },
          { title: "Passo 3: Configurar sede", description: "Crie/selecione sua sede, atribua papéis e defina detalhes operacionais." },
          { title: "Passo 4: Conectar câmeras", description: "Escolha modo API ou FTPS, copie credenciais de uso único e inicialize zonas para dados ao vivo." },
        ],
        checklistTitle: "Antes de continuar",
        checklist: [
          "Você é dono da sede ou gestor autorizado",
          "Você pode gerenciar assinatura e cobrança da sede",
          "Você tem acesso às câmeras ou suporte da equipe de instalação",
        ],
        primaryCta: "Continuar para configurar conta",
        primaryHref: "/auth/signup?returnTo=/main-app/court-intelligence/onboarding/account",
        secondaryCta: "Já tenho conta",
        secondaryHref: "/auth/login?returnTo=/main-app/court-intelligence/onboarding/account",
        signedInPrimaryCta: "Continuar para assinatura",
        signedInSecondaryCta: "Ver planos",
        backCta: "Voltar para visão geral do Court Intelligence",
        backHref: "/court-intelligence",
        foundingCallout: {
          eyebrow: "Founding Venue Program",
          title: "Vai se candidatar a uma das 7 vagas fundadoras?",
          description:
            "Use a rota fundadora se voce quer apoio de lancamento, acesso antecipado e a candidatura fundadora dentro do app principal.",
          cta: "Ir para sedes fundadoras",
          href: "/court-intelligence/founding-venues",
        },
      },
      highlights: [
        {
          value: "Reduza reclamações no pico",
          label: "Veja a pressão ao vivo antes que sessões lotadas virem frustração para os jogadores.",
        },
        {
          value: "Veja a pressão das quadras em tempo real",
          label: "Entenda como a sede está se comportando agora, se as quadras estão abrindo e quando a espera está aumentando.",
        },
        {
          value: "Planeje com histórico real de uso",
          label: "Use tendências históricas para apoiar decisões de equipe, planejamento de quadras e expansão.",
        },
      ],
      whatYouGet: {
        title: "Por que operadores usam",
        description:
          "Court Intelligence ajuda operadores a tomar decisões mais rápidas em horários cheios, melhorar a visibilidade para jogadores e construir um histórico real de uso da instalação.",
        items: [
          {
            title: "Para clubes com reservas",
            points: [
              "Seu sistema de reservas mostra agendamentos. Court Intelligence mostra o que realmente está acontecendo na quadra.",
              "Veja quando quadras reservadas continuam ocupadas e quando o fluxo de open play começa a virar espera.",
              "Apoie decisões da equipe e políticas de uso com visibilidade real no local.",
            ],
          },
          {
            title: "Para HOA e quadras comunitárias",
            points: [
              "Ajude moradores a saber quando ir antes de saírem para as quadras.",
              "Reduza reclamações de lotação no open play sem precisar de equipe presente no local.",
              "Use dados reais de uso para orientar decisões sobre amenidades e futuras quadras.",
            ],
          },
          {
            title: "Para operadores que planejam crescer",
            points: [
              "Revise o tráfego histórico por horário, sede e quadra.",
              "Compare a atividade real com suas premissas operacionais nos horários de maior demanda.",
              "Construa o caso para mais equipe, ajustes de política ou mais quadras.",
            ],
          },
        ],
      },
      setupOutcomes: {
        title: "Escolha o resultado da sua configuração",
        description:
          "Comece com uma estimativa rápida ou mapeie o local com mais detalhe para liberar disponibilidade de quadras e insights de espera.",
      },
      setupGuide: {
        title: "Escolha o resultado da sua configuração",
        subtitle: "Comece com uma estimativa rápida ou mapeie quadras e áreas de fila para ganhar mais visibilidade.",
        openGuideLabel: "Ver opções de configuração",
        eyebrowLabel: "Melhor para",
        descriptionLabel: "O que significa",
        outcomes: {
          quickEstimate: {
            label: "Estimativa rápida",
            eyebrow: "Ideal para pilotos iniciais",
            description: "Obtenha um sinal geral de atividade ao vivo com uma visão ampla do local.",
            unlocksTitle: "O que libera",
            unlocks: [
              "Atividade geral ao vivo",
              "Nível de lotação",
              "Visibilidade para jogadores no WhoPlays",
            ],
            setupTitle: "Configuração recomendada",
            setup: [
              "Uma câmera ampla pode ser suficiente",
              "Cobertura geral ou uma visão limpa de cobertura total",
              "Ideal quando você quer lançar rapidamente",
            ],
          },
          courtAvailability: {
            label: "Disponibilidade de quadras",
            eyebrow: "Ideal para locais ativos",
            description:
              "Mapeie as quadras visíveis para estimar quais estão em uso e quais podem ficar livres em breve.",
            unlocksTitle: "O que libera",
            unlocks: [
              "Visibilidade de quadras ocupadas vs livres",
              "Contexto por quadra",
              "Melhor planejamento para jogadores",
            ],
            setupTitle: "Configuração recomendada",
            setup: [
              "Busque 1 a 2 quadras por câmera",
              "Crie uma área do tipo quadra para cada quadra visível",
              "Use um ângulo estável e elevado com separação clara",
            ],
          },
          courtAndQueue: {
            label: "Quadras + fila",
            eyebrow: "Ideal para horários de pico",
            description:
              "Adicione áreas de espera, bancos ou racks para estimar pressão de fila e tempo de espera.",
            unlocksTitle: "O que libera",
            unlocks: [
              "Visibilidade de jogadores aguardando",
              "Pressão de espera estimada",
              "Sinais operacionais mais fortes ao vivo",
            ],
            setupTitle: "Configuração recomendada",
            setup: [
              "Use a mesma base de mapeamento de quadras",
              "Adicione áreas de espera ou bancos claramente visíveis",
              "Mantenha as áreas de fila visualmente separadas das quadras ativas",
            ],
          },
        },
        modal: {
          title: "Opções de configuração",
          closeLabel: "Fechar",
        },
      },
      workflow: {
        title: "Como funciona",
        steps: [
          { title: "Assine", description: "Ative um plano base de Court Intelligence para habilitar acesso operacional." },
          { title: "Conecte câmeras", description: "Crie câmeras, escolha modo API ou FTPS, copie credenciais de uso único e inicialize o feed." },
          { title: "Configure zonas", description: "Desenhe polígonos para quadras, espera, bancos, racks e cobertura total." },
          { title: "Opere com dados", description: "Use KPIs ao vivo e histórico para otimizar equipe e rotação." },
        ],
      },
      playerBenefit: {
        title: "Vantagem grátis para jogadores no WhoPlays US",
        description: "Quando uma quadra ativa Court Intelligence, jogadores ganham benefício sem custo adicional.",
        points: [
          "Ver se a quadra está lotada antes de sair de casa",
          "Analisar pressão de fila e melhores horários para jogar",
          "Reduzir incerteza e deslocamentos perdidos em horários de pico",
        ],
      },
      playerAdvocacy: {
        title: "Quer isso no seu clube, HOA ou quadras comunitarias?",
        description:
          "Jogadores nao compram a infraestrutura, mas podem ajudar a criar demanda. Compartilhe a pagina de sedes fundadoras com quem administra suas quadras.",
        points: [
          "Envie uma unica pagina pensada para owners em vez de explicar o produto por conta propria.",
          "Entregue a rota padrao de setup e a opcao do programa fundador no mesmo lugar.",
          "Ajude sua sede a avaliar lotacao ao vivo, pressao de espera e visibilidade de quadras sem adivinhar.",
        ],
        primaryCta: "Enviar por email para a sede",
        secondaryCta: "Abrir a pagina para owners",
        shareSubject: "Court Intelligence para nossas quadras de pickleball",
        shareBody:
          "Ola,\n\nEncontrei esta pagina do Court Intelligence para sedes de pickleball e achei que poderia ser util para nossas quadras. Ela explica a rota para owners e o programa limitado de sedes fundadoras.\n\n{url}\n",
      },
      privacy: {
        title: "Privacidade e dados de câmeras",
        description: "O Court Intelligence foi projetado para visibilidade operacional com comportamento de câmera controlado pela sede.",
        points: [
          "As câmeras enviam snapshots para o WhoPlays por API push ou upload FTPS; não nos conectamos à sua rede local.",
          "Por padrão, os snapshots são processados para sinais de lotação/espera e não são armazenados.",
          "Se o modo debug do grupo de câmeras estiver ativo, snapshots podem ser armazenados para melhoria de ML até a expiração definida pelo usuário.",
          "Enquanto a câmera estiver em inicialização, apenas o snapshot mais recente é mantido e substituído por um novo.",
          "Operadores podem interromper o envio a qualquer momento removendo a configuração na câmera.",
        ],
        playerNote:
          "Jogadores no WhoPlays US recebem indicadores ao vivo de lotação e espera em quadras habilitadas, não controles de configuração de câmeras.",
        policyCta: "Ler Política de Privacidade completa",
        policyHref: "/privacy-policy",
      },
      pricing: {
        title: "Preços simples para operadores de pickleball",
        description:
          "Comece com um local e quatro câmeras, depois amplie a capacidade conforme a operação cresce. Jogadores recebem visibilidade ao vivo pelo WhoPlays quando seu local ativa o Court Intelligence.",
        cards: [
          {
            name: "Court Intelligence Base (Mensal)",
            price: "$99 / mês",
            description: "Lance um local com visibilidade operacional ao vivo e cobrança mensal flexível.",
            highlighted: true,
            features: [
              "Inclui 1 local",
              "Inclui até 4 câmeras",
              "Tráfego do local, disponibilidade e sinais de espera ao vivo conforme o nível de setup",
              "Análises históricas, logs e gestão por papéis",
              "Visibilidade grátis para jogadores pelo WhoPlays",
            ],
          },
          {
            name: "Court Intelligence Base (Anual)",
            price: "$948 / ano",
            description: "Lance um local com a mesma capacidade base e menor custo anual.",
            highlighted: false,
            features: [
              "Inclui 1 local",
              "Inclui até 4 câmeras",
              "Os mesmos recursos ao vivo e históricos do plano mensal",
              "Economize $240/ano em relação à cobrança mensal",
              "Visibilidade grátis para jogadores pelo WhoPlays",
            ],
          },
          {
            name: "Add-on de câmeras",
            price: "$12 / mês ou $115 / ano",
            description: "Adicione capacidade quando um local precisar de cobertura mais ampla ou visibilidade mais detalhada.",
            highlighted: false,
            features: ["Adiciona +1 vaga de câmera", "Útil para locais maiores ou mapeamento mais preciso", "Gerenciado diretamente na assinatura"],
          },
          {
            name: "Add-on de locais",
            price: "$59 / mês ou $567 / ano",
            description: "Adicione mais um local e inclua quatro vagas de câmera para essa nova unidade.",
            highlighted: false,
            features: ["Adiciona +1 local", "Inclui +4 vagas de câmera para esse local", "Ideal para HOA, clubes com vários locais e operadores em crescimento"],
          },
        ],
        footnote:
          "Jogadores sempre recebem visibilidade ao vivo para locais habilitados pelo WhoPlays. Hardware, compra de câmeras e instalação no local não estão incluídos. Cupons do Founding Venue Program podem estar disponíveis para locais selecionados durante o lançamento.",
      },
      faq: {
        title: "Perguntas frequentes",
        items: [
          {
            question: "Preciso de outra conta para usar Court Intelligence?",
            answer: "Não. Use a mesma conta do WhoPlays e alterne o modo conforme seu acesso.",
          },
          {
            question: "Jogadores pagam para ver os dados?",
            answer: "Não. A visibilidade em tempo real para jogadores é gratuita em quadras habilitadas.",
          },
          {
            question: "Posso começar com uma quadra e escalar depois?",
            answer: "Sim. Comece com o que já vem no plano base e amplie com add-ons.",
          },
          {
            question: "Quais modos de conexão de câmera são suportados?",
            answer: "API push e FTPS (TLS explícito na porta 21). Intervalos recomendados: API a cada 30s e FTPS a cada 60s.",
          },
          {
            question: "Gestores e monitores precisam de assinatura própria?",
            answer: "O owner assina e concede permissões de quadra para o restante da equipe.",
          },
          {
            question: "Vocês fornecem hardware ou instalação de câmeras?",
            answer: "Não. Cada quadra fornece e configura suas próprias câmeras e rede local.",
          },
        ],
      },
      finalCta: {
        title: "Candidate-se a uma vaga no Founding Venue Program",
        description:
          "Estamos abrindo o Court Intelligence para um número limitado de clubes, HOAs e operadores comunitários que querem apoio direto de lançamento, acesso antecipado e a chance de ajudar a moldar o produto.",
        primaryCta: "Candidatar-se",
        primaryHref: "/court-intelligence/founding-venues",
        secondaryCta: "Falar com o time",
        secondaryHref:
          "mailto:support@whoplays.us?subject=Court%20Intelligence%20Founding%20Venue",
      },
    },
    foundingVenues: {
      hero: {
        eyebrow: "Founding Venue Program do Court Intelligence",
        title: "Lance o Court Intelligence com suporte direto de configuração.",
        highlight: "Limitado a 7 locais de lancamento para clubes, HOAs e operadores comunitarios.",
        description:
          "Locais fundadores recebem acesso antecipado ao Court Intelligence, acompanhamento do time da WhoPlays e a chance de influenciar os primeiros playbooks operacionais e o roadmap.",
        note: "Jogadores sempre recebem visibilidade ao vivo dos locais habilitados pelo WhoPlays sem custo adicional.",
        primaryCta: "Candidatar-se",
        primaryHref: "/court-intelligence/get-started",
        secondaryCta: "Falar com o time",
        secondaryHref:
          "mailto:support@whoplays.us?subject=Court%20Intelligence%20Founding%20Venue",
        visualTitle: "O que os locais fundadores recebem",
        visualPoints: [
          "Suporte direto do onboarding até as primeiras câmeras ao vivo",
          "Canal prioritário de feedback sobre setup, operação e relatórios",
          "Possíveis cupons de lançamento para locais selecionados quando disponíveis",
        ],
        visualFootnote: "Hardware, compra de câmeras e instalação no local não estão incluídos.",
      },
      highlights: [
        {
          value: "7 locais",
          label: "Estamos abrindo o programa fundador para uma primeira coorte muito controlada.",
        },
        {
          value: "Acesso antecipado",
          label: "Receba novas capacidades para operadores antes da liberação ampla.",
        },
        {
          value: "Feedback operacional",
          label: "Ajude a moldar fluxos de fila, disponibilidade e relatórios.",
        },
      ],
      benefits: {
        title: "O que o programa inclui",
        description:
          "Este programa foi criado para locais que querem um lançamento mais acompanhado enquanto o Court Intelligence se expande para mais operadores.",
        items: [
          {
            title: "Orientação de lançamento",
            points: [
              "Revisamos com você a configuração do local, o plano de câmeras e os objetivos do rollout.",
              "Avance por acesso da conta, assinatura, configuração do local e inicialização das câmeras com suporte mais claro.",
              "Reduza erros de setup antes de entrar no ar para os jogadores.",
            ],
          },
          {
            title: "Acesso antecipado ao produto",
            points: [
              "Use o Court Intelligence enquanto workflows de lançamento e relatórios para operadores continuam evoluindo.",
              "Compartilhe feedback prático sobre busyness, disponibilidade de quadras e insights de espera.",
              "Influencie o que será priorizado para operações de locais.",
            ],
          },
          {
            title: "Visibilidade para jogadores desde o primeiro dia",
            points: [
              "Jogadores no WhoPlays podem ver sinais ao vivo quando seu local estiver habilitado.",
              "Ajude jogadores a evitar deslocamentos perdidos e a planejar melhor horários de pico.",
              "Dê ao seu local uma experiência ao vivo mais transparente sem criar um paywall para jogadores.",
            ],
          },
        ],
      },
      fit: {
        title: "Quem deve se candidatar",
        description:
          "O melhor perfil é um local que consiga avançar no setup e queira ajudar a moldar a operação de lançamento com feedback real.",
        items: [
          "Clubes de pickleball que querem visibilidade ao vivo além das reservas.",
          "HOAs e quadras comunitárias que precisam de visão de lotação e espera sem equipe no local.",
          "Operadores com múltiplos locais que querem dados históricos para planejar.",
          "Locais que conseguem coordenar o setup de câmeras ou trabalhar direto com o instalador.",
        ],
      },
      launchSupport: {
        title: "O que acontece depois da candidatura",
        steps: [
          {
            title: "Compartilhe detalhes do local",
            description: "Conte sobre suas quadras, seu modelo operacional e qual resultado mais importa no lançamento.",
          },
          {
            title: "Confirmamos o encaixe",
            description: "Analisamos se seu local combina com a coorte atual e com os requisitos de setup.",
          },
          {
            title: "Configure acesso e câmeras",
            description: "Avance pelo onboarding do owner, assinatura, criação do local e inicialização das câmeras.",
          },
          {
            title: "Entre no ar e evolua",
            description: "Lance o local, acompanhe sinais ao vivo e compartilhe feedback sobre o que operadores precisam em seguida.",
          },
        ],
      },
      checklist: {
        title: "Antes de se candidatar",
        description:
          "Os melhores locais fundadores conseguem avançar rápido no setup e coordenar as pessoas necessárias para colocar as câmeras no ar.",
        items: [
          "Você é owner, gestor ou operador autorizado do local.",
          "Você pode gerenciar a cobrança ou coordenar com quem pode.",
          "Você consegue acessar as configurações das câmeras ou trabalhar com a equipe instaladora.",
          "Você quer lançar um local primeiro e expandir depois se necessário.",
        ],
        footnote:
          "Snapshots de câmera são processados para sinais de ocupação e espera. A retenção opcional de debug é controlada nas configurações do local.",
      },
      finalCta: {
        title: "Veja se uma das 7 vagas combina com seu local",
        description:
          "Inicie o fluxo para owners ou fale conosco antes se quiser confirmar prazo de lançamento, setup de câmeras ou necessidades multi-site.",
        primaryCta: "Iniciar setup de owner",
        primaryHref: "/court-intelligence/get-started",
        secondaryCta: "Enviar e-mail ao time",
        secondaryHref:
          "mailto:support@whoplays.us?subject=Court%20Intelligence%20Founding%20Venue",
      },
    },
    foundingCiApplication: {
      loading: "Carregando sua inscricao de sede fundadora...",
      backToProgram: "Voltar ao programa de sedes fundadoras",
      signOutToProgram: "Sair e voltar ao programa",
      supportCta: "Enviar email ao suporte",
      signedInAs: "Sessao iniciada como {value}",
      retry: "Tentar novamente",
      loadErrorTitle: "Nao foi possivel carregar o status da sua inscricao.",
      approvedRedirect: "Sua inscricao foi aprovada. Redirecionando para o onboarding do Court Intelligence...",
      intro: {
        eyebrow: "Inscricao de sede fundadora do Court Intelligence",
        title: "Envie sua inscricao de sede fundadora",
        description:
          "Use a mesma conta WhoPlays que voce vai usar no onboarding. Se sua inscricao for aprovada, voce continua no fluxo padrao de onboarding do Court Intelligence.",
      },
      form: {
        title: "Conte sobre sua sede",
        description:
          "Usamos isso para confirmar aderencia, prazo de lancamento e necessidades de suporte da coorte atual.",
        required: "Os campos obrigatorios estao marcados com *.",
        sections: {
          contact: "Contato",
          venue: "Sede",
          operations: "Operacao",
          notes: "Notas",
        },
        fields: {
          contactName: "Nome do contato",
          contactEmail: "Email",
          contactPhone: "Telefone",
          organizationName: "Nome da organizacao ou operadora",
          venueName: "Nome da sede",
          venueType: "Tipo de sede",
          city: "Cidade",
          state: "Estado",
          country: "Pais",
          courtsCount: "Quantidade estimada de quadras",
          hasExistingCameras: "Voce ja tem cameras instaladas?",
          operationsModel: "Como a sede opera hoje?",
          targetLaunchTiming: "Prazo alvo de lancamento",
          willingToGiveFeedback: "Voce aceita compartilhar feedback operacional durante o lancamento?",
          notes: "Existe algo que devemos saber antes da revisao?",
        },
        options: {
          empty: "Selecione uma opcao",
          boolean: {
            yes: "Sim",
            no: "Nao",
            notSure: "Nao tenho certeza",
          },
          venueType: {
            hoa: "HOA",
            private_club: "Clube privado",
            city: "Cidade ou secretaria de parques",
            community: "Sede comunitaria",
            other: "Outro",
          },
          operationsModel: {
            reservations: "Reservas",
            open_play: "Open play",
            both: "Ambos",
            unknown: "Desconhecido",
          },
          targetLaunchTiming: {
            asap: "O quanto antes",
            within_30_days: "Dentro de 30 dias",
            within_60_days: "Dentro de 60 dias",
            later: "Mais tarde",
            exploring: "Ainda explorando",
          },
        },
      },
      validation: {
        contactName: "Informe seu nome.",
        contactMethod: "Adicione um email ou telefone.",
        organizationName: "Informe o nome da organizacao ou operadora.",
        venueType: "Selecione o tipo de sede.",
        city: "Informe a cidade.",
        state: "Informe o estado.",
        courtsCount: "Informe uma quantidade valida de quadras.",
      },
      errors: {
        load: "Nao foi possivel carregar sua inscricao.",
        submit: "Nao foi possivel enviar sua inscricao.",
      },
      actions: {
        submit: "Enviar inscricao",
        submitting: "Enviando...",
        reviewProgram: "Revisar detalhes do programa",
      },
      summary: {
        title: "Resumo da inscricao",
        organization: "Organizacao",
        venue: "Sede",
        location: "Localizacao",
        courts: "Quadras",
        submittedAt: "Enviada em",
        status: "Status",
        statusReason: "Nota da revisao",
      },
      states: {
        submitted: {
          badge: "Em revisao",
          title: "Inscricao recebida",
          description:
            "Recebemos sua inscricao e vamos revisar aderencia, prontidao de lancamento e capacidade atual da coorte.",
          helper:
            "Se for aprovada, voce vai continuar pelo fluxo normal de onboarding e checkout do Court Intelligence.",
          summaryNote: "Sua inscricao esta registrada. Nao e necessario fazer mais nada enquanto revisamos.",
        },
        waitlisted: {
          badge: "Lista de espera",
          title: "Sua sede esta na lista de espera",
          description:
            "A coorte atual esta cheia ou perto do limite. Vamos manter sua sede na fila para a proxima abertura.",
          helper: "Voce nao precisa enviar novamente. Entraremos em contato se surgir uma vaga fundadora.",
          summaryNote: "Estamos mantendo sua inscricao para uma futura abertura.",
        },
        rejected: {
          badge: "Nao aprovada",
          title: "Esta inscricao nao vai avancar agora",
          description:
            "Sua sede nao esta na coorte fundadora atual. Ainda assim voce pode falar conosco se o timing ou a configuracao mudarem.",
          helper: "Se houver uma nota de revisao abaixo, essa e a orientacao mais relevante da nossa equipe.",
          summaryNote: "Voce pode referenciar esta inscricao se falar com o suporte depois.",
        },
      },
    },
    subscriptionPlans: {
      hero: {
        eyebrow: "Preços flexíveis",
        title: "Planos feitos para cada organizador",
        description:
          "Comece gratuitamente e evolua para automações profissionais quando seus programas crescerem. Troque de plano sempre que precisar.",
        note: "Todos os planos incluem lembretes de eventos, insights de presença e acompanhamento de participação.",
      },
      courtIntelCallout: {
        title: "Você opera uma quadra ou programa municipal?",
        description:
          "Court Intelligence foi criado para operadores e inclui busyness ao vivo, insights de fila e análises históricas. Quando uma quadra ativa o recurso, jogadores também ganham visibilidade ao vivo grátis no WhoPlays US.",
        note: "Compra de hardware e instalação no local não estão incluídas. Cada quadra fornece e configura suas próprias câmeras.",
        primaryLabel: "Ver planos do Court Intelligence",
        primaryHref: "/court-intelligence",
      },
      cards: [
        {
          id: "free",
          name: "Free",
          badge: "Comece aqui",
          priceMonthly: "Gratuito / mês",
          priceYearly: "Gratuito / ano",
          description: "Ideal para experimentar os fluxos de agendamento básicos com grupos pequenos e confirmações manuais.",
          features: [
            "Agendamentos ilimitados sem convites",
            "Convide até 5 jogadores por evento",
            "15 convites SMS por mês",
            "Insights do calendário e notificações",
          ],
          ctaLabel: "Criar conta grátis",
        },
        {
          id: "basic",
          name: "Scheduling Basic",
          badge: "Mais popular",
          priceMonthly: "$2.99 / mês",
          priceYearly: "$24.99 / ano",
          description: "Administre drop-ins semanais com listas de convite maiores e lembretes SMS automáticos.",
          features: [
            "Até 5 agendas ativas com convites",
            "Convide até 20 jogadores por evento",
            "75 convites SMS por mês",
            "Experiência sem anúncios com a sua marca",
          ],
          ctaLabel: "Escolher Basic",
          highlighted: true,
        },
        {
          id: "pro",
          name: "Scheduling Pro",
          badge: "Melhor custo-benefício",
          priceMonthly: "$4.99 / mês",
          priceYearly: "$39.99 / ano",
          description:
            "Desbloqueie convites ilimitados, entrega prioritária de SMS e escale seus programas sem limites.",
          features: [
            "Agendas ilimitadas com convites",
            "Jogadores ilimitados por evento",
            "150 convites SMS por mês",
            "Entrega prioritária de SMS para confirmações mais rápidas",
          ],
          ctaLabel: "Migrar para Pro",
        },
      ],
      table: {
        title: "Compare o que está incluído",
        description:
          "Todos os planos mantêm as ferramentas principais ativas. Faça upgrade quando precisar de mais convites ou confirmações mais rápidas.",
        featureColumnLabel: "Recurso",
        planLabels: {
          free: "Free",
          basic: "Scheduling Basic",
          pro: "Scheduling Pro",
        },
        rows: [
          {
            label: "Máx. agendas ativas sem convites",
            values: { free: "Ilimitadas", basic: "Ilimitadas", pro: "Ilimitadas" },
          },
          {
            label: "Máx. agendas ativas com convites",
            values: { free: "1", basic: "5", pro: "Ilimitadas" },
          },
          {
            label: "Máx. jogadores por agenda",
            values: { free: "5", basic: "20", pro: "Ilimitados" },
          },
          {
            label: "Máx. quadras por evento",
            values: { free: "1", basic: "1", pro: "1" },
          },
          {
            label: "Máx. convites SMS por mês",
            values: { free: "15", basic: "75", pro: "150" },
          },
          {
            label: "Insights de presença (quem confirmou?)",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Lembretes e notificações",
            values: { free: "check", basic: "check", pro: "check" },
          },
          {
            label: "Experiência sem anúncios",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Janela de agendamento (dias antecipados)",
            values: { free: "30", basic: "30", pro: "30" },
          },
          {
            label: "Organizador fora da lista principal",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. eventos round robin por mês",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. jogadores por round robin",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. torneios eliminação simples/dúplice por mês",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Máx. jogadores por torneio",
            values: { free: "cross", basic: "cross", pro: "cross" },
          },
          {
            label: "Entrega prioritária de SMS",
            values: { free: "cross", basic: "check", pro: "check" },
          },
          {
            label: "Preço mensal",
            values: { free: "Gratuito", basic: "$2.99", pro: "$4.99" },
          },
          {
            label: "Preço anual",
            values: { free: "Gratuito", basic: "$24.99", pro: "$39.99" },
          },
        ],
        legend: {
          check: "Incluído",
          cross: "Não incluído",
        },
      },
      cta: {
        title: "Precisa de acesso para equipe ou torneios?",
        description:
          "Os formatos competitivos chegam em breve. Entre como organizador fundador para influenciar o roadmap e garantir preços antecipados.",
        primaryLabel: "Fale conosco",
        primaryHref: "/founding-organizers",
        secondaryLabel: "Ver atualizações do produto",
        secondaryHref: "/bugtracker",
      },
      footnote: "Gerencie upgrades diretamente no app móvel do WhoPlays. Valores em USD.",
    },
    foundingOrganizers: {
      hero: {
        eyebrow: "Programa de organizadores fundadores",
        title: "Administre sua liga de pickleball melhor — e grátis para sempre",
        highlight: "Os primeiros 100 organizadores ganham acesso vitalício ao WhoPlays US.",
        description:
          "Organize eventos, automatize confirmações e ajude a definir os próximos recursos. Mantenha seus grupos ativos e liberaremos o Scheduler Pro vitalício quando as assinaturas forem lançadas.",
        badges: [
          {
            id: "google-play",
            label: "Disponível no Google Play",
            href: "https://play.google.com/store/apps/details?id=com.whosplays.app&pli=1",
            image: "/images/google-play-badge.png",
            width: 209,
            height: 63,
            className: "h-18 w-auto",
          },
          {
            id: "app-store",
            label: "Baixar na App Store",
            href: "https://apps.apple.com/app/apple-store/id6451442623?pt=126911496&ct=Founding%20Organizers&mt=8",
            image: "/images/apple-store-badge.svg",
            width: 175,
            height: 60,
            className: "h-12 w-auto",
          },
        ],
        primaryCtaLabel: "Agende seu primeiro evento",
          primaryCtaHref: "/app-stores",
      },
      lifetime: {
        title: "Scheduler Pro vitalício para organizadores ativos",
        description:
          "Quando as assinaturas estiverem no ar, concederemos acesso Pro vitalício aos organizadores que permanecerem engajados.",
        bullets: [
          "Use o WhoPlays para agendar e conduzir seus eventos.",
          "Realize pelo menos um evento a cada 60 dias.",
          "Mantenha sua comunidade crescendo e manteremos o Pro liberado.",
        ],
        footnote: "É a nossa forma de agradecer aos líderes que constroem o futuro do pickleball conosco.",
      },
      benefits: {
        title: "Benefícios principais",
        items: [
          "Agende partidas em segundos",
          "Convide jogadores por SMS — sem precisar de app",
          "Limite vagas e acompanhe confirmações",
          "Veja quem joga por nível e horário",
          "Gerencie seus eventos como um profissional",
        ],
      },
      reminder: {
        headline: "Apenas 100 organizadores ganham acesso vitalício gratuito",
        subheadline: "Garanta sua vaga antes que acabe.",
      },
      video: {
        title: "Veja o agendamento do WhoPlays em ação",
        src: "/videos/WhoPlays-Scheduler-Web-Version.mp4",
        poster: "https://whoplays.us/wp-content/uploads/2025/03/Image.png",
      },
    },
    privacyPolicy: {
      title: "Política de Privacidade",
      lastUpdated: "Última atualização: 30 de março de 2025",
      intro: [
        "Esta Política de Privacidade descreve nossas políticas e procedimentos sobre a coleta, o uso e a divulgação das suas informações ao utilizar o Serviço, e explica seus direitos de privacidade e como a lei o protege.",
        "Usamos seus dados pessoais para fornecer e aprimorar o Serviço. Ao utilizar o Serviço, você concorda com a coleta e o uso das informações de acordo com esta Política de Privacidade.",
      ],
      sections: [
        {
          heading: "Interpretação e definições",
          subSections: [
            {
              subheading: "Interpretação",
              paragraphs: [
                "As palavras com iniciais em maiúscula têm significados definidos nas condições abaixo. As seguintes definições têm o mesmo significado, independentemente de aparecerem no singular ou no plural.",
              ],
            },
            {
              subheading: "Definições",
              paragraphs: ["Para os fins desta Política de Privacidade:"],
              definitions: [
                {
                  term: "Conta",
                  description: "Significa uma conta exclusiva criada para que você acesse nosso Serviço ou partes dele.",
                },
                {
                  term: "Afiliada",
                  description:
                    "Significa uma entidade que controla, é controlada por ou está sob controle comum com uma parte.",
                },
                {
                  term: "Aplicativo",
                  description:
                    "Refere-se ao WhoPlays US – Pickleball Scheduler & Tracker, o software fornecido pela Empresa.",
                },
                {
                  term: "Empresa",
                  description:
                    '("a Empresa", "Nós" ou "Nosso") refere-se a WhoPlays US, Inc, 45 Whistler Trace, Ponte Vedra FL 32081.',
                },
                {
                  term: "País",
                  description: "Refere-se a: Flórida, Estados Unidos.",
                },
                {
                  term: "Dispositivo",
                  description:
                    "Significa qualquer dispositivo que possa acessar o Serviço, como um computador, um celular ou um tablet.",
                },
                {
                  term: "Dados Pessoais",
                  description: "São todas as informações relacionadas a uma pessoa identificada ou identificável.",
                },
                { term: "Serviço", description: "Refere-se ao Aplicativo." },
                {
                  term: "Fornecedor de serviços",
                  description:
                    "Significa qualquer pessoa física ou jurídica que processe dados em nome da Empresa.",
                },
                {
                  term: "Dados de Uso",
                  description:
                    "Referem-se aos dados coletados automaticamente, gerados pelo uso do Serviço ou pela própria infraestrutura do Serviço.",
                },
                {
                  term: "Você",
                  description: "Significa o indivíduo que acessa ou utiliza o Serviço.",
                },
              ],
            },
          ],
        },
        {
          heading: "Coleta e uso dos seus dados pessoais",
          subSections: [
            {
              subheading: "Dados pessoais",
              paragraphs: ["Podemos coletar informações pessoais como:"],
              bullets: [
                "Endereço de e-mail",
                "Nome e sobrenome",
                "Número de telefone",
                "Endereço, estado, província, CEP/código postal e cidade",
              ],
            },
            {
              subheading: "Dados de uso",
              paragraphs: [
                "Isso inclui seu endereço IP, tipo e versão do navegador, páginas visitadas, data e hora da visita, tempo gasto e outros dados de diagnóstico.",
              ],
            },
            {
              subheading: "Dados coletados pelo aplicativo",
              paragraphs: ["Com sua permissão, podemos coletar informações adicionais:"],
              bullets: [
                "Dados de localização",
                "Contatos (para a função de convites)",
                "Acesso à câmera e à galeria de fotos (para fotos de perfil ou de eventos)",
              ],
              footnote: "Você pode ativar ou desativar o acesso a esses dados a qualquer momento nas configurações do dispositivo.",
            },
            {
              subheading: "Uso dos seus dados pessoais",
              paragraphs: ["Utilizamos seus dados para as seguintes finalidades:"],
              bullets: [
                "Fornecer e manter nosso Serviço",
                "Gerenciar sua conta",
                "Facilitar convites por SMS e notificações do aplicativo",
                "Gerenciar assinaturas e validar direitos de uso",
                "Oferecer suporte e responder a solicitações",
                "Informar sobre atualizações, ofertas e mudanças",
                "Analisar desempenho e uso para melhorias",
              ],
            },
            {
              subheading: "Serviços de assinatura",
              paragraphs: [
                "WhoPlays US oferece assinaturas gratuitas, básicas e profissionais, além de assinaturas específicas para torneios. Para fins promocionais, alguns usuários podem receber acesso vitalício. Reservamo-nos o direito de revogar esse acesso se o usuário não tiver agendado um evento ou utilizado recursos essenciais em um período de 60 dias.",
                "As assinaturas feitas pelo aplicativo podem ser gerenciadas pela Apple App Store ou pelo Google Play. Essas assinaturas seguem as políticas de cobrança, cancelamento e renovação definidas por essas plataformas.",
              ],
            },
          ],
        },
        {
          heading: "Dados de câmeras no Court Intelligence",
          paragraphs: [
            "O Court Intelligence recebe snapshots enviados por câmeras configuradas pela sede via API push ou upload FTPS. O WhoPlays não se conecta à rede local da sede para capturar imagens.",
            "Por padrão, os snapshots são processados para calcular sinais operacionais (como lotação e espera) e não são armazenados.",
          ],
          subSections: [
            {
              subheading: "Modo debug e comportamento de inicialização",
              bullets: [
                "Se o usuário da sede ativar o modo debug do grupo de câmeras, snapshots podem ser mantidos para melhoria de ML até a expiração configurada.",
                "Enquanto a câmera permanecer em estado de inicialização, apenas o snapshot mais recente é mantido e substituído por snapshots mais novos.",
                "Quando a câmera sai da inicialização (por exemplo, ativa ou inativa), esse comportamento de snapshot de inicialização termina.",
              ],
            },
            {
              subheading: "Visibilidade para operadores e jogadores",
              bullets: [
                "Operadores da sede podem remover a configuração na câmera a qualquer momento para interromper o envio de snapshots.",
                "Jogadores no WhoPlays US veem indicadores ao vivo de lotação e espera em sedes habilitadas, não controles de configuração de câmeras.",
              ],
            },
          ],
        },
        {
          heading: "Retenção e exclusão de dados",
          paragraphs: [
            "Seus dados são mantidos apenas pelo tempo necessário para fins legais, operacionais e analíticos.",
            "Você pode excluir sua conta ou solicitar a exclusão dos seus dados pelo aplicativo ou entrando em contato conosco.",
          ],
        },
        {
          heading: "Divulgação legal e segurança",
          paragraphs: [
            "Podemos divulgar seus dados quando exigido por lei, para proteger nossos direitos ou para garantir a segurança dos usuários.",
            "Utilizamos meios comercialmente aceitáveis para proteger seus dados, mas não podemos garantir segurança absoluta.",
          ],
        },
        {
          heading: "Privacidade de crianças",
          paragraphs: [
            "Não coletamos intencionalmente informações de menores de 13 anos. Se você acredita que coletamos dados de uma criança, entre em contato conosco.",
          ],
        },
        {
          heading: "Alterações a esta Política de Privacidade",
          paragraphs: [
            "Podemos atualizar esta Política de Privacidade. Se mudanças materiais forem realizadas, você será notificado por e-mail ou por um aviso no aplicativo.",
            "Todas as alterações entram em vigor quando publicadas.",
          ],
        },
      ],
      contact: {
        heading: "Fale conosco",
        paragraphs: ["Se tiver dúvidas, entre em contato conosco:"],
        entries: [{ label: "E-mail", value: "support@whoplays.us" }],
      },
    },
    mySchedule: {
      back: "Voltar ao app",
      title: "Minha agenda",
      create: "Criar evento",
      edit: "Editar",
      delete: "Excluir",
      empty: "Sem partidas agendadas.",
      untitled: "Partida agendada",
      startLabel: "Início",
      endLabel: "Fim",
      typeLabel: "Tipo",
      courtLabel: "Quadra",
      loading: "Carregando agenda...",
      refreshError: "Não foi possível atualizar sua agenda.",
      cancel: "Cancelar",
      confirmCancel: "Cancelar este horário?",
    },
    myScheduleEditor: {
      titleCreate: "Agendar evento",
      titleEdit: "Editar evento",
      subtitle: "Defina horário, quadra, formato e convites.",
      encourage: "Convide amigos — mesmo sem app — para deixar o jogo mais divertido.",
      dateLabel: "Data",
      startLabel: "Hora de início",
      endLabel: "Hora de término",
      courtLabel: "Quadra",
      formatLabel: "Formato",
      invitesLabel: "Convites",
      invitesCount: "Convidados: {count}",
      inviteCta: "Convidar jogadores ({count})",
      limitToggle: "Habilitar limite de confirmação",
      limitLabel: "Definir limite",
      limitHelper: "Começa em 4 quando o limite é habilitado.",
      limitLocked: "O limite aparece após convidar 4 jogadores.",
      saveCreate: "Salvar evento",
      saveEdit: "Salvar alterações",
      reset: "Reiniciar",
      validation: {
        dateRequired: "Selecione uma data.",
        courtRequired: "Selecione uma quadra.",
        endAfterStart: "A hora de término deve ser depois da de início.",
        conflict: "Você já tem um jogo agendado nesse horário.",
        saveError: "Falha ao salvar. Tente novamente.",
        saveSuccess: "Evento salvo com sucesso.",
      },
      noCourt: "Você precisa de uma quadra primária para agendar um jogo.",
    },
    myScheduleInvite: {
      title: "Enviar convites",
      selected: "Selecionados {count} convidados",
      back: "Voltar à agenda",
      addGuest: "Adicionar convidado",
      playerNamePlaceholder: "Nome do jogador",
      phonePlaceholder: "Número de telefone",
      add: "Adicionar",
      contactsUnavailable: "Adicionar dos contatos (indisponível)",
      contactsAvailable: "Adicionar dos contatos",
      myGroup: "Meu grupo",
      following: "Seguindo",
      searchPlaceholder: "Buscar jogadores",
      noPlayers: "Nenhum jogador encontrado.",
      validation: {
        nameEmpty: "O nome não pode ficar vazio. Informe o nome do convidado.",
        phoneEmpty: "O número de telefone não pode ficar vazio. Informe um telefone válido.",
        phoneNumeric: "O telefone deve conter apenas dígitos.",
        phoneLength: "Número dos EUA inválido. Deve ter exatamente 10 dígitos.",
        phoneInvalid: "Número de telefone inválido.",
        selfNumber: "Você adicionou seu próprio número. Você já está incluído como anfitrião.",
        duplicateGuest: "Este jogador já foi adicionado como convidado neste horário.",
        registered: "Este jogador já está registrado no WhoPlays. Adicionamos a conta existente: {name}.",
      },
    },
  },
} as const;

export const AVAILABLE_LOCALES = Object.entries(messages).map(([value, config]) => ({
  value: value as Locale,
  label: config.languageName,
}));

export function isLocale(value: string | null | undefined): value is Locale {
  if (!value) return false;
  return value === "en" || value === "es" || value === "pt";
}

export type Translations = (typeof messages)[Locale];
